import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { BOOK_GLYPH, CAMERA_GLYPH } from '@shared/components/SkillGlyph'
import { createPortal, flushSync } from 'react-dom'
import type { Connection, Edge, Node, NodeChange, OnNodeDrag, SelectionDragHandler } from '@xyflow/react'
import {
  ControlButton,
  Controls,
  NodeToolbar,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  addEdge,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import type { Asset, LibraryKey, SkillDefinition } from '@shared/types'
import type { CanvasDocument, CanvasNode, CanvasPage, ImageRef, ProjectFile } from '@shared/services'
import {
  EMPTY_CANVAS,
  REF_DRAG_TYPE,
  copyLibraryToProject,
  copyProjectFiles,
  decodeRefs,
  getAssetRepository,
  listProjectFiles,
  loadCanvas,
  planFor,
  measure,
  resolveImages,
  runSkill,
  saveCanvas,
  sendToMiroQueue,
  smartFor,
  uploadProjectFile,
  withRetry,
} from '@shared/services'
import { ImageNode, SkillNode } from './nodes'
import { sendMoodboard, stageMoodboardPicture, takeMiroPieces } from '@shared/services/miroLayouts'
import { moodboardFromFrame } from '@shared/moodboard/frame'
import type { SentMoodboard } from '@shared/moodboard/send'
import { cutForBoard } from '../pages/moodboardRender'
import type { ImageNodeData, SkillNodeData } from './nodes'
import { PictureNode } from './pictureNode'
import type { PictureNodeData } from './pictureNode'
import { UpscaleNode, type UpscaleNodeData } from './upscaleNode'
import { FIRST_PORT, LAST_PORT, MAX_MIDDLE_PORTS, MID_PORT, VideoNode, partsFor, type VideoNodeData } from './videoNode'
import { AngleNode, type AngleNodeData } from './angleNode'
import { VectorNode, type VectorNodeData } from './vectorNode'
import { BackgroundNode, type BackgroundNodeData } from './backgroundNode'
import { EraserNode, paintStrokes, strokesOf, type EraseStroke, type EraserNodeData } from './eraserNode'
import { BRIA_CREDITS, BRIA_MODEL_LABEL, runBria } from '@shared/services/briaService'
import { GRADE_DEFAULT_NAME, GradeNode, type GradeNodeData } from './gradeNode'
import { defaultGrade, normaliseGrade, type GradeSettings } from './grade/gradeSettings'
import type { StudioPicture } from './grade/GradeStudio'
import { MoodboardNode, type MoodboardNodeData } from './moodboardNode'
import { WorkflowNode, type WorkflowNodeData } from './workflow/workflowNode'
import { WorkflowBuilder } from './workflow/WorkflowBuilder'
import { WORKFLOW_SKILL_ID, isToolbarWorkflow, useWorkflows } from './workflow/useWorkflows'
import { portOf, runLeftovers } from './workflow/workflowModel'
import { fromSaved, type SavedBoard } from '../moodboard/savedBoard'
import { buildBoard, layOut, paletteOf, readPictures } from '../moodboard/MoodboardBoard'
import { cutMoodboardFrame } from '../moodboard/cutFrame'
import type { SourcePicture } from '../pages/MoodboardStart'
import { encodeRef } from '@shared/services/imageRefs'
import { FAL_EXPAND_CREDITS, expandWithFal } from '@shared/services/expandService'
import { deleteProjectFile } from '@shared/services/projectFileService'
import { vectorize } from './vectorize'
import { vectorizeWithFal } from '../../shared/services/vectorizeService'
import { angleWords, startAngle } from '../../shared/services/angleService'
import { NoteNode, type NoteNodeData } from './noteNode'
import { DEFAULT_LABEL_STYLE, LabelNode, type LabelNodeData } from './labelNode'
import type { TextStyle } from '@shared/data/miroFonts'
import { EditNode, type EditNodeData, type EditStudio } from './editNode'
import { TextNode, type TextBlend, type TextNodeData, type TextPending, type TextStudio } from './textNode'
import { ColourPicker } from './ColourPicker'
import {
  blobOf,
  compose,
  cropped,
  defaultMask,
  fileOf,
  fit,
  loadBitmap,
  maskedPatch,
  patchLayer,
  planText,
  prepareEdit,
  scaledCopy,
  turnedCanvas,
  type ComposeRecipe,
  type TextLine,
} from './pictureOps'
import {
  awaitUpscale,
  startUpscale,
  takes,
  upscaleOptions,
  type UpscaleOptions,
} from '../../shared/services/upscaleService'
import { usableSize } from '../../shared/components/UpscaleControls'
import { awaitVideo, startVideo, videoOptions, type VideoModel } from '../../shared/services/videoService'
import {
  CANVAS_EDIT_MODEL,
  CANVAS_EDIT_QUALITY,
  awaitRun,
  detectCanvasText,
  estimateCredits,
  listRuns,
  pictureModels,
  sizeFor,
  startCanvasEdit,
  startPictures,
  type HistoryRun,
  type PictureModel,
  type PictureRun,
} from '../../shared/services/pictureService'
import type { MadeWith, MadeWithFound } from './MadeWith'
import { SkillRail } from './SkillRail'
import { QuickBar, QuickSkills } from './QuickSkills'
import { readPref, savePref } from '@shared/services/prefsService'
import { readUi, writeUi } from '../uiPrefs'
import { boardMoved, setFullSizeDistance } from './fullSize'
import { GroupScale, type Side } from './groupScale'
import { BoardGrid } from './boardGrid'
import { SelectionFilter, selectKind, type SelectKind } from './selectionFilter'
import { around, neighbourGaps, snapEdge, snapMove, type Segment } from './snap'
import { SnapGuides } from './snapGuides'
import { FrameNode, type FrameNodeData } from './frameNode'
import {
  DEFAULT_FRAME_COLOR,
  EMPTY_FRAME,
  FRAME_Z,
  boxOf,
  bounds,
  clearSpot,
  fitInFrame,
  frameAround,
  frameOf,
  isFrame,
  readFrameColor,
  readingOrder,
  reshape,
  rows,
  settle,
  writeFrameColor,
  type Rect,
} from './frames'
import { fullSize, keepIntoProject, searchAmazon, type Market } from '../../shared/services/amazonService'
import { keepWebIntoProject, searchUnsplash, searchWebImages } from '../../shared/services/webImageService'
import { markStockUsed } from '../../shared/services/stockService'
import { LibraryNode, SOURCE_NAMES, kindOf, sourceOf as librarySourceOf, type LibraryNodeData, type LibrarySource } from './libraryNode'
import type { ClipItem } from '../../shared/services/projectClip'
import {
  putClip,
  readClip as readProjectClip,
  takeClip,
  watchClip,
  type ProjectClip,
} from '../../shared/services/projectClip'
import './canvas.css'
/** The corner's Download and Send to Miro buttons; hidden for now (Huanrui, 2026-10-19). */
const SHOW_CORNER = false

const nodeTypes = { library: LibraryNode, image: ImageNode, skill: SkillNode, picture: PictureNode, upscale: UpscaleNode, video: VideoNode, angle: AngleNode, vector: VectorNode, grade: GradeNode, moodboard: MoodboardNode, workflow: WorkflowNode, frame: FrameNode, note: NoteNode, edit: EditNode, text: TextNode, label: LabelNode, background: BackgroundNode, eraser: EraserNode }
/** Steady values for React Flow's props: a new array each render is a change to announce. */
const PAN_WITH_ANY = [0, 1, 2]
const PAN_WITH_MIDDLE = [1, 2]
const PRO_OPTIONS = { hideAttribution: true }

/** The one skill that is a card with a form in it rather than a labelled port. */
const PICTURE_SKILL = 'skill-image-generation'
/** Upscale is a card with settings too (upscaleNode.tsx), 2026-10-05. */
const UPSCALE_SKILL = 'skill-upscale'
/** Video Generation is a card with two ports and settings (videoNode.tsx), 2026-10-08. */
const VIDEO_SKILL = 'skill-video-generation'

/**
 * The sticky note (noteNode.tsx, 2026-10-06), offered in the rail and the
 * wheel's quick skills as if it were a skill, so it is put down, ordered and
 * ticked the same way. It is not one: it runs nothing, costs nothing and
 * exists only on the board, so it is made here rather than listed among the
 * platform's skills.
 */
const EDIT_SKILL: SkillDefinition = {
  id: 'canvas-edit-area',
  slug: 'edit-area',
  name: 'Edit area',
  summary: 'Change one part of a picture; the rest stays as it is',
  category: 'render',
  status: 'available',
  glyph: '⬚',
  inputs: [],
  usesPaidApi: true,
  area: 'design',
}

/**
 * Edit text (textNode.tsx, Huanrui 2026-10-07). It takes the place of the
 * platform's "Edit Text" skill, which was listed as planned and did nothing:
 * the same id, so it keeps that skill's place and colour in the rail and in
 * anyone's quick tools. On the board only for now.
 */
const TEXT_SKILL: SkillDefinition = {
  id: 'skill-edit-text',
  slug: 'edit-text',
  name: 'Edit text',
  summary: 'Change the words on a picture, keeping how they are printed',
  category: 'render',
  status: 'available',
  glyph: 'Aa',
  inputs: [],
  usesPaidApi: true,
  area: 'design',
}

/**
 * Angle (angleNode.tsx, Huanrui 2026-10-09): the wired picture seen from
 * somewhere else, FLUX 2 through fal (routes/angles.ts). On the board only,
 * so made here like Edit area rather than listed among the platform's skills.
 */
const ANGLE_SKILL: SkillDefinition = {
  id: 'canvas-angle',
  slug: 'angle',
  name: 'Angle',
  summary: 'See the picture from another side, above or closer',
  category: 'render',
  status: 'available',
  glyph: '⟲',
  inputs: [],
  usesPaidApi: true,
  area: 'design',
}

/**
 * Background and Eraser (backgroundNode.tsx, eraserNode.tsx, Huanrui
 * 2026-10-27): Bria's models through fal (routes/bria.ts). On the board only.
 */
const BACKGROUND_SKILL: SkillDefinition = {
  id: 'canvas-background',
  slug: 'background',
  // Background remove only (Huanrui, 2026-10-27: Replace taken out).
  name: 'Background remove',
  summary: 'Background remove',
  category: 'render',
  status: 'available',
  glyph: '◩',
  inputs: [],
  usesPaidApi: true,
  area: 'design',
}
const ERASER_SKILL: SkillDefinition = {
  id: 'canvas-eraser',
  slug: 'eraser',
  // Clean up (Huanrui, 2026-10-27; was Eraser): it takes things out, it does not make pictures.
  name: 'Clean up',
  summary: 'Clean up',
  category: 'render',
  status: 'available',
  glyph: '⌫',
  inputs: [],
  usesPaidApi: true,
  area: 'design',
}
/** Bria is sent a copy no larger than this in all; the finished picture is put together at full size. */
const BRIA_MODEL_PX = 4_000_000

/**
 * Vectorize (vectorNode.tsx, Huanrui 2026-10-09): the wired picture traced
 * into an SVG in this browser, free (vectorize.ts). On the board only.
 */
const VECTOR_SKILL: SkillDefinition = {
  id: 'canvas-vectorize',
  slug: 'vectorize',
  name: 'Vectorize',
  summary: 'Trace a picture into an SVG: logos, line art, flat graphics',
  category: 'render',
  status: 'available',
  glyph: '✒',
  inputs: [],
  usesPaidApi: false,
  area: 'design',
}

/**
 * Photo adjust (gradeNode.tsx; Colour grade from 2026-10-15, renamed
 * 2026-10-30 when Geometry joined it): Camera Raw's colour work and its
 * Geometry on the wired pictures, in this browser, free. On the board only.
 * The id stays, so cards and rails from before keep it.
 */
const GRADE_SKILL: SkillDefinition = {
  id: 'canvas-colour-grade',
  slug: 'colour-grade',
  name: 'Photo adjust',
  summary: 'Adjust a photo like Camera Raw: colour, tone, curves, masks, geometry',
  category: 'render',
  status: 'available',
  glyph: CAMERA_GLYPH,
  inputs: [],
  usesPaidApi: false,
  area: 'design',
}

/**
 * The full-screen studio of the Colour grade card, loaded the first time
 * somebody presses Adjust, so the board itself downloads none of it.
 */
const GradeStudio = lazy(() => import('./grade/GradeStudio'))

/**
 * Mood board (moodboardNode.tsx, Huanrui 2026-10-16): the pictures of a
 * wired frame laid out as a 16:9 mood board, made as a frame beside the
 * card. Free, in this browser. On the board only.
 */
const MOODBOARD_SKILL: SkillDefinition = {
  id: 'canvas-moodboard',
  slug: 'moodboard-card',
  name: 'Mood board',
  summary: "Lay out a frame's pictures as a 16:9 mood board",
  category: 'render',
  status: 'available',
  glyph: '▦',
  inputs: [],
  usesPaidApi: false,
  area: 'design',
}

/** The Mood board card's full-screen studio, loaded the first time Adjust is pressed. */
const MoodboardStudio = lazy(() => import('../moodboard/MoodboardStudio'))
/** A Library card, full screen (LibraryBrowser.tsx, 2026-10-16). */
const LibraryBrowser = lazy(() => import('./LibraryBrowser'))

/**
 * The pictures in some frames, as the Mood board card takes them: each
 * picture once, top to bottom then left to right within a frame; the colour
 * swatches of an earlier mood board and pictures still being made left out.
 */
function picturesInFrames(frameIds: string[], list: Node[]): Node[] {
  const out: Node[] = []
  const seen = new Set<string>()
  for (const frameId of frameIds) {
    const members = list
      .filter((node) => node.type === 'image' && frameOf(node) === frameId && refOf(node) !== null)
      .filter((node) => {
        const data = node.data as Record<string, unknown>
        return !data.swatch && !data.making
      })
      .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)
    for (const node of members) {
      const ref = refOf(node)!
      const key = `${ref.store}:${ref.id}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push(node)
    }
  }
  return out
}

/** At most this many parts in one Apply of Edit text; each part is one Sunburst run. */
const MAX_TEXT_PARTS = 6

/**
 * Edit area (editNode.tsx, 2026-10-06): a card on the board only, for now --
 * not on the overview, the skills page or Miro, so it is made here like the
 * sticky note rather than listed among the platform's skills. Its runs go
 * through POST /api/canvas-edit.
 */
const NOTE_SKILL: SkillDefinition = {
  id: 'canvas-note',
  slug: 'note',
  name: 'Note',
  summary: 'A sticky note on the board',
  category: 'compose',
  status: 'available',
  glyph: '✎',
  inputs: [],
  usesPaidApi: false,
  area: 'design',
}

/**
 * Text set in type (labelNode.tsx, Huanrui 2026-10-12): Miro's typefaces, a
 * size, bold, alignment and colour. On the board only, and like the note it
 * runs nothing, so it is made here.
 */
/**
 * Library (libraryNode.tsx, 2026-10-13): a card on the board only. Words, a
 * library and a number; Search puts down a frame of that many pictures.
 * Two cards since 2026-10-14 (Huanrui): Internal Library, every library of
 * ours at once, and External Library, Amazon, Google or Unsplash. The
 * internal one keeps the old card's id, so where it sat on the rail stays.
 */
const LIBRARY_SKILL: SkillDefinition = {
  id: 'canvas-library',
  slug: 'library',
  name: 'Internal Library',
  summary: 'A frame of pictures from Audit, Visual Language and Design References, picked blind from a search',
  category: 'compose',
  status: 'available',
  glyph: BOOK_GLYPH,
  inputs: [],
  usesPaidApi: false,
  area: 'research',
}

const EXTERNAL_LIBRARY_SKILL: SkillDefinition = {
  id: 'canvas-external-library',
  slug: 'external-library',
  name: 'External Library',
  summary: 'A frame of pictures from Amazon or Google Images, picked from a search',
  category: 'compose',
  status: 'available',
  glyph: '◎',
  inputs: [],
  usesPaidApi: true,
  area: 'research',
}

const LABEL_SKILL: SkillDefinition = {
  id: 'canvas-text',
  slug: 'text',
  name: 'Text',
  summary: 'Words set in type: typeface, size, weight, alignment, colour',
  category: 'compose',
  status: 'available',
  glyph: 'T',
  inputs: [],
  usesPaidApi: false,
  area: 'design',
}

/**
 * How the board itself looks.
 *
 * Two things, cycled by two buttons in the corner. Kept apart because they are
 * apart in use: the grid is about being able to line things up, the paper is
 * about what the pictures are being looked at against — a cut-out pack shot on
 * white is a very different judgement from the same shot on black, and that is
 * the whole of a designer's afternoon.
 *
 * The first entry of each is the default, so a board nobody has touched looks
 * exactly as it did before this existed.
 */
const GRIDS = ['dots', 'lines', 'none'] as const
/** Expand (2026-10-26): the most pixels Bria is asked to work at, and the largest finished picture. */
const EXPAND_MODEL_PX = 8_000_000
const EXPAND_MOST_PX = 60_000_000
/** Full-size pictures kept read, for the cards that work on them (sourceOf). */
const FULL_SIZE_KEPT = 3
type Grid = (typeof GRIDS)[number]

const GRID_NAME: Record<Grid, string> = { dots: 'Dots', lines: 'Grid', none: 'No grid' }

/**
 * The paints.
 *
 * Greys, end to end, because that is what this platform is — and because the
 * thing being judged on the board has its own colours and should be the only
 * thing on screen that does. The first is the board as it always was.
 */
/**
 * The board's paper is any grey from white to black, chosen on a slider
 * (Huanrui, 2026-10-02) -- it was six fixed swatches. The slider runs white
 * at the left to black at the right; a paper is kept as its hex, as before,
 * so a board painted with one of the old swatches opens as it was.
 */
function greyFromSlider(at: number): string {
  const level = Math.round(255 * (1 - Math.min(100, Math.max(0, at)) / 100))
  const hex = level.toString(16).padStart(2, '0')
  return `#${hex}${hex}${hex}`
}
function sliderFromPaper(hex: string): number {
  const n = parseInt(hex.slice(1), 16)
  const lum = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255
  return Math.round((1 - lum) * 100)
}

/**
 * A paper's colour moved a little towards black (on a light paper) or white
 * (on a dark one): close enough to belong to the board, far enough to be seen
 * on it. The page tabs are drawn in these (2026-10-02).
 */
function shadeOf(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16)
  const toward = isPale(hex) ? 0 : 255
  const mix = (channel: number) => Math.round(channel + (toward - channel) * amount)
  const r = mix((n >> 16) & 255)
  const g = mix((n >> 8) & 255)
  const b = mix(n & 255)
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`
}

/**
 * Is this paint light enough that white would disappear on it?
 *
 * Perceived brightness, not the average of the three channels: the eye reads
 * green as far brighter than blue, and an average calls #0000ff and #00ff00
 * equally dark. Everything the board draws in white — the wires, the ports,
 * the corner controls — flips at the same moment, off this one number.
 */
export function isPale(hex: string): boolean {
  const n = parseInt(hex.slice(1), 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 > 0.55
}

/**
 * Remembered in this browser rather than in the database.
 *
 * It is a way of looking, not a fact about the board: two people opening the
 * same project may honestly want different ones, and nothing about the work
 * changes either way. Wrapped because storage throws outright in a private
 * window, and a board that will not open because it could not read a
 * preference would be a poor trade.
 */
const VIEW_KEY = 'ww.canvas.view'

interface BoardView {
  grid: Grid
  /** A paint from the list above, as a hex string. */
  paper: string
}

const PLAIN: BoardView = { grid: 'dots', paper: '#0a0a0a' }

/** The paper this browser last had, for the page to start in the right set of colours. */
export function readBoardPaper(): string {
  return readView().paper
}

/**
 * Changed on this page since it loaded (2026-10-21). The paper is the
 * person's, not the project's: one paper on every project. The account's copy
 * is fetched each time a project opens, and could arrive older than a change
 * made a moment ago in another project (the save waits for the slider to
 * stop) -- so once it has been changed here, this browser's copy wins.
 */
let viewChangedHere = false

function readView(): BoardView {
  try {
    const raw = JSON.parse(window.localStorage.getItem(VIEW_KEY) ?? '{}') as Record<string, unknown>
    return {
      grid: GRIDS.includes(raw.grid as Grid) ? (raw.grid as Grid) : PLAIN.grid,
      // Checked rather than taken as written: this comes back from storage,
      // which anything on this machine can have put anything in. Any six-digit
      // colour is a paper now (the slider makes greys; the old swatches were
      // greys too).
      paper: typeof raw.paper === 'string' && /^#[0-9a-f]{6}$/i.test(raw.paper)
        ? raw.paper
        : PLAIN.paper,
    }
  } catch {
    return PLAIN
  }
}

function writeView(view: BoardView, keep = true) {
  try {
    window.localStorage.setItem(VIEW_KEY, JSON.stringify(view))
  } catch {
    /* the board still works; the account's copy still has it */
  }
  if (keep) viewChangedHere = true
  // With the account too (2026-10-02), so the grid and the paper follow the
  // person to another computer. A slider's run of changes sends only the last.
  if (keep) savePref('board', view)
}

/** The account's grid and paper, if it has them and they make sense. */
function viewFrom(value: unknown): BoardView | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  if (!GRIDS.includes(raw.grid as Grid)) return null
  if (typeof raw.paper !== 'string' || !/^#[0-9a-f]{6}$/i.test(raw.paper)) return null
  return { grid: raw.grid as Grid, paper: raw.paper }
}

/** The mark on the grid button: four dots, which is what a grid is here. */
function GridMark() {
  return (
    <svg viewBox="0 0 14 14" aria-hidden="true">
      <g fill="currentColor">
        <circle cx="4.5" cy="4.5" r="1.3" />
        <circle cx="9.5" cy="4.5" r="1.3" />
        <circle cx="4.5" cy="9.5" r="1.3" />
        <circle cx="9.5" cy="9.5" r="1.3" />
      </g>
    </svg>
  )
}

/** The mark on the paper button: a disc, half of it filled. */
function PaperMark() {
  return (
    <svg viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="7" r="5.2" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M7 1.8a5.2 5.2 0 0 1 0 10.4z" fill="currentColor" />
    </svg>
  )
}

/**
 * Node kinds that are a piece of media — something that can be downloaded and
 * put on a Miro board. A set rather than `type === 'image'` so that the day a
 * video node exists, it is one word here and not a hunt through the file.
 */
const MEDIA_NODES = new Set(['image'])

/**
 * Everything that does work, as opposed to everything that holds a picture.
 *
 * A set for the same reason MEDIA_NODES is one, and written after learning why
 * the hard way. The skill node was the only kind for weeks, so the behaviours
 * that belong to "a card that does work" — the white glow when something is
 * wired in, triple-click to gather up what it is connected to — were written
 * as `node.type === 'skill'`. Adding the picture card gave it none of them,
 * and neither absence announced itself: the card simply sat there not glowing
 * and not responding to three clicks, looking like two separate small bugs
 * rather than one missing idea.
 *
 * So the idea has a name now. A new card added to this set gets the lot.
 */
const SKILL_NODES = new Set(['skill', 'picture', 'upscale', 'video', 'angle', 'vector', 'grade', 'moodboard', 'workflow', 'edit', 'text', 'background', 'eraser'])
/** The cards that fold: a form when open, a title bar when folded. */
const FOLDABLE = new Set(['picture', 'upscale', 'video', 'angle', 'vector', 'grade', 'moodboard', 'workflow', 'edit', 'text', 'library', 'background', 'eraser'])
/** What a card knows about its runs: never put back by undo. */
const CARD_RUN_KEYS = ['runs', 'running', 'progress', 'error', 'credits', 'madeCount', 'tried', 'blend', 'applying', 'pending', 'preparing', 'detecting', 'updating']
/** What a picture a run made holds: never put back by undo. Its place, size and turn are. */
const PICTURE_RUN_KEYS = ['assetId', 'fileId', 'from', 'made', 'runId', 'runIndex', 'making', 'drawing', 'aspect', 'compose', 'clip']

/**
 * Skills that take a frame: every picture in it, in no particular order.
 *
 * Empty, on purpose. The frame has its ports from the start (Huanrui,
 * 2026-09-30), and the skills that will use them come later; the ones there
 * are today take pictures one wire at a time and are not given frames. A
 * skill joins by its ref being added here -- and nothing else changes.
 */
const FRAME_SKILLS = new Set<string>(['canvas-moodboard'])

/*
 * Saving one picture to disk.
 *
 * `<a download>` is ignored the moment the link points at another origin — and
 * every picture here is a signed Supabase link, which is exactly that. The
 * browser is not being awkward: honouring it would let any page write a file
 * named whatever it liked out of someone else's server. So what it does
 * instead is navigate, and the picture opens full-screen in a tab.
 *
 * Fetching the bytes first makes them this page's own, and `download` is
 * respected again. It needs the store to allow the read from this origin;
 * when it does not, there is nothing left but the tab, and saying so is
 * better than a tab appearing for no stated reason.
 */
const ALREADY_NAMED = /\.(png|jpe?g|webp|gif|svg|avif|mp4|mov)$/i
const EXTENSION: Record<string, string> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/svg+xml': '.svg',
  'image/avif': '.avif',
  // A video from a Video card (videoNode.tsx).
  'video/mp4': '.mp4',
  'video/quicktime': '.mov',
}

function saveAs(title: string, type: string): string {
  // Windows refuses these outright; the others make a mess of a shell.
  const clean = (title || 'image').replace(/[\\/:*?"<>|]/g, '').trim() || 'image'
  return ALREADY_NAMED.test(clean) ? clean : clean + (EXTENSION[type] ?? '.png')
}

async function savePicture(url: string, title: string): Promise<void> {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const blob = await response.blob()
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = saveAs(title, blob.type)
  document.body.appendChild(link)
  link.click()
  link.remove()
  // Late, deliberately: Safari has not finished reading the blob when click()
  // returns, and revoking on the next line hands it an empty file.
  window.setTimeout(() => URL.revokeObjectURL(href), 10_000)
}

/**
 * The same picture, as PNG bytes.
 *
 * Clipboards take PNG and very little else, so anything else is redrawn as
 * one. The bytes come from `fetch` rather than from the <img> already on the
 * screen because drawing a picture from another origin into a canvas taints
 * it, and a tainted canvas refuses to hand its bytes back.
 */
async function pngOf(url: string, turn = 0): Promise<Blob> {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const blob = await response.blob()
  if (blob.type === 'image/png' && turn % 360 === 0) return blob

  // Turned on the board, it is copied turned (Huanrui, 2026-10-06): pasted
  // back onto a board, or anywhere else, it is the picture as it was seen.
  const bitmap = await createImageBitmap(blob)
  const canvas = turnedCanvas(bitmap, turn)
  bitmap.close()
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (made) => (made ? resolve(made) : reject(new Error('Could not encode it'))),
      'image/png',
    )
  })
}

/**
 * Put a picture on the system clipboard, so it can be pasted into Miro — or
 * into anything else that takes a picture.
 *
 * The promise is handed to `ClipboardItem` rather than awaited first. A write
 * has to happen inside the gesture that asked for it, and awaiting the fetch
 * spends that gesture before the write is ever reached: the copy then fails,
 * in Chrome, with nothing but a console line to say why.
 */
async function copyPicture(url: string, turn = 0): Promise<void> {
  if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
    throw new Error('This browser cannot copy pictures')
  }
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngOf(url, turn) })])
}

/** The mark on the download button: into a tray, which is where it goes. */
function DownloadMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M10 3v9m0 0 3.5-3.5M10 12 6.5 8.5M4 15.5h12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** How long after the last change the canvas is written. */
const SAVE_AFTER_MS = 900
/** How many steps back Ctrl+Z can go. */
const HISTORY = 50

/**
 * Gives back what an Edit area or Edit text card held: the model's patches
 * and their masks. A canvas set to no size lets go of its memory at once,
 * where waiting for the browser to notice could take minutes (2026-10-15).
 *
 * Not the full-size picture (2026-10-26): that is the one every card and step
 * shares (sourceOf), and emptying it here left the next card that asked for
 * it a picture of no size -- the page stopped with "drawImage ... width or
 * height of 0". It goes when the few kept are replaced.
 */
function releaseStudio(held: unknown): void {
  if (!held || typeof held !== 'object') return
  const free = (one: unknown) => {
    if (one instanceof HTMLCanvasElement) {
      one.width = 0
      one.height = 0
    }
  }
  const h = held as { patch?: { layer?: unknown; mask?: unknown } | null; patches?: { layer?: unknown; mask?: unknown }[] }
  if (h.patch) {
    free(h.patch.layer)
    free(h.patch.mask)
  }
  for (const p of h.patches ?? []) {
    free(p.layer)
    free(p.mask)
  }
}

/** A Mac: its right-click menu comes as the button goes down (startPan). */
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent)

/** Somewhere words are typed: a text area, a text field, or editable text. */
function inTextBox(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null
  if (!el) return false
  return Boolean(
    el.closest(
      'textarea, input:not([type]), input[type="text"], input[type="search"], [contenteditable=""], [contenteditable="true"]',
    ),
  )
}

let seq = 0
const newId = (kind: string) => `${kind}-${Date.now().toString(36)}-${(seq += 1).toString(36)}`

/**
 * The card a tool puts on the board, not yet on it: dragged out of the rail,
 * or picked from the quick tools on a picture (QuickSkills.tsx), which then
 * wires the picture into it. Not the sticky note, which is not a card.
 */
function skillCard(skill: SkillDefinition, glow: string, position: { x: number; y: number }): Node {
  const base = { skillRef: skill.id, name: skill.name, glyph: skill.glyph, glow }
  // Every card with a form in it arrives open, however it was put down --
  // dragged from the rail, picked from a picture's quick tools, or any other
  // way (Huanrui, 2026-10-06): a card is put down to be filled in. It folds
  // only when somebody folds it.
  if (skill.id === EDIT_SKILL.id) {
    return {
      id: newId('edit'),
      type: 'edit',
      position,
      data: { ...base, open: true, prompt: '' } satisfies EditNodeData,
    } as Node
  }
  if (skill.id === TEXT_SKILL.id) {
    return {
      id: newId('text'),
      type: 'text',
      position,
      data: { ...base, open: true } satisfies TextNodeData,
    } as Node
  }
  if (skill.id === VIDEO_SKILL) {
    return {
      id: newId('video'),
      type: 'video',
      position,
      data: { ...base, open: true, prompt: '', seconds: 5, audio: false } satisfies VideoNodeData,
    } as Node
  }
  if (skill.id === ANGLE_SKILL.id) {
    return {
      id: newId('angle'),
      type: 'angle',
      position,
      data: { ...base, open: true, horizontal: 45, vertical: 15, zoom: 5 } satisfies AngleNodeData,
    } as Node
  }
  if (skill.id === LIBRARY_SKILL.id || skill.id === EXTERNAL_LIBRARY_SKILL.id) {
    const external = skill.id === EXTERNAL_LIBRARY_SKILL.id
    return {
      id: newId('library'),
      type: 'library',
      position,
      data: {
        ...base,
        open: true,
        prompt: '',
        kind: external ? 'external' : 'internal',
        source: external ? 'google' : 'internal',
        n: 20,
      } satisfies LibraryNodeData,
    } as Node
  }
  if (skill.id === BACKGROUND_SKILL.id) {
    return {
      id: newId('background'),
      type: 'background',
      position,
      data: { ...base, open: true } satisfies BackgroundNodeData,
    } as Node
  }
  if (skill.id === ERASER_SKILL.id) {
    return {
      id: newId('eraser'),
      type: 'eraser',
      position,
      data: { ...base, open: true, strokes: [] } satisfies EraserNodeData,
    } as Node
  }
  if (skill.id === VECTOR_SKILL.id) {
    return {
      id: newId('vector'),
      type: 'vector',
      position,
      data: { ...base, open: true, style: 'colour', colours: 8, detail: 'balanced' } satisfies VectorNodeData,
    } as Node
  }
  // A new Colour grade card starts with nothing changed (Huanrui, 2026-10-15).
  if (skill.id === GRADE_SKILL.id) {
    return {
      id: newId('grade'),
      type: 'grade',
      position,
      data: { ...base, open: true, grade: defaultGrade(), gradeName: GRADE_DEFAULT_NAME } satisfies GradeNodeData,
    } as Node
  }
  // A new Mood board card keeps nothing yet: it starts from the pictures' colours.
  if (skill.id === MOODBOARD_SKILL.id) {
    return {
      id: newId('moodboard'),
      type: 'moodboard',
      position,
      data: { ...base, open: true } satisfies MoodboardNodeData,
    } as Node
  }
  if (skill.id === UPSCALE_SKILL) {
    return {
      id: newId('upscale'),
      type: 'upscale',
      position,
      data: { ...base, open: true, size: '2k', face: false } satisfies UpscaleNodeData,
    } as Node
  }
  // The picture skill is a card with a form in it, not a labelled port, so it
  // is a different node type rather than the same one with a flag.
  if (skill.id === PICTURE_SKILL) {
    return {
      id: newId('picture'),
      type: 'picture',
      position,
      data: { ...base, open: true, prompt: '', n: 1 } satisfies PictureNodeData,
    } as Node
  }
  return {
    id: newId('skill'),
    type: 'skill',
    position,
    data: base satisfies SkillNodeData,
  } as Node
}

/**
 * Is the person typing?
 *
 * Every shortcut below asks this first. The chat sits immediately to the left
 * of this canvas, so without it, copying a sentence out of the composer would
 * copy three pictures instead — the single most likely way for a canvas with
 * keyboard shortcuts to become annoying.
 */
function typing(): boolean {
  // A full-screen layer over the board (LibraryBrowser.tsx): the board's own
  // keys -- Delete, undo, paste, Space -- rest until it closes (2026-10-16).
  if (document.querySelector('[data-canvas-layer]')) return true
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable
}

/**
 * Is there text selected somewhere on the page -- a line of the conversation,
 * a caption -- that Ctrl+C should copy as text? (Huanrui, 2026-10-22.)
 *
 * `typing()` only knows about boxes being typed in. Text selected in a chat
 * message is not in one, so with cards still selected on the board, Ctrl+C
 * copied the cards instead and the clipboard held the board's marker
 * ("wonderworks-canvas:..."), which other apps paste as a link. Selected text
 * is what the person means to copy; the board steps aside.
 *
 * BUT ONLY IF THE LAST PRESS WAS OFF THE BOARD. Pressing a card does not
 * clear the page's text selection (the board takes the press for itself), so
 * a sentence selected in the chat earlier stayed "selected" and Ctrl+C on the
 * board copied it instead of the cards -- which broke copy and paste on the
 * board. The last press says which one the person is working in.
 */
let pressedOnBoard = false
function textSelected(): boolean {
  if (pressedOnBoard) return false
  const chosen = window.getSelection()
  return Boolean(chosen && !chosen.isCollapsed && chosen.toString().trim())
}

/**
 * Which picture a node shows, and turned how: what an Eraser card's paint
 * belongs to (eraserNode.tsx). Another picture, or the same one turned, is a
 * clean card.
 */
function pictureKeyOf(node: Node): string {
  const data = node.data as ImageNodeData
  const turn = Number(data.rotate) || 0
  return data.assetId ? `library:${data.assetId}:${turn}` : data.fileId ? `project:${data.fileId}:${turn}` : ''
}

/**
 * A copy for Bria: no larger than BRIA_MODEL_PX in all (the same canvas when
 * it is already small enough). White behind it when it is to go as a JPEG.
 */
function modelCopy(picture: HTMLCanvasElement, png: boolean): HTMLCanvasElement {
  const k = Math.min(1, Math.sqrt(BRIA_MODEL_PX / Math.max(1, picture.width * picture.height)))
  if (k >= 1 && png) return picture
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(16, Math.round(picture.width * k))
  canvas.height = Math.max(16, Math.round(picture.height * k))
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser could not draw the picture')
  if (!png) {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(picture, 0, 0, canvas.width, canvas.height)
  return canvas
}

/** What is written down: positions and wiring, never signed links or callbacks. */
/**
 * What the server wrote on a picture a model made: the prompt and settings,
 * and the run. Nothing for an upload, or for a file with no prompt recorded.
 */
function recordOf(file: ProjectFile | undefined): { made: MadeWith; runId: string } | null {
  if (!file || file.origin !== 'generated') return null
  const by = file.made_by ?? {}
  // An upscaled picture (routes/upscale.ts): no prompt, its own record.
  const up = by.upscale as { model?: unknown; size?: unknown; face?: unknown } | undefined
  if (up && typeof up === 'object') {
    return {
      runId: typeof by.runId === 'string' ? by.runId : '',
      made: {
        prompt: '',
        model: String(up.model ?? ''),
        size: String(up.size ?? '').toUpperCase(),
        quality: '',
        at: file.created_at,
        upscale: {
          face: typeof up.face === 'boolean' ? up.face : null,
          pixels: typeof by.size === 'string' ? by.size.replace('x', ' × ') : '',
        },
      },
    }
  }
  if (typeof by.prompt !== 'string') return null
  return {
    runId: typeof by.runId === 'string' ? by.runId : '',
    made: {
      prompt: by.prompt,
      model: String(by.model ?? ''),
      size: String(by.size ?? 'auto'),
      quality: String(by.quality ?? 'auto'),
      at: file.created_at,
    },
  }
}

function toDocument(nodes: Node[], edges: Edge[], pages: CanvasPage[]): CanvasDocument {
  return {
    pages,
    nodes: nodes.map((node) => {
      const data = node.data as Record<string, unknown>
      const kept: Record<string, unknown> = {}
      for (const key of [
        'assetId', 'fileId', 'title', 'from', 'skillRef', 'name', 'glyph', 'glow',
        // What was typed into a picture card. Kept because it is the work:
        // a board reopened without its prompts is a board of empty forms.
        'prompt', 'model', 'size', 'quality', 'n', 'open',
        // An Upscale card's face switch (upscaleNode.tsx).
        'face',
        // An Angle card's place to look from (angleNode.tsx).
        'horizontal',
        'vertical',
        'zoom',
        // A Vectorize card's settings (vectorNode.tsx).
        'engine',
        'style',
        'colours',
        'detail',
        // A Colour grade card's settings and its name (gradeNode.tsx).
        'grade',
        'gradeName',
        // A Mood board card's sheet (moodboardNode.tsx, 2026-10-16).
        'board',
        // A workflow card's workflow and its page; a picture or card one of
        // its runs made (canvas/workflow, 2026-10-16).
        'workflow',
        'wfPage',
        'wfRun',
        // A Library card's library (libraryNode.tsx); its words are `prompt`, its number `n`.
        'source',
        // Internal or External Library (2026-10-14).
        'kind',
        // A Video card's length and sound (videoNode.tsx), and a frame that
        // holds, or is waiting for, a video rather than a picture.
        'seconds',
        'audio',
        'clip',
        // A Video card told part by part, and its parts (Kling v4, 2026-10-27).
        'perPart',
        'parts',
        // What is written on a sticky note (noteNode.tsx) or a text (labelNode.tsx).
        'text',
        // A text's typeface, size, weight, alignment and colour (labelNode.tsx).
        'textStyle',
        // The box drawn on an Edit area card, and what it keeps to blend its
        // last result again (editNode.tsx).
        'box',
        'blend',
        // A run the card is still waiting on, so a reload carries on (finishEdit).
        'pending',
        // What an Edit text card read, what each line should say, and from
        // which picture (textNode.tsx).
        'lines',
        'readFor',
        // ...and the part of it to read, when one was chosen (Area).
        'area',
        // How a frame waiting for an Edit area is put together
        // with its original when the model's picture arrives (pictureOps.ts).
        'compose',
        // A frame still waiting for its picture: which run, and which picture
        // of it. What lets a reload go on waiting instead of losing it.
        'runId', 'runIndex',
        // How a card made this picture: the prompt and settings behind the
        // corner button, which have to outlive the card being edited again.
        'made',
        // The frame a picture is in (frames.ts). A frame's own name is `name`,
        // and its colour `color`.
        'frame',
        'color',
        // A mood board's frame, and the colour squares of its strip
        // (shared/moodboard/frame.ts): kept, so a reload still knows them
        // (2026-10-16; they were lost on saving before).
        'moodboard',
        'swatch',
        // How a picture is turned for looking at; the picture is not changed.
        'rotate',
        // Marked from the right-click menu: a gold edge (2026-10-06).
        'mark',
        // A Background card's Remove or Replace; an Eraser card's paint and
        // the picture it was painted on (2026-10-27).
        'mode',
        'strokes',
        'strokesFor',
      ]) {
        if (data[key] !== undefined) kept[key] = data[key]
      }
      return {
        id: node.id,
        type: (node.type ?? 'image') as CanvasNode['type'],
        page: (node as { page?: string }).page,
        position: node.position,
        width: node.width ?? undefined,
        height: node.height ?? undefined,
        data: kept,
      }
    }),
    edges: edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      targetHandle: edge.targetHandle ?? undefined,
    })),
  }
}

/**
 * An edge, with its ends named.
 *
 * Every node has one `in` on the left and one `out` on the right, and an edge
 * has to say so. React Flow looks the handle up by name to find out where to
 * start and finish the line; an edge that names neither is drawn from the
 * node's top-left corner, nowhere near the port it appears to come out of.
 *
 * That is not hypothetical: what is written down for a board is the id, the
 * source and the target, and nothing else — so every wire lost its names the
 * first time the board was saved, and came back attached to the corners. Put
 * back here, on the way in, which also repairs every board saved before now.
 */
function wire(edge: {
  id: string
  source: string
  target: string
  targetHandle?: string | null
}): Edge {
  return {
    ...edge,
    sourceHandle: 'out',
    // Named if the edge says so, `in` if it does not. It used to be `in`
    // always, which was true right up until a node had more than one input:
    // a card that makes pictures has a numbered port per reference and no
    // port called `in`, so every reloaded wire attached to nothing and was
    // drawn from the node's top-left corner.
    targetHandle: edge.targetHandle || 'in',
  }
}

/**
 * Where a drag is, whichever kind of pointer is doing it.
 *
 * React Flow hands back a mouse event or a touch event; only one of the two
 * carries the coordinates on itself.
 */
function pointerOf(event: MouseEvent | TouchEvent): { x: number; y: number } | null {
  if ('clientX' in event) return { x: event.clientX, y: event.clientY }
  const touch = event.changedTouches?.[0] ?? event.touches?.[0]
  return touch ? { x: touch.clientX, y: touch.clientY } : null
}

/**
 * An Edit area card's ports (editNode.tsx): `in` is the picture being edited;
 * `ref-1`, `ref-2` ... are more pictures for the model to look at. A wire with
 * no port named is the picture being edited, as wires were before the
 * reference ports existed.
 */
const isEditMain = (edge: Edge) => !edge.targetHandle || edge.targetHandle === 'in'

/** The picture a node holds, or nothing if it does not hold one. */
function refOf(node: Node): ImageRef | null {
  if (!MEDIA_NODES.has(node.type ?? '')) return null
  const data = node.data as ImageNodeData
  if (data.assetId) return { store: 'library', id: data.assetId }
  if (data.fileId) return { store: 'project', id: data.fileId }
  return null
}

/**
 * A picture node's size with the picture's OWN proportions, at its width.
 *
 * Not the same as `sizeOf` when a node's box is not the picture's shape. A
 * picture fills its box with `object-fit: cover`, so a box saved as 260x200
 * shows a 16:9 picture cropped to 13:10 -- and anything that lines pictures
 * up by their boxes sees them as already equal and leaves them uneven. The
 * boards "Create project" made were all 260x200, and so were their copies.
 *
 * Read off the picture element itself, which knows its natural size once it
 * has loaded. Falls back to the box for a node with no picture showing.
 */
function pictureSizeOf(node: Node): { w: number; h: number } {
  const box = sizeOf(node)
  if (!MEDIA_NODES.has(node.type ?? '')) return box
  const img = document.querySelector<HTMLImageElement>(
    `.react-flow__node[data-id="${CSS.escape(node.id)}"] img.cnode__img`,
  )
  if (!img || img.naturalWidth <= 0 || img.naturalHeight <= 0) return box
  // Turned a quarter, the picture's shape on the board is the other way round.
  const turn = Number((node.data as { rotate?: unknown }).rotate) || 0
  const [nw, nh] = turn % 180 === 90 ? [img.naturalHeight, img.naturalWidth] : [img.naturalWidth, img.naturalHeight]
  return { w: box.w, h: Math.round((box.w * nh) / nw) }
}

/**
 * The box of a picture turned a quarter: the turned picture's shape, in the
 * room its saved box has (the same area), so neither a box saved the old way
 * round nor one given only a width can draw it cropped or larger.
 */
function turnedBox(width: number, height: number | undefined, shape: number): { width: number; height: number } {
  if (!height) return { width, height: Math.round(width / shape) }
  const area = width * height
  return { width: Math.round(Math.sqrt(area * shape)), height: Math.round(Math.sqrt(area / shape)) }
}

/** What a node actually takes up, measured if it has been, guessed if not. */
function sizeOf(node: Node): { w: number; h: number } {
  return {
    w: node.measured?.width ?? node.width ?? 220,
    h: node.measured?.height ?? node.height ?? 160,
  }
}

/**
 * Whether two versions of what a node is handed are the same: the same
 * position, size, selection and look, and every value in its data the very
 * same. Handed the same object as last time, React Flow does not redraw it.
 */
/** Every value in two nodes' data the very same. */
function sameData(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key])
}

function sameNode(a: Node, b: Node): boolean {
  if (
    a.position !== b.position ||
    a.width !== b.width ||
    a.height !== b.height ||
    a.selected !== b.selected ||
    a.dragging !== b.dragging ||
    a.className !== b.className ||
    a.hidden !== b.hidden ||
    a.zIndex !== b.zIndex ||
    a.measured !== b.measured
  ) {
    return false
  }
  const x = a.data as Record<string, unknown>
  const y = b.data as Record<string, unknown>
  const keys = Object.keys(x)
  if (keys.length !== Object.keys(y).length) return false
  return keys.every((key) => x[key] === y[key])
}

/**
 * A node the selection box being drawn would pick up: marked, so it can be
 * drawn as selected (canvas.css, .ww-pending) without being selected.
 */
function withPending(node: Node, pending: Set<string>, build: () => Node): Node {
  const built = build()
  return pending.has(node.id)
    ? { ...built, className: [built.className, 'ww-pending'].filter(Boolean).join(' ') }
    : built
}

/** Space between a skill and the things either side of it. */
const FAMILY_GAP = 130
/** Space between two things stacked in the same column. */
const STACK_GAP = 28
/**
 * How tall an empty frame is assumed to be while laying out a run.
 *
 * A guess, and unavoidably so: the pictures do not exist yet, so nothing knows
 * their shape. It only decides where the frames start; once the pictures
 * arrive they take their own height and the column settles.
 */
const FRAME_HEIGHT = 170

/**
 * The page every board has, including the ones made before pages existed.
 *
 * Their nodes carry no page at all, and rather than rewriting them the loader
 * reads "no page" as "this one". A migration for something that can be
 * answered by a default is a migration that can go wrong for nothing.
 */
const FIRST_PAGE = { id: 'page-1', name: 'Page 1' }

/** The page last open on a board, kept with the account (uiPrefs.ts). */
const PAGE_KEY = 'ww.canvas.page.'

function readLastPage(projectId: string): string {
  return readUi(PAGE_KEY + projectId, '', (saved) => typeof saved === 'string')
}

function writeLastPage(projectId: string, pageId: string): void {
  writeUi(PAGE_KEY + projectId, pageId)
}

/**
 * The ceiling, until the server says otherwise.
 *
 * Five runs at once per person, counted across the board, the skills page and
 * the Miro panel together — they all spend from one account, so a limit that
 * counted each surface separately would not be a limit.
 *
 * The real arithmetic is the server's: it can see all three and a browser can
 * see one. This is only what the button greys on before the first answer comes
 * back, and it is replaced by the number the server sends with every start and
 * every status.
 */
const MAX_LIVE_RUNS = 5

/**
 * A copy of a card is a new card, not a copy of what it is doing.
 *
 * Duplicating a card mid-run used to hand over `running` and the id of a run
 * belonging to the original — so the copy sat saying "Making…" for ever,
 * waiting for something that was never going to report to it, and its own
 * Run button was disabled the whole time.
 *
 * What is worth carrying is what was typed and chosen; what is happening is
 * not a property of the card, it is a property of the moment.
 */
function freshCopy(data: Record<string, unknown>): Record<string, unknown> {
  // `making`, `drawing` and `runIndex` too: a copy of a frame still waiting
  // is not waiting for anything, and would otherwise wait for ever.
  const { runs, error, credits, madeCount, runId, running, making, drawing, runIndex, compose: recipe, blend, pending, preparing, detecting, applying, ...kept } =
    data
  void runs, error, credits, madeCount, runId, running, making, drawing, runIndex, recipe, blend, pending, preparing, detecting, applying
  return kept
}

/** The most a single picture may be (the Worker's own limit, routes/projectFiles.ts). */
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024
const megabytes = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`

/** Everything reachable from one node, in either direction along the wires. */
function connectedTo(start: string, edges: Edge[]): Set<string> {
  const near = new Map<string, string[]>()
  for (const edge of edges) {
    near.set(edge.source, [...(near.get(edge.source) ?? []), edge.target])
    near.set(edge.target, [...(near.get(edge.target) ?? []), edge.source])
  }
  const seen = new Set([start])
  const queue = [start]
  while (queue.length > 0) {
    for (const next of near.get(queue.shift()!) ?? []) {
      if (seen.has(next)) continue
      seen.add(next)
      queue.push(next)
    }
  }
  return seen
}

/**
 * Lay a connected set out left to right, and say where each one goes.
 *
 * A column per step along the wires, so the board reads in the order the work
 * happened: what went in, what did it, what came out, and again for whatever
 * was done to that.
 *
 * Nothing is moved by this function — it only works out the positions, so the
 * caller can apply them in the same update as the selection and the whole
 * tidy-up is one step on the undo stack.
 */
function arrange(
  members: Node[],
  edges: Edge[],
  anchor: Node,
  /** Sizes about to be given to some members, used instead of what they measure now. */
  resized: Map<string, { w: number; h: number }> = new Map(),
): Map<string, { x: number; y: number }> {
  const sizeFor = (node: Node) => resized.get(node.id) ?? sizeOf(node)
  const ids = new Set(members.map((node) => node.id))
  const inner = edges.filter((edge) => ids.has(edge.source) && ids.has(edge.target))

  // Which column each one belongs in: the longest run of wires leading to it.
  // Relaxed in passes rather than sorted topologically, because a board is not
  // guaranteed to be a tidy graph — a pass count equal to the number of nodes
  // settles every acyclic case and gives up rather than spinning on a loop.
  const layer = new Map(members.map((node) => [node.id, 0]))
  for (let pass = 0; pass < members.length; pass += 1) {
    let moved = false
    for (const edge of inner) {
      const next = (layer.get(edge.source) ?? 0) + 1
      if (next > (layer.get(edge.target) ?? 0)) {
        layer.set(edge.target, next)
        moved = true
      }
    }
    if (!moved) break
  }

  const columns = new Map<number, Node[]>()
  for (const node of members) {
    const at = layer.get(node.id) ?? 0
    columns.set(at, [...(columns.get(at) ?? []), node])
  }

  const places = new Map<string, { x: number; y: number }>()
  let x = 0
  for (const at of [...columns.keys()].sort((a, b) => a - b)) {
    // Kept in the top-to-bottom order they are already in, so tidying up moves
    // as little as it can: the arrangement should look like the one you had,
    // straightened.
    const group = [...(columns.get(at) ?? [])].sort((a, b) => a.position.y - b.position.y)
    const sizes = group.map(sizeFor)
    const width = Math.max(...sizes.map((size) => size.w))
    const total = sizes.reduce((sum, size) => sum + size.h, 0) + STACK_GAP * (group.length - 1)
    let y = -total / 2
    group.forEach((node, index) => {
      // Centred within its column, so a narrow skill card between two wide
      // pictures does not end up against one edge of the row.
      places.set(node.id, { x: x + (width - sizes[index].w) / 2, y })
      y += sizes[index].h + STACK_GAP
    })
    x += width + FAMILY_GAP
  }

  // Slide the finished arrangement so the thing that was clicked ends up
  // exactly where it already was. Something that jumps out from under the
  // pointer that just asked about it reads as a mistake.
  const home = places.get(anchor.id)
  if (home) {
    const dx = anchor.position.x - home.x
    const dy = anchor.position.y - home.y
    for (const [id, spot] of places) places.set(id, { x: spot.x + dx, y: spot.y + dy })
  }
  return places
}

interface CanvasProps {
  projectId: string
  skills: SkillDefinition[]
  /**
   * Make a project, if there is not one yet, and hand back its id.
   *
   * The canvas calls this the first time it has something worth keeping. A
   * board arranged before a word is typed used to have nowhere to be saved to.
   */
  ensureProject: () => Promise<string>
  /**
   * Where the conversation is on screen, and what to hand it.
   *
   * The board only needs to know the rectangle: it asks for it when a drag
   * ends, so a panel that has since been folded away or made wider is never
   * an old measurement.
   */
  chatArea?: () => DOMRect | null
  /** Called when pictures are dropped onto the conversation. */
  onPicturesToChat?: (refs: ImageRef[]) => void
  /** Called as pictures pass over the conversation, so it can light up. */
  onOverChat?: (over: boolean) => void
  /** The paper went light or dark (2026-10-21): the page around the board takes the same set of colours. */
  onPale?: (pale: boolean) => void
  /** The paper's colour itself, for the full-window conversation, which stands on it. */
  onPaper?: (paper: string) => void
}

/** Keys that add a click to the selection: Shift as well as Ctrl (Cmd). */
const MULTI_SELECT_KEYS = ['Shift', 'Control', 'Meta']

export function ProjectCanvas(props: CanvasProps) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  )
}

function CanvasInner({
  projectId,
  skills,
  ensureProject: makeProject,
  chatArea,
  onPicturesToChat,
  onOverChat,
  onPale,
  onPaper,
}: CanvasProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  /**
   * THE FIRST PICTURE ON A NEW BOARD (fixed 2026-10-09). A board with no
   * project yet asks for one the moment something goes on it. The project's
   * id then arrives, and the effect that opens a board would READ IT BACK
   * from the server -- empty -- and put that on screen, unless the board
   * already had something on it. The first upload or paste asks for the
   * project BEFORE its frame goes down, so the board was still empty, the
   * empty project replaced it, and the picture vanished: "the first one
   * always fails". Asked for from here, the new project is this board, and
   * is never read back over it.
   */
  const adopting = useRef(false)
  const ensureProject = useCallback(async () => {
    adopting.current = true
    try {
      return await makeProject()
    } catch (error) {
      adopting.current = false
      throw error
    }
  }, [makeProject])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [ready, setReady] = useState(false)
  const [note, setNote] = useState('')
  /**
   * Something that did NOT happen, said where it cannot be missed: along the
   * bottom of the board, with a mark, like Miro's own (Huanrui, 2026-10-01).
   * A note in the corner is for what did happen -- copied, saved.
   */
  const [alert, setAlert] = useState('')
  /** What a selection box being drawn would pick up; see `previewBox`. */
  const [pending, setPending] = useState<Set<string>>(() => new Set())
  const [spaceHeld, setSpaceHeld] = useState(false)
  /** How this person likes the board to look. Read once, saved on every change. */
  const [view, setView] = useState(readView)
  // The account's grid and paper win over this browser's once they arrive.
  useEffect(() => {
    let cancelled = false
    readPref('board')
      .then((value) => {
        const kept = viewFrom(value)
        if (!kept || cancelled || viewChangedHere) return
        setView((prev) => (prev.grid === kept.grid && prev.paper === kept.paper ? prev : kept))
        writeView(kept, false)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])
  const [painting, setPainting] = useState(false)
  /**
   * Where the things being dragged started, so they can be put back.
   *
   * Dropping a picture on the conversation is not moving it: the board should
   * look exactly as it did, and the picture should also be attached to the
   * next message. React Flow has already moved it by the time the drag ends,
   * so the only way to have both is to remember where it was.
   */
  const dragFrom = useRef<Map<string, { x: number; y: number }>>(new Map())
  const overChat = useRef(false)
  /**
   * The picture following the cursor once it has left the board.
   *
   * On the board there is nothing to draw — the node itself is moving under
   * the hand, which is as clear as it gets. The moment the cursor crosses into
   * the conversation that stops being true: the node is left behind at the
   * edge, clipped, and the hand is carrying nothing anybody can see. This is
   * that missing half-second.
   */
  const [carry, setCarry] = useState<{ url: string; x: number; y: number; more: number } | null>(
    null,
  )

  /** Is this point inside the conversation? Asked fresh, never remembered. */
  const inChat = useCallback(
    (x: number, y: number) => {
      const box = chatArea?.()
      return Boolean(box && x >= box.left && x <= box.right && y >= box.top && y <= box.bottom)
    },
    [chatArea],
  )
  const pale = isPale(view.paper)
  useEffect(() => {
    onPale?.(pale)
  }, [pale, onPale])
  useEffect(() => {
    onPaper?.(view.paper)
  }, [view.paper, onPaper])

  const toggleGrid = useCallback(() => {
    setView((prev) => {
      const next = { ...prev, grid: GRIDS[(GRIDS.indexOf(prev.grid) + 1) % GRIDS.length] }
      writeView(next)
      setNote(GRID_NAME[next.grid])
      return next
    })
  }, [])

  // Follows the slider as it moves; the slider stays open until a press
  // elsewhere or Escape puts it away.
  const paint = useCallback((value: string) => {
    setView((prev) => {
      if (prev.paper === value) return prev
      const next = { ...prev, paper: value }
      writeView(next)
      return next
    })
  }, [])

  // Put the paints away on Escape or on a press anywhere else. Without this
  // the only way to close them is to choose one, which makes opening them a
  // decision rather than a look.
  useEffect(() => {
    if (!painting) return
    const away = (event: Event) => {
      if (!(event.target as HTMLElement | null)?.closest('.ccanvas__paints, .ccanvas__paint-open')) {
        setPainting(false)
      }
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPainting(false)
    }
    window.addEventListener('pointerdown', away)
    window.addEventListener('keydown', escape)
    return () => {
      window.removeEventListener('pointerdown', away)
      window.removeEventListener('keydown', escape)
    }
  }, [painting])

  const { screenToFlowPosition, fitView, fitBounds, setCenter, zoomTo, getViewport, setViewport, getInternalNode } = useReactFlow()
  /*
   * The title bar's ⋯ menu (ProjectPage, 2026-10-21) does what the three
   * small buttons at the board's bottom left did: fit, grid, board colour.
   */
  useEffect(() => {
    const onAction = (event: Event) => {
      const what = (event as CustomEvent<string>).detail
      if (what === 'fit') void fitView({ padding: 0.2, duration: 300 })
      else if (what === 'grid') toggleGrid()
      else if (what === 'paint') setPainting((on) => !on)
    }
    window.addEventListener('ww-board', onAction)
    return () => window.removeEventListener('ww-board', onAction)
  }, [fitView, toggleGrid])
  /** The board's own box on screen, for working out what part of it is in view. */
  const board = useRef<HTMLElement>(null)

  // Which pictures get their full-size file first: the ones nearest the
  // middle of the view (fullSize.ts). Measured only when the board settles.
  useEffect(() => {
    setFullSizeDistance((id) => {
      const node = getInternalNode(id)
      const box = board.current?.getBoundingClientRect()
      if (!node || !box) return Number.POSITIVE_INFINITY
      const { x, y, zoom } = getViewport()
      const w = node.measured?.width ?? node.width ?? 0
      const h = node.measured?.height ?? node.height ?? 0
      const cx = (node.internals.positionAbsolute.x + w / 2) * zoom + x
      const cy = (node.internals.positionAbsolute.y + h / 2) * zoom + y
      return Math.hypot(cx - box.width / 2, cy - box.height / 2)
    })
  }, [getInternalNode, getViewport])

  /**
   * Moving the board with the middle or right button, from anywhere.
   *
   * React Flow already pans on those buttons, but only where the press lands
   * on the board itself. Over a node it does not, and over a text box or a
   * button inside a card it cannot — those swallow the press before anything
   * else sees it. On a card that is mostly form, that means most of the card
   * is a hole where the board stops moving, which reads as the canvas being
   * stuck rather than as a rule about where you may press.
   *
   * So it is done here, on the wrapper, in the capture phase: before any
   * child has had the chance to keep the press to itself. Left button is
   * untouched, so dragging nodes, drawing wires, selecting and typing all work
   * exactly as they did.
   */
  /** A pan under way; `text`: it began in a text box (the spelling menu, below). */
  const panning = useRef<{ x: number; y: number; vx: number; vy: number; text?: boolean } | null>(null)
  /** The browser's own menu may come once, for a right-click in a text box that did not move. */
  const nativeMenu = useRef(false)

  /**
   * The right-click menu, where the press was: on the board's own box, and
   * the same point on the board.
   *
   * The right button pans here, so the menu is a right CLICK -- pressed and
   * let go without the board moving. A right drag stays a pan.
   */
  /** Replace (right-click, 2026-10-12): which picture the file chooser is answering for. */
  const replaceInput = useRef<HTMLInputElement | null>(null)
  const replacing = useRef('')
  /** "From the canvas": the picture waiting to be replaced by the next one clicked. */
  const [pickFor, setPickFor] = useState('')
  /** The right-click menu's Replace, opened to its three sources. */
  const [replaceOpen, setReplaceOpen] = useState(false)
  const [menu, setMenu] = useState<{ x: number; y: number; flow: { x: number; y: number } } | null>(
    null,
  )
  /**
   * Where the mouse wheel was clicked, which brings up the person's own row of
   * skills there (QuickSkills). A wheel press that moves is a pan, as before.
   */
  const [quick, setQuick] = useState<{ x: number; y: number } | null>(null)
  /**
   * The menu itself, measured once it is drawn. Opening it used to assume a
   * height (440), and the menu has grown past that: opened low on the board,
   * its last items were under the bottom edge (Huanrui, 2026-10-07). Now it
   * is moved up, and left, by what it actually measures.
   */
  const menuBox = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const el = menuBox.current
    const room = el?.offsetParent as HTMLElement | null
    if (!el || !room || !menu) return
    const gap = 8
    const top = Math.max(gap, Math.min(menu.y, room.clientHeight - el.offsetHeight - gap))
    const left = Math.max(gap, Math.min(menu.x, room.clientWidth - el.offsetWidth - gap))
    el.style.top = `${top}px`
    el.style.left = `${left}px`
  }, [menu])
  /** The frame whose eyedropper is out (ColourPicker.tsx). */
  const [pickingFor, setPickingFor] = useState<string | null>(null)
  /** The picture in crop mode (CropBox.tsx), opened by a double-click. */
  const [croppingId, setCroppingId] = useState<string | null>(null)
  /** The Colour grade card whose studio is open (grade/GradeStudio.tsx). */
  const [gradingId, setGradingId] = useState<string | null>(null)
  /** The Mood board card whose studio is open (moodboard/MoodboardStudio.tsx). */
  const [boardingId, setBoardingId] = useState<string | null>(null)
  /** The Library card being browsed full screen (LibraryBrowser.tsx). */
  const [browsingId, setBrowsingId] = useState<string | null>(null)
  const cropSoon = useRef(0)
  /** Copy, paste, duplicate, delete, select all: done by Shortcuts, asked for by the menu. */
  const boardActions = useRef<BoardActions | null>(null)

  /** Where the left button last went down on the board, for the selection box. */
  const pressedAt = useRef<{ x: number; y: number } | null>(null)

  /*
   * THE KNIFE (Huanrui, 2026-10-09). Alt held and the left button pressed on
   * the empty board, or on a wire: a line follows the pointer, and every wire
   * it crosses is cut when the button is let go -- an Alt-click on one wire
   * cuts that one. Alt pressed on a picture is still "leave a copy behind"
   * (onNodeDragStart), so the two never meet. One step on the undo stack.
   */
  const knife = useRef<{ x: number; y: number }[] | null>(null)
  const [knifeLine, setKnifeLine] = useState<{ x: number; y: number }[] | null>(null)
  /** The cut itself, defined further down where the undo stack is (cutAlong). */
  const cutRef = useRef<(points: { x: number; y: number }[]) => void>(() => {})

  const startPan = useCallback(
    (event: React.PointerEvent) => {
      if (event.button === 0 && event.altKey) {
        const target = event.target as Element
        const onBoard = target.closest('.react-flow__pane') || target.closest('.react-flow__edge')
        // Not on a thing, nor on the box round a selection of several, which
        // drags them -- Alt there leaves a copy behind (2026-10-16).
        if (onBoard && !target.closest('.react-flow__node') && !target.closest('.react-flow__nodesselection')) {
          knife.current = [{ x: event.clientX, y: event.clientY }]
          setKnifeLine(knife.current)
          event.currentTarget.setPointerCapture(event.pointerId)
          // Before React Flow sees it: no selection box, no pan, no drag.
          event.stopPropagation()
          event.preventDefault()
          return
        }
      }
      if (event.button === 0) pressedAt.current = { x: event.clientX, y: event.clientY }
      if (event.button !== 1 && event.button !== 2) return
      // Alt + right button in a card's picture window sets the brush there
      // (brushStudio.tsx, 2026-10-27): not a pan of the board.
      if (event.button === 2 && event.altKey && (event.target as Element).closest('.enode__window')) return
      /*
       * THE SPELLING MENU IN TEXT BOXES (Huanrui, 2026-10-27). The browser's
       * spelling suggestions are in its own right-click menu, which the board
       * keeps for itself. In a text box on a card: on Windows a right-drag
       * still moves the board and a right-click that does not move opens the
       * browser's menu (it comes as the button is let go, once the board has
       * seen whether it moved); on a Mac the menu comes as the button goes
       * down, so there a right-press in a text box is the menu and nothing else.
       */
      const text = event.button === 2 && inTextBox(event.target)
      if (text && IS_MAC) return
      const view = getViewport()
      panning.current = { x: event.clientX, y: event.clientY, vx: view.x, vy: view.y, text }
      event.currentTarget.setPointerCapture(event.pointerId)
      // Not in a text box: the browser keeps the word under the pointer for its spelling menu.
      if (!text) event.preventDefault()
    },
    [getViewport],
  )

  /**
   * Where the pointer last was over the board.
   *
   * A paste has no coordinates of its own -- the clipboard event says nothing
   * about where anybody is looking -- so a pasted picture used to land in the
   * middle of the window, which is wherever the board happened to be scrolled
   * to and almost never where the person was pointing. This is the only place
   * that knows, so it is written down as the pointer passes.
   *
   * A ref and not state: it changes on every mouse move and nothing renders
   * from it. As state it would re-render the whole board a hundred times a
   * second to hold a number only a paste ever reads.
   */
  const pointer = useRef<{ x: number; y: number } | null>(null)
  /** The pointer has left the board since it last moved over it. */
  const pointerAway = useRef(true)

  /**
   * Where a keyboard paste lands (Huanrui, 2026-11-02): the pointer, in board
   * coordinates, while it is over the board itself -- the empty board, a node
   * or a wire, not a panel or toolbar floating on it. Null otherwise, and the
   * paste goes just beside the original as it always did.
   */
  const pointerOnBoard = useCallback((): { x: number; y: number } | null => {
    const at = pointer.current
    if (!at || pointerAway.current) return null
    const under = document.elementFromPoint(at.x, at.y)
    if (!under?.closest('.react-flow__pane, .react-flow__node, .react-flow__edge')) return null
    return screenToFlowPosition(at)
  }, [screenToFlowPosition])

  const movePan = useCallback(
    (event: React.PointerEvent) => {
      pointer.current = { x: event.clientX, y: event.clientY }
      pointerAway.current = false
      if (knife.current) {
        const last = knife.current[knife.current.length - 1]!
        if (Math.hypot(event.clientX - last.x, event.clientY - last.y) >= 3) {
          knife.current = [...knife.current, { x: event.clientX, y: event.clientY }]
          setKnifeLine(knife.current)
        }
        event.stopPropagation()
        return
      }
      // A selection box being drawn: show what it would pick up.
      if (boxFrom.current) previewLatest.current()
      const from = panning.current
      if (!from) return
      const view = getViewport()
      setViewport({
        x: from.vx + (event.clientX - from.x),
        y: from.vy + (event.clientY - from.y),
        zoom: view.zoom,
      })
    },
    [getViewport, setViewport],
  )

  const endPan = useCallback(
    (event: React.PointerEvent) => {
      if (knife.current) {
        const line = knife.current
        knife.current = null
        setKnifeLine(null)
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId)
        }
        event.stopPropagation()
        if (event.type === 'pointerup') cutRef.current([...line, { x: event.clientX, y: event.clientY }])
        return
      }
      const from = panning.current
      if (!from) return
      panning.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      if (
        event.type === 'pointerup' &&
        event.button === 1 &&
        Math.hypot(event.clientX - from.x, event.clientY - from.y) < 5
      ) {
        setMenu(null)
        setQuick({ x: event.clientX, y: event.clientY })
        return
      }
      if (
        event.type === 'pointerup' &&
        event.button === 2 &&
        Math.hypot(event.clientX - from.x, event.clientY - from.y) < 5
      ) {
        // In a text box: the browser's menu, with its spelling (above), not the board's.
        if (from.text) {
          nativeMenu.current = true
          return
        }
        // Right-clicking something that is not selected selects it, alone,
        // so the menu is about what was clicked -- as everywhere else.
        const hit = document
          .elementFromPoint(event.clientX, event.clientY)
          ?.closest<HTMLElement>('.react-flow__node')
        const hitId = hit?.dataset.id
        if (hitId) {
          setNodes((prev) =>
            prev.find((node) => node.id === hitId)?.selected
              ? prev
              : prev.map((node) => ({ ...node, selected: node.id === hitId })),
          )
        }
        const box = event.currentTarget.getBoundingClientRect()
        setMenu({
          // Kept inside the board, so a menu opened near an edge is all there.
          x: Math.min(event.clientX - box.left, box.width - 230),
          y: Math.min(event.clientY - box.top, box.height - 440),
          flow: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
        })
      }
    },
    [screenToFlowPosition, setNodes],
  )
  /**
   * True once React Flow has measured every node.
   *
   * Framing the view needs their sizes, and a node that has been put into
   * state has not been drawn yet — asking to fit immediately after loading
   * fits an empty rectangle and leaves the viewport where it was.
   */
  const nodesReady = useNodesInitialized()
  /** The opening frame is set once. After that the view is the person's. */
  const framed = useRef(false)
  const repository = useMemo(() => getAssetRepository(), [])

  /** Signed links, fetched after the arrangement is drawn. Never stored. */
  const [urls, setUrls] = useState<Map<string, Asset>>(new Map())
  /**
   * The shape (width / height) of each picture as it was drawn, by file or
   * asset id: for a picture whose size the store does not say, so a picture
   * turned a quarter still gets a box its shape (2026-10-16).
   */
  const [seenShapes, setSeenShapes] = useState<Map<string, number>>(new Map())
  const noteShape = useCallback((key: string, shape: number) => {
    setSeenShapes((was) => {
      const had = was.get(key)
      if (had && Math.abs(had - shape) < 0.005) return was
      const next = new Map(was)
      next.set(key, shape)
      return next
    })
  }, [])
  /**
   * Every id already asked about, answered or not.
   *
   * Without this, an asset the library no longer has is asked for on every
   * render for ever: it is wanted, it never arrives, so it stays wanted. A
   * quiet infinite loop, and the kind that only appears once somebody deletes
   * an image that is on a board.
   */
  const asked = useRef<Set<string>>(new Set())
  /** The same, for the project's own files. Same reason, different store. */
  const askedFiles = useRef<Set<string>>(new Set())

  /** The project's own images, signed. Read once when the board opens. */
  const [files, setFiles] = useState<Map<string, ProjectFile>>(new Map())

  /**
   * Take a fresh file list without changing a single picture's address.
   *
   * Every read of the list signs every link again, and a new link is a new
   * address, so each read made every picture on the board load again -- the
   * pictures kept flicking back to "loading" whenever anything asked for the
   * list, which several things do. A picture keeps the links it has while
   * they are good (signed for two hours; renewed after ninety minutes), and
   * only new files, or `fresh` (the Retry), take new ones.
   */
  const signedAt = useRef(new Map<string, number>())
  const takeFiles = useCallback((rows: ProjectFile[], fresh = false) => {
    const now = Date.now()
    setFiles((prev) => {
      const next = new Map<string, ProjectFile>()
      for (const row of rows) {
        const had = prev.get(row.id)
        const at = signedAt.current.get(row.id) ?? 0
        if (had?.url && !fresh && now - at < 90 * 60 * 1000) {
          next.set(row.id, { ...row, url: had.url, thumb: had.thumb })
        } else {
          next.set(row.id, row)
          signedAt.current.set(row.id, now)
        }
      }
      return next
    })
  }, [])

  /**
   * What the picture models can do, fetched once when the board opens.
   *
   * Not stored on the card. A card saved in March would then still be offering
   * March's sizes in June, on a model that has since dropped one — and the
   * refusal would arrive after the button had been pressed.
   */
  const [models, setModels] = useState<PictureModel[]>([])
  /** Upscale's models and sizes, fetched once like the picture models. */
  const [upscaling, setUpscaling] = useState<UpscaleOptions | null>(null)
  /** Video's models, fetched once the same way (videoNode.tsx). */
  const [videoModels, setVideoModels] = useState<VideoModel[] | null>(null)

  /**
   * How many runs this person has going, anywhere, and the ceiling.
   *
   * Across the board, the skills page and the Miro panel — they all spend from
   * one account, so a per-card limit would not be a limit at all. The server
   * counts and refuses; this is only so the button can go grey instead of
   * letting somebody press it and be told no.
   *
   * Learned from the answers that already come back rather than from a poll of
   * its own: every start and every status carries the number.
   */
  const [live, setLive] = useState({ live: 0, limit: MAX_LIVE_RUNS })

  /**
   * Sheets of the same board.
   *
   * A page is a view and not a container: nothing is divided by it. The
   * project's files, its runs and its library are exactly what they were, and
   * a picture copied to another page is the same picture. What changes is only
   * which nodes are drawn.
   */
  const [pages, setPages] = useState<CanvasPage[]>([FIRST_PAGE])
  const [pageId, setPageId] = useState(FIRST_PAGE.id)

  /* ---------- history ---------- */

  const past = useRef<CanvasDocument[]>([])
  const future = useRef<CanvasDocument[]>([])
  /** True while an undo is being applied, so it does not record itself. */
  const applying = useRef(false)

  /** When the last step was recorded, so a drag is one step and not two hundred. */
  const lastAt = useRef(0)

  /*
   * UNDO LEAVES WHAT WAS PAID FOR ALONE (Huanrui, 2026-10-06).
   *
   * Ctrl+Z takes back moving, turning, copying, wiring, deleting and the
   * like. It never takes back a run: a picture a card made -- or is making --
   * stays on the board, and a card keeps what it knows about its runs, however
   * far back the undo goes. Money spent cannot be unspent, and a Ctrl+Z that
   * quietly removed a picture that cost a dollar, or a frame still filling,
   * read as the picture being lost.
   *
   * Each step remembers when it was taken, and the board remembers when each
   * picture a run made first appeared and which pictures those are. Putting a
   * step back (keepRuns, below) keeps every run-made picture that came after
   * the step, keeps each picture's contents and each card's run state as they
   * are now, and changes everything else back. A picture made by a run and
   * then deleted by hand is an ordinary deletion, and undo brings it back.
   */
  const takenAt = useRef(new WeakMap<CanvasDocument, number>())
  const stamp = useCallback((doc: CanvasDocument): CanvasDocument => {
    takenAt.current.set(doc, Date.now())
    return doc
  }, [])
  /** Pictures put down by a run on this visit: a frame, then the picture in it. */
  const ranHere = useRef(new Set<string>())
  /** When each node was first on the board on this visit. */
  const bornAt = useRef(new Map<string, number>())
  useEffect(() => {
    const now = Date.now()
    for (const node of nodes) if (!bornAt.current.has(node.id)) bornAt.current.set(node.id, now)
  }, [nodes])

  const remember = useCallback(
    (force = false) => {
      if (applying.current) return
      // Resizing and dragging fire a change per frame. Without this the undo
      // stack fills with fifty snapshots of one gesture, and Ctrl+Z moves a
      // node a pixel at a time — which reads as undo being broken.
      const now = Date.now()
      if (!force && now - lastAt.current < 700) return
      lastAt.current = now
      past.current = [...past.current, stamp(toDocument(nodes, edges, pages))].slice(-HISTORY)
      future.current = []
    },
    [nodes, edges],
  )

  const cutAlong = useCallback(
    (points: { x: number; y: number }[]) => {
      const hits = new Set<string>()
      // Looked at every few pixels along the line: a wire is a 20px band to
      // the pointer (.react-flow__edge-interaction), so nothing slips between.
      const look = (x: number, y: number) => {
        for (const element of document.elementsFromPoint(x, y)) {
          const id = element.closest<SVGElement>('.react-flow__edge')?.dataset.id
          if (id) hits.add(id)
        }
      }
      look(points[0]!.x, points[0]!.y)
      for (let i = 1; i < points.length; i += 1) {
        const a = points[i - 1]!
        const b = points[i]!
        const steps = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / 4))
        for (let k = 1; k <= steps; k += 1) look(a.x + ((b.x - a.x) * k) / steps, a.y + ((b.y - a.y) * k) / steps)
      }
      if (hits.size === 0) return
      remember(true)
      setEdges((prev) => prev.filter((edge) => !hits.has(edge.id)))
    },
    [remember, setEdges],
  )

  cutRef.current = cutAlong

  /**
   * A note says its piece and goes.
   *
   * Every one of them reports something that has already happened — sent,
   * copied, saved, could not — so once it has been read there is nothing left
   * to do with it, and a line of text sitting in the corner of a board for the
   * rest of the afternoon is just something else to look past.
   *
   * Here rather than at each of the dozen places that set one, so a note added
   * later cannot forget to clear itself.
   */
  useEffect(() => {
    if (!note) return
    const timer = window.setTimeout(() => setNote(''), 3000)
    return () => window.clearTimeout(timer)
  }, [note])
  useEffect(() => {
    if (!alert) return
    const timer = window.setTimeout(() => setAlert(''), 6000)
    return () => window.clearTimeout(timer)
  }, [alert])

  /* ---------- loading ---------- */

  /**
   * Runs somebody on this page is already waiting on, so the effect that
   * resumes waiting after a reload does not wait on them a second time.
   */
  const watching = useRef(new Set<string>())

  /** The project this board was last for, so a first id can be told from a switch. */
  const previousProject = useRef(projectId)
  /** The board whose pages are showing, once loaded: the open page is remembered for it. */
  const pageFor = useRef('')

  useEffect(() => {
    const was = previousProject.current
    previousProject.current = projectId
    if (!projectId) {
      setReady(true)
      return
    }
    /*
     * A board that was being worked on before it had a project, and has just
     * been given one. It IS that project now: reading it back from the server
     * would find the new project empty and wipe what is on screen -- the
     * pictures being copied in from "Copy to project", say. The save that
     * follows writes it.
     */
    if (!was && (latestNodes.current.length > 0 || adopting.current)) {
      adopting.current = false
      pageFor.current = projectId
      setReady(true)
      return
    }
    adopting.current = false
    // A different board deserves its own opening frame.
    framed.current = false
    let cancelled = false
    withRetry(() => loadCanvas(projectId))
      .then((doc) => {
        if (cancelled) return
        const sheets = doc.pages?.length ? doc.pages : [FIRST_PAGE]
        setPages(sheets)
        // Back on the page that was open when this board was last left.
        const last = readLastPage(projectId)
        setPageId(sheets.some((page) => page.id === last) ? last : sheets[0]!.id)
        pageFor.current = projectId
        setNodes(
          doc.nodes.map((n) => {
            // A frame saved while its run was going: it is still waiting,
            // and says so, until the effect below finds out how it went.
            const waiting =
              typeof n.data?.runId === 'string' && !n.data?.fileId && !n.data?.assetId
            return {
              ...n,
              page: n.page ?? sheets[0]!.id,
              data: waiting ? { ...n.data, making: true, drawing: true } : { ...n.data },
            } as Node
          }),
        )
        setEdges(doc.edges.map(wire))
        saved.current = JSON.stringify(doc)
      })
      .catch(() => {
        if (!cancelled) setAlert('The canvas could not be loaded')
      })
      .finally(() => {
        if (!cancelled) setReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, setNodes, setEdges])

  /**
   * Open on the work, not on the empty space beside it.
   *
   * A board is saved wherever its contents happened to be put, which is rarely
   * the origin — so opening one showed a blank grid and left the person to go
   * looking for their own pictures. Zoom is capped at 1: a board holding one
   * small image would otherwise open at four hundred per cent.
   *
   * Only ever once per board. After the first frame the viewport belongs to
   * whoever is looking at it, and a canvas that re-centres itself every time
   * something is added is a canvas that will not hold still.
   */
  useEffect(() => {
    if (framed.current || !ready || !nodesReady || nodes.length === 0) return
    framed.current = true
    void fitView({ padding: 0.25, maxZoom: 1 })
  }, [ready, nodesReady, nodes.length, fitView])

  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    withRetry(() => listProjectFiles(projectId, { thumbs: true }))
      .then((rows) => {
        if (!cancelled) takeFiles(rows)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [projectId])

  // The pictures. Asked for by id, because a signed link lasts two hours and
  // the arrangement lasts for months.
  useEffect(() => {
    const wanted = nodes
      .filter((node) => node.type === 'image')
      .map((node) => (node.data as ImageNodeData).assetId ?? '')
      .filter((id) => id !== '' && !asked.current.has(id))
    if (wanted.length === 0) return

    const unique = [...new Set(wanted)]
    for (const id of unique) asked.current.add(id)

    let cancelled = false
    void repository
      .getByIds(unique)
      .then((assets) => {
        if (cancelled) return
        setUrls((prev) => {
          const next = new Map(prev)
          for (const asset of assets) next.set(asset.id, asset)
          return next
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [nodes, repository])

  /**
   * A picture on the board that this page has never heard of.
   *
   * An image node carries an id, not a picture, and every part of the board
   * that needs the actual bytes — drawing it, downloading it, copying it,
   * dragging it into the chat, sending it to Miro — looks that id up in the
   * file map. The map is filled when the board opens. So anything that arrives
   * afterwards is a node pointing at nothing, which draws as an empty frame,
   * which on a black board looks exactly like a picture that came back black.
   *
   * That happened twice, from two different directions: first a picture the
   * canvas had just generated, then one dragged in from the chat. Fixing the
   * first one at its own call site is what let the second one happen — so it
   * is fixed here instead, once, where it catches every source including the
   * ones that do not exist yet.
   *
   * Guarded the same way the library one above is: an id that is asked for and
   * never arrives must not be asked for again on every render, or a file
   * somebody deleted becomes a quiet infinite loop.
   */
  useEffect(() => {
    if (!projectId) return
    const missing = nodes
      .filter((node) => node.type === 'image')
      .map((node) => (node.data as ImageNodeData).fileId ?? '')
      .filter((id) => id !== '' && !files.has(id) && !askedFiles.current.has(id))
    if (missing.length === 0) return

    for (const id of missing) askedFiles.current.add(id)

    // NOT CANCELLED WHEN THIS EFFECT RE-RUNS, AND THAT IS THE WHOLE POINT.
    //
    // It used to be, and the two halves combined into a picture that never
    // arrived. The guard above says "already asked"; the cancellation meant
    // the one request that was asked got thrown away. Any change to `nodes`
    // between asking and answering — and finishing a run changes `nodes`
    // immediately afterwards, to put the price on the card — discarded the
    // reply, and the guard then refused to ask again. The frame stayed empty
    // for ever and nothing anywhere said why.
    //
    // Which is this project's oldest defect wearing yet another coat: "asked"
    // and "answered" were one value pretending to be one state.
    //
    // There is nothing to cancel anyway. The answer is the project's whole
    // file list, which is correct whenever it lands and belongs to no
    // particular render.
    void listProjectFiles(projectId, { thumbs: true })
      .then((rows) => {
        takeFiles(rows)
      })
      .catch(() => {
        // Asked and not answered. Released, so a later render may try again —
        // a dropped request must not become a permanent refusal to look.
        //
        // Only on failure. A reply that simply did not contain the id keeps
        // its guard, because that is the other case: a file that is genuinely
        // gone, which would otherwise be asked for on every render for ever.
        for (const id of missing) askedFiles.current.delete(id)
      })
  }, [nodes, files, projectId])

  /* ---------- saving ---------- */

  /** What the server last agreed to, so nothing is written twice. */
  const saved = useRef('')

  useEffect(() => {
    if (!ready) return
    const timer = setTimeout(() => {
      const doc = JSON.stringify(toDocument(nodes, edges, pages))
      // Opening a board would otherwise write it straight back exactly as it
      // was read — a pointless round trip on every visit, and one that would
      // have hidden a save that silently did nothing.
      if (doc === saved.current) return
      // Nothing on it and no project yet: there is nothing to keep, and
      // creating a project for an empty board would litter the list with one
      // every time somebody opened this page and left.
      if (!projectId && JSON.parse(doc).nodes.length === 0) return

      void (async () => {
        const id = projectId || (await ensureProject())
        if (!id) return
        await saveCanvas(id, JSON.parse(doc) as CanvasDocument)
        saved.current = doc
      })().catch(() => setAlert('The canvas could not be saved'))
    }, SAVE_AFTER_MS)
    return () => clearTimeout(timer)
    // `pages` is in here, and it is the whole reason a new page used to
    // vanish on reload. Adding one changes no node and no edge — so the effect
    // that saves the board never re-ran, the document was never written, and
    // the page existed only in this tab's memory. A change nothing was
    // watching, which is the quietest way for work to be lost.
  }, [nodes, edges, pages, ready, projectId, ensureProject])

  /* ---------- running ---------- */

  const run = useCallback(
    async (skillNodeId: string) => {
      const inputNodes = edges
        .filter((edge) => edge.target === skillNodeId)
        .map((edge) => nodes.find((node) => node.id === edge.source))
        .filter((node): node is Node => Boolean(node) && node!.type === 'image')

      // Whichever store each picture is in. A skill takes an image, not a
      // library entry — a dropped file and a generated one are as connectable
      // as anything else on the board.
      const inputs: ImageRef[] = inputNodes
        .map((node) => {
          const data = node.data as ImageNodeData
          if (data.assetId) return { store: 'library' as const, id: data.assetId }
          if (data.fileId) return { store: 'project' as const, id: data.fileId }
          return null
        })
        .filter((ref): ref is ImageRef => ref !== null)

      if (inputs.length === 0) return

      const skillNode = nodes.find((node) => node.id === skillNodeId)
      if (!skillNode) return
      const skill = skillNode.data as SkillNodeData

      const mark = (patch: Partial<SkillNodeData>) =>
        setNodes((prev) =>
          prev.map((node) =>
            node.id === skillNodeId ? { ...node, data: { ...node.data, ...patch } } : node,
          ),
        )

      mark({ running: true, error: '' })
      try {
        const result = await runSkill(skill.skillRef, inputs, projectId)
        // Not a step on the undo stack: what a run made stays (see UNDO
        // LEAVES WHAT WAS PAID FOR ALONE).

        // The output lands to the right of the skill, at the same height, and
        // the wire is drawn from the skill. The skill is what made it — and
        // read left to right the board now says picture, skill, result, which
        // is the order the work actually happened in. Drawn from the input
        // instead, as it was, the chain read picture, picture, with the thing
        // that did the work hanging off the side.
        //
        // The wire is a record, not a wiring: it drives nothing, and deleting
        // it changes nothing about the picture.
        const at = { x: skillNode.position.x + 260, y: skillNode.position.y }
        const made = result.outputs.map((ref, index) => ({
          id: newId('image'),
          type: 'image',
          position: { x: at.x, y: at.y + index * 200 },
          width: 220,
          data: {
            ...(ref.store === 'library' ? { assetId: ref.id } : { fileId: ref.id }),
            from: { skill: skill.skillRef, inputs, at: new Date().toISOString() },
          } satisfies ImageNodeData,
        })) as Node[]

        for (const output of made) ranHere.current.add(output.id)
        setNodes((prev) => [...prev, ...made])
        setEdges((prev) => [
          ...prev,
          ...made.map((output) =>
            wire({ id: newId('edge'), source: skillNode.id, target: output.id }),
          ),
        ])
        mark({ running: false })
      } catch (error) {
        mark({
          running: false,
          error: error instanceof Error ? error.message : 'It did not run',
        })
      }
    },
    [edges, nodes, projectId, remember, setEdges, setNodes],
  )

  /**
   * Making pictures.
   *
   * Started and then asked about, rather than waited on: ten pictures at 4K is
   * minutes of work, and a page holding a connection open that long is a bet
   * that ends with the money spent and the pictures lost. The run is written
   * down on the server before anything else happens, so a closed laptop costs
   * the pictures' place on this board and nothing else — they are saved to the
   * project either way.
   */
  const runPictures = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as PictureNodeData

      // In port order, because the order is the point: somebody writes "the
      // label from the second picture" meaning the second one. Sorted by the
      // port's number and not by the order the wires happen to sit in the
      // array, which is the order they were drawn in.
      const references: ImageRef[] = edges
        .filter((edge) => edge.target === cardId)
        .map((edge) => ({
          port: Number(String(edge.targetHandle ?? '').replace('ref-', '')) || 0,
          node: nodes.find((node) => node.id === edge.source),
        }))
        .sort((a, b) => a.port - b.port)
        .map((entry) => (entry.node ? refOf(entry.node) : null))
        .filter((ref): ref is ImageRef => ref !== null)

      /**
       * Change the card this run belongs to, working from whatever it says now.
       *
       * A function rather than a fixed patch, because several runs are in
       * flight at once and each finishes by taking its own id out of a list the
       * others are also in. Written as a plain object, the last to finish would
       * put back a list from before the others started.
       *
       * AND FOUND BY RUN ID, NOT BY NODE ID, once the run has started.
       *
       * Alt-dragging a card leaves a copy behind and carries the original
       * away — so the node that started a run can end up somewhere else on the
       * board, or be the one the person thinks of as "the new one". A run
       * reporting to a fixed node id followed the wrong card and lit it up as
       * busy. It belongs to whichever card is holding its id, and that is a
       * thing the board can be asked rather than assumed.
       */
      const patchCard = (change: (was: PictureNodeData) => Partial<PictureNodeData>, run = '') =>
        setNodes((prev) => {
          const mine = run
            ? prev.find((node) => ((node.data as PictureNodeData).runs ?? []).includes(run))
            : prev.find((node) => node.id === cardId)
          if (!mine) return prev
          return prev.map((node) =>
            node.id === mine.id
              ? { ...node, data: { ...node.data, ...change(node.data as PictureNodeData) } }
              : node,
          )
        })

      const model = models.find((one) => one.slug === data.model) ?? models[0]
      if (!model) {
        patchCard(() => ({ error: 'No image model is switched on' }))
        return
      }

      if (live.live >= (live.limit || MAX_LIVE_RUNS)) return

      const wanted = Math.min(Math.max(1, data.n ?? 1), model.maxImages)
      patchCard(() => ({ error: '' }))

      // What this run is asked for, kept with each picture it makes. Taken now,
      // because by the time the pictures land the card may already hold the
      // next prompt.
      const made: MadeWith = {
        prompt: (data.prompt ?? '').trim(),
        model: model.slug,
        size: sizeFor(model, data.size ?? ''),
        quality: data.quality ?? 'auto',
        at: new Date().toISOString(),
      }

      let runId = ''
      try {
        const began = await startPictures({
          projectId,
          prompt: made.prompt,
          model: made.model,
          size: made.size,
          quality: made.quality,
          n: wanted,
          references,
        })
        runId = began.runId
        setLive({ live: began.live, limit: began.limit })
      } catch (error) {
        patchCard(() => ({
          error: error instanceof Error ? error.message : 'It did not start',
        }))
        return
      }

      // In flight. Taken out again by whichever branch below finishes it, so
      // every exit from here has to remember to — which is why they all go
      // through `done`.
      patchCard((was) => ({ runs: [...(was.runs ?? []), runId] }))
      const done = (patch: Partial<PictureNodeData>) =>
        patchCard(
          (was) => ({ ...patch, runs: (was.runs ?? []).filter((one) => one !== runId) }),
          runId,
        )

      /**
       * The pictures appear now, empty, and fill in when they arrive.
       *
       * A greyed word on a button was the only sign that anything was
       * happening, on a board where the interesting part is somewhere else
       * entirely. Putting the frames down straight away says three things a
       * spinner cannot: that it started, how many are coming, and where they
       * will be — so the wire is drawn before there is anything on the end of
       * it, and the board is already arranged when they land.
       *
       * They are ordinary image nodes with nothing in them yet. Which is also
       * what a picture that has not loaded looks like, so `making` marks the
       * difference: one of them is going to fill and the other is broken, and
       * those must not look the same.
       *
       * Not a step on the undo stack: what a run puts down stays (UNDO LEAVES
       * WHAT WAS PAID FOR ALONE).
       */

      // Directly to the right of the card, and level with its middle.
      //
      // The offset used to be a flat 300 pixels from the card's left edge,
      // which was fine while a card was 200 wide and put the pictures on top
      // of it the moment one was unfolded to 400. Measured rather than
      // assumed: the card is whatever width it currently is.
      const card2 = sizeOf(card)
      const stack = wanted * FRAME_HEIGHT + (wanted - 1) * STACK_GAP
      const at = {
        x: card.position.x + card2.w + FAMILY_GAP,
        y: card.position.y + card2.h / 2 - stack / 2,
      }
      const frames = Array.from({ length: wanted }, (_, index) => ({
        id: newId('image'),
        type: 'image',
        position: { x: at.x, y: at.y + index * (FRAME_HEIGHT + STACK_GAP) },
        width: 220,
        data: { making: true, drawing: true, runId, runIndex: index, made } satisfies ImageNodeData,
      })) as Node[]

      for (const frame of frames) ranHere.current.add(frame.id)
      setNodes((prev) => [...prev, ...frames])
      setEdges((prev) => [
        ...prev,
        ...frames.map((frame) => wire({ id: newId('edge'), source: cardId, target: frame.id })),
      ])

      /** Take the empty frames away again. Used whenever there is nothing to fill them with. */
      const clearFrames = () => {
        const ids = new Set(frames.map((frame) => frame.id))
        setNodes((prev) => prev.filter((node) => !ids.has(node.id)))
        setEdges((prev) => prev.filter((edge) => !ids.has(edge.target)))
      }

      // Asked about, not waited on. The work is on a queue, so nothing here
      // has to stay alive for it — and closing the board no longer costs the
      // pictures, only the arranging of them.
      watching.current.add(runId)
      const run: PictureRun | null = await awaitRun(runId)
      if (run?.limit) setLive({ live: run.live, limit: run.limit })

      if (!run || run.status !== 'succeeded') {
        clearFrames()
        done({
          error:
            run?.error ||
            (run?.status === 'running' || run?.status === 'queued'
              ? 'Lost the connection while it was working — reload the board to see if it finished'
              : 'It did not finish'),
        })
        return
      }

      // The frames that are already on the board become the pictures. Nothing
      // is fetched here: the nodes now name pictures this page has not seen,
      // and the effect that watches for exactly that goes and gets them — the
      // same effect that catches a picture dragged in from the chat. Doing it
      // here as well would be a second way of doing one thing, which is how
      // the chat case came to be missed in the first place.
      //
      // A run may come back with fewer pictures than were asked for. The
      // frames left over are taken away rather than left waiting for something
      // that is not coming.
      const spare = new Set(frames.slice(run.pictures.length).map((frame) => frame.id))
      setNodes((prev) =>
        prev
          .filter((node) => !spare.has(node.id))
          .map((node) => {
            const index = frames.findIndex((frame) => frame.id === node.id)
            const picture = index < 0 ? null : run.pictures[index]
            if (!picture) return node
            return {
              ...node,
              data: {
                ...(picture.store === 'library'
                  ? { assetId: picture.id }
                  : { fileId: picture.id }),
                from: {
                  skill: data.skillRef,
                  inputs: references,
                  at: new Date().toISOString(),
                },
                made: { ...made, credits: run.credits },
              } satisfies ImageNodeData,
            }
          }),
      )
      setEdges((prev) => prev.filter((edge) => !spare.has(edge.target)))
      // The real figure, which only exists now. The one on the button before
      // the run says "about" for exactly this reason.
      done({ error: '', credits: run.credits, madeCount: run.pictures.length })
    },
    [edges, live, models, nodes, projectId, remember, setEdges, setNodes],
  )


  /** A picture node's real pixels, where the board knows them. 0 when it does not. */
  const pixelsOf = useCallback(
    (node: Node): { width: number; height: number } => {
      const data = node.data as ImageNodeData
      const file = data.fileId ? files.get(data.fileId) : undefined
      const asset = data.assetId ? urls.get(data.assetId) : undefined
      return { width: file?.width || asset?.width || 0, height: file?.height || asset?.height || 0 }
    },
    [files, urls],
  )

  /**
   * Upscaling what is wired into an Upscale card (upscaleNode.tsx, 2026-10-05).
   *
   * One run for all of them, and a frame for each picture that will actually
   * be enlarged -- one already that big is left out here rather than sent to
   * be refused. The frames fill one by one as Topaz finishes each picture,
   * not all at the end, because a run of ten can take minutes.
   */
  const runUpscale = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as UpscaleNodeData

      const patchCard = (change: (was: UpscaleNodeData) => Partial<UpscaleNodeData>, run = '') =>
        setNodes((prev) => {
          const mine = run
            ? prev.find((node) => ((node.data as UpscaleNodeData).runs ?? []).includes(run))
            : prev.find((node) => node.id === cardId)
          if (!mine) return prev
          return prev.map((node) =>
            node.id === mine.id
              ? { ...node, data: { ...node.data, ...change(node.data as UpscaleNodeData) } }
              : node,
          )
        })
      // Every way of not starting says why, on the card: a press answered with
      // nothing reads as a fault (2026-10-05).
      const refuse = (error: string) => patchCard(() => ({ error, tried: Date.now() }))
      if (!upscaling) return refuse('Upscale could not be loaded — reload the board')
      if (!projectId) return refuse('Save the board first — put something on it')

      const seen = new Set<string>()
      const wired = edges
        .filter((edge) => edge.target === cardId)
        .map((edge) => nodes.find((node) => node.id === edge.source))
        .map((node) => (node ? { node, ref: refOf(node) } : null))
        .filter((one): one is { node: Node; ref: ImageRef } => {
          if (!one?.ref) return false
          const key = `${one.ref.store}:${one.ref.id}`
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
      if (wired.length === 0) return refuse('Connect a picture first')
      // One picture per card; a board saved before that rule may still hold more.
      if (wired.length > 1) return refuse('One picture at a time — take the extra wires off')
      if (live.live >= (live.limit || MAX_LIVE_RUNS)) {
        return refuse(`${live.live} already going — wait for one to finish`)
      }

      const dims = wired.map((one) => pixelsOf(one.node))
      const model =
        upscaling.models.find((one) => one.key === data.model) ??
        upscaling.models.find((one) => one.key === upscaling.defaultModel) ??
        upscaling.models[0]
      const size = upscaling.sizes.find((one) => one.value === usableSize(upscaling, data.size ?? '2k', dims, model?.key))
      if (!model || !size) return refuse('Choose a model and a size')
      const going = wired.filter((_, index) => {
        return takes(size, dims[index]!)
      })
      if (going.length === 0) {
        return refuse(`Already ${size.label} or larger`)
      }
      if (going.length > upscaling.maxPictures) {
        return refuse(`${upscaling.maxPictures} pictures at a time is the most`)
      }
      patchCard(() => ({ error: '' }))

      let runId = ''
      try {
        const began = await startUpscale({
          projectId,
          inputs: going.map((one) => one.ref),
          model: model.key,
          size: size.value,
          face: model.face && data.face === true,
        })
        runId = began.runId
        setLive({ live: began.live, limit: began.limit })
      } catch (error) {
        return refuse(error instanceof Error ? error.message : 'It did not start')
      }

      patchCard((was) => ({ runs: [...(was.runs ?? []), runId] }))
      const done = (patch: Partial<UpscaleNodeData>) =>
        patchCard((was) => ({ ...patch, runs: (was.runs ?? []).filter((one) => one !== runId) }), runId)

      // Not a step on the undo stack (UNDO LEAVES WHAT WAS PAID FOR ALONE).
      const card2 = sizeOf(card)
      const stack = going.length * FRAME_HEIGHT + (going.length - 1) * STACK_GAP
      const at = {
        x: card.position.x + card2.w + FAMILY_GAP,
        y: card.position.y + card2.h / 2 - stack / 2,
      }
      const frames = going.map((one, index) => {
        const shape = dims[wired.indexOf(one)]!
        return {
          id: newId('image'),
          type: 'image',
          position: { x: at.x, y: at.y + index * (FRAME_HEIGHT + STACK_GAP) },
          width: 220,
          data: {
            making: true,
            drawing: true,
            runId,
            runIndex: index,
            ...(shape.width && shape.height ? { aspect: shape.width / shape.height } : {}),
          } satisfies ImageNodeData,
        }
      }) as Node[]
      for (const frame of frames) ranHere.current.add(frame.id)
      setNodes((prev) => [...prev, ...frames])
      setEdges((prev) => [
        ...prev,
        ...frames.map((frame) => wire({ id: newId('edge'), source: cardId, target: frame.id })),
      ])

      // Each frame becomes its picture as soon as that picture exists.
      const filled = new Set<number>()
      const fill = (results: { output?: string; credits?: number; width?: number; height?: number }[]) => {
        const now: { index: number; ref: ImageRef }[] = []
        results.forEach((result, index) => {
          if (filled.has(index) || !result.output || !frames[index]) return
          const colon = result.output.indexOf(':')
          const store = result.output.slice(0, colon)
          const id = result.output.slice(colon + 1)
          if (store !== 'project' && store !== 'library') return
          filled.add(index)
          now.push({ index, ref: { store, id } })
        })
        if (now.length === 0) return
        setNodes((prev) =>
          prev.map((node) => {
            const hit = now.find((one) => frames[one.index]!.id === node.id)
            if (!hit) return node
            const result = results[hit.index]
            return {
              ...node,
              data: {
                ...(hit.ref.store === 'library' ? { assetId: hit.ref.id } : { fileId: hit.ref.id }),
                from: { skill: data.skillRef, inputs: [going[hit.index]!.ref], at: new Date().toISOString() },
                // For the corner button, as a picture card's pictures have.
                made: {
                  prompt: '',
                  model: model.key,
                  size: size.label,
                  quality: '',
                  at: new Date().toISOString(),
                  ...(result?.credits ? { credits: Math.round(result.credits * upscaling.usdPerCredit * 1000) } : {}),
                  upscale: {
                    face: model.face ? data.face === true : null,
                    pixels: result?.width ? `${result.width} × ${result.height}` : '',
                  },
                },
              } satisfies ImageNodeData,
            }
          }),
        )
      }

      watching.current.add(runId)
      const run = await awaitUpscale(runId, (progress) => fill(progress.results))
      if (run.limit) setLive({ live: run.live, limit: run.limit })
      fill(run.results)

      // Whatever did not become a picture is taken away again.
      const spare = new Set(frames.filter((_, index) => !filled.has(index)).map((frame) => frame.id))
      if (spare.size > 0) {
        setNodes((prev) => prev.filter((node) => !spare.has(node.id)))
        setEdges((prev) => prev.filter((edge) => !spare.has(edge.target)))
      }
      done({
        error: filled.size === going.length ? '' : run.error || 'It did not finish',
        credits: run.credits,
        madeCount: filled.size,
      })
    },
    [edges, live, nodes, pixelsOf, projectId, remember, setEdges, setNodes, upscaling],
  )

  /**
   * Making a video from what is wired into a Video card (videoNode.tsx,
   * 2026-10-08). Port 1 is the first frame, port 2 the last. One frame goes
   * down to the right at once, at the first frame's shape, with the loader in
   * it; it becomes the video when Kling has made it and the server has kept
   * it -- minutes, so the wait is awaitVideo's, not the pictures' twelve.
   */
  const runVideo = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as VideoNodeData

      const patchCard = (change: (was: VideoNodeData) => Partial<VideoNodeData>, run = '') =>
        setNodes((prev) => {
          const mine = run
            ? prev.find((node) => ((node.data as VideoNodeData).runs ?? []).includes(run))
            : prev.find((node) => node.id === cardId)
          if (!mine) return prev
          return prev.map((node) =>
            node.id === mine.id ? { ...node, data: { ...node.data, ...change(node.data as VideoNodeData) } } : node,
          )
        })
      const refuse = (error: string) => patchCard(() => ({ error, tried: Date.now() }))
      if (!videoModels) return refuse('Video could not be loaded — reload the board')
      if (!projectId) return refuse('Save the board first — put something on it')

      const onPort = (port: string) => {
        const edge = edges.find(
          (one) => one.target === cardId && (one.targetHandle ?? FIRST_PORT) === port,
        )
        return edge ? nodes.find((node) => node.id === edge.source) : undefined
      }
      const firstNode = onPort(FIRST_PORT)
      const lastNode = onPort(LAST_PORT)
      const first = firstNode ? refOf(firstNode) : null
      const last = lastNode ? refOf(lastNode) : null
      if (!first) return refuse('Wire a first frame into port 1')
      const isClip = (node: Node | undefined) => {
        const id = node ? (node.data as ImageNodeData).fileId : undefined
        return Boolean(id && (files.get(id)?.mime_type ?? '').startsWith('video/'))
      }
      if (isClip(firstNode) || isClip(lastNode)) return refuse('The frames have to be pictures, not videos')
      if (lastNode && !last) return refuse('The picture on the last port is not ready yet')
      if (live.live >= (live.limit || MAX_LIVE_RUNS)) {
        return refuse(`${live.live} already going — wait for one to finish`)
      }
      const model = videoModels.find((one) => one.key === data.model) ?? videoModels[0]
      if (!model?.ready) return refuse('Video is not switched on yet')

      // Frames between (Kling v4, 2026-10-27): the ports in use, in their order.
      const middleNodes: Node[] = []
      for (let n = 1; n <= Math.min(MAX_MIDDLE_PORTS, model.maxMiddle ?? 0); n += 1) {
        const one = onPort(MID_PORT(n))
        if (one) middleNodes.push(one)
      }
      if (middleNodes.some((one) => isClip(one))) return refuse('The frames have to be pictures, not videos')
      const middles = middleNodes.map((one) => refOf(one))
      if (middles.some((one) => !one)) return refuse('A frame between is not ready yet')
      // Told part by part: one part more than the frames between; the length is their sum.
      const parts =
        data.perPart !== false && middles.length > 0
          ? partsFor(data.parts, middles.length + 1).map((one) => ({ prompt: one.prompt.trim(), seconds: Math.round(one.seconds) }))
          : undefined
      if (parts?.some((one) => !one.prompt)) return refuse('Say what happens in each part')
      const prompt = parts ? parts.map((one, index) => `${index + 1}. ${one.prompt}`).join('\n') : (data.prompt ?? '').trim()
      if (!prompt) return refuse('Say what should happen in the video')
      const seconds = parts
        ? parts.reduce((sum, one) => sum + one.seconds, 0)
        : Math.min(model.maxSeconds, Math.max(model.minSeconds, data.seconds ?? model.defaultSeconds))
      if (parts && (seconds < model.minSeconds || seconds > model.maxSeconds)) {
        return refuse(`The parts add up to ${seconds} s; ${model.label} makes ${model.minSeconds}–${model.maxSeconds} s`)
      }
      // Always with sound (Huanrui, 2026-10-27): no longer a choice.
      const audio = true
      patchCard(() => ({ error: '' }))

      let runId = ''
      try {
        const began = await startVideo({
          projectId,
          firstFrame: first,
          lastFrame: last ?? undefined,
          middleFrames: middles as ImageRef[],
          parts,
          model: model.key,
          seconds,
          audio,
          prompt,
        })
        runId = began.runId
        setLive({ live: began.live, limit: began.limit })
      } catch (error) {
        return refuse(error instanceof Error ? error.message : 'It did not start')
      }

      patchCard((was) => ({ runs: [...(was.runs ?? []), runId] }))
      const done = (patch: Partial<VideoNodeData>) =>
        patchCard((was) => ({ ...patch, runs: (was.runs ?? []).filter((one) => one !== runId) }), runId)

      // The video takes the first frame's shape.
      const shape = pixelsOf(firstNode!)
      const aspect = shape.width && shape.height ? shape.width / shape.height : 16 / 9
      const box = sizeOf(card)
      const made = {
        prompt,
        model: model.key,
        size: `${model.resolution.toUpperCase()} · ${seconds} s${audio ? ' · sound' : ''}${middles.length ? ` · ${middles.length} between` : ''}`,
        quality: '',
        at: new Date().toISOString(),
      }
      const frame = {
        id: newId('image'),
        type: 'image',
        position: { x: card.position.x + box.w + FAMILY_GAP, y: card.position.y + box.h / 2 - FRAME_HEIGHT / 2 },
        width: 300,
        data: { making: true, drawing: true, clip: true, runId, runIndex: 0, aspect, made } satisfies ImageNodeData,
      } as Node
      ranHere.current.add(frame.id)
      setNodes((prev) => [...prev, frame])
      setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frame.id })])

      watching.current.add(runId)
      const run = await awaitVideo(runId)
      if (run.limit) setLive({ live: run.live, limit: run.limit })
      if (run.status === 'succeeded' && run.output) {
        const fileId = run.output.id
        setNodes((prev) =>
          prev.map((node) =>
            node.id === frame.id
              ? {
                  ...node,
                  data: {
                    fileId,
                    clip: true,
                    aspect,
                    from: {
                      skill: data.skillRef,
                      inputs: [first, ...(middles as ImageRef[]), ...(last ? [last] : [])],
                      at: new Date().toISOString(),
                    },
                    made: { ...made, ...(run.credits ? { credits: run.credits } : {}) },
                  } satisfies ImageNodeData,
                }
              : node,
          ),
        )
        // The board's list of files does not have it yet: ask again, so it plays.
        void listProjectFiles(projectId, { thumbs: true })
          .then((rows) => takeFiles(rows))
          .catch((error: unknown) => console.warn('could not read the files after a video', error))
        done({ error: '', credits: run.credits, madeCount: 1 })
      } else {
        setNodes((prev) => prev.filter((node) => node.id !== frame.id))
        setEdges((prev) => prev.filter((edge) => edge.target !== frame.id))
        done({ error: run.error || 'The video could not be made', credits: run.credits, madeCount: 0 })
      }
    },
    [edges, files, live, nodes, pixelsOf, projectId, setEdges, setNodes, takeFiles, videoModels],
  )

  useEffect(() => {
    void videoOptions()
      .then(setVideoModels)
      .catch(() => {
        // The card says it could not load.
      })
  }, [])

  useEffect(() => {
    void upscaleOptions()
      .then(setUpscaling)
      .catch(() => {
        // The card says it could not load, which is where somebody is looking.
      })
  }, [])

  useEffect(() => {
    void pictureModels()
      .then(setModels)
      .catch(() => {
        // Nothing to say here. A card with no models draws an empty picker and
        // its button refuses, which is the same news said in the one place
        // somebody is actually looking.
      })
  }, [])

  /**
   * Every node belongs to a page, and that is settled here rather than at each
   * of the eight places a node can be created.
   *
   * Dragging a skill in, dropping files, pasting, Alt-dragging, a run putting
   * its empty frames down — each of them would have had to remember, and the
   * one that forgot would put a node on a board that no page draws. Which is
   * the same shape as the card that inherited no glow because the rule was
   * spelled `type === 'skill'`: a property of every node, enforced at every
   * call site instead of once.
   */
  useEffect(() => {
    if (nodes.every((node) => (node as { page?: string }).page)) return
    // On the page of the card it came out of, when it came out of one: a run
    // on a workflow's page puts its pictures there, whichever page is being
    // looked at (2026-10-16). Otherwise the page being looked at.
    const pageOf = new Map(nodes.map((node) => [node.id, (node as { page?: string }).page ?? '']))
    const from = new Map<string, string>()
    for (const edge of edges) if (pageOf.get(edge.source)) from.set(edge.target, pageOf.get(edge.source)!)
    setNodes((prev) =>
      prev.map((node) =>
        (node as { page?: string }).page ? node : ({ ...node, page: from.get(node.id) || pageId } as Node),
      ),
    )
  }, [nodes, edges, pageId, setNodes])

  /** What this page draws. The rest of the board is still there, just not here. */
  // A workflow's page shows the workflow only, never what a run is making on
  // it (Huanrui, 2026-10-16; workflowModel.ts, runLeftovers).
  const onWorkflowPage = pages.some((page) => page.id === pageId && page.workflow)
  const pageEdgesIfWorkflow = onWorkflowPage ? edges : null
  const onPage = useMemo(() => {
    const here = nodes.filter((node) => ((node as { page?: string }).page ?? pageId) === pageId)
    if (!pageEdgesIfWorkflow) return here
    const runs = runLeftovers(here, pageEdgesIfWorkflow, pageId)
    return runs.size > 0 ? here.filter((node) => !runs.has(node.id)) : here
  }, [nodes, pageId, pageEdgesIfWorkflow])
  // Which things are on this page, as one string: unchanged while something
  // is only being moved, so the edges are not worked out again -- and not
  // handed to React Flow as new -- on every frame of a drag.
  const pageMembers = useMemo(() => onPage.map((node) => `${node.id}\u0001${node.type ?? ''}`).join('\u0002'), [onPage])
  const pageEdges = useMemo(() => {
    const typeOf = new Map(
      pageMembers
        ? pageMembers.split('\u0002').map((one) => one.split('\u0001') as [string, string])
        : [],
    )
    return edges
      .filter((edge) => typeOf.has(edge.source) && typeOf.has(edge.target))
      .map((edge) =>
        // Picture to picture: drawn dashed, and only drawn -- nothing reads it
        // but triple-click (canConnect).
        typeOf.get(edge.source) === 'image' && typeOf.get(edge.target) === 'image'
          ? { ...edge, className: 'cedge--relation' }
          : edge,
      )
  }, [edges, pageMembers])

  // Which page is open, remembered per board, so leaving on Page 2 comes back
  // to Page 2 (Huanrui, 2026-09-30). Only once the board has loaded: before
  // that the page is the default, not a choice.
  useEffect(() => {
    if (ready && projectId && pageFor.current === projectId) writeLastPage(projectId, pageId)
  }, [ready, projectId, pageId])

  // Show the whole of a sheet when it is opened. Each one is arranged
  // differently, and arriving at the previous page's corner of a new one is
  // arriving at nothing.
  useEffect(() => {
    if (!ready) return
    const timer = window.setTimeout(() => void fitView({ padding: 0.25, maxZoom: 1 }), 30)
    return () => window.clearTimeout(timer)
  }, [pageId, ready, fitView])

  /**
   * Take a sheet away.
   *
   * An empty one goes without asking — there is nothing to lose and a
   * confirmation for nothing teaches people to click through confirmations
   * that matter.
   *
   * One with work on it asks first, and then takes the work with it. That is
   * what was chosen over quietly moving the contents to another page: a page
   * that empties itself into somewhere else leaves you hunting for pictures
   * you did not ask to move, and the undo stack can give them back if the
   * answer turns out to be wrong.
   *
   * The last page cannot go. A board with no sheets has nowhere to put
   * anything, and "add one back" is a worse answer than "not this one".
   */
  const removePage = useCallback(
    (id: string) => {
      if (pages.filter((page) => !page.workflow).length < 2) {
        setNote('A board keeps at least one page')
        return
      }
      const here = nodes.filter((node) => ((node as { page?: string }).page ?? '') === id)
      if (here.length > 0) {
        const name = pages.find((page) => page.id === id)?.name ?? 'this page'
        const what = here.length === 1 ? '1 thing' : `${here.length} things`
        if (!window.confirm(`Delete ${name} and the ${what} on it?`)) return
      }

      remember(true)
      const going = new Set(here.map((node) => node.id))
      setNodes((prev) => prev.filter((node) => !going.has(node.id)))
      setEdges((prev) => prev.filter((edge) => !going.has(edge.source) && !going.has(edge.target)))
      setPages((prev) => {
        const left = prev.filter((page) => page.id !== id)
        setPageId((was) => (was === id ? left[0]!.id : was))
        return left
      })
    },
    [nodes, pages, remember, setEdges, setNodes],
  )

  /**
   * Renaming a page (Huanrui, 2026-10-12): double-click its tab, type, Enter
   * or click away. Escape keeps the old name; an empty name is not kept.
   */
  const [renamingPage, setRenamingPage] = useState('')
  const renamePage = useCallback(
    (id: string, name: string) => {
      const trimmed = name.trim().slice(0, 40)
      setRenamingPage('')
      if (!trimmed || pages.find((page) => page.id === id)?.name === trimmed) return
      remember(true)
      setPages((prev) => prev.map((page) => (page.id === id ? { ...page, name: trimmed } : page)))
    },
    [pages, remember],
  )

  const addPage = useCallback(() => {
    const page = { id: newId('page'), name: `Page ${pages.length + 1}` }
    setPages((prev) => [...prev, page])
    setPageId(page.id)
    setNote(page.name)
  }, [pages.length])

  /* ---------- tidying up a selection ---------- */

  /**
   * The pictures currently picked out, in reading order.
   *
   * Sorted by where they are rather than by when they were selected, because
   * every operation below rearranges them and "the order I clicked" is not
   * something anybody can see on a board.
   */
  const picked = useMemo(
    () =>
      onPage
        .filter((node) => node.selected && MEDIA_NODES.has(node.type ?? ''))
        .sort((a, b) =>
          Math.abs(a.position.y - b.position.y) > 40
            ? a.position.y - b.position.y
            : a.position.x - b.position.x,
        ),
    [onPage],
  )
  /** Frames selected on this page, in reading order (frames.ts readingOrder). */
  const selectedFrames = useMemo(() => readingOrder(onPage.filter((node) => node.selected && isFrame(node))), [onPage])

  /**
   * What is selected, by kind, for the selection's filter (selectionFilter.tsx,
   * Huanrui 2026-10-23): what is selected and what is in a selected frame,
   * each once; a mood board's colour swatches go with their frame and are not
   * counted. `mixed`: what is itself selected is of two kinds or more -- the
   * filter shows, and each thing's own tools do not.
   */
  const selection = useMemo(() => {
    const frames = new Set(onPage.filter((node) => node.selected && isFrame(node)).map((node) => node.id))
    const members: Record<SelectKind, string[]> = { frame: [], picture: [], text: [], card: [] }
    const ids: string[] = []
    const kinds = new Set<SelectKind>()
    for (const node of onPage) {
      if (!node.selected && !frames.has(frameOf(node))) continue
      if ((node.data as { swatch?: unknown }).swatch) continue
      const kind = selectKind(node)
      members[kind].push(node.id)
      if (node.selected) {
        ids.push(node.id)
        kinds.add(kind)
      }
    }
    return { members, ids, mixed: kinds.size >= 2 }
  }, [onPage])

  /**
   * Level them and lay them out, in one go.
   *
   * ONE BUTTON BECAUSE IT IS ONE INTENTION
   *
   * It was two — arrange, and make the same height — and separately they are
   * both half a job. Pictures laid out in rows at six different heights are
   * still not comparable, and pictures at one height still sitting where they
   * were dropped are still a pile. Nobody wants one without the other.
   *
   * ONE PASS BECAUSE TWO WOULD READ A STALE MEASUREMENT
   *
   * Setting a width does not change what a node measures until it has been
   * drawn again, so levelling and then arranging would place everything using
   * the heights they had a moment ago. Both are worked out here from the same
   * arithmetic instead, and written once.
   *
   * The median, not the average or the tallest: one panorama among six pack
   * shots drags an average up and a maximum further, and the point is that the
   * set becomes comparable, not that it becomes large. Only the width is set —
   * the height follows from the picture, which is what keeps them undistorted.
   *
   * It does not move the group somewhere else. The arrangement was what was
   * wrong; where they are was not.
   */
  /**
   * Frames still waiting after a reload: find out how their run went.
   *
   * A board saved mid-run keeps each frame's run and place in it (runId,
   * runIndex). The run is on the queue and carries on without this page, so
   * all that was ever lost was the waiting. This picks it back up: filled
   * with the picture if the run succeeded, taken away if it did not.
   *
   * Only runs nobody here is already watching. The page that started a run
   * waits on it itself, and two waiters would fill the same frame twice.
   */
  const latestNodes = useRef(nodes)
  latestNodes.current = nodes
  const latestEdges = useRef(edges)
  latestEdges.current = edges

  /** A step of the undo stack, with what runs made and are making kept as it is now. */
  const keepRuns = useCallback(
    (step: CanvasDocument): CanvasDocument => {
      const at = takenAt.current.get(step) ?? 0
      const now = latestNodes.current
      const live = new Map(now.map((node) => [node.id, node]))
      const madeByRun = (node: Node) =>
        node.type === 'image' && (ranHere.current.has(node.id) || typeof (node.data as { runId?: unknown }).runId === 'string')
      const busy = (node: Node) => {
        const data = node.data as { runs?: unknown; running?: unknown }
        return SKILL_NODES.has(node.type ?? '') && ((Array.isArray(data.runs) && data.runs.length > 0) || data.running === true)
      }
      const copyOver = (keys: string[], from: Record<string, unknown>, to: Record<string, unknown>) => {
        for (const key of keys) {
          if (from[key] === undefined) delete to[key]
          else to[key] = from[key]
        }
      }

      const nodes: CanvasDocument['nodes'] = []
      for (const node of step.nodes) {
        const was = live.get(node.id)
        // A frame that was waiting then and is gone now was cleared by its own
        // run (it failed, or came back with fewer pictures). Not brought back.
        if (!was && typeof node.data.runId === 'string') continue
        if (!was) {
          nodes.push(node)
          continue
        }
        const data = { ...node.data }
        if (madeByRun(was)) copyOver(PICTURE_RUN_KEYS, was.data as Record<string, unknown>, data)
        else if (SKILL_NODES.has(was.type ?? '')) copyOver(CARD_RUN_KEYS, was.data as Record<string, unknown>, data)
        nodes.push({ ...node, data })
      }

      const inStep = new Set(step.nodes.map((node) => node.id))
      const added = now.filter(
        (node) =>
          !inStep.has(node.id) &&
          ((madeByRun(node) && (bornAt.current.get(node.id) ?? 0) >= at) || busy(node)),
      )
      if (added.length === 0) return { ...step, nodes }
      const addedIds = new Set(added.map((node) => node.id))
      const present = new Set([...nodes.map((node) => node.id), ...addedIds])
      const stepEdges = new Set(step.edges.map((edge) => edge.id))
      const theirEdges = latestEdges.current.filter(
        (edge) =>
          !stepEdges.has(edge.id) &&
          (addedIds.has(edge.source) || addedIds.has(edge.target)) &&
          present.has(edge.source) &&
          present.has(edge.target),
      )
      const kept = toDocument(added, theirEdges, step.pages ?? [])
      // As they are now, including what toDocument does not write down.
      kept.nodes.forEach((node, index) => {
        node.data = { ...(added[index]!.data as Record<string, unknown>) }
        for (const key of Object.keys(node.data)) if (typeof node.data[key] === 'function') delete node.data[key]
      })
      return { ...step, nodes: [...nodes, ...kept.nodes], edges: [...step.edges, ...kept.edges] }
    },
    [],
  )
  useEffect(() => {
    const waiting = new Set<string>()
    for (const node of nodes) {
      const data = node.data as ImageNodeData
      if (node.type !== 'image' || !data.making || !data.runId) continue
      if (data.fileId || data.assetId || watching.current.has(data.runId)) continue
      waiting.add(data.runId)
    }
    for (const runId of waiting) {
      watching.current.add(runId)
      // A video left waiting (videoNode.tsx): its own wait, which is longer,
      // and its own filling -- one frame, one video.
      const clipFrame = nodes.find(
        (node) => node.type === 'image' && (node.data as ImageNodeData).runId === runId && (node.data as ImageNodeData).clip,
      )
      if (clipFrame) {
        void (async () => {
          const run = await awaitVideo(runId)
          const data = clipFrame.data as ImageNodeData
          if (run.status === 'succeeded' && run.output) {
            const fileId = run.output.id
            setNodes((prev) =>
              prev.map((node) =>
                node.id === clipFrame.id
                  ? {
                      ...node,
                      data: {
                        fileId,
                        clip: true,
                        ...(data.aspect ? { aspect: data.aspect } : {}),
                        ...(data.made ? { made: { ...data.made, credits: run.credits } } : {}),
                      } satisfies ImageNodeData,
                    }
                  : node,
              ),
            )
            if (projectId) {
              void listProjectFiles(projectId, { thumbs: true })
                .then((rows) => takeFiles(rows))
                .catch(() => {})
            }
          } else {
            setNodes((prev) => prev.filter((node) => node.id !== clipFrame.id))
            setEdges((prev) => prev.filter((edge) => edge.source !== clipFrame.id && edge.target !== clipFrame.id))
          }
        })()
        continue
      }
      void (async () => {
        let run: PictureRun | null = null
        try {
          run = await awaitRun(runId)
        } catch (error) {
          console.warn('could not ask about a run left waiting on the board', runId, error)
        }
        const mine = latestNodes.current.filter(
          (node) => (node.data as ImageNodeData).runId === runId && (node.data as ImageNodeData).making,
        )
        const gone = new Set<string>()
        const filled = new Map<string, ImageNodeData>()
        for (const node of mine) {
          const data = node.data as ImageNodeData
          const picture = run?.status === 'succeeded' ? run.pictures[data.runIndex ?? 0] : undefined
          if (!picture) {
            gone.add(node.id)
            continue
          }
          // An Edit area: put together with its original first.
          if (data.compose) {
            void latest.current.composeInto(
              node.id,
              { store: picture.store, id: picture.id, url: picture.originalPath },
              data.compose,
              data.made ? { ...data.made, credits: run?.credits } : undefined,
            )
            continue
          }
          // Which skill made it, from the card wired to it -- the same
          // provenance a live run writes, less the references, which the
          // board no longer has in hand.
          const card = latestEdges.current.find((edge) => edge.target === node.id)
          const skill = latestNodes.current.find((one) => one.id === card?.source)
          filled.set(node.id, {
            ...(picture.store === 'library' ? { assetId: picture.id } : { fileId: picture.id }),
            from: {
              skill: String((skill?.data as { skillRef?: string } | undefined)?.skillRef ?? ''),
              inputs: [],
              at: new Date().toISOString(),
            },
            // Written on the frame when the run began, so a reload keeps it.
            ...(data.made ? { made: { ...data.made, credits: run?.credits } } : {}),
          })
        }
        setNodes((prev) =>
          prev
            .filter((node) => !gone.has(node.id))
            .map((node) => (filled.has(node.id) ? { ...node, data: filled.get(node.id)! } : node)),
        )
        if (gone.size > 0) {
          setEdges((prev) => prev.filter((edge) => !gone.has(edge.source) && !gone.has(edge.target)))
        }
      })()
    }
  }, [nodes, projectId, setNodes, setEdges, takeFiles])

  const tidy = useCallback(() => {
    if (picked.length < 2) return

    // By each picture's own shape, not its box: a box that crops its picture
    // would otherwise count as already the right height.
    const heights = picked.map((node) => pictureSizeOf(node).h).sort((a, b) => a - b)
    const target = heights[Math.floor(heights.length / 2)] ?? 0
    if (!target) return

    const sized = picked.map((node) => {
      const { w, h } = pictureSizeOf(node)
      const scale = h ? target / h : 1
      return { id: node.id, w: Math.round(w * scale), h: target }
    })

    const left = Math.min(...picked.map((node) => node.position.x))
    const top = Math.min(...picked.map((node) => node.position.y))
    // The selection keeps its overall width (Huanrui, 2026-11-02): rows are
    // filled left to right and wrap when the next picture would pass the
    // right edge the selection had before. All one height, so a row's right
    // end is wherever its last picture ends.
    const width = Math.max(...picked.map((node) => node.position.x + sizeOf(node).w)) - left
    // The gap is a share of the row's height, the same across and down
    // (Huanrui, 2026-10-05): 5%, so a wall of small pictures sits as tightly
    // as a wall of large ones. A fixed gap looked loose on small pictures.
    const gap = Math.max(1, Math.round(target * 0.05))

    const places = new Map<string, { x: number; y: number; w: number }>()
    let x = left
    let y = top
    for (const one of sized) {
      if (x > left && x - left + one.w > width) {
        x = left
        y += target + gap
      }
      places.set(one.id, { x, y, w: one.w })
      x += one.w + gap
    }

    remember(true)
    setNodes((prev) =>
      prev.map((node) => {
        const spot = places.get(node.id)
        return spot
          ? { ...node, position: { x: spot.x, y: spot.y }, width: spot.w, height: undefined }
          : node
      }),
    )
  }, [picked, remember, setNodes])

  /* ---------- what the nodes are handed ---------- */

  /**
   * How a picture was made, from whichever record it has.
   *
   * Its own `made` first: written by the card when it ran, and exact. Failing
   * that, the file's `made_by`, which the server writes on every picture a
   * model makes and which a copy carries with it — so it answers for pictures
   * made before `made` existed, for the first pictures of a board built from
   * the generate page, and for anything pasted in from another board. What
   * `made_by` lacks is the cost; that comes from this board's runs, fetched
   * the first time it is needed and kept, and is simply left off for a
   * picture whose run belongs to another project.
   */
  const pastRuns = useRef<Promise<HistoryRun[]> | null>(null)
  const describe = useCallback(
    async (data: ImageNodeData): Promise<MadeWithFound | null> => {
      let made = data.made
      const by = recordOf(data.fileId ? files.get(data.fileId) : undefined)
      if (!made && by) {
        let credits: number | undefined
        if (by.runId && projectId) {
          if (!pastRuns.current) {
            pastRuns.current = listRuns(projectId).catch((error: unknown) => {
              // Not kept: the next ask should try again, not get the same failure.
              pastRuns.current = null
              throw error
            })
          }
          try {
            credits = (await pastRuns.current).find((one) => one.id === by.runId)?.credits
          } catch (error) {
            // The cost is the one thing missing; the rest is still worth showing.
            console.warn('could not read the runs for a picture\'s cost', error)
          }
        }
        made = { ...by.made, credits }
      }
      if (!made) return null
      const slug = made.model
      // Crop and Expand (2026-10-27): no model to look up.
      if (made.crop) return { ...made, modelLabel: '' }
      if (made.expand) return { ...made, modelLabel: 'Bria Expand' }
      if (made.bria) return { ...made, modelLabel: BRIA_MODEL_LABEL[made.bria.op] }
      if (made.upscale) {
        const model = upscaling?.models.find((one) => one.key === slug)
        return {
          ...made,
          // A face switch the model does not have says nothing either way.
          upscale: { ...made.upscale, face: model && !model.face ? null : made.upscale.face },
          modelLabel: model?.label ?? slug,
        }
      }
      return { ...made, modelLabel: models.find((one) => one.slug === slug)?.label ?? slug }
    },
    [files, models, projectId, upscaling],
  )

  /**
   * Fresh links for every picture on the board: the Retry on a picture that
   * would not load. A signed link lasts two hours, and a board left open all
   * afternoon outlives it.
   */
  const refreshPictures = useCallback(() => {
    if (projectId) {
      void listProjectFiles(projectId, { thumbs: true })
        .then((rows) => takeFiles(rows, true))
        .catch((error: unknown) => console.warn('could not refresh the pictures', error))
    }
    // Library pictures are asked for again by the effect that fetches them.
    asked.current.clear()
    setUrls(new Map())
  }, [projectId])

  const selectedCount = useMemo(() => onPage.filter((node) => node.selected).length, [onPage])

  /**
   * A quarter turn clockwise, for looking at only (Huanrui, 2026-09-30).
   *
   * The node's box swaps its width and height about its middle, so the box is
   * the shape of what is seen and the ports stay at the middle of its left and
   * right sides; the picture inside is drawn turned (nodes.tsx). Nothing is
   * done to the picture: downloads, Miro and the models get it as it was made.
   * Four turns and it is back where it started.
   */
  const turnPicture = useCallback(
    (id: string, quarters = 1) => {
      const q = ((Math.round(quarters) % 4) + 4) % 4
      if (q === 0) return
      remember(true)
      setNodes((prev) =>
        prev.map((node) => {
          if (node.id !== id || node.type !== 'image') return node
          const box = boxOf(node)
          const turn = ((Number((node.data as { rotate?: unknown }).rotate) || 0) + 90 * q) % 360
          const data = { ...(node.data as Record<string, unknown>) }
          if (turn) data.rotate = turn
          else delete data.rotate
          // A half turn keeps the box's shape; a quarter either way swaps it.
          if (q === 2) return { ...node, data }
          return {
            ...node,
            position: { x: box.x + (box.w - box.h) / 2, y: box.y + (box.h - box.w) / 2 },
            width: Math.round(box.h),
            height: Math.round(box.w),
            data,
          }
        }),
      )
    },
    [remember, setNodes],
  )

  /* ---------- crop and edit area (pictureOps.ts, 2026-10-06) ---------- */

  /** Image 2.5 Sunburst, which Edit area always uses, and about what a run costs. */
  const sunburst = useMemo(() => models.find((one) => one.slug === CANVAS_EDIT_MODEL), [models])
  const editCredits = useMemo(
    () => (sunburst ? estimateCredits(sunburst, CANVAS_EDIT_QUALITY, 'auto', 1, 1) : 0),
    [sunburst],
  )

  /** A picture at full size, the way round it is shown on the board. */
  /*
   * A full-size picture, read once and kept (2026-10-26). An Edit area card
   * read the same picture for its windows, again to put the result together,
   * and again for its mask -- each time from a freshly signed link the
   * browser could not recognise, so a 12 MB PNG was downloaded and decoded
   * two or three times a run. Now the reading is shared, by picture and turn,
   * by every card and every step; the last few are kept (a 3840 x 2160
   * picture is about 33 MB once decoded). Nothing draws on what is handed
   * out: it is only read from.
   */
  const fullSizeRead = useRef(new Map<string, Promise<{ canvas: HTMLCanvasElement; png: boolean }>>())
  const sourceOfRef = useRef<(ref: ImageRef, turn: number) => Promise<{ canvas: HTMLCanvasElement; png: boolean }>>(() =>
    Promise.reject(new Error('Not ready')),
  )
  const sourceOf = useCallback((ref: ImageRef, turn: number): Promise<{ canvas: HTMLCanvasElement; png: boolean }> => {
    const key = `${ref.store}:${ref.id}:${turn}`
    const kept = fullSizeRead.current.get(key)
    if (kept) {
      // The most recently used last, so it is the last to be let go.
      fullSizeRead.current.delete(key)
      fullSizeRead.current.set(key, kept)
      // Should anything ever empty it, read it again rather than hand on a picture of no size.
      return kept.then((one) => {
        if (one.canvas.width > 0 && one.canvas.height > 0) return one
        if (fullSizeRead.current.get(key) === kept) fullSizeRead.current.delete(key)
        return sourceOfRef.current(ref, turn)
      })
    }
    const reading = (async () => {
      const started = performance.now()
      const [found] = await resolveImages([ref])
      const url = found?.originalPath
      if (!url) throw new Error('The picture could not be found')
      const { bitmap, png } = await loadBitmap(url)
      const canvas = turnedCanvas(bitmap, turn)
      bitmap.close()
      console.info(`[WonderWorks] full-size picture ready: ${canvas.width} x ${canvas.height}, ${Math.round(performance.now() - started)} ms`, key)
      return { canvas, png }
    })()
    fullSizeRead.current.set(key, reading)
    // A failed read is not kept: the next ask tries again.
    reading.catch(() => {
      if (fullSizeRead.current.get(key) === reading) fullSizeRead.current.delete(key)
    })
    while (fullSizeRead.current.size > FULL_SIZE_KEPT) {
      const oldest = fullSizeRead.current.keys().next().value
      if (oldest === undefined) break
      fullSizeRead.current.delete(oldest)
    }
    return reading
  }, [])
  sourceOfRef.current = sourceOf

  /** A drawn picture into the project, and into this board's list of files. */
  const keepCanvas = useCallback(
    async (id: string, canvas: HTMLCanvasElement, title: string, png: boolean, show = true) => {
      const started = performance.now()
      const file = await fileOf(canvas, title, png)
      const encoded = performance.now()
      const stored = await uploadProjectFile(id, file, { width: canvas.width, height: canvas.height })
      // Timed when it takes a while (2026-10-26): a full-size PNG is the slow part of Apply.
      const done = performance.now()
      if (done - started > 1500) {
        console.info(
          `[WonderWorks] picture saved "${title}": ${canvas.width} x ${canvas.height}, ${(file.size / 1048576).toFixed(1)} MB ${file.type} -- ` +
            `encode ${Math.round(encoded - started)} ms, upload ${Math.round(done - encoded)} ms`,
        )
      }
      if (show) setFiles((prev) => new Map(prev).set(stored.id, stored))
      return stored
    },
    [],
  )

  /*
   * PICTURES DRAWN TOGETHER, AS THEY SIT ON THE BOARD (Huanrui, 2026-10-06)
   *
   * Used by Merge (several pictures into one, like merging layers) and by
   * Copy as picture on a frame. Each picture is drawn from its full-size
   * file, turned as shown and cropped the way its box crops it, in the
   * board's stacking order. The result is as sharp as the sharpest picture
   * in it allows, up to 6,000 pixels on the longer side.
   */
  const flatten = useCallback(
    async (pictures: Node[], area: Rect, background?: string) => {
      const loaded = await Promise.all(
        pictures.map(async (node) => {
          const ref = refOf(node)
          if (!ref) return null
          const turn = Number((node.data as ImageNodeData).rotate) || 0
          const src = await sourceOf(ref, turn)
          return { box: boxOf(node), src }
        }),
      )
      const drawn = loaded.filter((one): one is NonNullable<typeof one> => one !== null)
      let k = 1
      for (const one of drawn) {
        const s = Math.max(one.box.w / one.src.canvas.width, one.box.h / one.src.canvas.height)
        k = Math.max(k, 1 / s)
      }
      k = Math.min(k, 6000 / Math.max(area.w, area.h), Math.sqrt(36_000_000 / (area.w * area.h)))
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(area.w * k))
      canvas.height = Math.max(1, Math.round(area.h * k))
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('This browser could not draw the pictures')
      ctx.imageSmoothingQuality = 'high'
      if (background) {
        ctx.fillStyle = background
        ctx.fillRect(0, 0, canvas.width, canvas.height)
      }
      for (const { box, src } of drawn) {
        const pw = src.canvas.width
        const ph = src.canvas.height
        // object-fit: cover, as the board draws it.
        const s = Math.max(box.w / pw, box.h / ph)
        const dw = pw * s
        const dh = ph * s
        ctx.save()
        ctx.beginPath()
        ctx.rect((box.x - area.x) * k, (box.y - area.y) * k, box.w * k, box.h * k)
        ctx.clip()
        ctx.drawImage(
          src.canvas,
          (box.x + (box.w - dw) / 2 - area.x) * k,
          (box.y + (box.h - dh) / 2 - area.y) * k,
          dw * k,
          dh * k,
        )
        ctx.restore()
      }
      return { canvas, png: !background || drawn.some((one) => one.src.png) }
    },
    [sourceOf],
  )

  /**
   * Merge (right-click, under Mark): the selected pictures become one, as they
   * are laid out, where they were -- like merging layers. One step on the
   * undo stack; the originals' files are left in the project.
   */
  const mergePictures = useCallback(async () => {
    const picked = nodes.filter((node) => node.selected && node.type === 'image' && refOf(node))
    if (picked.length < 2) return
    const id = projectId || (await ensureProject())
    if (!id) return
    const boxes = picked.map(boxOf)
    const x0 = Math.min(...boxes.map((b) => b.x))
    const y0 = Math.min(...boxes.map((b) => b.y))
    const x1 = Math.max(...boxes.map((b) => b.x + b.w))
    const y1 = Math.max(...boxes.map((b) => b.y + b.h))
    const area = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }
    setNote(`Merging ${picked.length} pictures…`)
    try {
      const { canvas, png } = await flatten(picked, area)
      const stored = await keepCanvas(id, canvas, 'Merged', png)
      const gone = new Set(picked.map((node) => node.id))
      const homes = new Set(picked.map((node) => frameOf(node)))
      const home = homes.size === 1 ? [...homes][0] : undefined
      const mergedId = newId('image')
      remember(true)
      setNodes((prev) => [
        ...prev.filter((node) => !gone.has(node.id)).map((node) => (node.selected ? { ...node, selected: false } : node)),
        {
          id: mergedId,
          type: 'image',
          position: { x: area.x, y: area.y },
          width: Math.round(area.w),
          height: Math.round(area.h),
          selected: true,
          data: { fileId: stored.id, title: stored.title, ...(home ? { frame: home } : {}) } satisfies ImageNodeData,
        } as Node,
      ])
      setEdges((prev) => prev.filter((edge) => !gone.has(edge.source) && !gone.has(edge.target)))
      setNote(`${picked.length} pictures merged into one`)
    } catch (error) {
      setAlert(`They could not be merged: ${error instanceof Error ? error.message : 'no answer'}`)
    }
  }, [ensureProject, flatten, keepCanvas, nodes, projectId, remember, setEdges, setNodes])

  /** Take a frame, and its wires, off the board. */
  const dropFrame = useCallback(
    (frameId: string) => {
      setNodes((prev) => prev.filter((node) => node.id !== frameId))
      setEdges((prev) => prev.filter((edge) => edge.source !== frameId && edge.target !== frameId))
    },
    [setNodes, setEdges],
  )

  /*
   * EDIT AREA STUDIOS (editNode.tsx, Huanrui 2026-10-06)
   *
   * Each Edit area card shows two windows of the full-size original. After a
   * run the lower one shows the result, and a brush paints where the model's
   * drawing shows and where the original does. What the card draws from is
   * kept here, per card, in memory: the turned original, and after a run the
   * fitted patch and its mask. The mask is painted on the card directly; Apply
   * reads it from here. Saved with the board is only `blend` on the card --
   * the model's picture, the recipe, and the mask once applied -- from which
   * this is rebuilt when the board is opened again.
   */
  const studios = useRef(new Map<string, EditStudio & { key: string; patchKey: string }>())
  const [studioTick, setStudioTick] = useState(0)
  const putStudio = useCallback((cardId: string, change: Partial<EditStudio & { key: string; patchKey: string }>) => {
    const was = studios.current.get(cardId) ?? { source: null, patch: null, version: 0, key: '', patchKey: '' }
    studios.current.set(cardId, { ...was, ...change, version: was.version + 1 })
    setStudioTick((tick) => tick + 1)
  }, [])
  const studioLoading = useRef(new Set<string>())

  /** A saved mask, back as a canvas the size of the patch. */
  const maskFrom = useCallback(async (ref: ImageRef, place: Rect): Promise<HTMLCanvasElement> => {
    const [found] = await resolveImages([ref])
    if (!found?.originalPath) throw new Error('The mask could not be found')
    const { bitmap } = await loadBitmap(found.originalPath)
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(place.w))
    canvas.height = Math.max(1, Math.round(place.h))
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()
    return canvas
  }, [])

  // Keep each card's studio in step with the picture wired into it and its last result.
  useEffect(() => {
    // A card that is gone (deleted, cut, undone) lets go of its full-size
    // pictures at once; until 2026-10-15 they were held until the page was
    // reloaded, a few hundred MB of the graphics card each.
    let dropped = false
    for (const id of [...studios.current.keys()]) {
      if (!nodes.some((node) => node.id === id && node.type === 'edit')) {
        releaseStudio(studios.current.get(id))
        studios.current.delete(id)
        dropped = true
      }
    }
    if (dropped) setStudioTick((tick) => tick + 1)
    for (const node of nodes) {
      if (node.type !== 'edit') continue
      // A folded card holds nothing: its full-size pictures are let go, and
      // read again when it is opened (Huanrui, 2026-10-06). A board with a
      // few of these folded costs no more memory than one without.
      if ((node.data as EditNodeData).open === false) {
        if (studios.current.has(node.id)) {
          releaseStudio(studios.current.get(node.id))
          studios.current.delete(node.id)
          setStudioTick((tick) => tick + 1)
        }
        continue
      }
      const into = edges.find((edge) => edge.target === node.id && isEditMain(edge))
      const picture = into ? nodes.find((one) => one.id === into.source) : undefined
      const ref = picture ? refOf(picture) : null
      const turn = picture ? Number((picture.data as ImageNodeData).rotate) || 0 : 0
      const key = ref ? `${ref.store}:${ref.id}:${turn}` : ''
      const held = studios.current.get(node.id)
      if ((held?.key ?? '') !== key || (ref && !held?.source && !studioLoading.current.has(`${node.id}:source`))) {
        if ((held?.key ?? '') !== key) putStudio(node.id, { key, source: null, patch: null, patchKey: '' })
        if (ref) {
          const loading = `${node.id}:source`
          studioLoading.current.add(loading)
          void sourceOf(ref, turn)
            .then(({ canvas }) => {
              if (studios.current.get(node.id)?.key === key) putStudio(node.id, { source: canvas })
            })
            .catch((error: unknown) => console.warn('could not read the picture for an Edit area card', node.id, error))
            .finally(() => studioLoading.current.delete(loading))
        }
      }
      const blend = (node.data as EditNodeData).blend
      const now = studios.current.get(node.id)
      const loadingPatch = `${node.id}:patch`
      if (
        blend &&
        ref &&
        blend.recipe.source.id === ref.id &&
        blend.recipe.turn === turn &&
        now?.patchKey !== blend.drawn.id &&
        !studioLoading.current.has(loadingPatch)
      ) {
        studioLoading.current.add(loadingPatch)
        void (async () => {
          const [found] = await resolveImages([blend.drawn])
          if (!found?.originalPath) throw new Error('The edited picture could not be found')
          const [{ bitmap }, source] = await Promise.all([
            loadBitmap(found.originalPath),
            studios.current.get(node.id)?.source
              ? Promise.resolve({ canvas: studios.current.get(node.id)!.source! })
              : sourceOf(blend.recipe.source, blend.recipe.turn),
          ])
          const fitted = fit(source.canvas, bitmap, blend.recipe)
          bitmap.close()
          const mask = blend.mask ? await maskFrom(blend.mask, fitted.place) : defaultMask(blend.recipe, fitted.place)
          if (studios.current.get(node.id)?.key !== key) return
          putStudio(node.id, {
            source: studios.current.get(node.id)?.source ?? source.canvas,
            patch: { layer: patchLayer(fitted), mask, place: fitted.place },
            patchKey: blend.drawn.id,
          })
        })()
          .catch((error: unknown) => console.warn('could not rebuild an Edit area result', node.id, error))
          .finally(() => studioLoading.current.delete(loadingPatch))
      }
    }
  }, [nodes, edges, maskFrom, putStudio, sourceOf])

  /**
   * The model's picture, put together with its original and filled into the
   * waiting frame: lined up and colour-matched (fit), and let through only
   * inside the box with a soft edge. The card that made it gets what it needs
   * to be painted further. Should that fail, the frame shows the model's
   * picture as it came rather than nothing: it was paid for.
   */
  const composeInto = useCallback(
    async (
      frameId: string,
      drawn: { store: 'library' | 'project'; id: string; url: string },
      recipe: ComposeRecipe,
      made?: MadeWith,
      /** The Edit area card it came from, when known; otherwise found by the wire into the frame. */
      fromCard?: string,
    ) => {
      const fill = (data: ImageNodeData) =>
        setNodes((prev) =>
          prev.map((node) =>
            node.id === frameId
              ? { ...node, data: { ...data, ...(frameOf(node) ? { frame: frameOf(node) } : {}) } satisfies ImageNodeData }
              : node,
          ),
        )
      const from = { skill: EDIT_SKILL.id, inputs: [recipe.source], at: new Date().toISOString() }
      const raw = () =>
        fill({
          ...(drawn.store === 'library' ? { assetId: drawn.id } : { fileId: drawn.id }),
          from,
          ...(made ? { made } : {}),
        })
      // A frame left from the few hours Expand existed: shown as the model drew it.
      if (recipe.kind !== 'edit') return raw()
      try {
        if (!projectId) throw new Error('This board has no project')
        const [{ bitmap }, source] = await Promise.all([loadBitmap(drawn.url), sourceOf(recipe.source, recipe.turn)])
        const fitted = fit(source.canvas, bitmap, recipe)
        const got = { w: bitmap.width, h: bitmap.height }
        bitmap.close()
        const mask = defaultMask(recipe, fitted.place)
        const out = compose(source.canvas, fitted, recipe, mask)
        const stored = await keepCanvas(projectId, out, recipe.title, recipe.png)
        fill({ fileId: stored.id, title: stored.title, from, ...(made ? { made } : {}) })
        const card =
          fromCard ??
          latestEdges.current.find(
            (edge) =>
              edge.target === frameId && latestNodes.current.find((one) => one.id === edge.source)?.type === 'edit',
          )?.source
        if (card) {
          setNodes((prev) =>
            prev.map((node) =>
              node.id === card
                ? {
                    ...node,
                    data: {
                      ...node.data,
                      blend: { frame: frameId, drawn: { store: drawn.store, id: drawn.id }, recipe: { ...recipe, got } },
                    },
                  }
                : node,
            ),
          )
          const key = `${recipe.source.store}:${recipe.source.id}:${recipe.turn}`
          if (studios.current.get(card)?.key === key) {
            putStudio(card, {
              source: studios.current.get(card)?.source ?? source.canvas,
              patch: { layer: patchLayer(fitted), mask, place: fitted.place },
              patchKey: drawn.id,
            })
          }
        }
      } catch (error) {
        console.warn('could not put the picture back together', frameId, error)
        raw()
        setAlert(
          `The new picture could not be joined to the original, so it is shown as the model drew it` +
            (error instanceof Error ? ` (${error.message})` : ''),
        )
      }
    },
    [keepCanvas, projectId, putStudio, setNodes, sourceOf],
  )

  /** The picture and its mask, up to the project, for the model to read. */
  const sendUp = useCallback(
    async (id: string, request: HTMLCanvasElement): Promise<ImageRef> => {
      const image = await keepCanvas(id, request, 'edit-input', true, false)
      return { store: 'project', id: image.id }
    },
    [keepCanvas],
  )

  /**
   * Apply, on an Edit area card: the original with the painted patch over
   * it, at full size, in place of the picture the run made. The mask is kept
   * with the project too, so the painting is there when the board is opened
   * again. Nothing is sent to a model.
   */
  const applyBlend = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      const blend = (card?.data as EditNodeData | undefined)?.blend
      const studio = studios.current.get(cardId)
      if (!card || !blend || !projectId || !studio?.source || !studio.patch) return
      const patchCard = (patch: Partial<EditNodeData>) =>
        setNodes((prev) =>
          prev.map((node) => (node.id === cardId ? { ...node, data: { ...node.data, ...patch } } : node)),
        )
      patchCard({ applying: true, error: '' })

      // The picture on the board: the one an earlier Apply made, or a new one
      // to the right of the card. Either way the loader is on it, not on the
      // card, while the full-size result is put together (Huanrui, 2026-10-06).
      const existing = blend.frame ? latestNodes.current.find((node) => node.id === blend.frame) : undefined
      const frameId = existing ? existing.id : newId('image')
      if (existing) {
        setNodes((prev) =>
          prev.map((node) => (node.id === frameId ? { ...node, data: { ...node.data, working: true } } : node)),
        )
      } else {
        ranHere.current.add(frameId)
        const box = sizeOf(card)
        const r = blend.recipe
        setNodes((prev) => [
          ...prev,
          {
            id: frameId,
            type: 'image',
            position: { x: card.position.x + box.w + FAMILY_GAP, y: card.position.y + box.h / 2 - FRAME_HEIGHT / 2 },
            width: 220,
            data: { making: true, drawing: true, aspect: r.out.w / r.out.h, title: r.title } satisfies ImageNodeData,
          } as Node,
        ])
        setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frameId })])
      }

      try {
        const { layer, mask, place } = studio.patch
        const out = document.createElement('canvas')
        out.width = studio.source.width
        out.height = studio.source.height
        const ctx = out.getContext('2d')
        if (!ctx) throw new Error('This browser could not draw the picture')
        ctx.drawImage(studio.source, 0, 0)
        ctx.drawImage(maskedPatch(layer, mask), place.x, place.y, place.w, place.h)
        const [stored, keptMask] = await Promise.all([
          keepCanvas(projectId, out, blend.recipe.title, blend.recipe.png),
          keepCanvas(projectId, mask, 'edit-brush', true, false),
        ])
        setNodes((prev) =>
          prev.map((node) => {
            if (node.id !== frameId) return node
            const kept = node.data as ImageNodeData
            return {
              ...node,
              data: {
                fileId: stored.id,
                title: stored.title,
                from: { skill: EDIT_SKILL.id, inputs: [blend.recipe.source], at: new Date().toISOString() },
                ...(blend.made ? { made: blend.made } : {}),
                ...(kept.frame ? { frame: kept.frame } : {}),
                ...(kept.rotate ? { rotate: kept.rotate } : {}),
                ...(kept.mark ? { mark: kept.mark } : {}),
              } satisfies ImageNodeData,
            }
          }),
        )
        // Folded once it is applied: the work is in the picture now, and a
        // folded card lets go of its full-size copies (studios, above).
        patchCard({
          applying: false,
          open: false,
          blend: { ...blend, frame: frameId, mask: { store: 'project', id: keptMask.id } },
        })
        setNote('Applied')
      } catch (error) {
        if (existing) {
          setNodes((prev) =>
            prev.map((node) => (node.id === frameId ? { ...node, data: { ...node.data, working: false } } : node)),
          )
        } else {
          dropFrame(frameId)
        }
        patchCard({ applying: false, error: error instanceof Error ? error.message : 'Could not apply it' })
      }
    },
    [dropFrame, keepCanvas, nodes, projectId, setEdges, setNodes],
  )

  /**
   * Crop mode finished (CropBox.tsx): the part inside the box, cut from the
   * full-size file at once, lands to the right of the original, joined to it
   * by a dashed line. The original stays as it was. One step on the undo
   * stack.
   */
  const cropPicture = useCallback(
    async (nodeId: string, rect: Rect, box: { w: number; h: number }) => {
      setCroppingId(null)
      const node = nodes.find((one) => one.id === nodeId)
      const ref = node ? refOf(node) : null
      if (!node || !ref) return
      const id = projectId || (await ensureProject())
      if (!id) return
      const data = node.data as ImageNodeData
      const turn = Number(data.rotate) || 0
      const title = (data.title || 'picture').replace(/\.[a-z0-9]+$/i, '')

      const frameId = newId('image')
      remember(true)
      setNodes((prev) => [
        ...prev.map((one) => (one.selected ? { ...one, selected: false } : one)),
        {
          id: frameId,
          type: 'image',
          position: { x: node.position.x + box.w + 80, y: node.position.y + rect.y },
          width: Math.max(40, Math.round(rect.w)),
          data: { making: true, aspect: rect.w / rect.h, title } satisfies ImageNodeData,
        } as Node,
      ])
      setEdges((prev) => [...prev, wire({ id: newId('edge'), source: nodeId, target: frameId })])

      try {
        const source = await sourceOf(ref, turn)
        const pw = source.canvas.width
        const ph = source.canvas.height
        // The box may show less than the whole picture (object-fit: cover), so
        // the box's units are mapped onto the picture, not simply scaled.
        const s = Math.max(box.w / pw, box.h / ph)
        const ox = (box.w - pw * s) / 2
        const oy = (box.h - ph * s) / 2
        const area = { x: (rect.x - ox) / s, y: (rect.y - oy) / s, w: rect.w / s, h: rect.h / s }
        const out = cropped(source.canvas, area)
        const stored = await keepCanvas(id, out, `${title} crop`, source.png)
        // What the corner button says about it (2026-10-27).
        const made: MadeWith = {
          prompt: '',
          model: '',
          size: '',
          quality: '',
          at: new Date().toISOString(),
          crop: { from: title, size: `${out.width} × ${out.height}`, of: `${pw} × ${ph}` },
        }
        setNodes((prev) =>
          prev.map((one) =>
            one.id === frameId
              ? { ...one, data: { fileId: stored.id, title: stored.title, made } satisfies ImageNodeData }
              : one,
          ),
        )
      } catch (error) {
        dropFrame(frameId)
        setAlert(`The crop could not be saved: ${error instanceof Error ? error.message : 'it did not upload'}`)
      }
    },
    [dropFrame, ensureProject, keepCanvas, nodes, projectId, remember, setEdges, setNodes, sourceOf],
  )

  /**
   * Expand (Huanrui, 2026-10-26): the picture made larger by Bria Expand (fal),
   * the picture itself kept exactly as it is.
   *
   * 1. The new picture's size and the original's place in it, in full-size
   *    pixels, from the crop box dragged out past the picture's edges
   *    (CropBox.tsx). A box may crop one side and expand another: what is
   *    left of the picture is laid over Bria's picture just the same.
   * 2. Bria works on a copy no larger than EXPAND_MODEL_PX in all; it is sent
   *    that copy and told where it sits (routes/expand.ts).
   * 3. What comes back is drawn at full size, and the full-size original laid
   *    over it pixel for pixel -- only what is round it is Bria's.
   * 4. The finished picture lands to the right, joined by a dashed line, like
   *    a crop. Bria's own picture is deleted once the finished one is kept.
   */
  const expandPicture = useCallback(
    async (nodeId: string, rect: Rect, box: { w: number; h: number }, prompt: string) => {
      setCroppingId(null)
      const node = nodes.find((one) => one.id === nodeId)
      const ref = node ? refOf(node) : null
      if (!node || !ref) return
      const id = projectId || (await ensureProject())
      if (!id) return
      const data = node.data as ImageNodeData
      const turn = Number(data.rotate) || 0
      const title = (data.title || 'picture').replace(/\.[a-z0-9]+$/i, '')

      const frameId = newId('image')
      remember(true)
      setNodes((prev) => [
        ...prev.map((one) => (one.selected ? { ...one, selected: false } : one)),
        {
          id: frameId,
          type: 'image',
          position: { x: node.position.x + Math.max(box.w, rect.x + rect.w) + 80, y: node.position.y + rect.y },
          width: Math.max(40, Math.round(rect.w)),
          data: { making: true, aspect: rect.w / rect.h, title } satisfies ImageNodeData,
        } as Node,
      ])
      setEdges((prev) => [...prev, wire({ id: newId('edge'), source: nodeId, target: frameId })])

      const started = performance.now()
      try {
        const source = await sourceOf(ref, turn)
        const pw = source.canvas.width
        const ph = source.canvas.height
        // The box's units onto the picture's pixels (the box may show it cut, object-fit: cover).
        const s = Math.max(box.w / pw, box.h / ph)
        const offX = (box.w - pw * s) / 2
        const offY = (box.h - ph * s) / 2
        // The new picture in the original's pixels; a side inside the picture crops it.
        const x0 = Math.round((rect.x - offX) / s)
        const y0 = Math.round((rect.y - offY) / s)
        const x1 = Math.round((rect.x + rect.w - offX) / s)
        const y1 = Math.round((rect.y + rect.h - offY) / s)
        const W = x1 - x0
        const H = y1 - y0
        if (W * H > EXPAND_MOST_PX) throw new Error('That would be too large a picture -- make the box smaller')
        /*
         * The size Bria works at: the same shape, no more than EXPAND_MODEL_PX
         * in all. Each way across has its own scale, so what comes back,
         * drawn at full size, lines up with the original exactly.
         */
        const k = Math.min(1, Math.sqrt(EXPAND_MODEL_PX / (W * H)))
        const cw = Math.max(64, Math.round(W * k))
        const ch = Math.max(64, Math.round(H * k))
        const kx = cw / W
        const ky = ch / H
        // The whole picture and where it sits; a part outside the new picture is cut off.
        const ow = Math.max(16, Math.round(pw * kx))
        const oh = Math.max(16, Math.round(ph * ky))
        const ox = Math.round(-x0 * kx)
        const oy = Math.round(-y0 * ky)
        const copy = document.createElement('canvas')
        copy.width = ow
        copy.height = oh
        const cctx = copy.getContext('2d')
        if (!cctx) throw new Error('This browser could not draw the picture')
        cctx.fillStyle = '#ffffff'
        cctx.fillRect(0, 0, ow, oh)
        cctx.imageSmoothingQuality = 'high'
        cctx.drawImage(source.canvas, 0, 0, ow, oh)
        const sent = await new Promise<Blob>((resolve, reject) =>
          copy.toBlob((out) => (out ? resolve(out) : reject(new Error('The picture could not be prepared'))), 'image/jpeg', 0.95),
        )
        console.info(
          `[WonderWorks] expand: ${pw} x ${ph} -> ${W} x ${H}; Bria works at ${cw} x ${ch} with the picture at ${ox},${oy} ${ow} x ${oh}` +
            ` (${(sent.size / 1048576).toFixed(1)} MB sent)`,
        )
        const made = await expandWithFal({
          projectId: id,
          picture: sent,
          canvas: { w: cw, h: ch },
          place: { x: ox, y: oy, w: ow, h: oh },
          prompt,
          from: `${ref.store}:${ref.id}`,
          title,
        })
        const [found] = await resolveImages([{ store: 'project', id: made.fileId }])
        if (!found?.originalPath) throw new Error('Bria answered, but its picture could not be found')
        const { bitmap } = await loadBitmap(found.originalPath)
        if (bitmap.width !== cw || bitmap.height !== ch) {
          console.warn(`[WonderWorks] expand: asked for ${cw} x ${ch}, Bria made ${bitmap.width} x ${bitmap.height}`)
        }
        // Bria's picture at full size, then the original over it, untouched.
        const out = document.createElement('canvas')
        out.width = W
        out.height = H
        const octx = out.getContext('2d')
        if (!octx) throw new Error('This browser could not draw the picture')
        octx.imageSmoothingQuality = 'high'
        octx.drawImage(bitmap, 0, 0, W, H)
        bitmap.close()
        octx.drawImage(source.canvas, -x0, -y0)
        const stored = await keepCanvas(id, out, `${title} expanded`, source.png)
        // What the corner button says about it (2026-10-27).
        const record: MadeWith = {
          prompt,
          model: 'fal-bria-expand',
          size: '',
          quality: '',
          credits: FAL_EXPAND_CREDITS,
          at: new Date().toISOString(),
          expand: { from: title, before: `${pw} × ${ph}`, after: `${W} × ${H}` },
        }
        setNodes((prev) =>
          prev.map((one) =>
            one.id === frameId
              ? { ...one, data: { fileId: stored.id, title: stored.title, made: record } satisfies ImageNodeData }
              : one,
          ),
        )
        void deleteProjectFile(id, made.fileId).catch(() => undefined)
        console.info(`[WonderWorks] expand: done in ${Math.round((performance.now() - started) / 1000)} s`)
      } catch (error) {
        dropFrame(frameId)
        setAlert(`The picture could not be expanded: ${error instanceof Error ? error.message : 'it did not come back'}`)
      }
    },
    [dropFrame, ensureProject, keepCanvas, nodes, projectId, remember, setEdges, setNodes, sourceOf],
  )

  /*
   * BACKGROUND AND ERASER (backgroundNode.tsx, eraserNode.tsx, Huanrui 2026-10-27)
   *
   * Both take one picture and send Bria (routes/bria.ts) a copy of it no
   * larger than BRIA_MODEL_PX. Run puts a frame with the loader to the right
   * of the card, wired to it, and folds the card. What Bria makes is only
   * used where it was asked for: the finished picture is put together here at
   * full size from the original's own pixels -- the subject, for Background;
   * everything not painted, for Eraser -- saved as a file of its own, and
   * Bria's picture let go. Not a step on the undo stack, like Expand.
   */
  const beginCardRun = useCallback(
    (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return null
      const patchCard = (change: (was: Record<string, unknown>) => Record<string, unknown>) =>
        setNodes((prev) =>
          prev.map((node) =>
            node.id === cardId ? { ...node, data: { ...node.data, ...change(node.data as Record<string, unknown>) } } : node,
          ),
        )
      const source = edges
        .filter((edge) => edge.target === cardId)
        .map((edge) => nodes.find((node) => node.id === edge.source))
        .find((node): node is Node => Boolean(node) && refOf(node!) !== null)
      const ref = source ? refOf(source) : null
      if (!source || !ref) {
        patchCard(() => ({ error: 'Connect a picture first' }))
        return null
      }
      const runId = newId('local')
      const shape = pixelsOf(source)
      const box = sizeOf(card)
      const below = edges.filter((edge) => edge.source === cardId).length
      const frameId = newId('image')
      ranHere.current.add(frameId)
      const frame = {
        id: frameId,
        type: 'image',
        position: {
          x: card.position.x + box.w + FAMILY_GAP,
          y: card.position.y + box.h / 2 - FRAME_HEIGHT / 2 + below * (FRAME_HEIGHT + STACK_GAP),
        },
        width: 220,
        data: {
          making: true,
          drawing: true,
          ...(shape.width && shape.height ? { aspect: shape.width / shape.height } : {}),
        } satisfies ImageNodeData,
      } as Node
      // Folded as it starts (Huanrui, 2026-10-27): the frame on the right says it is going.
      patchCard((was) => ({ error: '', open: false, runs: [...((was.runs as string[] | undefined) ?? []), runId] }))
      setNodes((prev) => [...prev, frame])
      setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frameId })])
      const settle = (change: (was: Record<string, unknown>) => Record<string, unknown>) =>
        patchCard((was) => ({ ...change(was), runs: ((was.runs as string[] | undefined) ?? []).filter((one) => one !== runId) }))
      /** The finished picture into the frame. */
      const fill = (fileId: string, title: string, made: MadeWith, w: number, h: number) =>
        setNodes((prev) =>
          prev.map((node) =>
            node.id === frameId
              ? {
                  ...node,
                  height: Math.round((220 * h) / Math.max(1, w)),
                  data: {
                    fileId,
                    title,
                    from: { skill: String((card.data as { skillRef?: unknown }).skillRef ?? ''), inputs: [ref], at: made.at },
                    made,
                  } satisfies ImageNodeData,
                }
              : node,
          ),
        )
      return { card, source, ref, frameId, settle, fill }
    },
    [edges, nodes, pixelsOf, setEdges, setNodes],
  )

  /** A picture Bria made, read back to be put together with the original. */
  const briaBitmap = useCallback(async (fileId: string): Promise<ImageBitmap> => {
    const [found] = await resolveImages([{ store: 'project', id: fileId }])
    if (!found?.originalPath) throw new Error('Bria answered, but its picture could not be found')
    return (await loadBitmap(found.originalPath)).bitmap
  }, [])

  const runBackground = useCallback(
    async (cardId: string) => {
      const begun = beginCardRun(cardId)
      if (!begun) return
      const { source, ref, frameId, settle, fill } = begun
      const credits = BRIA_CREDITS.remove
      let id = ''
      let rawId = ''
      try {
        id = projectId || (await ensureProject()) || ''
        if (!id) throw new Error('Save the board first — put something on it')
        const sdata = source.data as ImageNodeData
        const full = await sourceOf(ref, Number(sdata.rotate) || 0)
        const W = full.canvas.width
        const H = full.canvas.height
        const title = (sdata.title || 'picture').replace(/\.[a-z0-9]+$/i, '')
        const sent = await blobOf(modelCopy(full.canvas, full.png), full.png ? 'image/png' : 'image/jpeg', 0.95)
        const cut = await runBria({ op: 'remove', projectId: id, picture: sent, from: `${ref.store}:${ref.id}`, title })
        rawId = cut.fileId

        // The subject: the original's own pixels, see-through where Bria's cut-out is, at full size.
        const out = document.createElement('canvas')
        out.width = W
        out.height = H
        const octx = out.getContext('2d')
        if (!octx) throw new Error('This browser could not draw the picture')
        octx.imageSmoothingQuality = 'high'
        const cutOut = await briaBitmap(cut.fileId)
        octx.drawImage(cutOut, 0, 0, W, H)
        cutOut.close()
        octx.globalCompositeOperation = 'source-in'
        octx.drawImage(full.canvas, 0, 0)
        octx.globalCompositeOperation = 'source-over'

        // See-through needs a PNG.
        const stored = await keepCanvas(id, out, `${title} no background`, true)
        const made: MadeWith = {
          prompt: '',
          model: 'fal-bria-rmbg',
          size: '',
          quality: '',
          credits,
          at: new Date().toISOString(),
          bria: { op: 'remove', from: title, size: `${W} × ${H}` },
        }
        fill(stored.id, stored.title, made, W, H)
        settle((was) => ({ error: '', credits, madeCount: (Number(was.madeCount) || 0) + 1 }))
      } catch (error) {
        dropFrame(frameId)
        settle(() => ({ error: `The background could not be removed: ${error instanceof Error ? error.message : 'it did not come back'}` }))
      } finally {
        if (rawId) void deleteProjectFile(id, rawId).catch(() => undefined)
      }
    },
    [beginCardRun, briaBitmap, dropFrame, ensureProject, keepCanvas, projectId, sourceOf],
  )

  const runEraser = useCallback(
    async (cardId: string) => {
      const card0 = nodes.find((node) => node.id === cardId)
      const into = card0 ? edges.find((edge) => edge.target === cardId) : undefined
      const wiredTo = into ? nodes.find((node) => node.id === into.source) : undefined
      const strokes: EraseStroke[] = card0 && wiredTo ? strokesOf(card0.data as EraserNodeData, pictureKeyOf(wiredTo)) : []
      if (card0 && wiredTo && strokes.length === 0) {
        setNodes((prev) =>
          prev.map((node) => (node.id === cardId ? { ...node, data: { ...node.data, error: 'Paint over what to take out first' } } : node)),
        )
        return
      }
      const begun = beginCardRun(cardId)
      if (!begun) return
      const { source, ref, frameId, settle, fill } = begun
      let id = ''
      let rawId = ''
      try {
        id = projectId || (await ensureProject()) || ''
        if (!id) throw new Error('Save the board first — put something on it')
        const sdata = source.data as ImageNodeData
        const full = await sourceOf(ref, Number(sdata.rotate) || 0)
        const W = full.canvas.width
        const H = full.canvas.height
        const title = (sdata.title || 'picture').replace(/\.[a-z0-9]+$/i, '')

        // What Bria sees, and the mask at the same size: white where to erase,
        // a little past the paint so nothing of what was painted is left at its edge.
        const copy = modelCopy(full.canvas, full.png)
        const mask = document.createElement('canvas')
        mask.width = copy.width
        mask.height = copy.height
        const mctx = mask.getContext('2d')
        if (!mctx) throw new Error('This browser could not draw the picture')
        mctx.fillStyle = '#000000'
        mctx.fillRect(0, 0, mask.width, mask.height)
        mctx.strokeStyle = '#ffffff'
        paintStrokes(mctx, strokes, mask.width, mask.height, Math.max(2, mask.width * 0.003))
        const made0 = await runBria({
          op: 'erase',
          projectId: id,
          picture: await blobOf(copy, full.png ? 'image/png' : 'image/jpeg', 0.95),
          mask: await blobOf(mask, 'image/png'),
          png: full.png,
          from: `${ref.store}:${ref.id}`,
          title,
        })
        rawId = made0.fileId
        mask.width = 0
        mask.height = 0
        if (copy !== full.canvas) {
          copy.width = 0
          copy.height = 0
        }

        /*
         * Bria's picture only where it was painted, at full size, with a soft
         * edge a little past the paint; every other pixel the original's. Done
         * only over the painted part's bounds, so a large picture needs one
         * full-size canvas, not three.
         */
        const grow = Math.max(2, W * 0.003)
        const feather = Math.max(4, Math.round(Math.max(W, H) * 0.006))
        let x0 = W
        let y0 = H
        let x1 = 0
        let y1 = 0
        for (const stroke of strokes) {
          const r = stroke.r * W + grow + feather
          for (let i = 0; i + 1 < stroke.pts.length; i += 2) {
            const x = stroke.pts[i]! * W
            const y = stroke.pts[i + 1]! * H
            x0 = Math.min(x0, x - r)
            y0 = Math.min(y0, y - r)
            x1 = Math.max(x1, x + r)
            y1 = Math.max(y1, y + r)
          }
        }
        const bx = Math.max(0, Math.floor(x0))
        const by = Math.max(0, Math.floor(y0))
        const bw = Math.min(W, Math.ceil(x1)) - bx
        const bh = Math.min(H, Math.ceil(y1)) - by
        if (bw <= 0 || bh <= 0) throw new Error('Nothing was painted on the picture')

        const erased = await briaBitmap(made0.fileId)
        const part = document.createElement('canvas')
        part.width = bw
        part.height = bh
        const pctx = part.getContext('2d')
        if (!pctx) throw new Error('This browser could not draw the picture')
        pctx.imageSmoothingQuality = 'high'
        const sx = erased.width / W
        const sy = erased.height / H
        pctx.drawImage(erased, bx * sx, by * sy, bw * sx, bh * sy, 0, 0, bw, bh)
        erased.close()
        // The soft edge: the paint drawn a few times, each a little wider and fainter, added up.
        const soft = document.createElement('canvas')
        soft.width = bw
        soft.height = bh
        const fctx = soft.getContext('2d')
        if (!fctx) throw new Error('This browser could not draw the picture')
        fctx.translate(-bx, -by)
        fctx.strokeStyle = '#ffffff'
        fctx.globalCompositeOperation = 'lighter'
        const STEPS = 6
        for (let i = 0; i < STEPS; i += 1) {
          fctx.globalAlpha = 1 / STEPS
          paintStrokes(fctx, strokes, W, H, grow + (feather * (STEPS - 1 - i)) / (STEPS - 1))
        }
        pctx.globalCompositeOperation = 'destination-in'
        pctx.drawImage(soft, 0, 0)
        soft.width = 0
        soft.height = 0

        const out = document.createElement('canvas')
        out.width = W
        out.height = H
        const octx = out.getContext('2d')
        if (!octx) throw new Error('This browser could not draw the picture')
        octx.drawImage(full.canvas, 0, 0)
        octx.drawImage(part, bx, by)
        part.width = 0
        part.height = 0
        const stored = await keepCanvas(id, out, `${title} erased`, full.png)
        const made: MadeWith = {
          prompt: '',
          model: 'fal-bria-eraser',
          size: '',
          quality: '',
          credits: BRIA_CREDITS.erase,
          at: new Date().toISOString(),
          bria: { op: 'erase', from: title, size: `${W} × ${H}`, strokes: strokes.length },
        }
        fill(stored.id, stored.title, made, W, H)
        settle((was) => ({ error: '', credits: BRIA_CREDITS.erase, madeCount: (Number(was.madeCount) || 0) + 1 }))
      } catch (error) {
        dropFrame(frameId)
        settle(() => ({ error: `It could not be erased: ${error instanceof Error ? error.message : 'it did not come back'}` }))
      } finally {
        if (rawId) void deleteProjectFile(id, rawId).catch(() => undefined)
      }
    },
    [beginCardRun, briaBitmap, dropFrame, edges, ensureProject, keepCanvas, nodes, projectId, setNodes, sourceOf],
  )

  /**
   * An Edit area run, from the moment it is on the queue to the picture on the
   * board. Nothing goes on the board while it runs: the loader is in the
   * card's lower window, and the picture appears to the right of the card
   * when it is done (Huanrui, 2026-10-06, said twice: Run's loader is in the
   * window, Apply's is on the picture to the right). Used by Run and, after a reload,
   * for a run the card was still waiting on (`pending`).
   */
  const finishEdit = useCallback(
    async (cardId: string, runId: string, recipe: ComposeRecipe, made: MadeWith) => {
      const patchCard = (change: (was: EditNodeData) => Partial<EditNodeData>) =>
        setNodes((prev) =>
          prev.map((node) =>
            node.id === cardId ? { ...node, data: { ...node.data, ...change(node.data as EditNodeData) } } : node,
          ),
        )
      const done = (patch: Partial<EditNodeData>) =>
        patchCard((was) => ({ ...patch, pending: undefined, runs: (was.runs ?? []).filter((one) => one !== runId) }))
      watching.current.add(runId)
      let run: PictureRun
      try {
        run = await awaitRun(runId)
      } catch (error) {
        done({ error: error instanceof Error ? error.message : 'Lost track of the run' })
        return
      }
      if (run.limit) setLive({ live: run.live, limit: run.limit })
      const picture = run.status === 'succeeded' ? run.pictures[0] : undefined
      if (!picture) {
        done({ error: run.error || 'It did not finish', credits: run.credits, madeCount: 0 })
        return
      }
      // Into the card only: the result shows in its lower window, to be
      // brushed, and nothing goes on the board until Apply (Huanrui,
      // 2026-10-06). The studio effect fits it and builds the window's view.
      done({
        error: '',
        credits: run.credits,
        madeCount: 1,
        blend: {
          drawn: { store: picture.store, id: picture.id },
          recipe,
          made: { ...made, credits: run.credits },
        },
      })
    },
    [setNodes],
  )

  // A run a card was still waiting on when the board was closed: carried on.
  useEffect(() => {
    for (const node of nodes) {
      if (node.type !== 'edit') continue
      const pending = (node.data as EditNodeData).pending
      if (!pending || watching.current.has(pending.runId)) continue
      void finishEdit(node.id, pending.runId, pending.recipe, pending.made)
    }
  }, [nodes, finishEdit])

  /**
   * Edit area (editNode.tsx): redraw the box on the picture wired in. The
   * picture is read at full size, the part round the box sent up with its
   * mask, and the run put on the queue; the card shows the loader meanwhile.
   */
  const runEdit = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as EditNodeData

      const patchCard = (change: (was: EditNodeData) => Partial<EditNodeData>) =>
        setNodes((prev) =>
          prev.map((node) =>
            node.id === cardId ? { ...node, data: { ...node.data, ...change(node.data as EditNodeData) } } : node,
          ),
        )
      const refuse = (error: string) => patchCard(() => ({ error, tried: Date.now(), preparing: false }))

      const wired = edges
        .filter((edge) => edge.target === cardId && isEditMain(edge))
        .map((edge) => nodes.find((node) => node.id === edge.source))
        .filter((node): node is Node => Boolean(node) && refOf(node!) !== null)
      if (wired.length === 0) return refuse('Connect a picture first')
      if (wired.length > 1) return refuse('One picture at a time — take the extra wires off')
      // More pictures for the model to look at, in port order (ref-1, ref-2 ...).
      const references: ImageRef[] = edges
        .filter((edge) => edge.target === cardId && !isEditMain(edge))
        .map((edge) => ({
          port: Number(String(edge.targetHandle ?? '').replace('ref-', '')) || 0,
          node: nodes.find((node) => node.id === edge.source),
        }))
        .sort((a, b) => a.port - b.port)
        .map((entry) => (entry.node ? refOf(entry.node) : null))
        .filter((ref): ref is ImageRef => ref !== null)
      const box = data.box
      if (!box || box.w <= 0.01 || box.h <= 0.01) return refuse('Drag a box on the picture')
      const prompt = (data.prompt ?? '').trim()
      if (!prompt) return refuse('Say what to change')
      if (!sunburst) return refuse('Image 2.5 Sunburst could not be loaded — reload the board')
      if (live.live >= (live.limit || MAX_LIVE_RUNS)) return refuse(`${live.live} already going — wait for one to finish`)
      const id = projectId || (await ensureProject())
      if (!id) return refuse('Save the board first — put something on it')
      // The loader shows from now, while the picture is read and sent up.
      patchCard(() => ({ error: '', preparing: true }))

      const pictureNode = wired[0]!
      const ref = refOf(pictureNode)!
      const pdata = pictureNode.data as ImageNodeData
      const turn = Number(pdata.rotate) || 0
      const title = (pdata.title || 'picture').replace(/\.[a-z0-9]+$/i, '')

      try {
        const held = studios.current.get(cardId)
        const source = held?.source && held.key === `${ref.store}:${ref.id}:${turn}`
          ? { canvas: held.source, png: false }
          : await sourceOf(ref, turn)
        // Whether to save as PNG follows the original file; read it once more if the studio's copy cannot say.
        const png = held?.source ? (await resolveImages([ref]))[0]?.mimeType === 'image/png' : source.png
        const pw = source.canvas.width
        const ph = source.canvas.height
        const prepared = prepareEdit(
          source.canvas,
          { x: box.x * pw, y: box.y * ph, w: box.w * pw, h: box.h * ph },
          ref,
          turn,
          png,
          `${title} edited`,
        )
        // The part round the box, as it is -- no mask. With a mask the model
        // was not shown what was under it, and drew something new in its place
        // (Huanrui's test, 2026-10-06: real tomatoes in place of the bottle,
        // where the same crop sent as a plain reference got the label right).
        // What changes is decided afterwards, on the card: only the box.
        const image = await sendUp(id, prepared.request)
        const began = await startCanvasEdit({ projectId: id, kind: 'edit', image, references, size: prepared.size, prompt })
        setLive({ live: began.live, limit: began.limit })
        const made: MadeWith = {
          prompt,
          model: CANVAS_EDIT_MODEL,
          size: prepared.size,
          quality: CANVAS_EDIT_QUALITY,
          at: new Date().toISOString(),
        }
        patchCard((was) => ({
          preparing: false,
          runs: [...(was.runs ?? []), began.runId],
          // Saved with the board, so a reload carries on waiting.
          pending: { runId: began.runId, recipe: prepared.recipe, made },
        }))
        await finishEdit(cardId, began.runId, prepared.recipe, made)
      } catch (error) {
        refuse(error instanceof Error ? error.message : 'It did not start')
      }
    },
    [edges, ensureProject, finishEdit, live, nodes, projectId, sendUp, setNodes, sourceOf, sunburst],
  )

  /*
   * EDIT TEXT (textNode.tsx, Huanrui 2026-10-07)
   *
   * Detect reads the lines of text on the picture wired in. Apply plans which
   * changed lines go to the model together (pictureOps.ts, planText), starts
   * one Sunburst run per part, puts the picture that will hold the result to
   * the right of the card with the loader on it, and waits; finishText then
   * lines each answer up with the original and lets only the changed lines
   * through. What is being waited on is saved on the card (`pending`), so a
   * board closed meanwhile finishes the job when it is opened again.
   */

  /** The one picture wired into an Edit text card, or why there is not one. */
  const textSource = useCallback(
    (cardId: string): { node: Node; ref: ImageRef; turn: number; key: string } | string => {
      const wired = edges
        .filter((edge) => edge.target === cardId)
        .map((edge) => nodes.find((node) => node.id === edge.source))
        .filter((node): node is Node => Boolean(node) && refOf(node!) !== null)
      if (wired.length === 0) return 'Connect a picture first'
      if (wired.length > 1) return 'One picture at a time — take the extra wires off'
      const node = wired[0]!
      const ref = refOf(node)!
      const turn = Number((node.data as ImageNodeData).rotate) || 0
      return { node, ref, turn, key: `${ref.store}:${ref.id}:${turn}` }
    },
    [edges, nodes],
  )

  const patchText = useCallback(
    (cardId: string, change: (was: TextNodeData) => Partial<TextNodeData>) =>
      setNodes((prev) =>
        prev.map((node) =>
          node.id === cardId ? { ...node, data: { ...node.data, ...change(node.data as TextNodeData) } } : node,
        ),
      ),
    [setNodes],
  )

  const detectText = useCallback(
    async (cardId: string) => {
      const refuse = (error: string) => patchText(cardId, () => ({ error, detecting: false }))
      const from = textSource(cardId)
      if (typeof from === 'string') return refuse(from)
      const card = nodes.find((node) => node.id === cardId)
      // Area mode: only what is in the box is read (Huanrui, 2026-10-07).
      const area = (card?.data as TextNodeData | undefined)?.area
      if (area === null) return refuse('Drag a box on the picture first')
      const id = projectId || (await ensureProject())
      if (!id) return refuse('Save the board first — put something on it')
      patchText(cardId, () => ({ detecting: true, error: '' }))
      try {
        // The picture the way round it is shown, so the boxes come back in
        // the frame Apply works in; no more than 2,048 across, which reads
        // small print well and keeps the upload small.
        const source = await sourceOf(from.ref, from.turn)
        const pw = source.canvas.width
        const ph = source.canvas.height
        // The part to read, in the picture's pixels: all of it, or the area.
        const part = area
          ? { x: area.x * pw, y: area.y * ph, w: area.w * pw, h: area.h * ph }
          : { x: 0, y: 0, w: pw, h: ph }
        const piece = area ? cropped(source.canvas, part) : source.canvas
        const small = scaledCopy(piece, 2048)
        const stored = await keepCanvas(id, small, 'text-read', false, false)
        const found = await detectCanvasText({ projectId: id, image: { store: 'project', id: stored.id } })
        if (found.lines.length === 0) {
          return refuse(area ? 'No text could be read in the box' : 'No text could be read on this picture')
        }
        // Back onto the whole picture: Apply always works on all of it.
        const lines: TextLine[] = found.lines.map((line) => ({
          text: line.text,
          to: line.text,
          box: {
            x: (part.x + line.box.x * part.w) / pw,
            y: (part.y + line.box.y * part.h) / ph,
            w: (line.box.w * part.w) / pw,
            h: (line.box.h * part.h) / ph,
          },
        }))
        patchText(cardId, () => ({ detecting: false, lines, readFor: from.key }))
      } catch (error) {
        refuse(error instanceof Error ? error.message : 'The text could not be read')
      }
    },
    [ensureProject, keepCanvas, nodes, patchText, projectId, sourceOf, textSource],
  )

  /** An Apply, from runs on the queue to the new picture on the board. */
  const finishText = useCallback(
    async (cardId: string, pending: TextPending) => {
      const runIds = pending.parts.map((part) => part.runId)
      for (const runId of runIds) watching.current.add(runId)
      const done = (patch: Partial<TextNodeData>) =>
        patchText(cardId, (was) => ({
          ...patch,
          applying: false,
          pending: undefined,
          runs: (was.runs ?? []).filter((one) => !runIds.includes(one)),
        }))
      const frameId = pending.frame
      // The loader on the picture to the right (again, after a reload).
      setNodes((prev) =>
        prev.map((node) =>
          node.id === frameId && !(node.data as ImageNodeData).fileId
            ? { ...node, data: { ...node.data, making: true, drawing: true } }
            : node,
        ),
      )

      const runs = await Promise.all(runIds.map((runId) => awaitRun(runId).catch(() => null)))
      const spent = runs.reduce((sum, run) => sum + (run?.credits ?? 0), 0)
      const last = runs.find((run) => run?.limit)
      if (last) setLive({ live: last.live, limit: last.limit })

      try {
        const first = pending.parts[0]!.recipe
        const source = await sourceOf(first.source, first.turn)
        let out = source.canvas
        let made = 0
        let firstError = ''
        // What Fine-tune needs later: each part the model drew, and its recipe.
        const keptParts: TextBlend['parts'] = []
        for (const [index, part] of pending.parts.entries()) {
          const run = runs[index]
          const picture = run?.status === 'succeeded' ? run.pictures[0] : undefined
          if (!picture) {
            firstError ||= run?.error || 'A part did not finish'
            continue
          }
          const [found] = await resolveImages([{ store: picture.store, id: picture.id }])
          if (!found?.originalPath) {
            firstError ||= 'A part could not be read back'
            continue
          }
          const { bitmap } = await loadBitmap(found.originalPath)
          const fitted = fit(source.canvas, bitmap, part.recipe)
          bitmap.close()
          out = compose(out, fitted, part.recipe)
          keptParts.push({ drawn: { store: picture.store, id: picture.id }, recipe: part.recipe })
          made += 1
        }
        if (made === 0) {
          dropFrame(frameId)
          done({ error: firstError || 'Nothing came back', credits: spent, madeCount: 0 })
          return
        }
        const project = projectId || (await ensureProject())
        if (!project) throw new Error('The board could not be saved')
        const stored = await keepCanvas(project, out, first.title, first.png)
        const exists = latestNodes.current.some((node) => node.id === frameId)
        const filled: ImageNodeData = {
          fileId: stored.id,
          title: stored.title,
          from: { skill: TEXT_SKILL.id, inputs: [first.source], at: new Date().toISOString() },
          made: { ...pending.made, credits: spent },
        }
        if (exists) {
          setNodes((prev) =>
            prev.map((node) => {
              if (node.id !== frameId) return node
              const kept = node.data as ImageNodeData
              return {
                ...node,
                data: {
                  ...filled,
                  ...(kept.frame ? { frame: kept.frame } : {}),
                  ...(kept.rotate ? { rotate: kept.rotate } : {}),
                  ...(kept.mark ? { mark: kept.mark } : {}),
                } satisfies ImageNodeData,
              }
            }),
          )
        } else {
          // Taken off the board while it was being made: put back beside the card.
          const card = latestNodes.current.find((node) => node.id === cardId)
          if (card) {
            const box = sizeOf(card)
            setNodes((prev) => [
              ...prev,
              {
                id: frameId,
                type: 'image',
                position: { x: card.position.x + box.w + FAMILY_GAP, y: card.position.y },
                width: 220,
                data: filled,
              } as Node,
            ])
            setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frameId })])
          }
        }
        const missed = (pending.missed ?? 0) + (pending.parts.length - made)
        done({
          error: missed > 0 ? `${missed} part${missed === 1 ? '' : 's'} could not be redrawn: ${firstError || 'it did not start'}` : '',
          credits: spent,
          madeCount: 1,
          blend: { frame: frameId, parts: keptParts, made: { ...pending.made, credits: spent } },
        })
        setNote('Text changed')
      } catch (error) {
        dropFrame(frameId)
        done({ error: error instanceof Error ? error.message : 'It could not be put together', credits: spent })
      }
    },
    [dropFrame, ensureProject, keepCanvas, patchText, projectId, setEdges, setNodes, sourceOf],
  )

  // An Apply a card was still waiting on when the board was closed: carried on.
  useEffect(() => {
    for (const node of nodes) {
      if (node.type !== 'text') continue
      const pending = (node.data as TextNodeData).pending
      if (!pending || pending.parts.length === 0) continue
      if (pending.parts.some((part) => watching.current.has(part.runId))) continue
      void finishText(node.id, pending)
    }
  }, [nodes, finishText])

  /*
   * FINE-TUNE STUDIOS (textNode.tsx, 2026-10-07): while a card's Fine-tune is
   * open, the turned original and each part the model drew, fitted, with its
   * mask -- the saved one, or the starting one (the changed lines with a
   * soft edge). Built only then, and let go when Fine-tune closes or the
   * card folds.
   */
  const textStudios = useRef(new Map<string, TextStudio & { key: string }>())
  const textLoading = useRef(new Set<string>())
  useEffect(() => {
    // The same for Edit text cards that are gone.
    let dropped = false
    for (const id of [...textStudios.current.keys()]) {
      if (!nodes.some((node) => node.id === id && node.type === 'text')) {
        releaseStudio(textStudios.current.get(id))
        textStudios.current.delete(id)
        dropped = true
      }
    }
    if (dropped) setStudioTick((tick) => tick + 1)
    for (const node of nodes) {
      if (node.type !== 'text') continue
      const data = node.data as TextNodeData
      const blend = data.blend
      const wanted = data.open !== false && data.tuning === true && blend && blend.parts.length > 0
      if (!wanted) {
        releaseStudio(textStudios.current.get(node.id))
        if (textStudios.current.delete(node.id)) setStudioTick((tick) => tick + 1)
        continue
      }
      const key = blend.parts.map((one) => `${one.drawn.id}:${one.mask?.id ?? ''}`).join('|')
      if (textStudios.current.get(node.id)?.key === key || textLoading.current.has(`${node.id}:${key}`)) continue
      textLoading.current.add(`${node.id}:${key}`)
      void (async () => {
        const first = blend.parts[0]!.recipe
        const source = await sourceOf(first.source, first.turn)
        const patches = []
        for (const part of blend.parts) {
          const [found] = await resolveImages([part.drawn])
          if (!found?.originalPath) throw new Error('A part of the result could not be found')
          const { bitmap } = await loadBitmap(found.originalPath)
          const fitted = fit(source.canvas, bitmap, part.recipe)
          bitmap.close()
          const mask = part.mask ? await maskFrom(part.mask, fitted.place) : defaultMask(part.recipe, fitted.place)
          patches.push({ layer: patchLayer(fitted), mask, place: fitted.place })
        }
        const was = textStudios.current.get(node.id)
        textStudios.current.set(node.id, { key, source: source.canvas, patches, version: (was?.version ?? 0) + 1 })
        setStudioTick((tick) => tick + 1)
      })()
        .catch((error: unknown) => {
          console.warn('could not open Fine-tune', node.id, error)
          patchText(node.id, () => ({ tuning: false, error: 'The result could not be opened for fine-tuning' }))
        })
        .finally(() => textLoading.current.delete(`${node.id}:${key}`))
    }
  }, [nodes, maskFrom, patchText, sourceOf])

  /**
   * Update picture, in Fine-tune: the original with each painted part over
   * it, at full size, in place of the picture Apply made (Huanrui,
   * 2026-10-07). The masks are kept with the project, so the painting is
   * there when Fine-tune is opened again. Nothing goes to the model.
   */
  const updateText = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      const blend = (card?.data as TextNodeData | undefined)?.blend
      const studio = textStudios.current.get(cardId)
      if (!card || !blend || !studio?.source || studio.patches.length !== blend.parts.length) return
      const id = projectId || (await ensureProject())
      if (!id) return
      patchText(cardId, () => ({ updating: true, error: '' }))

      const existing = latestNodes.current.find((node) => node.id === blend.frame)
      const frameId = existing ? existing.id : newId('image')
      if (existing) {
        setNodes((prev) =>
          prev.map((node) => (node.id === frameId ? { ...node, data: { ...node.data, working: true } } : node)),
        )
      } else {
        ranHere.current.add(frameId)
        const box = sizeOf(card)
        const first = blend.parts[0]!.recipe
        setNodes((prev) => [
          ...prev,
          {
            id: frameId,
            type: 'image',
            position: { x: card.position.x + box.w + FAMILY_GAP, y: card.position.y },
            width: 220,
            data: { making: true, drawing: true, aspect: first.pw / first.ph, title: first.title } satisfies ImageNodeData,
          } as Node,
        ])
        setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frameId })])
      }

      try {
        const out = document.createElement('canvas')
        out.width = studio.source.width
        out.height = studio.source.height
        const ctx = out.getContext('2d')
        if (!ctx) throw new Error('This browser could not draw the picture')
        ctx.drawImage(studio.source, 0, 0)
        for (const patch of studio.patches) {
          ctx.drawImage(maskedPatch(patch.layer, patch.mask), patch.place.x, patch.place.y, patch.place.w, patch.place.h)
        }
        const first = blend.parts[0]!.recipe
        const [stored, ...masks] = await Promise.all([
          keepCanvas(id, out, first.title, first.png),
          ...studio.patches.map((patch) => keepCanvas(id, patch.mask, 'text-brush', true, false)),
        ])
        setNodes((prev) =>
          prev.map((node) => {
            if (node.id !== frameId) return node
            const kept = node.data as ImageNodeData
            return {
              ...node,
              data: {
                fileId: stored.id,
                title: stored.title,
                from: { skill: TEXT_SKILL.id, inputs: [first.source], at: new Date().toISOString() },
                made: blend.made,
                ...(kept.frame ? { frame: kept.frame } : {}),
                ...(kept.rotate ? { rotate: kept.rotate } : {}),
                ...(kept.mark ? { mark: kept.mark } : {}),
              } satisfies ImageNodeData,
            }
          }),
        )
        // Folded once it is in the picture, like Apply (Huanrui, 2026-10-07).
        patchText(cardId, () => ({
          updating: false,
          tuning: false,
          open: false,
          blend: {
            ...blend,
            frame: frameId,
            parts: blend.parts.map((part, index) => ({ ...part, mask: { store: 'project', id: masks[index]!.id } })),
          },
        }))
        setNote('Updated')
      } catch (error) {
        if (existing) {
          setNodes((prev) =>
            prev.map((node) => (node.id === frameId ? { ...node, data: { ...node.data, working: false } } : node)),
          )
        } else {
          dropFrame(frameId)
        }
        patchText(cardId, () => ({ updating: false, error: error instanceof Error ? error.message : 'Could not update it' }))
      }
    },
    [dropFrame, ensureProject, keepCanvas, nodes, patchText, projectId, setEdges, setNodes],
  )

  const applyText = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as TextNodeData
      const refuse = (error: string) => patchText(cardId, () => ({ error, applying: false }))
      const from = textSource(cardId)
      if (typeof from === 'string') return refuse(from)
      const lines = data.lines ?? []
      if (lines.length === 0) return refuse('Detect the text first')
      if (data.readFor !== from.key) return refuse('The picture has changed — detect again')
      if (!lines.some((line) => line.to.trim() !== line.text.trim())) return refuse('Change at least one line')
      if (!sunburst) return refuse('Image 2.5 Sunburst could not be loaded — reload the board')
      const id = projectId || (await ensureProject())
      if (!id) return refuse('Save the board first — put something on it')
      // Folded at once (Huanrui, 2026-10-07): the loader is on the picture to
      // the right, and a folded card holds no full-size pictures.
      patchText(cardId, () => ({ applying: true, error: '', open: false, tuning: false }))

      const pdata = from.node.data as ImageNodeData
      const title = `${(pdata.title || 'picture').replace(/\.[a-z0-9]+$/i, '')} text`
      let frameId = ''
      try {
        const source = await sourceOf(from.ref, from.turn)
        const pw = source.canvas.width
        const ph = source.canvas.height
        // An area chosen on the card: one part, however far apart (planText).
        const groups = planText(lines, pw, ph, Boolean(data.area))
        if (groups.length > MAX_TEXT_PARTS) {
          return refuse(`The changes are spread over ${groups.length} parts of the picture — ${MAX_TEXT_PARTS} at most in one go`)
        }
        const room = (live.limit || MAX_LIVE_RUNS) - live.live
        if (groups.length > room) {
          return refuse(
            room <= 0
              ? `${live.live} already going — wait for one to finish`
              : `This needs ${groups.length} runs and only ${room} can start now — wait for one to finish`,
          )
        }

        // The picture the result goes into, to the right of the card, with
        // the loader on it from now on.
        frameId = newId('image')
        ranHere.current.add(frameId)
        const box = sizeOf(card)
        setNodes((prev) => [
          ...prev,
          {
            id: frameId,
            type: 'image',
            position: { x: card.position.x + box.w + FAMILY_GAP, y: card.position.y + box.h / 2 - FRAME_HEIGHT / 2 },
            width: 220,
            data: { making: true, drawing: true, aspect: pw / ph, title } satisfies ImageNodeData,
          } as Node,
        ])
        setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frameId })])

        const parts: TextPending['parts'] = []
        let missed = 0
        let firstError = ''
        for (const group of groups) {
          try {
            const prepared = prepareEdit(source.canvas, group.box, from.ref, from.turn, source.png, title)
            const recipe: ComposeRecipe = { ...prepared.recipe, boxes: group.boxes, feather: group.feather }
            const image = await sendUp(id, prepared.request)
            const began = await startCanvasEdit({
              projectId: id,
              kind: 'text',
              image,
              references: [],
              size: prepared.size,
              prompt: '',
              changes: group.changes,
              keep: group.keep,
            })
            setLive({ live: began.live, limit: began.limit })
            parts.push({ runId: began.runId, recipe })
          } catch (error) {
            missed += 1
            firstError ||= error instanceof Error ? error.message : 'It did not start'
          }
        }
        if (parts.length === 0) {
          dropFrame(frameId)
          return refuse(firstError || 'It did not start')
        }
        const said = lines
          .filter((line) => line.to.trim() !== line.text.trim())
          .map((line) => `${line.text.trim()} → ${line.to.trim() || '(removed)'}`)
          .join('; ')
        const pending: TextPending = {
          frame: frameId,
          parts,
          made: { prompt: said, model: CANVAS_EDIT_MODEL, size: 'auto', quality: CANVAS_EDIT_QUALITY, at: new Date().toISOString() },
          ...(missed ? { missed } : {}),
        }
        patchText(cardId, (was) => ({ runs: [...(was.runs ?? []), ...parts.map((part) => part.runId)], pending }))
        await finishText(cardId, pending)
      } catch (error) {
        if (frameId) dropFrame(frameId)
        refuse(error instanceof Error ? error.message : 'It did not start')
      }
    },
    [dropFrame, ensureProject, finishText, live, nodes, patchText, projectId, sendUp, setEdges, setNodes, sourceOf, sunburst, textSource],
  )

  /*
   * WHAT EACH NODE IS HANDED, WITHOUT REDRAWING THE ONES THAT DID NOT CHANGE.
   *
   * Every step of a drag changes one node, and this list used to be rebuilt
   * whole on each step with fresh objects and fresh functions for every node
   * on the page -- so React Flow redrew every picture on the board sixty
   * times a second to move one of them, and the board lagged in proportion
   * to how much was on it (measured: about twice the time per step at 150
   * pictures as at none).
   *
   * Now the functions a node is handed are made once per node and call the
   * board's latest code through a ref, and a node whose input and handed-in
   * values are all the same as last time is handed the very same object as
   * last time, which React Flow skips.
   *
   * EXCEPT AROUND A SELECTION BOX. React Flow keeps its own idea of which
   * nodes a box being drawn has reached, and a node it is handed unchanged it
   * does not read again -- so its idea stood even after the board overruled
   * it (nothing is selected until the box is let go), and nodes stayed
   * selected that should not have been. While a box is drawn, and for the
   * one redraw after it is let go, every node is handed afresh, which makes
   * React Flow read every node again.
   */
  /**
   * Angle (angleNode.tsx, 2026-10-09): the wired picture seen from where the
   * card's cube says, made by FLUX 2 through fal (routes/angles.ts). A frame
   * goes down to the right at once with the loader in it, and becomes the
   * picture when the run is done -- an ordinary picture run to wait on, so a
   * reload carries on waiting the usual way.
   */
  const runAngle = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as AngleNodeData

      const patchCard = (change: (was: AngleNodeData) => Partial<AngleNodeData>, run = '') =>
        setNodes((prev) => {
          const mine = run
            ? prev.find((node) => ((node.data as AngleNodeData).runs ?? []).includes(run))
            : prev.find((node) => node.id === cardId)
          if (!mine) return prev
          return prev.map((node) =>
            node.id === mine.id ? { ...node, data: { ...node.data, ...change(node.data as AngleNodeData) } } : node,
          )
        })
      const refuse = (error: string) => patchCard(() => ({ error, tried: Date.now() }))
      if (!projectId) return refuse('Save the board first — put something on it')
      const source = edges
        .filter((edge) => edge.target === cardId)
        .map((edge) => nodes.find((node) => node.id === edge.source))
        .find((node): node is Node => Boolean(node) && refOf(node!) !== null)
      const ref = source ? refOf(source) : null
      if (!source || !ref) return refuse('Connect a picture first')
      if (live.live >= (live.limit || MAX_LIVE_RUNS)) {
        return refuse(`${live.live} already going — wait for one to finish`)
      }
      const horizontal = Number(data.horizontal) || 0
      const vertical = Number(data.vertical) || 0
      const zoom = Number.isFinite(Number(data.zoom)) ? Number(data.zoom) : 5
      patchCard(() => ({ error: '' }))

      let runId = ''
      try {
        const began = await startAngle({ projectId, image: ref, horizontal, vertical, zoom })
        runId = began.runId
        setLive({ live: began.live, limit: began.limit })
      } catch (error) {
        return refuse(error instanceof Error ? error.message : 'It did not start')
      }
      patchCard((was) => ({ runs: [...(was.runs ?? []), runId] }))
      const done = (change: (was: AngleNodeData) => Partial<AngleNodeData>) =>
        patchCard((was) => ({ ...change(was), runs: (was.runs ?? []).filter((one) => one !== runId) }), runId)

      // Not a step on the undo stack (UNDO LEAVES WHAT WAS PAID FOR ALONE).
      const shape = pixelsOf(source)
      const box = sizeOf(card)
      const below = edges.filter((edge) => edge.source === cardId).length
      const frame = {
        id: newId('image'),
        type: 'image',
        position: {
          x: card.position.x + box.w + FAMILY_GAP,
          y: card.position.y + box.h / 2 - FRAME_HEIGHT / 2 + below * (FRAME_HEIGHT + STACK_GAP),
        },
        width: 220,
        data: {
          making: true,
          drawing: true,
          runId,
          runIndex: 0,
          ...(shape.width && shape.height ? { aspect: shape.width / shape.height } : {}),
        } satisfies ImageNodeData,
      } as Node
      ranHere.current.add(frame.id)
      setNodes((prev) => [...prev, frame])
      setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frame.id })])

      watching.current.add(runId)
      let run: PictureRun | null = null
      try {
        run = await awaitRun(runId)
      } catch (error) {
        console.warn('could not follow the angle run', runId, error)
      }
      if (run?.limit) setLive({ live: run.live, limit: run.limit })
      const output = run?.status === 'succeeded' ? run.outputs[0] : undefined
      if (!run || !output) {
        setNodes((prev) => prev.filter((node) => node.id !== frame.id))
        setEdges((prev) => prev.filter((edge) => edge.target !== frame.id))
        done(() => ({ error: run?.error || 'It did not finish' }))
        return
      }
      const at = new Date().toISOString()
      setNodes((prev) =>
        prev.map((node) =>
          node.id === frame.id
            ? {
                ...node,
                data: {
                  ...(output.store === 'library' ? { assetId: output.id } : { fileId: output.id }),
                  from: { skill: data.skillRef, inputs: [ref], at },
                  made: {
                    prompt: `Seen from ${angleWords(horizontal, vertical, zoom)}`,
                    model: 'fal-flux-2-angles',
                    size: '',
                    quality: '',
                    at,
                    credits: run!.credits,
                  },
                } satisfies ImageNodeData,
              }
            : node,
        ),
      )
      void listProjectFiles(projectId, { thumbs: true })
        .then((rows) => takeFiles(rows))
        .catch(() => {})
      // Folded once a picture is made (Huanrui, 2026-10-09): the open card's
      // turning cube is the heaviest thing on a board to draw while panning.
      // Unfold it with + to turn it again.
      done((was) => ({ error: '', credits: run!.credits, madeCount: (was.madeCount ?? 0) + 1, open: false }))
    },
    [edges, live, nodes, pixelsOf, projectId, setEdges, setNodes, takeFiles],
  )

  /**
   * Vectorize (vectorNode.tsx, 2026-10-09): the wired picture, turned as it
   * is shown, traced into an SVG in this browser and kept in the project. No
   * run and no cost; the new picture goes down to the right of the card.
   */
  const runVector = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as VectorNodeData
      if (data.running) return
      const patchCard = (change: Partial<VectorNodeData>) =>
        setNodes((prev) =>
          prev.map((node) => (node.id === cardId ? { ...node, data: { ...node.data, ...change } } : node)),
        )
      const source = edges
        .filter((edge) => edge.target === cardId)
        .map((edge) => nodes.find((node) => node.id === edge.source))
        .find((node): node is Node => Boolean(node) && refOf(node!) !== null)
      const ref = source ? refOf(source) : null
      if (!source || !ref) return patchCard({ error: 'Connect a picture first' })
      patchCard({ running: true, error: '' })

      // A frame with the loader in it, where the SVG will go (Huanrui, 2026-10-09).
      const shape = pixelsOf(source)
      const box = sizeOf(card)
      const below = edges.filter((edge) => edge.source === cardId).length
      const frame = {
        id: newId('image'),
        type: 'image',
        position: {
          x: card.position.x + box.w + FAMILY_GAP,
          y: card.position.y + box.h / 2 - FRAME_HEIGHT / 2 + below * (FRAME_HEIGHT + STACK_GAP),
        },
        width: 220,
        data: {
          making: true,
          drawing: true,
          ...(shape.width && shape.height ? { aspect: shape.width / shape.height } : {}),
        } satisfies ImageNodeData,
      } as Node
      setNodes((prev) => [...prev, frame])
      setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frame.id })])
      const drop = () => {
        setNodes((prev) => prev.filter((node) => node.id !== frame.id))
        setEdges((prev) => prev.filter((edge) => edge.target !== frame.id))
      }

      try {
        const id = projectId || (await ensureProject())
        const sdata = source.data as ImageNodeData
        const { canvas } = await sourceOf(ref, Number(sdata.rotate) || 0)
        const named = (sdata.title || 'Picture').replace(/\.[a-z0-9]{2,4}$/i, '').replace(/[\\/:*?"<>|]/g, '').trim() || 'Picture'
        let fileId = ''
        let size = { width: canvas.width, height: canvas.height }
        if (data.engine === 'fal') {
          fileId = await vectorizeWithFal({ projectId: id, source: canvas, from: `${ref.store}:${ref.id}`, title: named })
          void listProjectFiles(id, { thumbs: true })
            .then((rows) => takeFiles(rows))
            .catch(() => {})
        } else {
          const traced = await vectorize(canvas, {
            style: data.style ?? 'colour',
            colours: data.colours ?? 8,
            detail: data.detail ?? 'balanced',
          })
          const file = new File([traced.svg], `${named} vector.svg`, { type: 'image/svg+xml' })
          const stored = await uploadProjectFile(id, file, { width: traced.width, height: traced.height })
          setFiles((prev) => new Map(prev).set(stored.id, stored))
          fileId = stored.id
          size = { width: traced.width, height: traced.height }
        }
        const height = Math.round((220 * size.height) / Math.max(1, size.width))
        setNodes((prev) =>
          prev.map((node) =>
            node.id === frame.id
              ? {
                  ...node,
                  height,
                  data: {
                    fileId,
                    title: `${named} vector`,
                    from: { skill: data.skillRef, inputs: [ref], at: new Date().toISOString() },
                  } satisfies ImageNodeData,
                }
              : node.id === cardId
                ? { ...node, data: { ...node.data, running: false, error: '' } }
                : node,
          ),
        )
      } catch (error) {
        drop()
        patchCard({ running: false, error: error instanceof Error ? error.message : 'It could not be vectorized' })
      }
    },
    [edges, ensureProject, nodes, pixelsOf, projectId, setEdges, setNodes, sourceOf, takeFiles],
  )

  /**
   * Colour grade (gradeNode.tsx, 2026-10-15): every picture wired in, graded
   * at full size with the card's settings (or those just chosen in the
   * studio), one after another. A frame with the loader waits for each to
   * the right of the card, under what earlier Applies made; nothing they
   * made is replaced. Free: no run and no server work beyond keeping files.
   */
  const runGrade = useCallback(
    async (cardId: string, given?: GradeSettings) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as GradeNodeData
      if (data.running) return
      const settings = normaliseGrade(given ?? data.grade)
      const patchCard = (change: Partial<GradeNodeData>) =>
        setNodes((prev) =>
          prev.map((node) => (node.id === cardId ? { ...node, data: { ...node.data, ...change } } : node)),
        )
      const sources = edges
        .filter((edge) => edge.target === cardId)
        .map((edge) => nodes.find((node) => node.id === edge.source))
        .filter((node): node is Node => Boolean(node) && refOf(node!) !== null)
      if (sources.length === 0) return patchCard({ error: 'Connect a picture first' })
      patchCard({ running: true, error: '', progress: `0 / ${sources.length}` })

      const box = sizeOf(card)
      const below = edges.filter((edge) => edge.source === cardId).length
      const frames = sources.map((source, i) => {
        const shape = pixelsOf(source)
        return {
          id: newId('image'),
          type: 'image',
          position: {
            x: card.position.x + box.w + FAMILY_GAP,
            y: card.position.y + box.h / 2 - FRAME_HEIGHT / 2 + (below + i) * (FRAME_HEIGHT + STACK_GAP),
          },
          width: 220,
          data: {
            making: true,
            drawing: true,
            ...(shape.width && shape.height ? { aspect: shape.width / shape.height } : {}),
          } satisfies ImageNodeData,
        } as Node
      })
      setNodes((prev) => [...prev, ...frames])
      setEdges((prev) => [...prev, ...frames.map((frame) => wire({ id: newId('edge'), source: cardId, target: frame.id }))])
      const drop = (frameId: string) => {
        setNodes((prev) => prev.filter((node) => node.id !== frameId))
        setEdges((prev) => prev.filter((edge) => edge.target !== frameId))
      }

      let failed = 0
      let lastError = ''
      try {
        const id = projectId || (await ensureProject())
        const { gradeFull } = await import('./grade/gradeEngine')
        for (let i = 0; i < sources.length; i += 1) {
          const source = sources[i]
          const frame = frames[i]
          const ref = refOf(source)!
          try {
            const sdata = source.data as ImageNodeData
            const { canvas, png } = await sourceOf(ref, Number(sdata.rotate) || 0)
            const graded = gradeFull(canvas, settings)
            const named = (sdata.title || 'Picture').replace(/\.[a-z0-9]{2,4}$/i, '').replace(/[\\/:*?"<>|]/g, '').trim() || 'Picture'
            const stored = await keepCanvas(id, graded, `${named} graded`, png)
            const height = Math.round((220 * graded.height) / Math.max(1, graded.width))
            setNodes((prev) =>
              prev.map((node) =>
                node.id === frame.id
                  ? {
                      ...node,
                      height,
                      data: {
                        fileId: stored.id,
                        title: `${named} graded`,
                        from: { skill: data.skillRef, inputs: [ref], at: new Date().toISOString() },
                      } satisfies ImageNodeData,
                    }
                  : node,
              ),
            )
          } catch (error) {
            failed += 1
            lastError = error instanceof Error ? error.message : 'It could not be graded'
            drop(frame.id)
          }
          patchCard({ progress: `${i + 1} / ${sources.length}` })
        }
      } catch (error) {
        failed = sources.length
        lastError = error instanceof Error ? error.message : 'It could not be graded'
        frames.forEach((frame) => drop(frame.id))
      }
      patchCard({
        running: false,
        progress: '',
        error:
          failed === 0
            ? ''
            : failed === sources.length
              ? lastError
              : `${failed} of ${sources.length} could not be graded: ${lastError}`,
      })
    },
    [edges, ensureProject, keepCanvas, nodes, pixelsOf, projectId, setEdges, setNodes, sourceOf],
  )

  /**
   * The pictures a Mood board card takes, from the frames wired into it,
   * as the mood board reads them (MoodboardStart.tsx SourcePicture).
   */
  const moodboardSources = useCallback(
    (cardId: string, list: Node[] = nodes): { frames: number; sources: SourcePicture[]; name: string } => {
      const wired = edges
        .filter((edge) => edge.target === cardId)
        .map((edge) => list.find((node) => node.id === edge.source))
        .filter((node): node is Node => Boolean(node) && isFrame(node!))
      const frameIds = wired.map((node) => node.id)
      // The first frame's name: what a new sheet is titled.
      const name = String((wired[0]?.data as { name?: unknown } | undefined)?.name ?? '').trim()
      const sources: SourcePicture[] = []
      for (const node of picturesInFrames(frameIds, list)) {
        const data = node.data as ImageNodeData
        const ref = refOf(node)!
        const file = data.fileId ? files.get(data.fileId) : undefined
        const asset = data.assetId ? urls.get(data.assetId) : undefined
        const url = file?.url || asset?.originalPath || ''
        const thumb = file?.thumb || file?.url || asset?.thumbnailPath || asset?.originalPath || ''
        if (!url || !thumb) continue
        const px = pixelsOf(node)
        sources.push({
          key: `board:${ref.store}:${ref.id}`,
          source: 'board',
          title: data.title || 'Picture',
          thumb,
          url,
          width: px.width,
          height: px.height,
          ref: encodeRef(ref),
        })
      }
      return { frames: frameIds.length, sources, name }
    },
    [edges, files, nodes, pixelsOf, urls],
  )

  /**
   * Mood board (moodboardNode.tsx, 2026-10-16): the wired frames' pictures
   * laid out with the card's sheet (or the one just set in the studio), each
   * picture cut as the sheet shows it and kept in this project, and the whole
   * put down as a mood board frame to the right of the card -- the frame the
   * Mood board page's Copy to project makes (cutFrame.ts). Under what earlier
   * Applies made; nothing is replaced. Free.
   */
  const runMoodboard = useCallback(
    async (cardId: string, given?: SavedBoard) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as MoodboardNodeData
      if (data.running) return
      const patchCard = (change: Partial<MoodboardNodeData>) =>
        setNodes((prev) =>
          prev.map((node) => (node.id === cardId ? { ...node, data: { ...node.data, ...change } } : node)),
        )
      const { frames, sources, name } = moodboardSources(cardId)
      if (frames === 0) return patchCard({ error: 'Connect a frame first' })
      if (sources.length === 0) return patchCard({ error: 'The frame has no pictures in it' })
      patchCard({ running: true, error: '', progress: '' })
      try {
        const id = projectId || (await ensureProject())
        // Imported as they are, not on demand: the Mood board page has them in
        // the site already, so a separate download would save nothing (vite).
        const { pictures, order } = await readPictures(sources)
        const settings = fromSaved(given ?? data.board, pictures, order, name)
        const palette = paletteOf(pictures, settings.order)
        const sheet = layOut(pictures, settings, palette)
        if (sheet.slots.length === 0) throw new Error('Nothing is on the sheet: open Adjust and put pictures back')
        const board = buildBoard(pictures, sheet, settings, palette)
        const made = await cutMoodboardFrame(
          board,
          sheet,
          pictures,
          async (blob, name, size) => {
            const stored = await uploadProjectFile(id, new File([blob], name, { type: blob.type || 'image/jpeg' }), size)
            setFiles((prev) => new Map(prev).set(stored.id, stored))
            return stored
          },
          (done, total) => patchCard({ progress: `${done} / ${total}` }),
        )

        // Beside the card, under the frames earlier Applies made.
        const box = sizeOf(card)
        const earlier = edges
          .filter((edge) => edge.source === cardId)
          .map((edge) => nodes.find((node) => node.id === edge.target))
          .filter((node): node is Node => Boolean(node) && isFrame(node!))
        const x = card.position.x + box.w + FAMILY_GAP
        const y = earlier.length > 0 ? Math.max(...earlier.map((one) => one.position.y + sizeOf(one).h)) + FAMILY_GAP : card.position.y
        const remap = new Map(made.piece.map((node) => [node.id, newId(node.type)]))
        const frameId = remap.get(made.piece[0].id)!
        const placed = made.piece.map((node) => {
          const own = { ...(node.data as Record<string, unknown>) }
          if (typeof own.frame === 'string') own.frame = remap.get(own.frame) ?? own.frame
          return {
            ...node,
            id: remap.get(node.id)!,
            page: pageId,
            position: { x: node.position.x + x, y: node.position.y + y },
            selected: false,
            data: own,
          } as Node
        })
        remember(true)
        setNodes((prev) => [...prev, ...placed])
        setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frameId })])
        patchCard({ running: false, progress: '', error: '' })
      } catch (error) {
        patchCard({ running: false, progress: '', error: error instanceof Error ? error.message : 'The mood board could not be made' })
      }
    },
    [edges, ensureProject, moodboardSources, nodes, pageId, projectId, remember, setEdges, setNodes],
  )

  const latest = useRef({
    turnPicture,
    run,
    runPictures,
    runUpscale,
    runVideo,
    runAngle,
    runVector,
    runGrade,
    runMoodboard,
    runEdit,
    detectText,
    applyText,
    updateText,
    applyBlend,
    cropPicture,
    expandPicture,
    runBackground,
    runEraser,
    composeInto,
    setNodes,
    remember,
    describe,
  })
  latest.current = {
    turnPicture,
    run,
    runPictures,
    runUpscale,
    runVideo,
    runAngle,
    runVector,
    runGrade,
    runMoodboard,
    runEdit,
    detectText,
    applyText,
    updateText,
    applyBlend,
    cropPicture,
    expandPicture,
    runBackground,
    runEraser,
    composeInto,
    setNodes,
    remember,
    describe,
  }

  /* ---------- workflows (canvas/workflow, 2026-10-16) ---------- */
  const liveRef = useRef(live)
  liveRef.current = live
  const wf = useWorkflows({
    nodes,
    edges,
    latestNodes,
    latestEdges,
    setNodes,
    setEdges,
    pages,
    setPages,
    pageId,
    setPageId,
    remember,
    newId,
    wire,
    sizeOf,
    freshCopy,
    runs: latest as unknown as Parameters<typeof useWorkflows>[0]['runs'],
    editReady: (cardId) => Boolean(studios.current.get(cardId)?.patch),
    live: liveRef,
    projectId,
    ensureProject,
    refreshFiles: async (id) => takeFiles(await listProjectFiles(id, { thumbs: true })),
    thumbOf: (node) => {
      const d = node.data as ImageNodeData
      const file = d.fileId ? files.get(d.fileId) : undefined
      const asset = d.assetId ? urls.get(d.assetId) : undefined
      return file?.thumb || file?.url || asset?.thumbnailPath || asset?.originalPath || ''
    },
    onNote: setNote,
    gap: FAMILY_GAP,
  })
  const wfRef = useRef(wf)
  wfRef.current = wf
  // A workflow's page goes with the last card that uses it (deleted, or undone).
  // Undo brings both back: the step it takes back holds the page and the card.
  useEffect(() => {
    if (!ready) return
    const used = new Set(
      nodes.filter((node) => node.type === 'workflow').map((node) => String((node.data as WorkflowNodeData).wfPage ?? '')),
    )
    const orphans = pages.filter((page) => page.workflow && !used.has(page.id)).map((page) => page.id)
    if (orphans.length === 0) return
    const gone = new Set(orphans)
    const leaving = new Set(nodes.filter((node) => gone.has((node as { page?: string }).page ?? '')).map((node) => node.id))
    setPages((prev) => prev.filter((page) => !gone.has(page.id)))
    if (leaving.size > 0) {
      setNodes((prev) => prev.filter((node) => !leaving.has(node.id)))
      setEdges((prev) => prev.filter((edge) => !leaving.has(edge.source) && !leaving.has(edge.target)))
    }
    if (gone.has(pageId)) setPageId(pages.find((page) => !page.workflow)?.id ?? FIRST_PAGE.id)
  }, [nodes, pages, pageId, ready, setEdges, setNodes])

  /** The Library card's Search, set once it is defined further down (after acceptOffer). */
  const runLibraryRef = useRef<(id: string) => void>(() => undefined)
  const handlers = useRef(new Map<string, Record<string, (...args: never[]) => unknown>>())
  const handlersFor = useCallback((id: string) => {
    let made = handlers.current.get(id)
    if (made) return made
    const edit = (change: (data: Record<string, unknown>) => Record<string, unknown>) =>
      latest.current.setNodes((prev) =>
        prev.map((one) => (one.id === id ? { ...one, data: change(one.data as Record<string, unknown>) } : one)),
      )
    made = {
      rotate: (quarters: number) => latest.current.turnPicture(id, quarters),
      run: () => void latest.current.run(id),
      runPictures: () => void latest.current.runPictures(id),
      runUpscale: () => void latest.current.runUpscale(id),
      runVideo: () => void latest.current.runVideo(id),
      runAngle: () => void latest.current.runAngle(id),
      runVector: () => void latest.current.runVector(id),
      runBackground: () => void latest.current.runBackground(id),
      runEraser: () => void latest.current.runEraser(id),
      runGrade: () => void latest.current.runGrade(id),
      adjustGrade: () => setGradingId(id),
      runMoodboard: () => void latest.current.runMoodboard(id),
      adjustMoodboard: () => setBoardingId(id),
      runWorkflow: () => void wfRef.current.runWorkflow(id),
      openWorkflow: () => wfRef.current.openWorkflow(id),
      toolbarWorkflow: (how: 'add' | 'update' | 'remove') => void wfRef.current.toolbarAction(id, how),
      runLibrary: () => runLibraryRef.current(id),
      browseLibrary: () => setBrowsingId(id),
      runEdit: () => void latest.current.runEdit(id),
      detectText: () => void latest.current.detectText(id),
      applyText: () => void latest.current.applyText(id),
      updateText: () => void latest.current.updateText(id),
      applyBlend: () => void latest.current.applyBlend(id),
      crop: (rect: Rect, box: { w: number; h: number }) => void latest.current.cropPicture(id, rect, box),
      cropCancel: () => setCroppingId((was) => (was === id ? null : was)),
      expand: (rect: Rect, box: { w: number; h: number }, prompt: string) =>
        void latest.current.expandPicture(id, rect, box, prompt),
      editPicture: (patch: Partial<PictureNodeData>) => edit((data) => ({ ...data, ...patch })),
      noteText: (text: string) => edit((data) => ({ ...data, text })),
      // A text's type, each change one step on the undo stack.
      labelStyle: (textStyle: TextStyle) => {
        latest.current.remember(true)
        edit((data) => ({ ...data, textStyle }))
      },
      // One step on the undo stack for each time a note is written in.
      noteStart: () => latest.current.remember(true),
      describe: () => {
        const node = latestNodes.current.find((one) => one.id === id)
        return node ? latest.current.describe(node.data as ImageNodeData) : Promise.resolve(null)
      },
      rename: (name: string) => {
        latest.current.remember(true)
        edit((data) => ({ ...data, name }))
      },
      // Another shape: the corner stays, the area stays about the same, and
      // what is inside is decided again by where it now is.
      ratio: (ratio: number) => {
        latest.current.remember(true)
        latest.current.setNodes((prev) => {
          const next = prev.map((one) => {
            if (one.id !== id) return one
            const to = reshape(boxOf(one), ratio)
            return { ...one, width: to.w, height: to.h }
          })
          return settle(
            next,
            new Set(next.filter((one) => !frameOf(one) || frameOf(one) === id).map((one) => one.id)),
          )
        })
      },
      pick: () => setPickingFor(id),
      // A colour picked is this person's colour for frames from now on.
      color: (color: string) => {
        latest.current.remember(true)
        writeFrameColor(color)
        edit((data) => ({ ...data, color }))
      },
    }
    handlers.current.set(id, made)
    return made
  }, [])

  /** What was handed to each node last time, and what it was made from. */
  const handedOut = useRef(new Map<string, { from: Node; out: Node }>())
  /** Set while a selection box is drawn and until the redraw after it (above). */
  const handOutAfresh = useRef(false)
  /** Everything but the board that the last hand-out was made from. */
  const othersBefore = useRef<unknown[]>([])

  /*
   * STEADY HANDLERS FOR REACT FLOW (2026-10-02)
   *
   * React Flow copies every prop it is given into its store, and every change
   * to the store is announced to every node on the board. The handlers below
   * used to be written inline, so each render -- every frame of a drag --
   * handed it a dozen new functions and it announced a dozen changes to
   * every node, which was most of what a drag cost on a full board. Each is
   * now the same function for the life of the board, calling whatever the
   * latest render wrote.
   */
  const liveHandlers = useRef<Record<string, (...args: never[]) => unknown>>({})
  const steadyHandlers = useRef(new Map<string, (...args: never[]) => unknown>())
  const steady = <F extends (...args: never[]) => unknown>(name: string, fn: F): F => {
    liveHandlers.current[name] = fn
    let one = steadyHandlers.current.get(name)
    if (!one) {
      one = (...args: never[]) => liveHandlers.current[name]!(...args)
      steadyHandlers.current.set(name, one)
    }
    return one as F
  }

  /**
   * Alt held as a drag starts (onNodeDragStart, and onSelectionDragStart for a
   * selection of several dragged by the box round it): a copy of what is being
   * dragged is left behind, and the original carries on under the cursor.
   */
  const leaveCopyBehind = (grabbed: Node[]) => {
    // A frame is copied with what is in it, as in Miro.
    const frameIds = new Set(grabbed.filter(isFrame).map((other) => other.id))
    const moving = [
      ...grabbed,
      ...nodes.filter(
        (other) => frameIds.has(frameOf(other)) && !grabbed.some((one) => one.id === other.id),
      ),
    ]
    const movingIds = new Set(moving.map((other) => other.id))

    /*
      WHICH OF THE TWO IS THE NEW ONE.
      
      React Flow carries the node that was grabbed, so the copy is the
      one that stays put — which means the card you drag away is, in
      the board's eyes, the original. Somebody dragging a copy out of a
      card mid-run therefore watched the copy sit still while the thing
      under their cursor said it was busy: exactly backwards, and not
      what Ctrl+C then Ctrl+V does.
      
      So the copy left behind inherits what was happening, and the card
      carried away starts clean. The runs go with it — and because a run
      now reports to whichever card holds its id rather than to a fixed
      node, it carries on updating the card that stayed.
    */
    const copies = moving.map(
      (other) =>
        ({
          ...other,
          id: newId(other.type ?? 'image'),
          dragging: false,
          data: { ...other.data },
        }) as Node,
    )
    const twin = new Map(moving.map((other, index) => [other.id, copies[index].id]))
    // The pictures left behind belong to the frame left behind.
    for (const copy of copies) {
      const home = frameOf(copy)
      if (home && twin.has(home)) copy.data = { ...copy.data, frame: twin.get(home) }
    }
    setNodes((prev) => [
      // The one being dragged loses what was happening to it; the copy
      // standing still keeps it.
      ...prev.map((node) =>
        movingIds.has(node.id) ? { ...node, data: freshCopy(node.data) } : node,
      ),
      ...copies.map((copy) => ({ ...copy, selected: false })),
    ])

    // Same rule as copy and paste: wires inside what you copied come
    // along, wires to anything outside it stay where they were.
    //
    // Both are arranged by handing every existing wire to the copies
    // left behind, and then drawing fresh ones between the originals
    // for the wires that had both ends inside the selection. What
    // stays put is the arrangement exactly as it was; what comes out
    // under the cursor is wired to itself and to nothing else. The
    // copies sit on top of the originals, so nothing is seen to jump.
    setEdges((prev) => [
      ...prev.map((edge) => ({
        ...edge,
        source: twin.get(edge.source) ?? edge.source,
        target: twin.get(edge.target) ?? edge.target,
      })),
      ...prev
        .filter((edge) => movingIds.has(edge.source) && movingIds.has(edge.target))
        .map((edge) => wire({ id: newId('edge'), source: edge.source, target: edge.target })),
    ])
  }

  const decorated = useMemo(() => {
    // Counted once for the page, not once per node.
    const inFrame = new Map<string, number>()
    for (const one of onPage) {
      const home = frameOf(one)
      if (home) inFrame.set(home, (inFrame.get(home) ?? 0) + 1)
    }
    const wiredIn = new Map<string, number>()
    for (const edge of edges) wiredIn.set(edge.target, (wiredIn.get(edge.target) ?? 0) + 1)
    const byId = new Map(onPage.map((one) => [one.id, one]))

    const build = (node: Node): Node => {
      const h = handlersFor(node.id)
      if (node.type === 'frame') {
        return {
          ...node,
          // Beneath everything, including its own pictures (frames.ts). A
          // press anywhere on it selects and drags it; a press on one of
          // its pictures is the picture's (Huanrui, 2026-09-30).
          zIndex: FRAME_Z,
          data: {
            ...node.data,
            count: inFrame.get(node.id) ?? 0,
            solo: Boolean(node.selected) && selectedCount === 1,
            onRename: h.rename as (name: string) => void,
            onRatio: h.ratio as (ratio: number) => void,
            onColor: h.color as (color: string) => void,
            onPick: h.pick as () => void,
          } satisfies FrameNodeData,
        }
      }
      if (node.type === 'skill') {
        return {
          ...node,
          data: { ...node.data, connected: (wiredIn.get(node.id) ?? 0) > 0, onRun: h.run },
        }
      }
      if (node.type === 'edit') {
        const into = edges.find((edge) => edge.target === node.id && isEditMain(edge))
        const source = into ? byId.get(into.source) : undefined
        const sdata = source?.type === 'image' ? (source.data as ImageNodeData) : undefined
        const sfile = sdata?.fileId ? files.get(sdata.fileId) : undefined
        const sasset = sdata?.assetId ? urls.get(sdata.assetId) : undefined
        const surl = sfile?.thumb || sfile?.url || sasset?.thumbnailPath || sasset?.originalPath || ''
        const sturn = Number(sdata?.rotate) || 0
        const sw = sfile?.width || sasset?.width || 0
        const sh = sfile?.height || sasset?.height || 0
        const sideways = (((sturn / 90) % 2) + 2) % 2 === 1
        return {
          ...node,
          data: {
            ...node.data,
            picture: surl
              ? {
                  url: surl,
                  turn: sturn,
                  aspect: sw && sh ? (sideways ? sh / sw : sw / sh) : 1,
                  key: sdata?.assetId ? `library:${sdata.assetId}:${sturn}` : `project:${sdata?.fileId ?? ''}:${sturn}`,
                }
              : null,
            wired: edges.filter((edge) => edge.target === node.id && isEditMain(edge)).length,
            refs: edges.filter((edge) => edge.target === node.id && !isEditMain(edge)).length,
            estimate: sunburst
              ? estimateCredits(
                  sunburst,
                  CANVAS_EDIT_QUALITY,
                  'auto',
                  1,
                  1 + edges.filter((edge) => edge.target === node.id && !isEditMain(edge)).length,
                )
              : editCredits,
            live: live.live,
            maxLive: live.limit || MAX_LIVE_RUNS,
            onEdit: h.editPicture as (patch: Partial<EditNodeData>) => void,
            onRun: h.runEdit as () => void,
            studio: studios.current.get(node.id) ?? null,
            onApply: h.applyBlend as () => void,
          } satisfies Partial<EditNodeData>,
        }
      }
      if (node.type === 'text') {
        const into = edges.filter((edge) => edge.target === node.id)
        const source = into.length > 0 ? byId.get(into[0]!.source) : undefined
        const sdata = source?.type === 'image' ? (source.data as ImageNodeData) : undefined
        const sturn = Number(sdata?.rotate) || 0
        const pictureKey = sdata?.assetId
          ? `library:${sdata.assetId}:${sturn}`
          : sdata?.fileId
            ? `project:${sdata.fileId}:${sturn}`
            : ''
        // How many parts Apply would send, from the picture's size when it is
        // known (planText); otherwise one per changed line, which is the most.
        const tdata = node.data as TextNodeData
        const lines = tdata.lines ?? []
        const changed = lines.filter((line) => line.to.trim() !== line.text.trim()).length
        const dims = source ? pixelsOf(source) : { width: 0, height: 0 }
        const sideways = (((sturn / 90) % 2) + 2) % 2 === 1
        const pw = sideways ? dims.height : dims.width
        const ph = sideways ? dims.width : dims.height
        const areas = changed === 0 ? 0 : tdata.area ? 1 : pw && ph ? planText(lines, pw, ph).length : changed
        const sfile = sdata?.fileId ? files.get(sdata.fileId) : undefined
        const sasset = sdata?.assetId ? urls.get(sdata.assetId) : undefined
        const surl = sfile?.thumb || sfile?.url || sasset?.thumbnailPath || sasset?.originalPath || ''
        return {
          ...node,
          data: {
            ...node.data,
            pictureKey,
            picture: surl ? { url: surl, turn: sturn } : null,
            wired: into.length,
            areas,
            estimate: areas * editCredits,
            live: live.live,
            maxLive: live.limit || MAX_LIVE_RUNS,
            onEdit: h.editPicture as (patch: Partial<TextNodeData>) => void,
            onDetect: h.detectText as () => void,
            onApply: h.applyText as () => void,
            studio: textStudios.current.get(node.id) ?? null,
            onUpdate: h.updateText as () => void,
          } satisfies Partial<TextNodeData>,
        }
      }
      if (node.type === 'label') {
        return {
          ...node,
          data: {
            ...node.data,
            onText: h.noteText as (text: string) => void,
            onStyle: h.labelStyle as (style: TextStyle) => void,
            onWriteStart: h.noteStart as () => void,
          } satisfies LabelNodeData,
        }
      }
      if (node.type === 'note') {
        return {
          ...node,
          data: {
            ...node.data,
            onText: h.noteText as (text: string) => void,
            onWriteStart: h.noteStart as () => void,
          } satisfies NoteNodeData,
        }
      }
      if (node.type === 'upscale') {
        return {
          ...node,
          data: {
            ...node.data,
            options: upscaling,
            live: live.live,
            maxLive: live.limit || MAX_LIVE_RUNS,
            inputs: edges
              .filter((edge) => edge.target === node.id)
              .map((edge) => byId.get(edge.source))
              .filter((one): one is Node => Boolean(one) && refOf(one!) !== null)
              .map((one) => pixelsOf(one)),
            onEdit: h.editPicture as (patch: Partial<UpscaleNodeData>) => void,
            onRun: h.runUpscale,
          },
        }
      }
      if (node.type === 'angle') {
        const edge = edges.find((one) => one.target === node.id)
        const source = edge ? byId.get(edge.source) : undefined
        const sdata = source?.type === 'image' ? (source.data as ImageNodeData) : undefined
        const sfile = sdata?.fileId ? files.get(sdata.fileId) : undefined
        const sasset = sdata?.assetId ? urls.get(sdata.assetId) : undefined
        return {
          ...node,
          data: {
            ...node.data,
            live: live.live,
            maxLive: live.limit || MAX_LIVE_RUNS,
            preview: sfile?.thumb || sfile?.url || sasset?.thumbnailPath || sasset?.originalPath || '',
            onEdit: h.editPicture as (patch: Partial<AngleNodeData>) => void,
            onRun: h.runAngle,
          } satisfies Partial<AngleNodeData>,
        }
      }
      if (node.type === 'library') {
        return {
          ...node,
          data: {
            ...node.data,
            onEdit: h.editPicture as (patch: Partial<LibraryNodeData>) => void,
            onRun: h.runLibrary as () => void,
            onBrowse: h.browseLibrary as () => void,
          } satisfies Partial<LibraryNodeData>,
        }
      }
      if (node.type === 'background') {
        return {
          ...node,
          data: {
            ...node.data,
            // Named as the tool is now, whatever it was called when the card was put down.
            name: BACKGROUND_SKILL.name,
            glyph: BACKGROUND_SKILL.glyph,
            wired: edges.some((edge) => {
              if (edge.target !== node.id) return false
              const source = byId.get(edge.source)
              return Boolean(source) && refOf(source!) !== null
            }),
            onEdit: h.editPicture as (patch: Partial<BackgroundNodeData>) => void,
            onRun: h.runBackground as () => void,
          } satisfies Partial<BackgroundNodeData>,
        }
      }
      if (node.type === 'eraser') {
        const into = edges.find((edge) => edge.target === node.id)
        const source = into ? byId.get(into.source) : undefined
        const sdata = source?.type === 'image' ? (source.data as ImageNodeData) : undefined
        const sfile = sdata?.fileId ? files.get(sdata.fileId) : undefined
        const sasset = sdata?.assetId ? urls.get(sdata.assetId) : undefined
        const surl = sfile?.thumb || sfile?.url || sasset?.thumbnailPath || sasset?.originalPath || ''
        return {
          ...node,
          data: {
            ...node.data,
            name: ERASER_SKILL.name,
            glyph: ERASER_SKILL.glyph,
            picture: surl && source ? { url: surl, turn: Number(sdata?.rotate) || 0, key: pictureKeyOf(source) } : null,
            onEdit: h.editPicture as (patch: Partial<EraserNodeData>) => void,
            onRun: h.runEraser as () => void,
          } satisfies Partial<EraserNodeData>,
        }
      }
      if (node.type === 'vector') {
        return {
          ...node,
          data: {
            ...node.data,
            wired: edges.some((edge) => {
              if (edge.target !== node.id) return false
              const source = byId.get(edge.source)
              return Boolean(source) && refOf(source!) !== null
            }),
            onEdit: h.editPicture as (patch: Partial<VectorNodeData>) => void,
            onRun: h.runVector,
          } satisfies Partial<VectorNodeData>,
        }
      }
      if (node.type === 'workflow') {
        // Each input that changes, and how many pictures are wired into it.
        const wd = node.data as WorkflowNodeData
        const ports = (wd.workflow?.inputs ?? [])
          .filter((one) => one.mode === 'replace')
          .map((one) => ({
            id: one.id,
            name: one.name,
            count: edges.filter(
              (edge) => edge.target === node.id && edge.targetHandle === portOf(one.id) && byId.get(edge.source)?.type === 'image',
            ).length,
          }))
        return {
          ...node,
          data: {
            ...node.data,
            ports,
            inToolbar: Boolean(wd.workflow?.toolbarId && wf.toolbar.some((one) => one.id === wd.workflow.toolbarId)),
            onEdit: h.editPicture as (patch: Partial<WorkflowNodeData>) => void,
            onRun: h.runWorkflow as () => void,
            onOpen: h.openWorkflow as () => void,
            onToolbar: h.toolbarWorkflow as (how: 'add' | 'update' | 'remove') => void,
          } satisfies Partial<WorkflowNodeData>,
        }
      }
      if (node.type === 'moodboard') {
        // The frames wired in, and how many pictures they hold.
        const frameIds = edges
          .filter((edge) => edge.target === node.id)
          .map((edge) => byId.get(edge.source))
          .filter((one): one is Node => Boolean(one) && isFrame(one!))
          .map((one) => one.id)
        return {
          ...node,
          data: {
            ...node.data,
            frames: frameIds.length,
            pictures: picturesInFrames(frameIds, onPage).length,
            onEdit: h.editPicture as (patch: Partial<MoodboardNodeData>) => void,
            onAdjust: h.adjustMoodboard as () => void,
            onRun: h.runMoodboard as () => void,
          } satisfies Partial<MoodboardNodeData>,
        }
      }
      if (node.type === 'grade') {
        // The wired pictures' thumbnails, in the order they were wired.
        const inputs = edges
          .filter((edge) => edge.target === node.id)
          .map((edge) => byId.get(edge.source))
          .filter((one): one is Node => Boolean(one) && refOf(one!) !== null)
          .map((one) => {
            const sdata = one.data as ImageNodeData
            const sfile = sdata.fileId ? files.get(sdata.fileId) : undefined
            const sasset = sdata.assetId ? urls.get(sdata.assetId) : undefined
            return sfile?.thumb || sfile?.url || sasset?.thumbnailPath || sasset?.originalPath || ''
          })
        return {
          ...node,
          data: {
            ...node.data,
            // Named and marked as the tool is now (Photo adjust, 2026-10-30), whenever the card was put down.
            name: GRADE_SKILL.name,
            glyph: GRADE_SKILL.glyph,
            inputs,
            onEdit: h.editPicture as (patch: Partial<GradeNodeData>) => void,
            onAdjust: h.adjustGrade as () => void,
            onRun: h.runGrade as () => void,
          } satisfies Partial<GradeNodeData>,
        }
      }
      if (node.type === 'video') {
        // The pictures on each port, for the card's small previews.
        const onPort = (port: string) => {
          const edge = edges.find((one) => one.target === node.id && (one.targetHandle ?? FIRST_PORT) === port)
          const source = edge ? byId.get(edge.source) : undefined
          if (!source || source.type !== 'image') return null
          const sdata = source.data as ImageNodeData
          const sfile = sdata.fileId ? files.get(sdata.fileId) : undefined
          const sasset = sdata.assetId ? urls.get(sdata.assetId) : undefined
          return sfile?.thumb || sfile?.url || sasset?.thumbnailPath || sasset?.originalPath || null
        }
        return {
          ...node,
          data: {
            ...node.data,
            models: videoModels,
            live: live.live,
            maxLive: live.limit || MAX_LIVE_RUNS,
            first: onPort(FIRST_PORT),
            last: onPort(LAST_PORT),
            // Which ports between have a picture (Kling v4): the card draws one more.
            middles: Array.from({ length: MAX_MIDDLE_PORTS }, (_, i) => i + 1).filter((n) => onPort(MID_PORT(n))),
            onEdit: h.editPicture as (patch: Partial<VideoNodeData>) => void,
            onRun: h.runVideo,
          } satisfies Partial<VideoNodeData>,
        }
      }
      if (node.type === 'picture') {
        return {
          ...node,
          data: {
            ...node.data,
            models,
            live: live.live,
            maxLive: live.limit || MAX_LIVE_RUNS,
            // A count and not a boolean: it decides how many ports the card
            // draws, which is always one more than are in use.
            connected: wiredIn.get(node.id) ?? 0,
            onEdit: h.editPicture,
            onRun: h.runPictures,
          },
        }
      }
      const data = node.data as ImageNodeData
      const asset = data.assetId ? urls.get(data.assetId) : undefined
      const file = data.fileId ? files.get(data.fileId) : undefined
      const turnedAside = (Number(data.rotate) || 0) % 180 === 90
      const w = (turnedAside ? file?.height || asset?.height : file?.width || asset?.width) || 0
      const hgt = (turnedAside ? file?.width || asset?.width : file?.height || asset?.height) || 0
      // The shape as seen on the board: from the store, else as it was drawn.
      const pictureKey = data.fileId ?? data.assetId ?? ''
      const seenShape = seenShapes.get(pictureKey)
      const boardShape = w && hgt ? w / hgt : seenShape ? (turnedAside ? 1 / seenShape : seenShape) : 0
      const roomW = node.width ?? node.measured?.width
      const turnBox = turnedAside && boardShape && roomW ? turnedBox(roomW, node.height ?? node.measured?.height, boardShape) : null
      return {
        ...node,
        // Turned a quarter (2026-10-16): the box takes the turned picture's
        // shape and keeps the room it had (the same area), whatever way round
        // its saved width and height are. One given only a width (Tidy, Level,
        // a frame arranging) gets the height its shape asks for.
        ...(turnBox ?? {}),
        data: {
          ...node.data,
          // The copy made for drawing, not the full-size file. A canvas
          // holding thirty pack shots at full resolution is a page that takes
          // a minute to become usable. The full-size file is laid over it
          // only when the board is zoomed in past what the copy can show
          // (nodes.tsx, SHARP AT ANY ZOOM), and for downloads.
          url: file?.thumb || file?.url || asset?.thumbnailPath || asset?.originalPath,
          fallback: file?.url || asset?.originalPath,
          // A video a Video card made: played, not drawn (nodes.tsx).
          video: (file?.mime_type ?? '').startsWith('video/'),
          shape: boardShape || undefined,
          onShape: pictureKey && !(w && hgt) && !seenShape ? (shape: number) => noteShape(pictureKey, shape) : undefined,
          // The turned box's own shape, which the picture is drawn into.
          boxShape: turnBox
            ? turnBox.width / turnBox.height
            : turnedAside && node.width && node.height
              ? node.width / node.height
              : undefined,
          fullEdge: Math.max(w, hgt) || undefined,
          onRetry: refreshPictures,
          solo: Boolean(node.selected) && selectedCount === 1,
          onRotate: h.rotate as (quarters: number) => void,
          cropping: node.id === croppingId,
          onCrop: h.crop as (rect: Rect, box: { w: number; h: number }) => void,
          onCropCancel: h.cropCancel as () => void,
          // The crop box may go past the edges to expand -- a picture, not a video or an SVG.
          onExpand: /^(video\/|image\/svg)/.test(file?.mime_type ?? '')
            ? undefined
            : (h.expand as (rect: Rect, box: { w: number; h: number }, prompt: string) => void),
          expandCredits: FAL_EXPAND_CREDITS,
          title: file?.title || asset?.title || data.title,
          // The corner button, on pictures a model made and on nothing else.
          describe:
            data.made || recordOf(file)
              ? (h.describe as () => Promise<MadeWithFound | null>)
              : undefined,
        },
      }
    }

    const seen = new Set<string>()
    const afresh = handOutAfresh.current
    /*
     * Only the board itself changed since last time -- what a drag does, every
     * frame. Then a node handed the very same object as before is handed the
     * very same result, without building it again to find that out: a drag
     * builds the one node that moved, not all of them (2026-10-02).
     */
    const others = [edges, urls, files, models, upscaling, videoModels, live, refreshPictures, selectedCount, pending, handlersFor, croppingId, editCredits, sunburst, studioTick]
    const onlyBoard = othersBefore.current.length === others.length && others.every((one, i) => one === othersBefore.current[i])
    othersBefore.current = others
    const out = onPage.map((node) => {
      seen.add(node.id)
      const kept = handedOut.current.get(node.id)
      if (!afresh && onlyBoard && kept && kept.from === node) return kept.out
      let fresh = withPending(node, pending, () => build(node))
      const before = handedOut.current.get(node.id)
      // Moved but otherwise the same: keep handing over the same data, so the
      // picture inside is not drawn again on every frame of a drag (nodes.tsx,
      // ImageNode's comparison). 2026-10-02.
      if (before && fresh.data !== before.out.data && sameData(fresh.data, before.out.data)) {
        fresh = { ...fresh, data: before.out.data }
      }
      if (!afresh && before && before.from === node && sameNode(before.out, fresh)) return before.out
      handedOut.current.set(node.id, { from: node, out: fresh })
      return fresh
    })
    for (const id of handedOut.current.keys()) if (!seen.has(id)) handedOut.current.delete(id)
    return out
  }, [onPage, edges, urls, files, models, upscaling, videoModels, pixelsOf, live, refreshPictures, selectedCount, pending, handlersFor, croppingId, editCredits, sunburst, studioTick, wf.toolbar, seenShapes, noteShape])

  /**
   * The pictures currently selected, named.
   *
   * One bar for the whole selection rather than a toolbar on each node: with
   * three images chosen, three toolbars is three copies of the same question,
   * and with one it still reads as a property of that node rather than of what
   * you have picked.
   */
  /** A link that will load for this node's picture, or nothing. */
  const linkOf = useCallback(
    (node: Node) => {
      const data = node.data as ImageNodeData
      const asset = data.assetId ? urls.get(data.assetId) : undefined
      const file = data.fileId ? files.get(data.fileId) : undefined
      return file?.url || asset?.originalPath || asset?.thumbnailPath || ''
    },
    [urls, files],
  )

  const chosen = useMemo(() => {
    const picked: { node: Node; ref: ImageRef; picture?: { url: string; title: string }; video?: boolean }[] = []
    // A selected frame counts as the pictures in it: sending a frame to Miro
    // or downloading it means its contents.
    const framesOn = new Set(nodes.filter((node) => node.selected && isFrame(node)).map((node) => node.id))
    for (const node of nodes) {
      if (!(node.selected || framesOn.has(frameOf(node))) || !MEDIA_NODES.has(node.type ?? '')) continue
      const data = node.data as ImageNodeData
      const asset = data.assetId ? urls.get(data.assetId) : undefined
      const file = data.fileId ? files.get(data.fileId) : undefined
      const ref: ImageRef | null = data.assetId
        ? { store: 'library', id: data.assetId }
        : data.fileId
          ? { store: 'project', id: data.fileId }
          : null
      if (!ref) continue
      picked.push({
        node,
        ref,
        picture: {
          url: file?.url || asset?.originalPath || asset?.thumbnailPath || '',
          title: file?.title || asset?.title || 'image',
        },
        video: (file?.mime_type ?? '').startsWith('video/'),
      })
    }
    return picked
  }, [nodes, urls, files])

  /**
   * What Send to Miro will send, counted as it goes (2026-10-13): a selected
   * frame is ONE (it goes as one frame), and a picture is one unless it goes
   * inside a selected frame.
   */
  const sendCount = useMemo(() => {
    const frames = new Set(nodes.filter((node) => node.selected && isFrame(node)).map((node) => node.id))
    return frames.size + chosen.filter((entry) => !frames.has(frameOf(entry.node))).length
  }, [nodes, chosen])

  /**
   * A FRAME goes to Miro as the frame it is (Huanrui, 2026-10-12): the same
   * shape and colour, everything in it where it sits and at its size, words
   * included -- not as a row of pictures of one height. Read from the board
   * as it is now (shared/moodboard/frame.ts) and drawn by the panel the way a
   * mood board is; its pictures are prepared here so Miro fetches them
   * (moodboardTemp.ts). A mood board copied from the Mood board page is one
   * such frame.
   */
  const prepareFrameForMiro = useCallback(
    async (frame: Node): Promise<{ board: SentMoodboard; ids: string[] } | null> => {
      const box = boxOf(frame)
      const inside = (node: Node) => {
        const b = boxOf(node)
        return b.x >= box.x - 1 && b.y >= box.y - 1 && b.x + b.w <= box.x + box.w + 1 && b.y + b.h <= box.y + box.h + 1
      }
      const samePage = (node: Node) => (node as { page?: string }).page === (frame as { page?: string }).page
      const members = nodes.filter((node) => {
        if (node.id === frame.id) return false
        if (node.type === 'label') return frameOf(node) === frame.id || (samePage(node) && inside(node))
        if (!MEDIA_NODES.has(node.type ?? '') || frameOf(node) !== frame.id) return false
        const fileId = (node.data as ImageNodeData).fileId
        return !(fileId && (files.get(fileId)?.mime_type ?? '').startsWith('video/'))
      })
      const ratioOf = (node: Node): number | undefined => {
        const data = node.data as ImageNodeData
        const file = data.fileId ? files.get(data.fileId) : undefined
        const asset = data.assetId ? urls.get(data.assetId) : undefined
        const w = file?.width ?? asset?.width ?? 0
        const h = file?.height ?? asset?.height ?? 0
        return w > 0 && h > 0 ? w / h : undefined
      }
      const board = moodboardFromFrame(
        { ...box, data: frame.data as Record<string, unknown> },
        members.map((node) => ({
          type: node.type ?? '',
          ...boxOf(node),
          data: node.data as Record<string, unknown>,
          ratio: node.type === 'label' ? undefined : ratioOf(node),
        })),
        DEFAULT_FRAME_COLOR,
      )
      // More than the panel takes in one frame: the usual way instead.
      const floating = board.floats?.length ?? 0
      if (board.pictures.length + floating + board.texts.length === 0 || board.pictures.length + floating > 200) return null
      /*
       * The ORIGINAL pictures (2026-10-13): one shown whole goes by its own
       * file -- the Worker signs it and Miro fetches it at full size. Only
       * one its box cuts is cut here, at full size, and handed over as is.
       */
      // A file that may be see-through (PNG, WebP, GIF) is cut even when shown
      // whole, so a translucent ground is made solid (2026-10-23; cutForBoard).
      const mayBeClear = (picture: (typeof board.pictures)[number]) => {
        const colon = picture.ref.indexOf(':')
        const id = picture.ref.slice(colon + 1)
        const mime = picture.ref.startsWith('project:') ? files.get(id)?.mime_type ?? '' : urls.get(id)?.mimeType ?? ''
        return /png|webp|gif/i.test(mime)
      }
      const queue = board.pictures.filter((picture) => {
        const whole = picture.crop.w >= 0.999 && picture.crop.h >= 0.999 && !mayBeClear(picture)
        if (whole) picture.original = true
        return !whole
      })
      // A lifted picture's file is its drawing already (outline and turn): its own file goes.
      for (const picture of board.floats ?? []) picture.original = true
      const worker = async () => {
        for (let picture = queue.shift(); picture; picture = queue.shift()) {
          try {
            const colon = picture.ref.indexOf(':')
            const store = picture.ref.slice(0, colon)
            const id = picture.ref.slice(colon + 1)
            const url = store === 'project' ? files.get(id)?.url : urls.get(id)?.originalPath
            if (!url) continue
            const blob = await cutForBoard({ url, x: picture.x, y: picture.y, w: picture.w, h: picture.h, crop: picture.crop }, true)
            picture.staged = await stageMoodboardPicture(blob)
          } catch (error) {
            console.warn('[WonderWorks] frame picture left for the panel', error)
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker))
      // Sent by the caller, in order (sendChosenToMiro).
      return { board, ids: members.map((node) => node.id) }
    },
    [nodes, files, urls],
  )

  const sendChosenToMiro = useCallback(() => {
    // In reading order (Huanrui, 2026-10-23): prepared together, then sent one
    // after another, so the panel's Add all places them in that order.
    const frames = readingOrder(nodes.filter((node) => node.selected && isFrame(node)))
    if (frames.length > 0) {
      setNote(frames.length === 1 ? 'Preparing the frame…' : `Preparing ${frames.length} frames…`)
      void Promise.all(frames.map((frame) => prepareFrameForMiro(frame)))
        .then(async (prepared) => {
          for (const one of prepared) if (one) await sendMoodboard(one.board)
          const taken = prepared.map((one) => one?.ids ?? null)
          // Anything else picked goes the usual way; what is in the frames went with them.
          const gone = new Set(taken.flatMap((ids) => ids ?? []))
          const rest = chosen.filter((entry) => !entry.video && !gone.has(entry.node.id))
          if (rest.length > 0) await sendToMiroQueue('web', rest.map((entry) => entry.ref))
          const laid = taken.filter(Boolean).length
          if (laid === 0 && rest.length === 0) {
            setNote('There is nothing in the frame to send.')
            return
          }
          setNote(
            laid === 0
              ? 'Sent. Open the WonderWorks panel in Miro to place it.'
              : `Sent ${laid === 1 ? 'the frame' : `${laid} frames`} as laid out here. Open the WonderWorks panel in Miro and press Add to board.`,
          )
          setNodes((prev) => prev.map((node) => (node.selected ? { ...node, selected: false } : node)))
        })
        .catch(() => setAlert('The frame could not be sent to Miro'))
      return
    }
    // The button is there the whole time, so it has to answer for the case
    // where there is nothing to send. A button that does nothing at all reads
    // as broken; one that says why reads as waiting.
    if (chosen.length === 0) {
      setNote('Pick something on the canvas first, then send it.')
      return
    }
    // A video cannot be placed on a Miro board by an app (Video Generation,
    // 2026-10-08): it is downloaded and dragged in by hand instead.
    const sendable = chosen.filter((entry) => !entry.video)
    if (sendable.length === 0) {
      setNote('Videos cannot be sent to Miro — download it and drag the file onto the board.')
      return
    }
    // One request for the whole selection. The queue merges, so sending twice
    // before opening the panel adds rather than replaces.
    const count = sendable.length
    void sendToMiroQueue('web', sendable.map((entry) => entry.ref))
      .then(() => {
        setNote(
          count === 1
            ? 'Sent. Open the WonderWorks panel in Miro to place it.'
            : `Sent ${count}. Open the WonderWorks panel in Miro to place them.`,
        )
        // Let go of the selection now that it has gone. Nothing on this screen
        // moves when a picture is sent — it lands on a board in another
        // window — so the count leaving the button, and the glow going out,
        // is the whole of the acknowledgement that the click did anything.
        setNodes((prev) => prev.map((node) => (node.selected ? { ...node, selected: false } : node)))
      })
      .catch(() => setAlert('They could not be sent to Miro'))
  }, [chosen, nodes, prepareFrameForMiro, setNodes])

  /**
   * Ctrl+Shift+C — the picture onto the clipboard, ready for Ctrl+V on a board.
   *
   * The same keys Miro uses for copying a selection as a picture, so the two
   * halves of the round trip are the one thing to remember. Ctrl+C is left
   * alone: on a canvas it means copy these nodes, and taking that away to
   * mean something else would cost more than it gave.
   *
   * One picture at a time, because a clipboard holds one.
   */
  const copyChosenImage = useCallback(() => {
    // A frame on its own: the frame, in its colour, with its pictures in it
    // as they sit, as one picture (Huanrui, 2026-10-06).
    const pickedNow = nodes.filter((node) => node.selected)
    if (pickedNow.length === 1 && isFrame(pickedNow[0]!)) {
      const frame = pickedNow[0]!
      const inside = nodes.filter((node) => node.type === 'image' && frameOf(node) === frame.id && refOf(node))
      const colour = (frame.data as FrameNodeData).color || DEFAULT_FRAME_COLOR
      if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
        setAlert('This browser cannot copy pictures')
        return
      }
      // The promise goes to the clipboard at once, inside the key press or click.
      const png = flatten(inside, boxOf(frame), colour).then(({ canvas }) => blobOf(canvas, 'image/png'))
      void navigator.clipboard
        .write([new ClipboardItem({ 'image/png': png })])
        .then(() => setNote('Frame copied as a picture. Ctrl+V on a board, or in Miro, to put it there.'))
        .catch(() => setAlert('This browser would not let the frame be copied'))
      return
    }
    if (chosen.length === 0) {
      setNote('Pick a picture first, then Ctrl+Shift+C')
      return
    }
    if (chosen.length > 1) {
      setNote('The clipboard holds one picture at a time')
      return
    }
    const url = chosen[0].picture?.url
    if (!url) return
    const turn = Number((chosen[0].node.data as ImageNodeData).rotate) || 0
    void copyPicture(url, turn)
      .then(() => setNote('Copied. Ctrl+V on a Miro board to put it there.'))
      .catch(() => setAlert('This browser would not let the picture be copied'))
  }, [chosen, flatten, nodes])

  const downloadChosen = useCallback(() => {
    if (chosen.length === 0) return
    // One file each, never a zip: six pictures are six things to look at, and
    // an archive makes every one of them a thing to unpack first.
    //
    // One at a time, with a gap. A browser asked for six files at once decides
    // it is being abused and silently drops all but the first. It may also ask
    // once, on the first go, whether this site may save several files.
    chosen.forEach((entry, index) => {
      const url = entry.picture?.url
      if (!url) return
      window.setTimeout(() => {
        void savePicture(url, entry.picture?.title || 'image').catch(() => {
          // The store would not hand the bytes to this page. The picture can
          // still be had, just by hand — and the tab is explained rather than
          // simply appearing.
          window.open(url, '_blank', 'noopener')
          setNote('Could not save it straight to disk — opened it in a tab instead')
        })
      }, index * 250)
    })
  }, [chosen])

  /* ---------- placing things ---------- */

  const dropSkill = useCallback(
    (skill: SkillDefinition, glow: string, clientX: number, clientY: number) => {
      const position = screenToFlowPosition({ x: clientX, y: clientY })
      // One of the person's own workflows (canvas/workflow): put down with its steps.
      if (isToolbarWorkflow(skill.id)) {
        void wfRef.current.placeFromToolbar(skill.id, position)
        return
      }
      remember(true)

      if (skill.id === LABEL_SKILL.id) {
        setNodes((prev) => [
          ...prev.map((node) => (node.selected ? { ...node, selected: false } : node)),
          {
            id: newId('label'),
            type: 'label',
            position,
            width: 360,
            selected: true,
            // Dark words on a pale board, light on a dark one.
            data: {
              text: '',
              textStyle: { ...DEFAULT_LABEL_STYLE, color: pale ? '#111111' : '#f2f2f2' },
            } satisfies LabelNodeData,
          } as Node,
        ])
        return
      }

      if (skill.id === NOTE_SKILL.id) {
        setNodes((prev) => [
          ...prev.map((node) => (node.selected ? { ...node, selected: false } : node)),
          {
            id: newId('note'),
            type: 'note',
            position,
            width: 200,
            height: 180,
            selected: true,
            data: { text: '' } satisfies NoteNodeData,
          } as Node,
        ])
        return
      }

      const card = skillCard(skill, glow, position)
      setNodes((prev) => [...prev, card])
    },
    [remember, screenToFlowPosition, setNodes, pale],
  )

  /**
   * A tool picked from a picture's quick tools (QuickSkills.tsx): its card to
   * the right of the picture, the picture wired into it, and the card chosen
   * so it can be filled in. One undo step, like a drop and a wire.
   *
   * Placed from the picture's position on the board, not its position inside
   * a frame, so a picture in a frame puts its card beside the picture rather
   * than somewhere relative to the frame.
   */
  const attachSkill = useCallback(
    (sourceId: string, skill: SkillDefinition, glow: string) => {
      const from = getInternalNode(sourceId)
      if (!from) return
      const at = from.internals.positionAbsolute
      const wide = from.measured?.width ?? from.width ?? 240
      const card = skillCard(skill, glow, { x: at.x + wide + 120, y: at.y })
      // The picture card numbers its ports from ref-0; the Video card takes
      // the picture as its first frame; every other card has `in`.
      const port = card.type === 'picture' ? 'ref-0' : card.type === 'video' ? FIRST_PORT : 'in'
      remember(true)
      setNodes((prev) => [
        ...prev.map((node) => (node.selected ? { ...node, selected: false } : node)),
        { ...card, selected: true },
      ])
      setEdges((prev) =>
        addEdge(
          { id: newId('edge'), source: sourceId, sourceHandle: 'out', target: card.id, targetHandle: port },
          prev,
        ),
      )
    },
    [getInternalNode, remember, setEdges, setNodes],
  )

  /**
   * Put one or more files onto the board.
   *
   * Uploaded one at a time rather than in a batch: each becomes its own node
   * the moment it arrives, so dropping twenty pictures fills the board as they
   * land instead of showing nothing for half a minute and then everything.
   */
  const placeFiles = useCallback(
    async (fileList: File[], at: { x: number; y: number }) => {
      const images = fileList.filter((file) => file.type.startsWith('image/'))
      if (images.length === 0) {
        setAlert('Only images can go on a board')
        return
      }

      const id = projectId || (await ensureProject())
      if (!id) return
      remember(true)

      for (const [index, file] of images.entries()) {
        // Too large is known before anything is sent, and said with the
        // numbers -- not after a long upload, as "That file is too large".
        if (file.size > MAX_UPLOAD_BYTES) {
          setAlert(
            `${file.name || 'This image'} is ${megabytes(file.size)} — the limit is ${megabytes(MAX_UPLOAD_BYTES)}`,
          )
          continue
        }
        /*
         * THE FRAME GOES DOWN BEFORE THE UPLOAD, NOT AFTER IT.
         *
         * A photograph off a phone is several megabytes and takes seconds to
         * reach the store. Nothing appeared during those seconds, so pasting a
         * large picture looked exactly like pasting nothing -- and the usual
         * response to that is to press it again, which uploads it twice.
         *
         * The shape is measured here in the browser first, so the frame is the
         * shape of the picture that is coming and nothing jumps when it
         * arrives. Measuring is local and quick; the upload is the slow half.
         */
        const nodeId = newId('image')
        const size = await measure(file)
        const width = Math.min(size?.width ?? 260, 320)
        const height =
          size && size.width ? Math.round((width * size.height) / size.width) : 200

        // Dropped over a frame, it is in that frame.
        setNodes((prev) =>
          settle(
            [
              ...prev,
              {
                id: nodeId,
                type: 'image',
                page: pageId,
                position: { x: at.x + (index % 4) * 30, y: at.y + index * 24 },
                width,
                height,
                data: { title: file.name, making: true } satisfies ImageNodeData,
              } as Node,
            ],
            new Set([nodeId]),
          ),
        )

        try {
          const stored = await uploadProjectFile(id, file, size)
          setFiles((prev) => new Map(prev).set(stored.id, stored))

          // The same node, filled in. Replaced rather than removed and added
          // again, so anything already wired to it stays wired -- and so a
          // picture does not blink out and back in at the moment it arrives.
          setNodes((prev) =>
            prev.map((node) =>
              node.id === nodeId
                ? {
                    ...node,
                    // Its stored shape now, capped so a 6000px photograph does
                    // not arrive filling the whole board.
                    width: Math.min(stored.width ?? width, 320),
                    height:
                      stored.width && stored.height
                        ? Math.round((Math.min(stored.width, 320) * stored.height) / stored.width)
                        : node.height,
                    data: {
                      fileId: stored.id,
                      title: stored.title,
                      ...(frameOf(node) ? { frame: frameOf(node) } : {}),
                    } satisfies ImageNodeData,
                  }
                : node,
            ),
          )
        } catch (error) {
          // The frame comes away with it. A frame left behind would go on
          // promising a picture that is never coming.
          setNodes((prev) => prev.filter((node) => node.id !== nodeId))
          setAlert(
            `${file.name || 'That image'} could not be added: ${error instanceof Error ? error.message : 'it did not upload'}`,
          )
        }
      }
    },
    [projectId, ensureProject, remember, setNodes, pageId],
  )

  /**
   * Replace (Huanrui, 2026-10-12): another picture in the same place and the
   * same box -- same position, size and frame, so a laid-out frame keeps its
   * layout; the new picture fills the box the way every picture does (cut to
   * it, from the middle). One step on the undo stack.
   */
  useEffect(() => {
    if (!menu) setReplaceOpen(false)
  }, [menu])
  useEffect(() => {
    if (!pickFor) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPickFor('')
        setNote('')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pickFor])

  /** Put a picture already known to the board into another's place. */
  const replaceWith = useCallback(
    (nodeId: string, picture: { fileId?: string; assetId?: string; title?: string }) => {
      remember(true)
      setNodes((prev) =>
        prev.map((node) => {
          if (node.id !== nodeId) return node
          const old = node.data as ImageNodeData
          return {
            ...node,
            data: {
              ...(picture.fileId ? { fileId: picture.fileId } : { assetId: picture.assetId }),
              title: picture.title || old.title,
              ...(old.frame ? { frame: old.frame } : {}),
              ...(old.mark ? { mark: true } : {}),
            } satisfies ImageNodeData,
          }
        }),
      )
      setNote('Replaced. Ctrl+Z puts the old one back.')
    },
    [remember, setNodes],
  )

  /**
   * "From the clipboard": a picture copied anywhere (a screenshot, a picture
   * on a website), or a picture copied on a board here (Ctrl+C), which is
   * used as it is -- brought over first if it belongs to another project.
   */
  const replaceFromClipboard = useCallback(
    async (nodeId: string) => {
      if (!navigator.clipboard?.read) {
        setAlert('This browser does not let the page read the clipboard. Use From the computer instead.')
        return
      }
      try {
        const items = await navigator.clipboard.read()
        for (const item of items) {
          const type = item.types.find((one) => one.startsWith('image/'))
          if (type) {
            const blob = await item.getType(type)
            const ext = type.split('/')[1]?.replace('jpeg', 'jpg') || 'png'
            await replacePicture(nodeId, new File([blob], `pasted.${ext}`, { type }))
            return
          }
        }
        for (const item of items) {
          if (!item.types.includes('text/plain')) continue
          const text = await (await item.getType('text/plain')).text()
          if (!text.startsWith(CLIP_MARK)) continue
          const clip = JSON.parse(window.localStorage.getItem(CLIP_KEY) ?? 'null') as StoredClip | null
          const token = text.slice(CLIP_MARK.length)
          const picture = clip?.token === token ? clip.doc.nodes.find((node) => node.type === 'image') : undefined
          const data = (picture?.data ?? {}) as ImageNodeData
          if (data.assetId) {
            replaceWith(nodeId, { assetId: data.assetId, title: data.title })
            return
          }
          if (data.fileId && clip) {
            let fileId = data.fileId
            if (clip.projectId && clip.projectId !== projectId) {
              const target = projectId || (await ensureProject())
              const moved = await copyProjectFiles(target, [fileId])
              fileId = moved[fileId] ?? fileId
              takeFiles(await listProjectFiles(target, { thumbs: true }))
            }
            replaceWith(nodeId, { fileId, title: data.title })
            return
          }
        }
        setNote('There is no picture on the clipboard. Copy one first, then try again.')
      } catch (error) {
        console.warn('the clipboard could not be read', error)
        setAlert('The clipboard could not be read. Allow it when the browser asks, or use From the computer.')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, ensureProject, replaceWith, takeFiles],
  )

  const replacePicture = useCallback(
    async (nodeId: string, file: File) => {
      let id = projectId
      try {
        id = id || (await ensureProject())
      } catch {
        id = ''
      }
      if (!id) {
        setAlert('This project could not be saved, so nothing was replaced')
        return
      }
      remember(true)
      setNodes((prev) =>
        prev.map((node) => (node.id === nodeId ? { ...node, data: { ...node.data, making: true } } : node)),
      )
      try {
        const size = await measure(file)
        const stored = await uploadProjectFile(id, file, size)
        setFiles((prev) => new Map(prev).set(stored.id, stored))
        setNodes((prev) =>
          prev.map((node) => {
            if (node.id !== nodeId) return node
            const old = node.data as ImageNodeData
            return {
              ...node,
              data: {
                fileId: stored.id,
                title: stored.title,
                ...(old.frame ? { frame: old.frame } : {}),
                ...(old.mark ? { mark: true } : {}),
              } satisfies ImageNodeData,
            }
          }),
        )
        setNote('Replaced. Ctrl+Z puts the old one back.')
      } catch (error) {
        setNodes((prev) =>
          prev.map((node) => {
            if (node.id !== nodeId) return node
            const data = { ...(node.data as Record<string, unknown>) }
            delete data.making
            return { ...node, data }
          }),
        )
        setAlert(`It could not be replaced: ${error instanceof Error ? error.message : 'the picture did not upload'}`)
      }
    },
    [projectId, ensureProject, remember, setNodes],
  )

  /* ---------- wiring ---------- */

  /**
   * What may be joined to what.
   *
   * One direction only: a picture into a skill. A skill wired to a skill is
   * the obvious thing to try — and it means nothing here, because a skill
   * takes pictures, not skills, so the second one would sit there lit up and
   * refuse to run. Two pictures joined together means nothing either.
   *
   * The wire out of a skill is drawn by a run, not by hand, so nothing here
   * stands in its way.
   */
  const canConnect = useCallback(
    (connection: Connection | Edge) => {
      if (connection.source === connection.target) return false
      const from = nodes.find((node) => node.id === connection.source)
      const to = nodes.find((node) => node.id === connection.target)
      // A frame feeds only a skill that takes a set of pictures. None does
      // yet, so for now its ports take no wire at all.
      if (from?.type === 'frame') {
        const ref = String((to?.data as { skillRef?: unknown } | undefined)?.skillRef ?? '')
        return SKILL_NODES.has(to?.type ?? '') && FRAME_SKILLS.has(ref)
      }
      // The Mood board card takes frames, not single pictures (2026-10-16).
      if (to?.type === 'moodboard') return false
      // Picture to picture: a dashed line that says only "these belong
      // together" (Huanrui, 2026-10-01). Feeds nothing; triple-click follows it.
      if (from?.type === 'image' && to?.type === 'image') return true
      return from?.type === 'image' && SKILL_NODES.has(to?.type ?? '')
    },
    [nodes],
  )

  /**
   * Triple-click a skill: select everything wired to it, and line it up.
   *
   * What goes into a skill and what came out of it is the one question asked
   * of a board over and over, and on a board of any size it is answered by
   * tracing a wire with a finger. Three clicks answers it: the skill, its
   * inputs and its outputs are selected — selected, not grouped, so they can
   * still be taken apart — and put in a row, inputs left, outputs right, all
   * centred on the skill.
   *
   * The pictures among them are levelled to one height first, as the tidy
   * button does, so a row of outputs can be compared at a glance.
   *
   * The skill itself does not move. It is the thing under the cursor, and
   * something that jumps away from the pointer that just asked about it reads
   * as a mistake. One step on the undo stack, so it can be taken back whole.
   */
  const gatherFamily = useCallback(
    (skill: Node, whole: boolean) => {
      const inputIds = new Set(edges.filter((e) => e.target === skill.id).map((e) => e.source))
      const outputIds = new Set(edges.filter((e) => e.source === skill.id).map((e) => e.target))
      // Held down, the question is not "what goes in and out of this one" but
      // "what is this part of" — so the walk keeps going through whatever the
      // outputs feed next, and whatever fed the inputs before that.
      const family = whole
        ? connectedTo(skill.id, edges)
        : new Set([skill.id, ...inputIds, ...outputIds])

      remember(true)

      /*
       * Every card in it is folded first, before anything is lined up
       * (Huanrui, 2026-10-06): an open card is four times the height of a
       * folded one, and a row laid out round it is mostly gap. The layout
       * then waits two frames, so React Flow has measured the folded cards
       * and the columns are worked out from the sizes they now have. Still
       * one step on the undo stack: the step was taken above.
       */
      const folding = new Set(
        nodes
          .filter((node) => family.has(node.id) && FOLDABLE.has(node.type ?? ''))
          .filter((node) => (node.data as { open?: unknown }).open !== false)
          .map((node) => node.id),
      )
      if (folding.size > 0) {
        setNodes((prev) =>
          prev.map((node) => (folding.has(node.id) ? { ...node, data: { ...node.data, open: false } } : node)),
        )
      }

      const lineUp = () => {
        const now = latestNodes.current
        const anchor = now.find((node) => node.id === skill.id) ?? skill
        /*
         * A frame's pictures go where the frame goes (Huanrui, 2026-10-14):
         * they are not lined up on their own -- that tore them out of the
         * frame -- but moved by however far the frame is moved, below.
         */
        const framesIn = new Set(now.filter((node) => family.has(node.id) && isFrame(node)).map((node) => node.id))
        const riding = (node: Node) => framesIn.has(frameOf(node) ?? '') && !framesIn.has(node.id)
        const members = now.filter((node) => family.has(node.id) && !riding(node))

        /*
         * The pictures are levelled first, then lined up (Huanrui, 2026-10-01):
         * the same median height the tidy button uses, widths following each
         * picture's own shape. Worked out before arranging, from the same
         * arithmetic, so the columns are laid out with the heights they are
         * about to have rather than the ones they had a moment ago -- the same
         * reason the tidy button does both in one pass. Skill cards keep their
         * size.
         */
        const pictures = members.filter((node) => MEDIA_NODES.has(node.type ?? ''))
        const resized = new Map<string, { w: number; h: number }>()
        if (pictures.length >= 2) {
          const heights = pictures.map((node) => pictureSizeOf(node).h).sort((a, b) => a - b)
          const target = heights[Math.floor(heights.length / 2)] ?? 0
          if (target > 0) {
            for (const node of pictures) {
              const { w, h } = pictureSizeOf(node)
              resized.set(node.id, { w: Math.round(w * (h ? target / h : 1)), h: target })
            }
          }
        }

        const places = arrange(members, latestEdges.current, anchor, resized)

        // How far each frame moves, for its pictures to move the same.
        const shift = new Map<string, { dx: number; dy: number }>()
        for (const id of framesIn) {
          const frame = now.find((node) => node.id === id)
          const to = places.get(id)
          if (frame && to) shift.set(id, { dx: to.x - frame.position.x, dy: to.y - frame.position.y })
        }

        setNodes((prev) =>
          prev.map((node) => {
            const ride = shift.get(frameOf(node) ?? '')
            if (ride && !framesIn.has(node.id)) {
              return {
                ...node,
                selected: true,
                position: { x: node.position.x + ride.dx, y: node.position.y + ride.dy },
              }
            }
            const size = resized.get(node.id)
            return {
              ...node,
              selected: family.has(node.id),
              position: places.get(node.id) ?? node.position,
              ...(size ? { width: size.w, height: undefined } : {}),
            }
          }),
        )
        setNote(
          family.size === 1
            ? 'Nothing is wired to this skill yet'
            : whole
              ? `${family.size} things in this chain`
              : `${inputIds.size} in, ${outputIds.size} out`,
        )
      }
      if (folding.size === 0) lineUp()
      else window.requestAnimationFrame(() => window.requestAnimationFrame(lineUp))
    },
    [edges, nodes, remember, setNodes],
  )

  /* ---------- frames ---------- */

  const byId = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes])

  /**
   * Shift held: a frame being resized keeps the shape it had when the resize
   * began.
   *
   * Done here, on each step of the resize, rather than with the resizer's own
   * keep-the-ratio switch: that one is read when the corner is grabbed, so
   * pressing Shift half way through a drag -- which is how everybody does it
   * -- did nothing. Here Shift can go down or up at any moment. The corner
   * opposite the one being dragged stays where it is.
   */
  const shiftHeld = useRef(false)
  /** Ctrl held: no snapping while dragging (snap.ts). */
  const ctrlHeld = useRef(false)
  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if (event.key === 'Shift') shiftHeld.current = true
      if (event.key === 'Control' || event.key === 'Meta') ctrlHeld.current = true
    }
    const up = (event: KeyboardEvent) => {
      if (event.key === 'Shift') shiftHeld.current = false
      if (event.key === 'Control' || event.key === 'Meta') ctrlHeld.current = false
    }
    const away = () => {
      shiftHeld.current = false
      ctrlHeld.current = false
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    window.addEventListener('blur', away)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
      window.removeEventListener('blur', away)
    }
  }, [])
  /**
   * The selection box: where it was started, on the screen, while one is
   * being drawn.
   *
   * Nothing is selected until the button is let go (Huanrui, 2026-09-30):
   * React Flow selects as the box grows, and a box still being drawn had
   * already done its work. So while it is drawn its selecting is set aside,
   * and on release the box decides, once:
   *
   *  - a picture, a card, anything but a frame: picked up if the box touches
   *    it at all -- the "sometimes it selects, sometimes it does not" was
   *    pictures a pixel over the edge of the box being left out;
   *  - a frame: only if the box goes all the way round it, because a box drawn
   *    among a frame's pictures touches the frame every time.
   *
   * Shift held on release adds to what was already selected.
   */
  const boxFrom = useRef<{ x: number; y: number } | null>(null)

  /** The box being drawn, on the board, from where it was pressed to the pointer. */
  const boxRect = useCallback((): Rect | null => {
    const from = boxFrom.current
    const to = pointer.current
    if (!from || !to) return null
    const a = screenToFlowPosition(from)
    const b = screenToFlowPosition(to)
    return {
      x: Math.min(a.x, b.x),
      y: Math.min(a.y, b.y),
      w: Math.abs(a.x - b.x),
      h: Math.abs(a.y - b.y),
    }
  }, [screenToFlowPosition])

  /** While a box is drawn, nothing it reaches is selected yet. */
  const holdBoxSelection = useCallback(
    (changes: NodeChange[]): NodeChange[] =>
      boxFrom.current ? changes.filter((change) => change.type !== 'select') : changes,
    [],
  )

  /** What a box of this size would pick up, by the rules above. */
  const picksFor = useCallback(
    (rect: Rect): Set<string> => {
      const hit = onPage.filter((node) => {
        const box = boxOf(node)
        if (isFrame(node)) {
          return (
            box.x >= rect.x &&
            box.y >= rect.y &&
            box.x + box.w <= rect.x + rect.w &&
            box.y + box.h <= rect.y + rect.h
          )
        }
        return (
          box.x < rect.x + rect.w && box.x + box.w > rect.x && box.y < rect.y + rect.h && box.y + box.h > rect.y
        )
      })
      // A frame picked up takes its pictures and words with it, as one, as in
      // Miro (Huanrui, 2026-10-23): they are not picked as well, each with its
      // own handles and tools. Moving, copying, deleting and sending a frame
      // already take what is in it.
      const frames = new Set(hit.filter(isFrame).map((node) => node.id))
      return new Set(hit.filter((node) => !frames.has(frameOf(node))).map((node) => node.id))
    },
    [onPage],
  )

  /**
   * What the box would pick up if it were let go now: drawn as selected,
   * without being selected (Huanrui, 2026-09-30). Worked out once per frame
   * of the screen, however fast the pointer moves.
   */
  const previewAsked = useRef(0)
  const previewBox = useCallback(() => {
    if (previewAsked.current) return
    previewAsked.current = window.requestAnimationFrame(() => {
      previewAsked.current = 0
      const rect = boxFrom.current ? boxRect() : null
      const next = rect ? picksFor(rect) : new Set<string>()
      setPending((prev) =>
        prev.size === next.size && [...next].every((id) => prev.has(id)) ? prev : next,
      )
    })
  }, [boxRect, picksFor])
  const previewLatest = useRef(previewBox)
  previewLatest.current = previewBox

  const finishBox = useCallback(() => {
    const rect = boxRect()
    boxFrom.current = null
    // Every node handed afresh once more, then back to handing out the same
    // objects (see `decorated`).
    handOutAfresh.current = true
    window.setTimeout(() => {
      if (!boxFrom.current) handOutAfresh.current = false
    }, 0)
    setNodes((prev) => [...prev])
    window.cancelAnimationFrame(previewAsked.current)
    previewAsked.current = 0
    setPending((prev) => (prev.size === 0 ? prev : new Set()))
    if (!rect || (rect.w < 2 && rect.h < 2)) return
    // Shift or Ctrl (Cmd) held: added to what is selected (Huanrui, 2026-10-23).
    const keep = shiftHeld.current || ctrlHeld.current
    const picked = picksFor(rect)
    setNodes((prev) =>
      prev.map((node) => {
        const on = picked.has(node.id) || (keep && Boolean(node.selected))
        return Boolean(node.selected) === on ? node : { ...node, selected: on }
      }),
    )
  }, [boxRect, picksFor, setNodes])

  /**
   * Snapping while dragging (snap.ts): what is being dragged lines up with,
   * or spaces itself evenly against, what else is on the page, within a few
   * pixels on the screen; blue lines say so. Ctrl held turns it off.
   *
   * Applied to every step of React Flow's drag AND to the last one: React
   * Flow keeps its own idea of where a dragged node is and reports that when
   * the button comes up, so correcting only the steps in between left every
   * node where the pointer was, a few pixels off the line just shown.
   */
  const [guides, setGuides] = useState<{ lines: Segment[]; gaps: Segment[] } | null>(null)
  const snapDrag = useCallback(
    (changes: NodeChange[]): NodeChange[] => {
      const drags = changes.filter(
        (c): c is Extract<NodeChange, { type: 'position' }> =>
          c.type === 'position' && typeof c.dragging === 'boolean' && Boolean(c.position),
      )
      if (drags.length === 0) return changes
      const ending = drags.every((c) => c.dragging === false)
      if (ctrlHeld.current || changes.some((c) => c.type === 'dimensions' && c.resizing)) {
        setGuides((was) => (was ? null : was))
        return changes
      }
      const ids = new Set(drags.map((c) => c.id))
      const moving: Rect[] = []
      for (const change of drags) {
        const node = byId.get(change.id)
        if (!node) continue
        moving.push({ ...boxOf(node), x: change.position!.x, y: change.position!.y })
      }
      const box = bounds(moving)
      if (!box) return changes
      // What moves with them -- the pictures in a dragged frame -- is not
      // something to line up with.
      const draggedFrames = new Set(drags.filter((c) => isFrame(byId.get(c.id) ?? ({} as Node))).map((c) => c.id))
      const zoom = Math.max(0.05, getViewport().zoom)
      // Only what is around it (snap.ts, around): a few things, the nearest.
      const targets = around(
        box,
        onPage.filter((node) => !ids.has(node.id) && !draggedFrames.has(frameOf(node))).map(boxOf),
        160 / zoom,
      )
      const snap = snapMove(box, targets, 6 / zoom)
      setGuides(ending || (snap.lines.length === 0 && snap.gaps.length === 0) ? null : { lines: snap.lines, gaps: snap.gaps })
      if (!snap.dx && !snap.dy) return changes
      return changes.map((c) =>
        c.type === 'position' && c.position && ids.has(c.id)
          ? { ...c, position: { x: c.position.x + snap.dx, y: c.position.y + snap.dy } }
          : c,
      )
    },
    [byId, getViewport, onPage],
  )

  /**
   * Snapping while a picture is resized: the side being dragged lines up with
   * the edges and middles of what is around it, the same few-pixel reach and
   * the same blue line as a drag. A picture keeps its proportions, so when
   * one side snaps the other follows; the corner opposite stays put.
   */
  const lastSized = useRef(new Map<string, Rect>())
  const snapResize = useCallback(
    (changes: NodeChange[]): NodeChange[] => {
      let out = changes
      for (const change of changes) {
        if (change.type !== 'dimensions' || change.resizing === undefined) continue
        const node = byId.get(change.id)
        if (!node || node.type !== 'image') continue
        if (change.resizing === false) {
          // The resizer reports its own size on letting go; the snapped one stands.
          const last = lastSized.current.get(change.id)
          lastSized.current.delete(change.id)
          setGuides((was) => (was ? null : was))
          if (last && !ctrlHeld.current) {
            out = out.map((c) => (c === change ? { ...change, dimensions: { width: last.w, height: last.h } } : c))
          }
          continue
        }
        if (!change.dimensions || ctrlHeld.current) {
          lastSized.current.delete(change.id)
          setGuides((was) => (was ? null : was))
          continue
        }
        const moved = out.find(
          (c): c is Extract<NodeChange, { type: 'position' }> => c.type === 'position' && c.id === change.id,
        )
        const start = boxOf(node)
        const x = moved?.position?.x ?? node.position.x
        const y = moved?.position?.y ?? node.position.y
        const { width, height } = change.dimensions
        const ratio = width / Math.max(1, height)
        const box: Rect = { x, y, w: width, h: height }
        // Which sides are moving. Dragging a left or top handle moves the box
        // and keeps its right or bottom where it was; a right or bottom handle
        // does not move the box at all.
        const leftMoves = Boolean(moved) && Math.abs(x + width - (start.x + start.w)) < 1.5
        const topMoves = Boolean(moved) && Math.abs(y + height - (start.y + start.h)) < 1.5
        const right = x + width
        const bottom = y + height
        const zoom = Math.max(0.05, getViewport().zoom)
        const others = around(
          box,
          onPage.filter((other) => other.id !== node.id && frameOf(node) !== other.id).map(boxOf),
          160 / zoom,
        )
        const sx = snapEdge('x', leftMoves ? x : right, box, others, 6 / zoom)
        const sy = snapEdge('y', topMoves ? y : bottom, box, others, 6 / zoom)
        const pickX = sx && (!sy || Math.abs(sx.shift) <= Math.abs(sy.shift))
        let w = width
        let h = height
        if (pickX && sx) {
          w = width + (leftMoves ? -sx.shift : sx.shift)
          h = w / ratio
        } else if (sy) {
          h = height + (topMoves ? -sy.shift : sy.shift)
          w = h * ratio
        }
        const nx = leftMoves ? right - w : x
        const ny = topMoves ? bottom - h : y
        const line = pickX ? sx?.line : sy?.line
        setGuides(line ? { lines: [line], gaps: [] } : null)
        if (!sx && !sy) {
          lastSized.current.delete(change.id)
          continue
        }
        lastSized.current.set(change.id, { x: nx, y: ny, w, h })
        out = out.map((c) =>
          c === change
            ? { ...change, dimensions: { width: w, height: h }, setAttributes: true }
            : c === moved
              ? { ...moved, position: { x: nx, y: ny } }
              : c,
        )
        if (!moved && (nx !== node.position.x || ny !== node.position.y)) {
          out = [...out, { id: change.id, type: 'position', position: { x: nx, y: ny } }]
        }
      }
      return out
    },
    [byId, getViewport, onPage],
  )

  /** Where each frame being resized started, and the last shape it was locked to. */
  const resizeFrom = useRef(new Map<string, Rect>())
  const lockedTo = useRef(new Map<string, Rect>())

  const lockFrameRatio = useCallback(
    (changes: NodeChange[]): NodeChange[] => {
      if (!changes.some((c) => c.type === 'dimensions' && c.resizing !== undefined)) return changes
      let out = changes
      for (const change of changes) {
        if (change.type !== 'dimensions' || change.resizing === undefined) continue
        const frame = byId.get(change.id)
        if (!frame || !isFrame(frame)) continue

        if (change.resizing === false) {
          // Let go: the resizer reports its own, unlocked, size. The locked one
          // stands if Shift was holding it.
          const last = lockedTo.current.get(change.id)
          if (last && shiftHeld.current) {
            out = out.map((c) =>
              c === change ? { ...change, dimensions: { width: last.w, height: last.h } } : c,
            )
          }
          resizeFrom.current.delete(change.id)
          lockedTo.current.delete(change.id)
          continue
        }

        if (!shiftHeld.current || !change.dimensions) {
          lockedTo.current.delete(change.id)
          resizeFrom.current.delete(change.id)
          continue
        }
        // The shape it has at the moment Shift goes down is the one kept.
        if (!resizeFrom.current.has(change.id)) resizeFrom.current.set(change.id, boxOf(frame))
        const start = resizeFrom.current.get(change.id)!
        const moved = out.find(
          (c): c is Extract<NodeChange, { type: 'position' }> => c.type === 'position' && c.id === change.id,
        )
        const x = moved?.position?.x ?? frame.position.x
        const y = moved?.position?.y ?? frame.position.y
        const { width, height } = change.dimensions
        const ratio = start.w / Math.max(1, start.h)
        // Whichever side moved further decides; the other follows.
        const byWidth = Math.abs(width / start.w - 1) >= Math.abs(height / start.h - 1)
        const w = Math.round(byWidth ? width : height * ratio)
        const h = Math.round(byWidth ? width / ratio : height)
        // A left or top edge being dragged moves the frame; keep the far side put.
        const nx = Math.abs(x - start.x) > 0.5 ? start.x + start.w - w : start.x
        const ny = Math.abs(y - start.y) > 0.5 ? start.y + start.h - h : start.y
        lockedTo.current.set(change.id, { x: nx, y: ny, w, h })
        out = out.map((c) =>
          c === change
            ? // Both sides written: dragging one edge would otherwise set
              // only the width, and the height would never follow.
              { ...change, dimensions: { width: w, height: h }, setAttributes: true }
            : c === moved
              ? { ...moved, position: { x: nx, y: ny } }
              : c,
        )
        if (!moved && (nx !== frame.position.x || ny !== frame.position.y)) {
          out = [...out, { id: change.id, type: 'position', position: { x: nx, y: ny } }]
        }
      }
      return out
    },
    [byId],
  )


  /**
   * A frame being moved takes its pictures with it.
   *
   * Pictures in a frame are ordinary nodes positioned on the board, and they
   * are not selected when the frame is: so every step of a frame's drag is
   * given a matching step for each of its members. Not for a member that is
   * itself being dragged (it is selected too, and already moving), and not
   * while the frame is being RESIZED -- dragging its left edge moves the
   * frame's position, and the pictures must stay where they are.
   */
  const carryMembers = useCallback(
    (changes: NodeChange[]): NodeChange[] => {
      const resizing = new Set(
        changes.filter((c) => c.type === 'dimensions' && c.resizing).map((c) => (c as { id: string }).id),
      )
      const moving = new Set(changes.filter((c) => c.type === 'position').map((c) => c.id))
      const extra: NodeChange[] = []
      for (const change of changes) {
        if (change.type !== 'position' || !change.position || resizing.has(change.id)) continue
        const frame = byId.get(change.id)
        if (!frame || !isFrame(frame)) continue
        const dx = change.position.x - frame.position.x
        const dy = change.position.y - frame.position.y
        if (dx === 0 && dy === 0) continue
        for (const node of nodes) {
          if (frameOf(node) !== frame.id || moving.has(node.id)) continue
          extra.push({
            id: node.id,
            type: 'position',
            position: { x: node.position.x + dx, y: node.position.y + dy },
            dragging: change.dragging,
          })
        }
      }
      return extra.length > 0 ? [...changes, ...extra] : changes
    },
    [byId, nodes],
  )

  /** The part of the board on screen, in board coordinates. */
  const viewRect = useCallback((): Rect => {
    const box = board.current?.getBoundingClientRect()
    const topLeft = screenToFlowPosition({ x: box?.left ?? 0, y: box?.top ?? 0 })
    const bottomRight = screenToFlowPosition({
      x: box?.right ?? window.innerWidth,
      y: box?.bottom ?? window.innerHeight,
    })
    return { x: topLeft.x, y: topLeft.y, w: bottomRight.x - topLeft.x, h: bottomRight.y - topLeft.y }
  }, [screenToFlowPosition])

  /** Room for a frame's name above it, so a new frame's name does not sit on something. */
  const LABEL_ROOM = 36

  /** Everything on this page, as the space it takes up -- a frame's name included. */
  const occupiedHere = useCallback(
    (): Rect[] =>
      onPage.map((node) => {
        const rect = boxOf(node)
        return isFrame(node) ? { ...rect, y: rect.y - LABEL_ROOM, h: rect.h + LABEL_ROOM } : rect
      }),
    [onPage],
  )

  /** These nodes, and the pictures in any frame among them. */
  const withContents = useCallback(
    (list: Node[]): Node[] => {
      const frames = new Set(list.filter(isFrame).map((node) => node.id))
      if (frames.size === 0) return list
      const ids = new Set(list.map((node) => node.id))
      return [...list, ...nodes.filter((node) => frames.has(frameOf(node)) && !ids.has(node.id))]
    },
    [nodes],
  )

  /**
   * Several frames tidied (Huanrui, 2026-10-23): all the height of the middle
   * one of them, in reading order, in rows from where the first one is --
   * wrapped as pictures are (tidy), a tenth of the height apart, room for the
   * name over each. A frame's pictures and words are scaled and moved with
   * it; a card in it only moves. One step on the undo stack.
   */
  const tidyFrames = useCallback(() => {
    const frames = selectedFrames
    if (frames.length < 2) return
    const heights = frames.map((frame) => boxOf(frame).h).sort((a, b) => a - b)
    const target = Math.round(heights[Math.floor(heights.length / 2)] ?? 0)
    if (!target) return
    const gap = Math.max(8, Math.round(target * 0.1))
    const left = Math.min(...frames.map((frame) => boxOf(frame).x))
    // Wrapped within the width the frames took up before (Huanrui,
    // 2026-11-02), as pictures are: a frame goes to the next row when it
    // would pass that right edge.
    const width = Math.max(...frames.map((frame) => boxOf(frame).x + boxOf(frame).w)) - left
    let x = left
    let y = Math.min(...frames.map((frame) => boxOf(frame).y))
    /** For each thing that moves: its frame's corner, the scale, and where that corner goes. */
    const moves = new Map<string, { fx: number; fy: number; s: number; nx: number; ny: number }>()
    frames.forEach((frame) => {
      const box = boxOf(frame)
      const w = Math.round(box.w * (target / Math.max(1, box.h)))
      if (x > left && x - left + w > width) {
        x = left
        y += target + gap
      }
      const move = { fx: box.x, fy: box.y, s: target / Math.max(1, box.h), nx: x, ny: y }
      const page = (frame as { page?: string }).page
      for (const node of nodes) {
        if (node.id === frame.id || (node as { page?: string }).page !== page) continue
        const b = boxOf(node)
        // Its own, and words lying on it that belong to no frame (a mood board's title).
        const inside = b.x >= box.x - 1 && b.y >= box.y - 1 && b.x + b.w <= box.x + box.w + 1 && b.y + b.h <= box.y + box.h + 1
        if (frameOf(node) === frame.id || (node.type === 'label' && !frameOf(node) && inside)) moves.set(node.id, move)
      }
      moves.set(frame.id, move)
      x += Math.round(box.w * move.s) + gap
    })
    remember(true)
    setNodes((prev) =>
      prev.map((node) => {
        const move = moves.get(node.id)
        if (!move) return node
        const { fx, fy, s, nx, ny } = move
        const b = boxOf(node)
        if (!(node.type === 'image' || isFrame(node) || node.type === 'label')) {
          // A card is a form: its middle moves with the rest, its size stays.
          return { ...node, position: { x: nx + (b.x + b.w / 2 - fx) * s - b.w / 2, y: ny + (b.y + b.h / 2 - fy) * s - b.h / 2 } }
        }
        const position = { x: nx + (b.x - fx) * s, y: ny + (b.y - fy) * s }
        if (node.type === 'label') {
          const style = { ...DEFAULT_LABEL_STYLE, ...((node.data as { textStyle?: object }).textStyle ?? {}) }
          return {
            ...node,
            position,
            width: Math.round(b.w * s),
            height: undefined,
            data: { ...node.data, textStyle: { ...style, size: Math.max(4, Math.round(style.size * s * 10) / 10) } },
          }
        }
        const sized = node.height !== undefined && node.height !== null
        return {
          ...node,
          position,
          width: Math.round(b.w * s),
          height: sized || isFrame(node) ? Math.round(b.h * s) : node.height,
        }
      }),
    )
  }, [selectedFrames, nodes, remember, setNodes])

  /** The filter's choice: only things of that kind stay selected, those in the frames included. */
  const keepKind = useCallback(
    (kind: SelectKind) => {
      const keep = new Set(selection.members[kind])
      setNodes((prev) =>
        prev.map((node) => {
          const on = keep.has(node.id)
          return Boolean(node.selected) === on ? node : { ...node, selected: on }
        }),
      )
    },
    [selection, setNodes],
  )

  /**
   * Put a frame around these pictures (the right-click menu), or an empty one
   * where the pointer is when nothing is selected.
   *
   * Padded, then made 16:9 with the pictures in the middle. Any picture whose
   * centre falls inside it is in it afterwards -- the selected ones, and one
   * that was merely lying there, the same rule as letting go of a picture.
   */
  const makeFrame = useCallback(
    (at: { x: number; y: number }) => {
      const chosenHere = onPage.filter((node) => node.selected && node.type === 'image')
      const content = bounds(chosenHere.map(boxOf))
      // Around the pictures picked; or, empty, beside the pointer in the
      // nearest space that covers nothing already on the board.
      const spot = content
        ? null
        : clearSpot(
            { w: EMPTY_FRAME.w, h: EMPTY_FRAME.h + LABEL_ROOM },
            occupiedHere(),
            viewRect(),
            at,
          )
      const rect: Rect = content
        ? frameAround(content)
        : { x: spot!.x, y: spot!.y + LABEL_ROOM, ...EMPTY_FRAME }
      const id = newId('frame')
      const count = nodes.filter(isFrame).length
      remember(true)
      setNodes((prev) => {
        const added = [
          ...prev.map((node) => ({ ...node, selected: false })),
          {
            id,
            type: 'frame',
            page: pageId,
            position: { x: rect.x, y: rect.y },
            width: rect.w,
            height: rect.h,
            selected: true,
            data: { name: `Frame ${count + 1}`, color: readFrameColor() },
          } as Node,
        ]
        const wanted = new Set(
          added
            .filter(
              (node) =>
                node.type === 'image' &&
                (chosenHere.some((one) => one.id === node.id) || !frameOf(node)),
            )
            .map((node) => node.id),
        )
        return settle(added, wanted)
      })
    },
    [nodes, onPage, pageId, remember, setNodes, occupiedHere, viewRect],
  )

  /* ---------- pictures waiting from "Copy to project" ---------- */

  /**
   * The copy waiting to be offered, if there is one (projectClip.ts).
   *
   * Offered on every board, open or opened later, until somebody answers it
   * on one of them; the storage event then takes it off all the others.
   */
  const [offer, setOffer] = useState<ProjectClip | null>(() => readProjectClip())
  useEffect(() => watchClip(setOffer), [])
  /*
   * Frames sent from Miro (the panel's Copy to project, 2026-10-13): parked
   * with the Worker, since the panel cannot reach this browser's storage.
   * Taken when the board opens and whenever it is looked at again, and turned
   * into the usual offer -- several sends as one piece, side by side.
   */
  useEffect(() => {
    let busy = false
    const pull = () => {
      if (busy || document.visibilityState !== 'visible') return
      busy = true
      void takeMiroPieces()
        .then((pieces) => {
          if (pieces.length === 0) return
          const nodes: CanvasNode[] = []
          let left = 0
          pieces.forEach((piece, p) => {
            const right = Math.max(0, ...piece.nodes.map((node) => node.position.x + (node.width ?? 0)))
            const rename = (id: string) => `p${p}-${id}`
            for (const node of piece.nodes) {
              const data = { ...node.data }
              if (typeof data.frame === 'string') data.frame = rename(data.frame)
              nodes.push({ ...node, id: rename(node.id), position: { x: node.position.x + left, y: node.position.y }, data })
            }
            left += right + 200
          })
          const title = pieces.length === 1 ? pieces[0].title : `${pieces.length} sends`
          setOffer(
            putClip(`Miro — ${title}`, pieces.flatMap((piece) => piece.items), {
              nodes,
              edges: [],
              files: pieces.flatMap((piece) => piece.files),
            }),
          )
        })
        .finally(() => {
          busy = false
        })
    }
    pull()
    document.addEventListener('visibilitychange', pull)
    window.addEventListener('focus', pull)
    return () => {
      document.removeEventListener('visibilitychange', pull)
      window.removeEventListener('focus', pull)
    }
  }, [])
  const [importing, setImporting] = useState(false)

  const declineOffer = useCallback((clip: ProjectClip) => {
    takeClip(clip.token)
    setOffer(null)
  }, [])

  /**
   * Yes: a new frame, clear of everything already on this page, with a
   * placeholder for each picture; then each is copied into this project and
   * fills its place as it lands. Once they are all in, they are laid out
   * again at their real shapes -- an Amazon picture's shape is not known
   * until it has been fetched, and an A+ banner is nothing like a pack shot.
   */
  const acceptOffer = useCallback(
    async (
      clip: ProjectClip,
      options: { direct?: boolean; near?: Rect; beside?: { x: number; y: number }; onFrame?: (frameId: string) => void } = {},
    ) => {
      // `direct`: pictures handed in here (the Library card), not an offer waiting in storage.
      const taken = options.direct ? clip : takeClip(clip.token)
      if (!options.direct) setOffer(null)
      if (!taken) {
        setNote('Those pictures have already been added somewhere else')
        return
      }
      setImporting(true)

      /*
       * A card with its pictures, from the generate or upscale page (Copy to
       * Project, Huanrui 2026-10-07): put down as it was laid out there, in a
       * clear spot, with its pictures copied into this project first.
       */
      if (taken.board) {
        const piece = taken.board
        let target = projectId
        try {
          target = target || (await ensureProject())
        } catch (error) {
          console.error('no project to copy into', error)
        }
        if (!target) {
          setImporting(false)
          setAlert('This project could not be saved, so nothing was added')
          return
        }
        try {
          const moved = piece.files.length > 0 ? await copyProjectFiles(target, piece.files) : {}
          const sized = piece.nodes.map((node) => ({
            node,
            w: node.width ?? (node.type === 'image' ? 260 : 340),
            h: node.height ?? (node.type === 'image' ? 200 : 420),
          }))
          const left = Math.min(...sized.map((one) => one.node.position.x))
          const top = Math.min(...sized.map((one) => one.node.position.y))
          const right = Math.max(...sized.map((one) => one.node.position.x + one.w))
          const bottom = Math.max(...sized.map((one) => one.node.position.y + one.h))
          const corner = board.current?.getBoundingClientRect()
          const a = screenToFlowPosition({ x: corner?.left ?? 0, y: corner?.top ?? 0 })
          const b = screenToFlowPosition({ x: corner?.right ?? window.innerWidth, y: corner?.bottom ?? window.innerHeight })
          const view: Rect = { x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y }
          const spot = clearSpot({ w: right - left, h: bottom - top }, onPage.map((node) => boxOf(node)), view)
          const dx = spot.x - left
          const dy = spot.y - top
          const remap = new Map(piece.nodes.map((node) => [node.id, newId(node.type)]))
          remember(true)
          setNodes((prev) => [
            ...prev.map((node) => (node.selected ? { ...node, selected: false } : node)),
            ...piece.nodes.map((node) => {
              const fileId = (node.data as { fileId?: unknown }).fileId
              // A piece with its own frame (a mood board, 2026-10-12): its
              // pictures belong to the new frame, not the id it had there.
              const inFrame = (node.data as { frame?: unknown }).frame
              const data: Record<string, unknown> = { ...node.data }
              if (typeof fileId === 'string' && moved[fileId]) data.fileId = moved[fileId]
              if (typeof inFrame === 'string') {
                if (remap.has(inFrame)) data.frame = remap.get(inFrame)
                else delete data.frame
              }
              return {
                ...node,
                id: remap.get(node.id)!,
                page: pageId,
                position: { x: node.position.x + dx, y: node.position.y + dy },
                selected: true,
                ...(node.type === 'frame' ? { zIndex: FRAME_Z } : {}),
                data,
              } as Node
            }),
          ])
          setEdges((prev) => [
            ...prev,
            ...piece.edges
              .filter((edge) => remap.has(edge.source) && remap.has(edge.target))
              .map((edge) =>
                wire({
                  id: newId('edge'),
                  source: remap.get(edge.source)!,
                  target: remap.get(edge.target)!,
                  targetHandle: edge.targetHandle,
                }),
              ),
          ])
          takeFiles(await listProjectFiles(target, { thumbs: true }))
          const w = right - left
          const h = bottom - top
          if (w * 1.15 <= view.w && h * 1.15 <= view.h) {
            void setCenter(spot.x + w / 2, spot.y + h / 2, { zoom: getViewport().zoom, duration: 300 })
          } else {
            void fitBounds({ x: spot.x, y: spot.y, width: w, height: h }, { padding: 0.15, duration: 300 })
          }
          setNote(`Added from ${taken.from}`)
        } catch (error) {
          console.error('could not copy the card and its pictures into the project', error)
          setAlert('They could not be copied into this project')
          // Offered again, so trying once more is one click.
          if (!readProjectClip()) {
            try {
              setOffer(putClip(taken.from, taken.items, taken.board))
            } catch (again) {
              console.warn('could not offer it again', again)
            }
          }
        }
        setImporting(false)
        return
      }

      const HEIGHT = 240
      const GAP = 24
      /** Room for the frame's name above it, so the name does not sit on something. */
      const LABEL = 36
      const items = taken.items
      const guess = items.map((item) =>
        item.kind === 'library' && item.width && item.height ? { w: item.width, h: item.height } : { w: 1, h: 1 },
      )
      // The frame is the size of a new empty one (Huanrui, 2026-09-30), and
      // the pictures are fitted inside it rather than it grown round them.
      const block = rows(guess, HEIGHT, GAP)
      const shape = { x: 0, y: 0, w: EMPTY_FRAME.w, h: EMPTY_FRAME.h }
      const fitted = fitInFrame(block, shape)

      // What is on screen, in board coordinates.
      const box = board.current?.getBoundingClientRect()
      const topLeft = screenToFlowPosition({ x: box?.left ?? 0, y: box?.top ?? 0 })
      const bottomRight = screenToFlowPosition({
        x: box?.right ?? window.innerWidth,
        y: box?.bottom ?? window.innerHeight,
      })
      const view: Rect = {
        x: topLeft.x,
        y: topLeft.y,
        w: bottomRight.x - topLeft.x,
        h: bottomRight.y - topLeft.y,
      }
      const occupied = onPage.map((node) => {
        const rect = boxOf(node)
        return isFrame(node) ? { ...rect, y: rect.y - LABEL, h: rect.h + LABEL } : rect
      })
      // `beside`: the frame's top left wanted there (the Library card), the
      // nearest free place to it otherwise -- not the middle of a wide area.
      const spot = clearSpot(
        { w: shape.w, h: shape.h + LABEL },
        occupied,
        options.near ?? view,
        options.beside ? { x: options.beside.x + shape.w / 2, y: options.beside.y + (shape.h + LABEL) / 2 } : undefined,
      )
      const frame: Rect = { x: spot.x, y: spot.y + LABEL, w: shape.w, h: shape.h }
      const originX = frame.x
      const originY = frame.y

      const frameId = newId('frame')
      const holders = items.map(() => newId('image'))
      remember(true)
      setNodes((prev) => [
        ...prev.map((node) => ({ ...node, selected: false })),
        {
          id: frameId,
          type: 'frame',
          page: pageId,
          position: { x: frame.x, y: frame.y },
          width: frame.w,
          height: frame.h,
          selected: true,
          // The count is shown beside the name already, and stays true as
          // pictures come and go; written into the name it would not.
          data: { name: taken.from, color: readFrameColor() },
        } as Node,
        ...items.map(
          (item, index) =>
            ({
              id: holders[index],
              type: 'image',
              page: pageId,
              position: { x: originX + fitted[index].x, y: originY + fitted[index].y },
              width: fitted[index].w,
              height: fitted[index].h,
              data: {
                title: item.title,
                making: true,
                frame: frameId,
                aspect: fitted[index].w / fitted[index].h,
              } satisfies ImageNodeData,
            }) as Node,
        ),
      ])
      options.onFrame?.(frameId)
      // Brought into view at the zoom the board is already at, when it fits:
      // zooming in on a small frame made it look far bigger than it is.
      // Zoomed out only when it would not fit.
      const fits =
        frame.x >= view.x && frame.y - LABEL >= view.y && frame.x + frame.w <= view.x + view.w && frame.y + frame.h <= view.y + view.h
      if (fits) {
        // Already where it can be seen; nothing to move.
      } else if (frame.w * 1.15 <= view.w && (frame.h + LABEL) * 1.15 <= view.h) {
        void setCenter(frame.x + frame.w / 2, frame.y + frame.h / 2 - LABEL / 2, {
          zoom: getViewport().zoom,
          duration: 300,
        })
      } else {
        void fitBounds(
          { x: frame.x, y: frame.y - LABEL, width: frame.w, height: frame.h + LABEL },
          { padding: 0.15, duration: 300 },
        )
      }

      // The project, once the frame is down: a board with no project yet
      // (a new one) is given one here, and keeps what is on it.
      let id = projectId
      try {
        id = id || (await ensureProject())
      } catch (error) {
        console.error('no project to copy into', error)
      }
      if (!id) {
        setNodes((prev) => prev.filter((node) => node.id !== frameId && !holders.includes(node.id)))
        setImporting(false)
        setAlert('This project could not be saved, so nothing was added')
        return
      }

      const made = new Map<number, string>()
      const fill = (index: number, fileId: string | null) => {
        const nodeId = holders[index]
        if (fileId) made.set(index, fileId)
        setNodes((prev) =>
          fileId
            ? prev.map((node) =>
                node.id === nodeId
                  ? {
                      ...node,
                      // Still `making` until the board has the file's address: without
                      // one it would draw as an empty dark box (2026-10-13). The sweep
                      // stays until the picture itself can be shown.
                      data: { fileId, title: items[index].title, frame: frameId, making: true } satisfies ImageNodeData,
                    }
                  : node,
              )
            : prev.filter((node) => node.id !== nodeId),
        )
      }

      const jobs: (() => Promise<void>)[] = []
      const library = items
        .map((item, index) => ({ item, index }))
        .filter((one) => one.item.kind === 'library')
      for (let start = 0; start < library.length; start += 40) {
        const batch = library.slice(start, start + 40)
        jobs.push(async () => {
          try {
            const answer = await copyLibraryToProject(
              id,
              batch.map((one) => (one.item.kind === 'library' ? one.item.id : '')),
            )
            for (const one of batch) {
              fill(one.index, one.item.kind === 'library' ? (answer[one.item.id] ?? null) : null)
            }
          } catch (error) {
            console.error('could not copy library pictures into the project', error)
            for (const one of batch) fill(one.index, null)
          }
        })
      }
      // Amazon a few at a time, so the placeholders fill as the pictures come
      // rather than all at the end of a long wait.
      const amazon = items
        .map((item, index) => ({ item, index }))
        .filter((one) => one.item.kind === 'amazon')
      for (let start = 0; start < amazon.length; start += 4) {
        const batch = amazon.slice(start, start + 4)
        const byMarket = new Map<Market, typeof batch>()
        for (const one of batch) {
          if (one.item.kind !== 'amazon') continue
          byMarket.set(one.item.market, [...(byMarket.get(one.item.market) ?? []), one])
        }
        for (const [market, mine] of byMarket) {
          jobs.push(async () => {
            try {
              const slots = await keepIntoProject(
                id,
                market,
                mine.map((one) =>
                  one.item.kind === 'amazon'
                    ? { url: one.item.url, asin: one.item.asin, title: one.item.title }
                    : { url: '', asin: '', title: '' },
                ),
              )
              mine.forEach((one, at) => fill(one.index, slots[at] ?? null))
            } catch (error) {
              console.error('could not copy Amazon pictures into the project', error)
              for (const one of mine) fill(one.index, null)
            }
          })
        }
      }
      // Google Images picks, the same way: fetched from their sites a few at a time.
      const web = items
        .map((item, index) => ({ item, index }))
        .filter((one) => one.item.kind === 'web')
      for (let start = 0; start < web.length; start += 4) {
        const batch = web.slice(start, start + 4)
        jobs.push(async () => {
          try {
            const slots = await keepWebIntoProject(
              id,
              batch.map((one) =>
                one.item.kind === 'web'
                  ? {
                      url: one.item.url,
                      thumb: one.item.thumb,
                      title: one.item.title,
                      page: one.item.page,
                      site: one.item.site,
                      source: one.item.source,
                      credit: one.item.credit,
                    }
                  : { url: '', thumb: '', title: '', page: '', site: '' },
              ),
            )
            batch.forEach((one, at) => fill(one.index, slots[at] ?? null))
          } catch (error) {
            console.error('could not copy Google pictures into the project', error)
            for (const one of batch) fill(one.index, null)
          }
        })
      }
      let next = 0
      await Promise.all(
        [0, 1, 2].map(async () => {
          while (next < jobs.length) {
            const job = jobs[next]
            next += 1
            await job()
          }
        }),
      )

      // Laid out again at the shapes they really are, inside the same frame.
      try {
        const stored = await listProjectFiles(id, { thumbs: true })
        takeFiles(stored)
        const shapeOf = new Map(stored.map((row) => [row.id, { w: row.width ?? 1, h: row.height ?? 1 }]))
        const order = [...made.entries()].sort((a, b) => a[0] - b[0])
        if (order.length > 0) {
          const real = rows(
            order.map(([, fileId]) => shapeOf.get(fileId) ?? { w: 1, h: 1 }),
            HEIGHT,
            GAP,
          )
          setNodes((prev) => {
            const current = prev.find((node) => node.id === frameId)
            if (!current) return prev
            // Fitted to the frame as it is now -- its size stays -- and kept
            // where it now is, in case it was moved or resized meanwhile.
            const now = boxOf(current)
            const fittedNow = fitInFrame(real, { w: now.w, h: now.h })
            const places = new Map(order.map(([index], at) => [holders[index], fittedNow[at]]))
            const x0 = now.x
            const y0 = now.y
            return prev.map((node) => {
              const place = places.get(node.id)
              return place && frameOf(node) === frameId
                ? {
                    ...node,
                    position: { x: x0 + place.x, y: y0 + place.y },
                    width: place.w,
                    height: place.h,
                  }
                : node
            })
          })
        }
      } catch (error) {
        console.warn('could not lay the copied pictures out again', error)
      }
      // The addresses are known now: the pictures take over from the sweep.
      {
        const filled = new Set([...made.keys()].map((index) => holders[index]))
        setNodes((prev) =>
          prev.map((node) => {
            if (!filled.has(node.id)) return node
            const data = { ...(node.data as Record<string, unknown>) }
            delete data.making
            return { ...node, data }
          }),
        )
      }

      setImporting(false)
      const missed = items.length - made.size
      // The ones that did not come are offered again, so trying once more is
      // one click -- unless something newer has been copied meanwhile, which
      // is not to be pushed aside. A copy that failed is usually a moment's
      // slowness in the store, not a picture that cannot be copied.
      let offeredAgain = false
      if (missed > 0 && !options.direct && !readProjectClip()) {
        try {
          setOffer(putClip(taken.from, items.filter((_, index) => !made.has(index))))
          offeredAgain = true
        } catch (error) {
          console.warn('could not offer the missing pictures again', error)
        }
      }
      setNote(
        missed === 0
          ? `${made.size} added to this project`
          : (made.size === 0 ? 'None of them could be copied' : `${made.size} added; ${missed} could not be copied`) +
              (offeredAgain ? ' — offered again, to try once more' : ''),
      )
    },
    [projectId, ensureProject, screenToFlowPosition, onPage, pageId, remember, setNodes, fitBounds, setCenter, getViewport],
  )

  /**
   * The Library card's Search (libraryNode.tsx, 2026-10-13): pictures picked
   * blind -- a library's at random from its first 200 matches, Amazon's
   * (United States) the first results -- and put down beside the card as a
   * frame, in rows of one height, the way Copy to project puts them down.
   */
  /**
   * Pictures from a Library card put down as a frame beside it, to its right
   * wherever there is room, and wired to it -- so it is plain which search
   * each frame came from. The card's Search and its full-screen Add to board
   * (LibraryBrowser.tsx) both end here.
   */
  const putLibraryFrame = useCallback(
    async (cardId: string, items: ClipItem[], from: string) => {
      const card = latestNodes.current.find((node) => node.id === cardId)
      if (!card) return
      const box = boxOf(card)
      await acceptOffer(
        { token: `library-${cardId}-${Date.now()}`, from, at: Date.now(), items },
        {
          direct: true,
          near: { x: box.x + box.w + 40, y: box.y - 4000, w: 8000, h: 8000 },
          beside: { x: box.x + box.w + 80, y: box.y - 36 },
          onFrame: (frameId) =>
            setEdges((prev) => [...prev, wire({ id: newId('edge'), source: cardId, target: frameId })]),
        },
      )
    },
    [acceptOffer, setEdges],
  )

  const runLibrary = useCallback(
    async (cardId: string) => {
      const card = nodes.find((node) => node.id === cardId)
      if (!card) return
      const data = card.data as LibraryNodeData
      const words = (data.prompt ?? '').trim()
      if (data.running || !words) return
      const kind = kindOf(data)
      const source = librarySourceOf(data)
      const n = data.n ?? 20
      const patchCard = (change: Partial<LibraryNodeData>) =>
        setNodes((prev) => prev.map((node) => (node.id === cardId ? { ...node, data: { ...node.data, ...change } } : node)))
      patchCard({ running: true, error: '' })
      try {
        let items: ClipItem[] = []
        if (source === 'amazon') {
          const found: { asin: string; title: string; image: string }[] = []
          for (let page = 1; page <= 4 && found.length < n; page += 1) {
            const answer = await searchAmazon('us', words, page)
            for (const one of answer.results) if (one.image && !found.some((had) => had.asin === one.asin)) found.push(one)
            if (page >= answer.totalPages) break
          }
          items = found.slice(0, n).map((one) => ({
            kind: 'amazon' as const,
            market: 'us' as Market,
            url: fullSize(one.image),
            asin: one.asin,
            title: one.title,
            thumb: one.image,
          }))
        } else if (source === 'google' || source === 'unsplash') {
          // The first results, in their order: a hundred a Google page (one
          // credit), thirty an Unsplash page (free).
          const found: Awaited<ReturnType<typeof searchWebImages>>['results'] = []
          for (let page = 1; page <= 3 && found.length < n; page += 1) {
            const answer = source === 'google' ? await searchWebImages('us', words, page) : await searchUnsplash(words, page)
            for (const one of answer.results) if (!found.some((had) => had.url === one.url)) found.push(one)
            if (!answer.more) break
          }
          const chosen = found.slice(0, n)
          // Unsplash's rule: say when its pictures are used.
          if (source === 'unsplash') {
            void markStockUsed(chosen.map((one) => one.downloadLocation ?? '').filter(Boolean))
          }
          items = chosen.map((one) => ({
            kind: 'web' as const,
            url: one.url,
            thumb: one.thumb,
            title: one.title,
            page: one.page,
            site: one.site,
            source: source === 'unsplash' ? ('unsplash' as const) : ('google' as const),
            ...(one.author ? { credit: `Photo by ${one.author} on Unsplash` } : {}),
          }))
        } else {
          // Our own: every library together (Internal Library), or the one a
          // card from before was set to. At random from the first matches, so
          // searching again brings different ones.
          const libraries: LibraryKey[] =
            source === 'internal' ? ['audit', 'visual-language', 'design-works'] : [source]
          // A sentence understood (2026-11-02, as the Internal Library's box):
          // the libraries' tags and some words, scored; else the words as before.
          const plan = await planFor(words, libraries)
          const pages = await Promise.all(
            libraries.map((library) => {
              const smart = plan ? smartFor(plan, library) : null
              if (plan && !smart) return Promise.resolve([] as Asset[])
              return repository
                .search({ text: words, library, limit: libraries.length > 1 ? 100 : 200, smart })
                .then((page) => page.items)
                .catch(() => [] as Asset[])
            }),
          )
          const seen = new Set<string>()
          const pool = pages.flat().filter((asset) => !seen.has(asset.id) && seen.add(asset.id))
          for (let i = pool.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[pool[i], pool[j]] = [pool[j], pool[i]]
          }
          // Shuffled, but the best matches first: whole words, then inside
          // words, then close spellings (docs/search-ranked.sql) -- or, for a
          // sentence understood, the highest score first.
          if (plan) pool.sort((x, y) => (y.matchScore ?? 0) - (x.matchScore ?? 0))
          else pool.sort((x, y) => (x.matchRank ?? 0) - (y.matchRank ?? 0))
          items = pool.slice(0, n).map((asset) => ({
            kind: 'library' as const,
            id: asset.id,
            thumb: asset.thumbnailPath,
            title: asset.title,
            width: asset.width,
            height: asset.height,
          }))
        }
        if (items.length === 0) {
          patchCard({ running: false, error: 'Nothing found. Try other words.' })
          return
        }
        await putLibraryFrame(cardId, items, `${SOURCE_NAMES[source] ?? 'Library'} · ${words}`)
        patchCard({ running: false })
      } catch (error) {
        patchCard({ running: false, error: error instanceof Error ? error.message : 'The search did not work' })
      }
    },
    [nodes, setNodes, repository, putLibraryFrame],
  )
  runLibraryRef.current = (id: string) => void runLibrary(id)

  /* ---------- a Library card, full screen (LibraryBrowser.tsx, 2026-10-16) ---------- */

  const browsingCard = browsingId ? nodes.find((node) => node.id === browsingId && node.type === 'library') ?? null : null
  // The card gone (deleted, undone, another page): the window goes with it.
  useEffect(() => {
    if (browsingId && !browsingCard) setBrowsingId(null)
  }, [browsingCard, browsingId])
  const closeBrowse = useCallback(() => setBrowsingId(null), [])
  /**
   * Add to board: the card takes the library and words last searched, and the
   * pictures go down as a frame beside it, wired to it, as its Search does.
   */
  const addFromBrowse = useCallback(
    (items: ClipItem[], from: string, source: LibrarySource, words: string) => {
      const cardId = browsingId
      if (!cardId || items.length === 0) return
      setBrowsingId(null)
      const patchCard = (change: Partial<LibraryNodeData>) =>
        setNodes((prev) => prev.map((node) => (node.id === cardId ? { ...node, data: { ...node.data, ...change } } : node)))
      patchCard({ source, ...(words ? { prompt: words } : {}), running: true, error: '' })
      void putLibraryFrame(cardId, items, from)
        .then(() => patchCard({ running: false }))
        .catch((error: unknown) =>
          patchCard({ running: false, error: error instanceof Error ? error.message : 'The pictures could not be added' }),
        )
    },
    [browsingId, putLibraryFrame, setNodes],
  )

  /* ---------- scaling a selection together (groupScale.tsx) ---------- */

  /**
   * Two or more things selected: what they take up, with the pictures inside
   * any selected frame, which scale with it.
   */
  const groupMembers = useMemo(() => {
    const picked = onPage.filter((node) => node.selected)
    if (picked.length < 2) return [] as Node[]
    return withContents(picked)
  }, [onPage, withContents])
  const groupBounds = useMemo(
    () => (groupMembers.length >= 2 && pending.size === 0 ? bounds(groupMembers.map(boxOf)) : null),
    [groupMembers, pending],
  )

  /** Where everything started, taken when a corner is grabbed. */
  const scaling = useRef<{
    anchor: { x: number; y: number }
    /** The shorter side of the whole selection when the corner was taken. */
    span: number
    items: Map<string, { box: Rect; grows: boolean; sized: boolean; textSize?: number }>
    /** The whole selection when the corner was taken. */
    whole: Rect | null
    /** What is around it, for the moving sides to line up with. */
    others: Rect[]
  } | null>(null)

  const beginScale = useCallback(
    (anchor: { x: number; y: number }) => {
      remember(true)
      const whole = bounds(groupMembers.map(boxOf))
      const ids = new Set(groupMembers.map((node) => node.id))
      const zoom = Math.max(0.05, getViewport().zoom)
      scaling.current = {
        anchor,
        span: whole ? Math.max(1, Math.min(whole.w, whole.h)) : 1,
        whole,
        others: whole
          ? around(whole, onPage.filter((node) => !ids.has(node.id)).map(boxOf), 240 / zoom, 12)
          : [],
        items: new Map(
          groupMembers.map((node) => [
            node.id,
            {
              box: boxOf(node),
              // Pictures and frames grow; a skill or picture card is a form,
              // and only moves.
              // Text grows too, its type with it (2026-10-12: a mood board frame
              // scaled down left its words full size).
              grows: node.type === 'image' || isFrame(node) || node.type === 'label',
              // Whether the node's height is written down, or follows its picture.
              sized: node.height !== undefined && node.height !== null,
              textSize:
                node.type === 'label'
                  ? ((node.data as { textStyle?: { size?: number } }).textStyle?.size ?? DEFAULT_LABEL_STYLE.size)
                  : undefined,
            },
          ]),
        ),
      }
    },
    [getViewport, groupMembers, onPage, remember],
  )

  const scaleGroup = useCallback(
    (factor: number) => {
      const held = scaling.current
      if (!held) return
      /*
       * How small it may go is a matter of the whole selection, not of its
       * smallest picture (2026-10-05). The floor used to be every picture's
       * shorter side at 40, so one slim picture -- a sachet seen edge on --
       * already at 40 stopped the whole wall from shrinking at all. Now the
       * selection as a whole keeps a shorter side of 24, and no picture goes
       * below 4, which only stops it vanishing.
       */
      let s = Math.max(factor, 24 / held.span)
      let least = 24 / held.span
      for (const item of held.items.values()) {
        if (item.grows) least = Math.max(least, 4 / Math.max(1, Math.min(item.box.w, item.box.h)))
      }
      s = Math.max(s, least)
      /*
       * Snapping (Huanrui, 2026-11-02): the two sides being pulled line up
       * with an edge or middle of something around them, within a few pixels
       * on the screen, as a picture's own resize does; the nearer of the two
       * wins, and the other follows, as the selection keeps its proportions.
       * Ctrl held turns it off.
       */
      const { whole } = held
      let line: Segment | null = null
      if (whole && !ctrlHeld.current) {
        const reach = 6 / Math.max(0.05, getViewport().zoom)
        const leftFixed = Math.abs(held.anchor.x - whole.x) < 0.5
        const topFixed = Math.abs(held.anchor.y - whole.y) < 0.5
        const w = whole.w * s
        const h = whole.h * s
        const box: Rect = {
          x: leftFixed ? held.anchor.x : held.anchor.x - w,
          y: topFixed ? held.anchor.y : held.anchor.y - h,
          w,
          h,
        }
        const sx = snapEdge('x', leftFixed ? box.x + w : box.x, box, held.others, reach)
        const sy = snapEdge('y', topFixed ? box.y + h : box.y, box, held.others, reach)
        const useX = sx && (!sy || Math.abs(sx.shift) <= Math.abs(sy.shift))
        const snapped = useX
          ? (w + (leftFixed ? sx!.shift : -sx!.shift)) / Math.max(1, whole.w)
          : sy
            ? (h + (topFixed ? sy.shift : -sy.shift)) / Math.max(1, whole.h)
            : s
        if ((sx || sy) && snapped >= least) {
          s = snapped
          line = useX ? sx!.line : sy!.line
        }
      }
      setGuides(line ? { lines: [line], gaps: [] } : null)
      const { anchor, items } = held
      setNodes((prev) =>
        prev.map((node) => {
          const item = items.get(node.id)
          if (!item) return node
          const { box } = item
          if (!item.grows) {
            // Its middle moves with the rest; its size stays.
            const cx = anchor.x + (box.x + box.w / 2 - anchor.x) * s
            const cy = anchor.y + (box.y + box.h / 2 - anchor.y) * s
            return { ...node, position: { x: cx - box.w / 2, y: cy - box.h / 2 } }
          }
          if (item.textSize !== undefined) {
            // Words: the box wider or narrower, the type in step; the height follows the words.
            const style = { ...DEFAULT_LABEL_STYLE, ...((node.data as { textStyle?: object }).textStyle ?? {}) }
            return {
              ...node,
              position: { x: anchor.x + (box.x - anchor.x) * s, y: anchor.y + (box.y - anchor.y) * s },
              width: Math.round(box.w * s),
              height: undefined,
              data: { ...node.data, textStyle: { ...style, size: Math.max(4, Math.round(item.textSize * s * 10) / 10) } },
            }
          }
          return {
            ...node,
            position: { x: anchor.x + (box.x - anchor.x) * s, y: anchor.y + (box.y - anchor.y) * s },
            width: Math.round(box.w * s),
            height: item.sized || isFrame(node) ? Math.round(box.h * s) : node.height,
          }
        }),
      )
    },
    [getViewport, setNodes],
  )

  const endScale = useCallback(() => {
    const held = scaling.current
    scaling.current = null
    setGuides(null)
    if (!held) return
    // Pictures now over another frame, or out of theirs, are settled as when
    // one is let go after a drag.
    const ids = new Set(
      [...held.items.keys()].filter((id) => byId.get(id)?.type === 'image'),
    )
    setNodes((prev) => settle(prev, ids))
  }, [byId, setNodes])

  /* ---------- spacing a selection out by its sides (groupScale.tsx, 2026-11-02) ---------- */

  /** The boxes of what is selected itself, whose own handles the sides leave alone. */
  const groupBoxes = useMemo(
    () => (groupBounds ? onPage.filter((node) => node.selected).map(boxOf) : []),
    [groupBounds, onPage],
  )

  /**
   * Taken when a side is grabbed: each thing that moves on its own, in rows
   * and in order along its row, and what goes with it.
   *
   * A selected frame moves with its pictures, and with anything selected
   * lying wholly on it (a mood board's title). Rows are things whose heights
   * overlap, which is exactly what a tidied wall is; a loose pile still works,
   * it simply makes fewer, longer rows.
   */
  const spacing = useRef<{
    side: Side
    start: Rect
    /** Where every member was, and which unit it moves with. */
    origin: Map<string, { x: number; y: number; unit: number }>
    units: { row: number; col: number; box: Rect }[]
    /** The units row by row, left to right: where the gaps are, for marking them. */
    rows: number[][]
    /** The most things in a row, and how many rows. */
    across: number
    down: number
    /** How far the gaps may close: the smallest gap now, across and down. */
    floorX: number
    floorY: number
    /** The smallest gap now, across and down; null where there is none. */
    gapX: number | null
    gapY: number | null
    /** What is around the selection, to line up with or space as. */
    others: Rect[]
  } | null>(null)

  const beginSpace = useCallback(
    (side: Side) => {
      const start = bounds(groupMembers.map(boxOf))
      if (!start) return
      remember(true)
      const frames = groupMembers.filter((node) => node.selected && isFrame(node))
      const frameIds = new Set(frames.map((frame) => frame.id))
      const leaders: Node[] = []
      const followerOf = new Map<string, string>()
      for (const node of groupMembers) {
        if (isFrame(node)) {
          leaders.push(node)
          continue
        }
        if (frameIds.has(frameOf(node))) {
          followerOf.set(node.id, frameOf(node))
          continue
        }
        const b = boxOf(node)
        const host = frames.find((frame) => {
          const f = boxOf(frame)
          return b.x >= f.x - 1 && b.y >= f.y - 1 && b.x + b.w <= f.x + f.w + 1 && b.y + b.h <= f.y + f.h + 1
        })
        if (host) followerOf.set(node.id, host.id)
        else leaders.push(node)
      }
      const boxes = leaders.map((node) => ({ node, box: boxOf(node) }))
      // Rows: down the page, a thing joins the row while it overlaps its height.
      const byTop = [...boxes].sort((a, b) => a.box.y - b.box.y)
      const rows: (typeof boxes)[] = []
      let bottom = -Infinity
      for (const one of byTop) {
        if (rows.length > 0 && one.box.y < bottom - 1) {
          rows[rows.length - 1]!.push(one)
          bottom = Math.max(bottom, one.box.y + one.box.h)
        } else {
          rows.push([one])
          bottom = one.box.y + one.box.h
        }
      }
      const unitOf = new Map<string, number>()
      const units: { row: number; col: number; box: Rect }[] = []
      const rowUnits: number[][] = []
      let floorX = Infinity
      let floorY = Infinity
      rows.forEach((row, r) => {
        row.sort((a, b) => a.box.x - b.box.x)
        rowUnits.push([])
        row.forEach((one, c) => {
          unitOf.set(one.node.id, units.length)
          rowUnits[r]!.push(units.length)
          units.push({ row: r, col: c, box: one.box })
          const next = row[c + 1]
          if (next) floorX = Math.min(floorX, next.box.x - (one.box.x + one.box.w))
        })
        const next = rows[r + 1]
        if (next) {
          const rowBottom = Math.max(...row.map((one) => one.box.y + one.box.h))
          floorY = Math.min(floorY, Math.min(...next.map((one) => one.box.y)) - rowBottom)
        }
      })
      const origin = new Map<string, { x: number; y: number; unit: number }>()
      for (const node of groupMembers) {
        const unit = unitOf.get(followerOf.get(node.id) ?? node.id)
        if (unit !== undefined) origin.set(node.id, { x: node.position.x, y: node.position.y, unit })
      }
      const zoom = Math.max(0.05, getViewport().zoom)
      spacing.current = {
        side,
        start,
        origin,
        units,
        rows: rowUnits,
        across: Math.max(...rows.map((row) => row.length)),
        down: rows.length,
        // A gap may close to nothing, never past it; things already touching
        // or overlapping can only be spread.
        floorX: Number.isFinite(floorX) ? -Math.max(0, floorX) : 0,
        floorY: Number.isFinite(floorY) ? -Math.max(0, floorY) : 0,
        gapX: Number.isFinite(floorX) ? floorX : null,
        gapY: Number.isFinite(floorY) ? floorY : null,
        // Taken once: what is not moving does not move while a side is dragged.
        others: around(
          start,
          onPage.filter((node) => !origin.has(node.id)).map(boxOf),
          240 / zoom,
          12,
        ),
      }
    },
    [getViewport, groupMembers, onPage, remember],
  )

  const spaceGroup = useCallback(
    (at: { x: number; y: number }) => {
      const held = spacing.current
      if (!held) return
      const { side, start, units, across, down } = held
      const sideways = side === 'l' || side === 'r'
      const count = sideways ? across : down
      if (count < 2) return
      const pulled =
        side === 'r'
          ? at.x - (start.x + start.w)
          : side === 'l'
            ? start.x - at.x
            : side === 'b'
              ? at.y - (start.y + start.h)
              : start.y - at.y
      const floor = sideways ? held.floorX : held.floorY
      // Every gap along the way changes by the same amount.
      let step = Math.max(floor, pulled / (count - 1))
      // From the left or the top, the far side stays put instead.
      const from = side === 'l' || side === 't'
      /** How far a unit moves for one step: its place along the way, less the far side's. */
      const rate = (unit: { row: number; col: number }) => (sideways ? unit.col : unit.row) - (from ? count - 1 : 0)
      /** Where the dragged side is, at a given step: the outermost thing's edge. */
      const sideAt = (by: number) => {
        const edges = units.map((unit) => {
          const b = unit.box
          const edge = side === 'r' ? b.x + b.w : side === 'l' ? b.x : side === 'b' ? b.y + b.h : b.y
          return edge + rate(unit) * by
        })
        const at = side === 'r' || side === 'b' ? Math.max(...edges) : Math.min(...edges)
        return { at, outer: units[edges.indexOf(at)]! }
      }
      /** The rows' boxes at a given step, for marking the gaps. */
      const rowsAt = (by: number) =>
        held.rows.map((row) =>
          row.map((index) => {
            const unit = units[index]!
            const move = rate(unit) * by
            return sideways ? { ...unit.box, x: unit.box.x + move } : { ...unit.box, y: unit.box.y + move }
          }),
        )

      /*
       * SNAPPING (Huanrui, 2026-11-02), as a drag snaps: within a few pixels
       * on the screen, the smaller correction winning and a tie going to the
       * line. Ctrl held turns it off.
       *  - The dragged side onto an edge or middle of something around it.
       *  - The gaps onto a gap already there: between things around it, or
       *    the selection's own the other way (across as wide as down).
       * Both measured as how far the side would move, which is what the
       * pointer feels.
       */
      const lines: Segment[] = []
      let marks: Segment[] = []
      if (!ctrlHeld.current) {
        const zoom = Math.max(0.05, getViewport().zoom)
        const reach = 6 / zoom
        // The side moves this far for one step.
        const perStep = count - 1
        const box = bounds(units.map((unit) => ({ ...unit.box })))!
        const { at: now, outer } = sideAt(step)
        const moving: Rect = sideways
          ? { ...box, x: side === 'l' ? now : box.x, w: Math.abs((side === 'l' ? box.x + box.w : now) - (side === 'l' ? now : box.x)) }
          : { ...box, y: side === 't' ? now : box.y, h: Math.abs((side === 't' ? box.y + box.h : now) - (side === 't' ? now : box.y)) }
        const edge = snapEdge(sideways ? 'x' : 'y', now, moving, held.others, reach)
        const own = sideways ? held.gapX : held.gapY
        let space: { step: number; moves: number; mark: Segment | null } | null = null
        if (own !== null) {
          const wanted = [
            ...neighbourGaps(held.others, sideways ? 'x' : 'y').map((one) => ({ gap: one.gap, mark: one.mark as Segment | null })),
            // The other way's gap in the selection itself, marked with the rest below.
            ...((sideways ? held.gapY : held.gapX) !== null && (sideways ? down : across) >= 2
              ? [{ gap: (sideways ? held.gapY : held.gapX)!, mark: null }]
              : []),
          ]
          for (const one of wanted) {
            const to = one.gap - own
            const moves = Math.abs(to - step) * perStep
            if (to >= floor && moves <= reach && (!space || moves < space.moves - 0.01)) {
              space = { step: to, moves, mark: one.mark }
            }
          }
        }
        const edgeMoves = edge ? Math.abs(edge.shift) : Infinity
        if (edge && (!space || edgeMoves <= space.moves + 0.01)) {
          // The outermost thing's rate turns the side's shift back into a step;
          // one that does not move with the steps cannot be snapped by them.
          const snapped = rate(outer) ? step + edge.shift / rate(outer) : step
          if (rate(outer) && snapped >= floor) {
            step = snapped
            lines.push(edge.line)
          }
        } else if (space) {
          step = space.step
          if (space.mark) marks.push(space.mark)
          // The selection's own gaps, now equal to it -- and, when it was
          // made equal to them, the selection's gaps the other way.
          const placed = rowsAt(step)
          marks = [...marks, ...ownGaps(placed, sideways)]
          if (!space.mark) marks = [...marks, ...ownGaps(placed, !sideways)]
        }
      }
      setGuides(lines.length === 0 && marks.length === 0 ? null : { lines, gaps: marks })

      const shifts = units.map((unit) => Math.round(rate(unit) * step))
      setNodes((prev) =>
        prev.map((node) => {
          const was = held.origin.get(node.id)
          if (!was) return node
          const shift = shifts[was.unit] ?? 0
          return {
            ...node,
            position: sideways ? { x: was.x + shift, y: was.y } : { x: was.x, y: was.y + shift },
          }
        }),
      )
    },
    [getViewport, setNodes],
  )

  const endSpace = useCallback(() => {
    const held = spacing.current
    spacing.current = null
    setGuides(null)
    if (!held) return
    // Pictures now over another frame, or out of theirs, are settled as after a drag.
    const ids = new Set([...held.origin.keys()].filter((id) => byId.get(id)?.type === 'image'))
    setNodes((prev) => settle(prev, ids))
  }, [byId, setNodes])

  const closeQuick = useCallback(() => setQuick(null), [])

  /* ---------- the Colour grade studio (grade/GradeStudio.tsx, 2026-10-15) ---------- */

  const gradingCard = gradingId ? nodes.find((node) => node.id === gradingId && node.type === 'grade') ?? null : null
  const gradeSources = useMemo(
    () =>
      gradingId
        ? edges
            .filter((edge) => edge.target === gradingId)
            .map((edge) => nodes.find((node) => node.id === edge.source))
            .filter((node): node is Node => Boolean(node) && refOf(node!) !== null)
        : [],
    [edges, gradingId, nodes],
  )
  // Closed if its card goes (undo, delete) or its last picture is unwired.
  useEffect(() => {
    if (gradingId && (!gradingCard || gradeSources.length === 0)) setGradingId(null)
  }, [gradeSources.length, gradingCard, gradingId])
  /** What the studio is shown, the same list for as long as the same pictures are wired in. */
  const gradeKey = gradeSources
    .map((node) => {
      const d = node.data as ImageNodeData
      return `${node.id}:${d.fileId ?? d.assetId ?? ''}:${Number(d.rotate) || 0}`
    })
    .join('|')
  const gradePictures = useMemo<StudioPicture[]>(
    () =>
      gradeSources.map((node) => {
        const d = node.data as ImageNodeData
        const file = d.fileId ? files.get(d.fileId) : undefined
        const asset = d.assetId ? urls.get(d.assetId) : undefined
        const ref = refOf(node)!
        return {
          key: node.id,
          title: d.title || 'Picture',
          thumb: file?.thumb || file?.url || asset?.thumbnailPath || asset?.originalPath || '',
          load: () => sourceOf(ref, Number(d.rotate) || 0).then((one) => one.canvas),
        }
      }),
    // Only when the wired pictures change, so the studio does not reload them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gradeKey],
  )
  const closeGrade = useCallback(() => setGradingId(null), [])
  const keepGrade = useCallback(
    (settings: GradeSettings) => {
      const id = gradingId
      setGradingId(null)
      if (!id) return
      remember(true)
      setNodes((prev) =>
        prev.map((node) => (node.id === id ? { ...node, data: { ...node.data, grade: settings, error: '' } } : node)),
      )
    },
    [gradingId, remember, setNodes],
  )
  const applyGrade = useCallback(
    (settings: GradeSettings) => {
      const id = gradingId
      keepGrade(settings)
      if (id) void latest.current.runGrade(id, settings)
    },
    [gradingId, keepGrade],
  )
  /* ---------- the Mood board studio (moodboard/MoodboardStudio.tsx, 2026-10-16) ---------- */

  const boardingCard = boardingId ? nodes.find((node) => node.id === boardingId && node.type === 'moodboard') ?? null : null
  /** Read once, when the studio opens: what it was given stays the same while it is open. */
  const boarding = useMemo(
    () => (boardingId ? moodboardSources(boardingId) : { frames: 0, sources: [] as SourcePicture[], name: '' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boardingId],
  )
  // Closed if its card goes (undo, delete), or there is nothing to show.
  useEffect(() => {
    if (boardingId && (!boardingCard || boarding.sources.length === 0)) setBoardingId(null)
  }, [boarding.sources.length, boardingCard, boardingId])
  const closeBoard = useCallback(() => setBoardingId(null), [])
  const keepBoard = useCallback(
    (board: SavedBoard) => {
      const id = boardingId
      setBoardingId(null)
      if (!id) return
      remember(true)
      setNodes((prev) => prev.map((node) => (node.id === id ? { ...node, data: { ...node.data, board, error: '' } } : node)))
    },
    [boardingId, remember, setNodes],
  )
  const applyBoard = useCallback(
    (board: SavedBoard) => {
      const id = boardingId
      keepBoard(board)
      if (id) void latest.current.runMoodboard(id, board)
    },
    [boardingId, keepBoard],
  )

  /** The rail's cards and the quick tools: the skills, and the sticky note last. */
  // Edit text in the place of the platform's planned "Edit Text" (TEXT_SKILL).
  const railSkills = useMemo(() => {
    const listed = skills.some((skill) => skill.id === TEXT_SKILL.id)
    // The Mood board page's own skill (moodboard-builder) is a page, not a
    // card: on the board its place is taken by the Mood board card
    // (MOODBOARD_SKILL), so it is not offered twice (Huanrui, 2026-10-16).
    const own = skills
      .filter((skill) => skill.slug !== 'moodboard-builder')
      .map((skill) => (skill.id === TEXT_SKILL.id ? TEXT_SKILL : skill))
    // The person's own workflows (canvas/workflow, 2026-10-16), after the cards.
    const mine = wf.toolbar.map(
      (one) =>
        ({
          id: `${wf.toolbarPrefix}${one.id}`,
          slug: `workflow-${one.id}`,
          name: one.name,
          summary: 'Your workflow: drag it onto the board',
          category: 'compose',
          status: 'available',
          glyph: one.glyph || '⧉',
          inputs: [],
          usesPaidApi: false,
          area: 'design',
        }) as SkillDefinition,
    )
    return [...own, EDIT_SKILL, ...(listed ? [] : [TEXT_SKILL]), BACKGROUND_SKILL, ERASER_SKILL, ANGLE_SKILL, VECTOR_SKILL, GRADE_SKILL, MOODBOARD_SKILL, LIBRARY_SKILL, EXTERNAL_LIBRARY_SKILL, LABEL_SKILL, NOTE_SKILL, ...mine]
  }, [skills, wf.toolbar])

  /*
   * The picture that shows its quick tools: one picture chosen, not being
   * dragged or cropped. Frames had them too for an afternoon, all greyed out,
   * and were taken off again (Huanrui, 2026-10-07).
   */
  const quickId = useMemo(() => {
    if (selectedCount !== 1) return ''
    const one = onPage.find((node) => node.selected)
    if (!one || one.dragging || one.id === croppingId || one.type !== 'image') return ''
    return one.id
  }, [onPage, selectedCount, croppingId])
  const refuseQuick = useCallback(
    (skill: SkillDefinition) =>
      skill.id === NOTE_SKILL.id
        ? 'A sticky note does not take a picture'
        : isToolbarWorkflow(skill.id)
          ? 'Drag a workflow onto the board, then wire the pictures into its inputs'
        : skill.id === MOODBOARD_SKILL.id
          ? 'A mood board takes a frame: wire a frame into it'
        : skill.id === LABEL_SKILL.id
          ? 'A text does not take a picture'
          : null,
    [],
  )
  const pickQuick = useCallback(
    (skill: SkillDefinition, glow: string) => {
      if (quickId) attachSkill(quickId, skill, glow)
    },
    [attachSkill, quickId],
  )

  /* ---------- the right-click menu ---------- */

  useEffect(() => {
    if (!menu) return
    const away = (event: Event) => {
      if (!(event.target as HTMLElement | null)?.closest('.ccanvas__menu')) setMenu(null)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenu(null)
    }
    window.addEventListener('pointerdown', away)
    window.addEventListener('keydown', escape)
    window.addEventListener('wheel', away, { passive: true })
    return () => {
      window.removeEventListener('pointerdown', away)
      window.removeEventListener('keydown', escape)
      window.removeEventListener('wheel', away)
    }
  }, [menu])

  const connect = useCallback(
    (connection: Connection) => {
      remember(true)
      setEdges((prev) => {
        // One input, so a second connection replaces the first rather than
        // piling up. Refusing it silently would look like a broken drag.
        const target = nodes.find((node) => node.id === connection.target)
        // A skill has one input, so a second connection replaces the first. A
        // picture card has a numbered port per reference, so what is replaced
        // is whatever was on THAT port — replacing the whole card's wiring
        // would mean every reference after the first unplugged the one before.
        // An Upscale card takes one picture too (Huanrui, 2026-10-05): a new
        // wire replaces the one already there.
        // An Edit area card: one wire per port, the picture being edited on
        // `in` and a reference on each numbered port.
        const cleaned =
          target?.type === 'skill' ||
          target?.type === 'upscale' ||
          target?.type === 'angle' ||
          target?.type === 'vector' ||
          target?.type === 'text' ||
          target?.type === 'background' ||
          target?.type === 'eraser'
            ? prev.filter((edge) => edge.target !== connection.target)
            : target?.type === 'picture' || target?.type === 'edit' || target?.type === 'video'
              ? prev.filter(
                  (edge) =>
                    edge.target !== connection.target ||
                    edge.targetHandle !== connection.targetHandle,
                )
              : prev
        return addEdge({ ...connection, id: newId('edge') }, cleaned)
      })
    },
    [nodes, remember, setEdges],
  )

  return (
    <>
      <main
        ref={board}
        className={`ccanvas project__canvas${pale ? ' ccanvas--pale' : ''}${
          spaceHeld ? ' ccanvas--panning' : ''
        }${selectedCount > 1 ? ' ccanvas--several' : ''}${selection.mixed ? ' ccanvas--mixed' : ''}${pickFor ? ' ccanvas--picking' : ''}`}
        /*
          Written on the element rather than matched by a class. The colour is
          data — one of a list, chosen by the person — and an inline style
          cannot lose an argument with a stylesheet. The first attempt did
          exactly that: `.ccanvas--black` and `.project__canvas` are equally
          specific, so which one won came down to the order the two files
          happened to be bundled in, and the button did nothing at all.
        */
        style={
          {
            background: view.paper,
            // The paper itself, as a value anything can read. The ports and
            // the resize handles outline themselves in it, so a dot on top of
            // a white pack shot still has an edge to be seen against.
            '--board-paint': view.paper,
            // The page tabs: the paper, a shade off it.
            '--board-tab': shadeOf(view.paper, 0.06),
            '--board-tab-on': shadeOf(view.paper, 0.16),
            '--board-tab-line': shadeOf(view.paper, 0.12),
            '--board-tab-text': pale ? 'rgba(0, 0, 0, 0.55)' : 'rgba(255, 255, 255, 0.55)',
            '--board-grid': pale ? 'rgba(0, 0, 0, 0.16)' : 'rgba(255, 255, 255, 0.07)',
            // Text in the board's own ink (labelNode.tsx), so it turns with the paper.
            '--board-label': pale ? '#111111' : '#f2f2f2',
            // Everything the board draws for itself, in one colour that the
            // paper decides. Written here with the paper rather than matched
            // by a class further down: the two rules that would have decided
            // it are equally specific, so which one won came down to the order
            // the stylesheets happened to be bundled in — which is exactly how
            // these three went invisible the moment the board went white.
            '--board-ink': pale ? 'rgba(0, 0, 0, 0.45)' : 'rgba(255, 255, 255, 0.34)',
            '--board-ink-strong': pale ? '#000000' : '#ffffff',
            // The ruled grid. Far fainter than the ink — it is a surface to
            // line things up against, and a grid you notice is a grid that is
            // competing with the work on it.
            '--board-line': pale ? 'rgba(0, 0, 0, 0.07)' : 'rgba(255, 255, 255, 0.055)',
            '--board-line-strong': pale ? 'rgba(0, 0, 0, 0.13)' : 'rgba(255, 255, 255, 0.1)',
          } as React.CSSProperties
        }
        // Capture, so the press is seen before a text box or a button inside
        // a card can keep it. Left button is not touched.
        // The wheel over a text box (Huanrui, 2026-10-27): it scrolls the
        // words while there are more to scroll that way, and otherwise moves
        // the board as it does anywhere else. React Flow leaves alone anything
        // marked `nowheel`, so for this one turn of the wheel the text box is
        // not; it is marked again straight after.
        onWheelCapture={(event) => {
          const box = (event.target as Element).closest<HTMLElement>('textarea, [contenteditable=""], [contenteditable="true"]')
          if (!box) return
          const dy = event.deltaY
          const more = (el: Element) =>
            dy < 0 ? el.scrollTop > 0 : dy > 0 ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : false
          // The words, or a list the box is in, still scrolling that way: let them.
          const marked: Element[] = []
          for (let el: Element | null = box; el && el !== event.currentTarget; el = el.parentElement) {
            if (!el.classList.contains('nowheel')) continue
            if (more(el)) return
            marked.push(el)
          }
          const unmarked = marked
          unmarked.forEach((el) => el.classList.remove('nowheel'))
          if (unmarked.length > 0) window.setTimeout(() => unmarked.forEach((el) => el.classList.add('nowheel')), 0)
        }}
        onPointerDownCapture={startPan}
        onPointerMoveCapture={movePan}
        onPointerLeave={() => {
          pointerAway.current = true
        }}
        onPointerUpCapture={endPan}
        onPointerCancelCapture={endPan}
        // The right button is for moving the board here, so the menu that
        // usually comes with it would arrive on top of every pan -- but for a
        // right-click in a text box, which is the browser's spelling (startPan).
        onContextMenu={(event) => {
          const allowed = inTextBox(event.target) && (IS_MAC || nativeMenu.current)
          nativeMenu.current = false
          if (!allowed) event.preventDefault()
        }}
        // Without this the browser opens the dropped file in the tab, losing
        // whatever was on the board.
        onDragOver={(event) => {
          if (
            event.dataTransfer.types.includes('Files') ||
            event.dataTransfer.types.includes(REF_DRAG_TYPE)
          ) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'copy'
          }
        }}
        onDrop={(event) => {
          // A picture from the conversation: already stored, already named, so
          // nothing is uploaded — it only needs somewhere to stand.
          const carried = event.dataTransfer.getData(REF_DRAG_TYPE)
          if (carried) {
            event.preventDefault()
            let refs: ImageRef[] = []
            try {
              refs = decodeRefs(JSON.parse(carried))
            } catch {
              refs = []
            }
            if (refs.length === 0) return
            const at = screenToFlowPosition({ x: event.clientX, y: event.clientY })
            remember(true)
            const arriving = refs.map((ref, index) => ({
              id: newId('image'),
              type: 'image',
              page: pageId,
              position: { x: at.x + index * 28, y: at.y + index * 28 },
              width: 220,
              data:
                ref.store === 'library' ? { assetId: ref.id } : ({ fileId: ref.id } as ImageNodeData),
            })) as Node[]
            setNodes((prev) =>
              settle([...prev, ...arriving], new Set(arriving.map((node) => node.id))),
            )
            return
          }

          const dropped = [...event.dataTransfer.files]
          if (dropped.length === 0) return
          event.preventDefault()
          void placeFiles(dropped, screenToFlowPosition({ x: event.clientX, y: event.clientY }))
        }}
      >
        {knifeLine ? (
          // The knife's line, in the window's own coordinates (startPan).
          <svg className="ccanvas__knife" aria-hidden="true">
            <polyline points={knifeLine.map((one) => `${one.x},${one.y}`).join(' ')} />
          </svg>
        ) : null}
        <ReactFlow
          // Only what is on screen is drawn: a board of many pictures stays
          // smooth to pan, and a picture coming back into view is in the
          // browser's cache (2026-10-01).
          onlyRenderVisibleElements
          nodes={decorated}
          edges={pageEdges}
          nodeTypes={nodeTypes}
          onNodesChange={steady('nodesChange', (changes: NodeChange[]) => {
            const discrete = changes.some((c) => c.type === 'remove')
            if (discrete) remember(true)
            else if (changes.some((c) => c.type === 'dimensions')) remember()
            // Applied NOW, not when React gets round to it (2026-10-02). Left to
            // React, a move made during a drag was drawn one frame late --
            // measured, on every move -- which is the "dragged behind the
            // pointer" feel. Forced through here it lands in the very next
            // frame. Affordable because a frame's work is a few milliseconds
            // (the steady handlers above).
            // Only for a drag in progress: React Flow also reports sizes from
            // inside React's own work, where forcing it is not allowed.
            const apply = () =>
              onNodesChange(
                carryMembers(snapDrag(snapResize(lockFrameRatio(holdBoxSelection(changes))))) as never,
              )
            if (changes.some((c) => c.type === 'position' && c.dragging)) flushSync(apply)
            else apply()
            // A frame let go after resizing: what is inside it now is decided
            // again, by where each picture's centre is.
            const resized = changes.filter(
              (c) => c.type === 'dimensions' && c.resizing === false && isFrame(byId.get(c.id) ?? ({} as Node)),
            )
            if (resized.length > 0) {
              const ids = new Set(resized.map((c) => (c as { id: string }).id))
              setNodes((prev) =>
                settle(
                  prev,
                  new Set(
                    prev
                      .filter((node) => !frameOf(node) || ids.has(frameOf(node)))
                      .map((node) => node.id),
                  ),
                ),
              )
            }
          })}
          onEdgesChange={onEdgesChange}
          onConnect={steady('connect', connect)}
          isValidConnection={steady('canConnect', canConnect)}
          onNodeDoubleClick={steady('nodeDoubleClick', (_event: React.MouseEvent, node: Node) => {
            // Double-click a picture: crop mode (CropBox.tsx). A moment's
            // wait first, because a triple-click passes through a double-click
            // on its way, and that one means "gather what this is wired to".
            const data = node.data as ImageNodeData
            if (node.type !== 'image' || !refOf(node) || data.making) return
            window.clearTimeout(cropSoon.current)
            cropSoon.current = window.setTimeout(() => {
              setNodes((prev) => prev.map((one) => ({ ...one, selected: one.id === node.id })))
              setCroppingId(node.id)
            }, 260)
          })}
          onNodeClick={steady('nodeClick', (event: React.MouseEvent, node: Node) => {
            // Replace, from the canvas: this click names the picture to use.
            if (pickFor) {
              const data = node.data as ImageNodeData
              if (node.type === 'image' && node.id !== pickFor && (data.fileId || data.assetId)) {
                replaceWith(pickFor, { fileId: data.fileId, assetId: data.fileId ? undefined : data.assetId, title: data.title })
                setNodes((prev) => prev.map((one) => ({ ...one, selected: one.id === pickFor })))
                setPickFor('')
              } else {
                setNote('Click another picture on the board to use it, or press Esc.')
              }
              return
            }
            if (event.detail >= 3) window.clearTimeout(cropSoon.current)
            // `detail` is the browser's own count of clicks in quick
            // succession, so nothing here has to time anything.
            // Shift widens it from "this skill" to "the whole chain this
            // skill is part of".
            // Not from inside a card's form. Triple-click is how anybody
            // selects a line of text, so a prompt box that gathered up the
            // whole chain every time somebody reached for a sentence would be
            // a gesture fighting a much older one.
            const inForm = (event.target as HTMLElement | null)?.closest?.('.pnode__body')
            // The Library cards (2026-10-13/14) gather the same way: the card
            // and the frames its searches put down.
            if (!inForm && (SKILL_NODES.has(node.type ?? '') || node.type === 'library') && event.detail >= 3) {
              gatherFamily(node, event.shiftKey)
            }
            // A frame wired to anything (a Library card's frame, 2026-10-14):
            // triple-click gathers it with what it is wired to, its pictures
            // going with it.
            if (node.type === 'frame' && event.detail >= 3 && edges.some((e) => e.source === node.id || e.target === node.id)) {
              gatherFamily(node, event.shiftKey)
            }
            // A picture wired to anything -- a skill, or another picture by a
            // dashed line: triple-click gathers it with what it is wired to
            // directly, on either side; with Shift, the whole chain it is part
            // of, which is how a workflow is pulled out of a busy board.
            if (node.type === 'image' && event.detail >= 3 && edges.some((e) => e.source === node.id || e.target === node.id)) {
              gatherFamily(node, event.shiftKey)
            }
          })}
          onNodeDragStart={steady<OnNodeDrag<Node>>('nodeDragStart', (event, node) => {
            remember(true)
            // Where everything started. Cheap to take, and the only way to put
            // things back if this drag turns out to be a drop on the chat.
            dragFrom.current = new Map(nodes.map((other) => [other.id, { ...other.position }]))
            overChat.current = false
            // Alt held: leave a copy behind and carry on dragging the original.
            // The result is what the person means — one stays, one moves — and
            // it needs no special case in the drag itself.
            //
            // Only Alt held at the START. Figma also lets you press it
            // mid-drag, which means inserting a node in the middle of a gesture
            // without interrupting it; much harder, and rarely how anyone
            // actually reaches for it.
            if (!(event as unknown as MouseEvent).altKey) return

            // Everything being dragged, not only the one under the cursor:
            // React Flow moves the whole selection together, so Alt has to
            // copy the whole selection or half of it would be left behind.
            // Grabbing something outside the selection drags that alone.
            const picked = nodes.filter((other) => other.selected)
            const grabbed = picked.some((other) => other.id === node.id) ? picked : [node]
            leaveCopyBehind(grabbed)
          })}
          onSelectionDragStart={steady<SelectionDragHandler<Node>>('selectionDragStart', (event, dragged) => {
            // A selection of several dragged by the box round it (Huanrui,
            // 2026-10-16): Alt held leaves a copy of all of it behind, as for
            // one thing -- one step on the undo stack.
            if (!event.altKey || dragged.length === 0) return
            remember(true)
            const ids = new Set(dragged.map((one) => one.id))
            leaveCopyBehind(nodes.filter((other) => ids.has(other.id)))
          })}
          onNodeDrag={steady<OnNodeDrag<Node>>('nodeDrag', (event, node) => {
            boardMoved()
            if (!chatArea || !onOverChat) return
            // A touch drag reports its position on the touch, not the event.
            const at = pointerOf(event)
            if (!at) return
            const over = inChat(at.x, at.y)

            if (over !== overChat.current) {
              overChat.current = over
              onOverChat(over)
            }

            if (!over) {
              setCarry((prev) => (prev ? null : prev))
              return
            }

            const picked = nodes.filter((other) => other.selected)
            const moving = withContents(picked.some((other) => other.id === node.id) ? picked : [node])
            const pictures = moving.filter((other) => refOf(other))
            if (pictures.length === 0) return
            setCarry({
              url: linkOf(pictures[0]),
              x: at.x,
              y: at.y,
              more: pictures.length - 1,
            })
          })}
          onNodeDragStop={steady<OnNodeDrag<Node>>('nodeDragStop', (event, node) => {
            setCarry(null)
            setGuides(null)
            if (!overChat.current) {
              // Let go on the board: each picture is in whichever frame its
              // centre is in now, or in none. A frame dragged on its own
              // leaves membership alone -- moving a frame over pictures does
              // not swallow them, as in Miro.
              const held = nodes.filter((other) => other.selected)
              const moved = held.some((other) => other.id === node.id) ? held : [node]
              const ids = new Set(moved.filter((other) => other.type === 'image').map((other) => other.id))
              if (ids.size > 0) setNodes((prev) => settle(prev, ids))
              return
            }
            overChat.current = false
            onOverChat?.(false)

            // Whatever was being dragged, which may be a whole selection.
            const picked = nodes.filter((other) => other.selected)
            const moving = withContents(picked.some((other) => other.id === node.id) ? picked : [node])
            const refs = moving.map(refOf).filter((ref): ref is ImageRef => Boolean(ref))

            // Back where they started, whether or not any of them was a
            // picture: the gesture was aimed at the conversation, so the board
            // should be untouched either way.
            setNodes((prev) =>
              prev.map((other) => {
                const home = dragFrom.current.get(other.id)
                return home ? { ...other, position: home } : other
              }),
            )

            if (refs.length === 0) {
              setNote('Only pictures can be dropped on the conversation')
              return
            }
            onPicturesToChat?.(refs)
            setNote(refs.length === 1 ? 'Added to the message' : `${refs.length} added to the message`)
          })}
          /*
            The board stays where it is while something is being dragged.
            
            React Flow scrolls it when the cursor nears an edge, which is
            helpful on a board bigger than the window and actively in the way
            here: carrying a picture out to the conversation means crossing the
            left edge, and the board would set off in the opposite direction
            just as you were letting go. Panning is the middle and right
            buttons, and space — three ways, all deliberate.
          */
          autoPanOnNodeDrag={false}
          // Zoomed or panned: full-size files wait until it is still (fullSize.ts).
          onMove={steady('move', boardMoved)}
          panOnDrag={spaceHeld ? PAN_WITH_ANY : PAN_WITH_MIDDLE}
          selectionOnDrag={!spaceHeld}
          selectionMode={SelectionMode.Partial}
          onSelectionStart={steady('selectionStart', (event: React.MouseEvent) => {
            // Where the button went down, not where React Flow decided a box
            // had begun -- that is a few pixels of movement later, and a box
            // measured from there came up short of frames it had gone round.
            boxFrom.current = pressedAt.current ?? { x: event.clientX, y: event.clientY }
            handOutAfresh.current = true
          })}
          onSelectionEnd={steady('selectionEnd', finishBox)}
          selectionKeyCode={null}
          // Shift adds to the selection as Ctrl (Cmd) does (Huanrui, 2026-10-23).
          multiSelectionKeyCode={MULTI_SELECT_KEYS}
          deleteKeyCode={null}
          /* Let go anywhere near a port and it connects. Requiring a direct
             hit on a 10px dot is the difference between a canvas that feels
             precise and one that feels unresponsive. */
          /* How far from a port a wire will still find it. Raised because a
             port is a ten-pixel dot on a board that is usually zoomed out, and
             a drag that misses does not fail visibly — it becomes a selection
             box, which reads as the canvas ignoring you. */
          connectionRadius={60}
          minZoom={0.1}
          // 2.5 times what it was (Huanrui, 2026-09-30), for looking closely at
          // a pack shot; pictures switch to the full-size file when drawn this
          // large (nodes.tsx).
          maxZoom={10}
          /* React Flow is MIT, so removing its mark is permitted; the
             maintainers ask for a Pro subscription in return, which is a
             request rather than a licence condition. Noted here so whoever
             reads this next knows the choice was made deliberately. */
          proOptions={PRO_OPTIONS}
        >
          {/*
            Two pictures or more, and a pair of things worth doing to them.
            
            Anchored to the selection rather than parked in a corner, because
            both of these act on *those* pictures and a button in a corner is a
            claim about the whole board. React Flow works out where the
            selection is on screen, which is the part that would otherwise have
            to be recomputed on every pan and every zoom.
          */}
          {/*
            Sheets of the same board, top right.
            
            A page divides nothing: the project's files, its runs and its
            library are untouched, and a picture copied onto another page is
            the same picture. It is a view, which is why adding one is a chip
            and not a dialogue asking what it should contain.
          */}
          {guides ? <SnapGuides lines={guides.lines} gaps={guides.gaps} /> : null}

          {groupBounds ? (
            <GroupScale
              bounds={groupBounds}
              onBegin={beginScale}
              onScale={scaleGroup}
              onEnd={endScale}
              boxes={groupBoxes}
              onSpaceBegin={beginSpace}
              onSpace={spaceGroup}
              onSpaceEnd={endSpace}
            />
          ) : null}

          <Panel position="top-right" className="ccanvas__pages">
            {pages.filter((page) => !page.workflow).map((page) => (
              <span
                key={page.id}
                className={`ccanvas__page${page.id === pageId ? ' ccanvas__page--on' : ''}`}
              >
                {renamingPage === page.id ? (
                  <input
                    className="ccanvas__page-name nodrag nopan"
                    defaultValue={page.name}
                    autoFocus
                    onFocus={(event) => event.currentTarget.select()}
                    size={Math.max(4, page.name.length)}
                    onBlur={(event) => renamePage(page.id, event.currentTarget.value)}
                    onKeyDown={(event) => {
                      // The board's own keys stay out of it.
                      event.stopPropagation()
                      if (event.key === 'Enter') event.currentTarget.blur()
                      if (event.key === 'Escape') setRenamingPage('')
                    }}
                    aria-label="Page name"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setPageId(page.id)}
                    onDoubleClick={() => {
                      setPageId(page.id)
                      setRenamingPage(page.id)
                    }}
                    title="Double-click to rename"
                  >
                    {page.name}
                  </button>
                )}
                {/* Only on the one you are looking at, and only when there is
                    more than one. A cross on every tab is five ways to lose a
                    page by accident; on the current tab it is one, and that one
                    is the page you can see the contents of. */}
                {page.id === pageId && pages.filter((one) => !one.workflow).length > 1 ? (
                  <button
                    type="button"
                    className="ccanvas__shut"
                    onClick={() => removePage(page.id)}
                    title={`Delete ${page.name}`}
                    aria-label={`Delete ${page.name}`}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            ))}
            <button
              type="button"
              className="ccanvas__page ccanvas__page--add"
              onClick={addPage}
              title="Add a page"
              aria-label="Add a page"
            >
              +
            </button>
          </Panel>

          <NodeToolbar
            nodeId={picked.map((node) => node.id)}
            // Frames chosen: the frames' own tidy instead (below).
            isVisible={picked.length >= 2 && selectedFrames.length < 2 && !selection.mixed}
            position={Position.Top}
            align="end"
            // Clear of the corner handle of the selection's scaling outline,
            // which sits on the same corner (groupScale.tsx).
            offset={24}
          >
            <button
              type="button"
              className="ccanvas__tidy"
              onClick={tidy}
              title="Same height, laid out in rows"
              aria-label="Same height, laid out in rows"
            >
              {/* Six squares in two columns — a pile becoming a grid, which is
                  the whole of what the button does and needs no caption. */}
              <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
                {[2, 6.5, 11].map((y) =>
                  [3.5, 9].map((x) => (
                    <rect key={`${x}-${y}`} x={x} y={y} width="3.5" height="3" rx="0.8" />
                  )),
                )}
              </svg>
            </button>
          </NodeToolbar>

          {/* Frames chosen (Huanrui, 2026-10-23): two or more are tidied at the
              top right corner -- the same height, in reading order -- and one
              or more are sent to Miro from the right side, low down, in that
              order, as from the right-click menu. Clear of the corner handles
              (groupScale.tsx). Over everything: React Flow stacks a toolbar
              just over its nodes, and a frame lies under the whole board
              (FRAME_Z), so these were drawn under the board -- seen, but not
              to be pressed. */}
          <NodeToolbar
            nodeId={selectedFrames.map((node) => node.id)}
            isVisible={selectedFrames.length >= 2 && !selection.mixed}
            position={Position.Top}
            align="end"
            offset={24}
            style={{ zIndex: 1000 }}
          >
            <button
              type="button"
              className="ccanvas__tidy"
              onClick={tidyFrames}
              title="Same height, laid out in rows, in reading order"
              aria-label="Tidy the frames: same height, laid out in rows"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
                {[2, 6.5, 11].map((y) =>
                  [3.5, 9].map((x) => (
                    <rect key={`${x}-${y}`} x={x} y={y} width="3.5" height="3" rx="0.8" />
                  )),
                )}
              </svg>
            </button>
          </NodeToolbar>
          <NodeToolbar
            nodeId={selectedFrames.map((node) => node.id)}
            isVisible={selectedFrames.length >= 1 && !selection.mixed}
            position={Position.Right}
            align="end"
            offset={24}
            style={{ zIndex: 1000 }}
          >
            <button
              type="button"
              className="ccanvas__tidy ccanvas__tomiro"
              onClick={sendChosenToMiro}
              title={sendCount > 1 ? `Send ${sendCount} to Miro, in reading order` : 'Send it to Miro'}
              aria-label={`Send ${sendCount} to Miro`}
            >
              <img src="/brand/miro-mark.svg" alt="" />
            </button>
          </NodeToolbar>

          {/* Several kinds selected: one bar over them, the filter, and
              nothing else (selectionFilter.tsx, 2026-10-23). Over everything,
              as the frames' buttons above. */}
          <NodeToolbar
            nodeId={selection.ids}
            isVisible={selection.mixed}
            position={Position.Top}
            align="center"
            offset={24}
            style={{ zIndex: 1000 }}
          >
            <SelectionFilter
              counts={{
                frame: selection.members.frame.length,
                picture: selection.members.picture.length,
                text: selection.members.text.length,
                card: selection.members.card.length,
              }}
              onPick={keepKind}
            />
          </NodeToolbar>

          {/* A chosen picture's quick tools, over its top-left corner
              (QuickSkills.tsx). Keyed by the node, so the + list closes when
              another one is chosen. */}
          {quickId ? (
            <NodeToolbar nodeId={quickId} isVisible position={Position.Top} align="start" offset={10}>
              <QuickBar key={quickId} skills={railSkills} refuse={refuseQuick} onPick={pickQuick} />
            </NodeToolbar>
          ) : null}

          {/* The grid, its fine lines coming in with the zoom (boardGrid.tsx). */}
          {view.grid === 'none' ? null : <BoardGrid kind={view.grid} />}
          {/* "Fit to what is here", and the two that decide how the board
              looks. Zooming is the wheel, and two buttons for it were a column
              of chrome standing on the board to offer what the board already
              does. */}
          <Controls showZoom={false} showInteractive={false}>
            <ControlButton onClick={toggleGrid} title={GRID_NAME[view.grid]}>
              <GridMark />
            </ControlButton>
            <ControlButton
              className="ccanvas__paint-open"
              onClick={() => setPainting((on) => !on)}
              title="Board colour"
            >
              <PaperMark />
            </ControlButton>
          </Controls>
        </ReactFlow>

        {/*
          The corner. One fixed place for "do something with what I picked",
          rather than a bar that appears somewhere else the moment you select
          and is gone again the moment you do not.

          It sits on the canvas frame, not in the flow, so panning and zooming
          leave it exactly where it was — which is the whole reason it can be
          learned as a place at all.
        */}
        {painting ? (
          <div className="ccanvas__paints" role="group" aria-label="Board colour">
            <input
              type="range"
              className="ccanvas__slider"
              min={0}
              max={100}
              step={1}
              value={sliderFromPaper(view.paper)}
              onChange={(event) => paint(greyFromSlider(Number(event.target.value)))}
              aria-label="Board colour, from white to black"
              style={{ '--paper-now': view.paper } as React.CSSProperties}
              autoFocus
            />
          </div>
        ) : null}

        {/* Hidden for now (Huanrui, 2026-10-19): Download and Send to Miro are
            at the foot of the right-click menu instead. */}
        {SHOW_CORNER ? (
        <div className="ccanvas__corner">
          {chosen.length > 0 ? (
            <button
              type="button"
              className="ccanvas__orb ccanvas__orb--small"
              onClick={downloadChosen}
              aria-label="Download what is selected"
              title={chosen.length === 1 ? 'Download it' : `Download ${chosen.length}`}
            >
              <DownloadMark />
            </button>
          ) : null}

          <button
            type="button"
            className={`ccanvas__orb${sendCount > 0 ? ' ccanvas__orb--lit' : ''}`}
            onClick={sendChosenToMiro}
            title={
              sendCount === 0
                ? 'Send to Miro — pick something first'
                : sendCount === 1
                  ? 'Send it to Miro'
                  : `Send ${sendCount} to Miro`
            }
            aria-label="Send what is selected to Miro"
          >
            {/*
              Miro's own mark, monochrome. `public/brand/miro-mark.svg` is
              their official file with the yellow tile dropped and the mark
              turned white — not a drawing of it, which would be both theirs
              and wrong. Colour is left to CSS, so it dims and lifts with the
              rest of the button.
            */}
            <img className="ccanvas__orb-mark" src="/brand/miro-mark.svg" alt="" width={26} height={26} />
            {sendCount > 0 ? <span className="ccanvas__orb-count">{sendCount}</span> : null}
          </button>
        </div>
        ) : null}

        <input
          ref={replaceInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0]
            const target = replacing.current
            event.target.value = ''
            replacing.current = ''
            if (file && target) void replacePicture(target, file)
          }}
        />
        {/* Build workflow: what the board worked out, to change before merging (canvas/workflow). */}
        {wf.draft ? (
          <WorkflowBuilder draft={wf.draft} onChange={wf.setDraft} onCancel={wf.cancelBuild} onMerge={wf.merge} />
        ) : null}
        {/* A workflow's own page: which, and the way back. */}
        {(() => {
          const here = pages.find((page) => page.id === pageId)
          if (!here?.workflow) return null
          return (
            <div className="wfbar">
              <span>
                Workflow <strong>{here.name}</strong> · its steps and what they make
              </span>
              <button type="button" onClick={() => wf.closeWorkflow()}>
                Back to the board
              </button>
            </div>
          )
        })()}
        {menu
          ? (() => {
              const anySelected = onPage.some((node) => node.selected)
              const pickedHere = onPage.filter((node) => node.selected)
              const soloFrame = pickedHere.length === 1 && isFrame(pickedHere[0]) ? pickedHere[0] : null
              const imagesSelected = onPage.some((node) => node.selected && node.type === 'image')
              const item = (
                label: string,
                act: () => void,
                options: { keys?: string; off?: boolean; icon?: React.ReactNode } = {},
              ) => (
                <button
                  type="button"
                  role="menuitem"
                  disabled={options.off}
                  onClick={() => {
                    setMenu(null)
                    act()
                  }}
                >
                  <span className={options.icon ? 'ccanvas__menu-label' : undefined}>
                    {options.icon}
                    {label}
                  </span>
                  {options.keys ? <kbd>{options.keys}</kbd> : null}
                </button>
              )
              return (
                <div ref={menuBox} className="ccanvas__menu" role="menu" style={{ left: menu.x, top: menu.y }}>
                  {item(imagesSelected ? 'Create frame' : 'Create an empty frame', () => makeFrame(menu.flow))}
                  {/* A gold edge on the selected pictures, to find them again
                      (Huanrui, 2026-10-06). Unmark once every one has it. */}
                  {(() => {
                    const pics = pickedHere.filter((node) => node.type === 'image')
                    const allMarked = pics.length > 0 && pics.every((node) => (node.data as { mark?: unknown }).mark === true)
                    return item(
                      allMarked ? 'Unmark' : 'Mark',
                      () => {
                        const ids = new Set(pics.map((node) => node.id))
                        remember(true)
                        setNodes((prev) =>
                          prev.map((node) => {
                            if (!ids.has(node.id)) return node
                            const data = { ...(node.data as Record<string, unknown>) }
                            if (allMarked) delete data.mark
                            else data.mark = true
                            return { ...node, data }
                          }),
                        )
                      },
                      { off: pics.length === 0 },
                    )
                  })()}
                  {/* Several pictures into one, as laid out -- like merging layers (2026-10-06). */}
                  {item('Merge', () => void mergePictures(), {
                    off: pickedHere.filter((node) => node.type === 'image').length < 2,
                  })}
                  {/* The selected chain of cards as one reusable card (canvas/workflow, 2026-10-16). */}
                  {item('Build workflow', () => wf.startBuild(), {
                    off: !pickedHere.some((node) => SKILL_NODES.has(node.type ?? '') && node.type !== 'workflow'),
                  })}
                  <hr />
                  {item('Cut', () => boardActions.current?.cut(), { keys: 'Ctrl+X', off: !anySelected })}
                  {item('Copy', () => boardActions.current?.copy(), { keys: 'Ctrl+C', off: !anySelected })}
                  {item('Paste', () => boardActions.current?.paste(menu.flow), { keys: 'Ctrl+V' })}
                  {item('Duplicate', () => boardActions.current?.duplicate(), {
                    keys: 'Ctrl+D',
                    off: !anySelected,
                  })}
                  {item('Delete', () => boardActions.current?.remove(), {
                    keys: 'Del',
                    off: !anySelected,
                  })}
                  <hr />
                  {item(soloFrame ? 'Copy frame as picture' : 'Copy as picture', copyChosenImage, {
                    keys: 'Ctrl+Shift+C',
                    off: !(soloFrame || chosen.length === 1),
                  })}
                  {/* Another picture in its place, same box (2026-10-12): from the
                      board, the clipboard or the computer. */}
                  {/* Turn 90°, Open picture in a new tab, Download and Send to Miro
                      left this menu (Huanrui, 2026-10-15): the selection bar and
                      the picture's own handles do them. */}
                  {(() => {
                    const off = chosen.length !== 1 || !chosen[0]?.node.selected || Boolean(chosen[0]?.video)
                    const target = chosen[0]?.node.id ?? ''
                    // Out to the right, or to the left when the menu is near the right edge.
                    const room = menuBox.current?.parentElement?.clientWidth ?? window.innerWidth
                    const flip = menu.x + 230 + 220 > room
                    return (
                      // Opened by a click, not by passing over it (Huanrui, 2026-10-15).
                      <div className="ccanvas__menu-more">
                        <button
                          type="button"
                          role="menuitem"
                          aria-haspopup="menu"
                          aria-expanded={replaceOpen}
                          disabled={off}
                          onClick={() => setReplaceOpen((open) => !open)}
                        >
                          <span>Replace</span>
                          <kbd>▸</kbd>
                        </button>
                        {replaceOpen && !off ? (
                          <div className={`ccanvas__menu ccanvas__menu-sub${flip ? ' ccanvas__menu-sub--left' : ''}`} role="menu">
                            {item('From the canvas', () => {
                              setPickFor(target)
                              setNote('Click the picture on the board to put in its place. Esc to cancel.')
                            })}
                            {item('From the clipboard', () => void replaceFromClipboard(target))}
                            {item('From the computer…', () => {
                              replacing.current = target
                              replaceInput.current?.click()
                            })}
                          </div>
                        ) : null}
                      </div>
                    )
                  })()}
                  <hr />
                  {soloFrame
                    ? item('Select the pictures in this frame', () => {
                        setNodes((prev) =>
                          prev.map((node) => ({ ...node, selected: frameOf(node) === soloFrame.id })),
                        )
                      })
                    : null}
                  {/* Was "Select all", which Ctrl+A still does (Huanrui,
                      2026-10-07): the marked pictures on this page, and
                      nothing else. No key of its own. */}
                  {(() => {
                    const marked = new Set(
                      onPage
                        .filter((node) => node.type === 'image' && (node.data as { mark?: unknown }).mark === true)
                        .map((node) => node.id),
                    )
                    return item(
                      'Select all marked',
                      () => setNodes((prev) => prev.map((node) => ({ ...node, selected: marked.has(node.id) }))),
                      { off: marked.size === 0 },
                    )
                  })()}
                  {/* At the foot since the corner buttons were hidden (Huanrui, 2026-10-19). */}
                  <hr />
                  {item(chosen.length > 1 ? `Download ${chosen.length}` : 'Download', downloadChosen, {
                    off: chosen.length === 0,
                    icon: (
                      <span className="ccanvas__menu-icon" aria-hidden="true">
                        <DownloadMark />
                      </span>
                    ),
                  })}
                  {item(sendCount > 1 ? `Send ${sendCount} to Miro` : 'Send to Miro', sendChosenToMiro, {
                    off: sendCount === 0,
                    icon: <img className="ccanvas__menu-icon ccanvas__menu-miro" src="/brand/miro-mark.svg" alt="" />,
                  })}
                </div>
              )
            })()
          : null}

        <div className="ccanvas__bar">
          {note ? (
            <button type="button" className="ccanvas__note" onClick={() => setNote('')}>
              {note}
            </button>
          ) : null}
        </div>

        {alert ? (
          <div className="ccanvas__alert" role="alert">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
              <path d="M8 4.5v4.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <circle cx="8" cy="11.2" r="0.95" fill="currentColor" />
            </svg>
            <span>{alert}</span>
            <button type="button" aria-label="Dismiss" onClick={() => setAlert('')}>
              ×
            </button>
          </div>
        ) : null}
      </main>

      {/* On the body rather than inside the board: the board clips what leaves
          it, and leaving it is the entire point of this. */}
      {carry
        ? createPortal(
            <div className="ccanvas__carry" style={{ left: carry.x, top: carry.y }}>
              {carry.url ? <img src={carry.url} alt="" /> : null}
              {carry.more > 0 ? <span className="ccanvas__carry-more">+{carry.more}</span> : null}
            </div>,
            document.body,
          )
        : null}

      {/* Pictures waiting from a skill page. In the middle of the window,
          small, and on the body so the board's clipping cannot cut it. Not
          until the board has loaded: a frame put down before that would be
          wiped out by the board arriving. */}
      {offer && ready && !importing
        ? createPortal(
            <div className="clip-offer" role="dialog" aria-label="Pictures waiting to be added">
              <div className="clip-offer__thumbs">
                {offer.items.slice(0, 3).map((item, index) => (
                  <img
                    key={index}
                    src={item.thumb}
                    alt=""
                    onError={(event) => {
                      event.currentTarget.style.visibility = 'hidden'
                    }}
                  />
                ))}
                {offer.items.length > 3 ? (
                  <span className="clip-offer__more">+{offer.items.length - 3}</span>
                ) : null}
              </div>
              <p className="clip-offer__text">
                {offer.board ? (
                  <>
                    {offer.items.length} picture{offer.items.length === 1 ? '' : 's'} and the card that made{' '}
                    {offer.items.length === 1 ? 'it' : 'them'}, copied from {offer.from}. Add them to this project?
                  </>
                ) : (
                  <>
                    {offer.items.length} picture{offer.items.length === 1 ? '' : 's'} copied from {offer.from}. Add{' '}
                    {offer.items.length === 1 ? 'it' : 'them'} to this project?
                  </>
                )}
              </p>
              <div className="clip-offer__actions">
                <button type="button" className="ww-btn ww-btn--ghost" onClick={() => declineOffer(offer)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ww-btn ww-btn--primary"
                  onClick={() => void acceptOffer(offer)}
                >
                  Copy here
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}

      <SkillRail skills={railSkills} projectId={projectId} onDrop={dropSkill} />

      {pickingFor ? (
        <ColourPicker
          onPick={(hex) => {
            const id = pickingFor
            setPickingFor(null)
            ;(handlersFor(id).color as (color: string) => void)(hex)
          }}
          onCancel={() => setPickingFor(null)}
        />
      ) : null}

      {gradingCard && gradePictures.length > 0 ? (
        <Suspense fallback={null}>
          <GradeStudio
            name={(gradingCard.data as GradeNodeData).gradeName?.trim() || GRADE_DEFAULT_NAME}
            glyph={GRADE_SKILL.glyph}
            settings={normaliseGrade((gradingCard.data as GradeNodeData).grade)}
            pictures={gradePictures}
            onCancel={closeGrade}
            onDone={keepGrade}
            onApply={applyGrade}
          />
        </Suspense>
      ) : null}

      {browsingCard ? (
        <Suspense fallback={null}>
          <LibraryBrowser
            kind={kindOf(browsingCard.data as LibraryNodeData)}
            source={
              (browsingCard.data as LibraryNodeData).source ??
              (kindOf(browsingCard.data as LibraryNodeData) === 'external' ? 'google' : 'internal')
            }
            words={(browsingCard.data as LibraryNodeData).prompt ?? ''}
            onCancel={closeBrowse}
            onAdd={addFromBrowse}
          />
        </Suspense>
      ) : null}

      {boardingCard && boarding.sources.length > 0 ? (
        <Suspense fallback={null}>
          <MoodboardStudio
            name={boarding.name}
            glyph={(boardingCard.data as MoodboardNodeData).glyph}
            sources={boarding.sources}
            saved={(boardingCard.data as MoodboardNodeData).board}
            onCancel={closeBoard}
            onDone={keepBoard}
            onApply={applyBoard}
          />
        </Suspense>
      ) : null}


      {quick ? (
        <QuickSkills skills={railSkills} at={quick} onPick={dropSkill} onClose={closeQuick} />
      ) : null}

      <Shortcuts
        projectId={projectId ?? ''}
        currentPage={pageId}
        actions={boardActions}
        onNote={setNote}
        nodes={nodes}
        edges={edges}
        pages={pages}
        setNodes={setNodes}
        setEdges={setEdges}
        past={past}
        future={future}
        applying={applying}
        stamp={stamp}
        keepRuns={keepRuns}
        onSpace={setSpaceHeld}
        remember={remember}
        pointerOnBoard={pointerOnBoard}
        onCopyImage={copyChosenImage}
        onImages={(list) =>
          placeFiles(
            list,
            // Where the pointer is, and the middle of the window only when
            // nothing has moved over the board yet -- pasting with the
            // keyboard alone, straight after opening it.
            screenToFlowPosition(
              pointer.current ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 },
            ),
          )
        }
        fitView={() => void fitView({ padding: 0.2, duration: 200 })}
        zoomTo={() => void zoomTo(1, { duration: 200 })}
      />
    </>
  )
}

/* ---------------------------------------------------------------- */

/**
 * What a board copy leaves on the SYSTEM clipboard: a marker, not the nodes.
 *
 * The nodes themselves go in this browser's storage under the marker's token.
 * The marker is what decides whether a Ctrl+V means them: if anything else has
 * been copied since -- a picture on a website, text, a screenshot -- the
 * clipboard no longer holds the marker, and the newer thing is what gets
 * pasted. Before this, a board kept its own copy in memory and it won every
 * time, so copying elsewhere and coming back pasted the board's old copy.
 *
 * Storage rather than memory is what lets a copy on one board be pasted on
 * another: the other board reads the same key.
 */
const CLIP_MARK = 'wonderworks-canvas:'
const CLIP_KEY = 'ww-canvas-clipboard'

interface StoredClip {
  token: string
  /** The board it was copied on. Its pictures belong to that project. */
  projectId: string
  doc: CanvasDocument
}

/**
 * The gaps inside a selection being spaced out (spaceGroup), each marked:
 * across, between neighbours in a row; or down, between one row and the
 * next. Handed the rows' boxes where they now are.
 */
function ownGaps(rows: Rect[][], sideways: boolean): Segment[] {
  const out: Segment[] = []
  if (sideways) {
    for (const row of rows) {
      row.forEach((one, c) => {
        const next = row[c + 1]
        if (!next) return
        const on = (Math.max(one.y, next.y) + Math.min(one.y + one.h, next.y + next.h)) / 2
        if (next.x > one.x + one.w) out.push({ x1: one.x + one.w, y1: on, x2: next.x, y2: on })
      })
    }
    return out
  }
  rows.forEach((row, r) => {
    const next = rows[r + 1]
    if (!next || row.length === 0 || next.length === 0) return
    const y1 = Math.max(...row.map((b) => b.y + b.h))
    const y2 = Math.min(...next.map((b) => b.y))
    const first = row[0]!
    const below = next[0]!
    const on = (Math.max(first.x, below.x) + Math.min(first.x + first.w, below.x + below.w)) / 2
    if (y2 > y1) out.push({ x1: on, y1, x2: on, y2 })
  })
  return out
}

/** What the right-click menu can ask the keyboard's half of the board to do. */
interface BoardActions {
  copy: () => void
  cut: () => void
  paste: (at: { x: number; y: number }) => void
  duplicate: () => void
  remove: () => void
  selectAll: () => void
}

interface ShortcutProps {
  /** Which board this is, so a paste from another knows to bring its pictures over. */
  projectId: string
  /** The page being looked at, where a paste lands. */
  currentPage: string
  /** Filled in here, for the right-click menu to call. */
  actions: React.MutableRefObject<BoardActions | null>
  /** A sentence under the board, for a paste that could not be done. */
  onNote: (note: string) => void
  /** Ctrl+Shift+C: the selected picture onto the system clipboard. */
  onCopyImage: () => void
  nodes: Node[]
  edges: Edge[]
  /** Carried only so that a copy or an undo writes a whole document. */
  pages: CanvasPage[]
  setNodes: (updater: (prev: Node[]) => Node[]) => void
  setEdges: (updater: (prev: Edge[]) => Edge[]) => void
  past: React.MutableRefObject<CanvasDocument[]>
  future: React.MutableRefObject<CanvasDocument[]>
  applying: React.MutableRefObject<boolean>
  /** Marks a step with when it was taken. */
  stamp: (doc: CanvasDocument) => CanvasDocument
  /** A step to put back, with what runs made left as it is now. */
  keepRuns: (step: CanvasDocument) => CanvasDocument
  onSpace: (held: boolean) => void
  remember: (force?: boolean) => void
  /** Where the pointer is on the board, if it is over it: where Ctrl+V puts things. */
  pointerOnBoard: () => { x: number; y: number } | null
  /** Images arriving from the system clipboard. */
  onImages: (files: File[]) => Promise<void>
  fitView: () => void
  zoomTo: () => void
}

/**
 * The keyboard.
 *
 * Kept apart from the canvas because it is a different kind of thing: one
 * listener, a lot of small rules, and none of it renders. Every rule asks
 * `typing()` first.
 */
function Shortcuts({
  projectId,
  currentPage,
  actions,
  onNote,
  nodes,
  edges,
  pages,
  setNodes,
  setEdges,
  past,
  future,
  applying,
  stamp,
  keepRuns,
  onSpace,
  remember,
  pointerOnBoard,
  onImages,
  onCopyImage,
  fitView,
  zoomTo,
}: ShortcutProps) {
  const clipboard = useRef<CanvasDocument>(EMPTY_CANVAS)
  /** The token of the last copy made here, and one waiting for its copy event. */
  const clipToken = useRef('')
  const pendingCopy = useRef('')
  /**
   * Which of the two pastes was asked for, read off the keys and left here for
   * the paste event to pick up. Reset after every paste, so a Ctrl+Shift+V
   * never changes what the next plain Ctrl+V does.
   */
  const pasteWanted = useRef<'nodes' | 'picture'>('nodes')

  useEffect(() => {
    const restore = (doc: CanvasDocument) => {
      applying.current = true
      setNodes(() => doc.nodes.map((n) => ({ ...n, data: { ...n.data } }) as Node))
      setEdges(() => doc.edges.map(wire))
      // Released after the state has settled, so the change this caused is not
      // itself recorded as a step.
      setTimeout(() => {
        applying.current = false
      }, 0)
    }

    /**
     * Put a copied set of nodes down, with new ids and the wires remapped.
     *
     * Getting the remapping wrong does not throw — it silently wires the
     * copies back to the originals.
     */
    const paste = (source: CanvasDocument, at?: { x: number; y: number }, centre = false) => {
      remember(true)
      // From the menu, its corner where the menu was opened; from the
      // keyboard, its middle on the pointer when that is over the board;
      // otherwise (and for a duplicate) just beside the original, as before.
      const left = Math.min(...source.nodes.map((node) => node.position.x))
      const top = Math.min(...source.nodes.map((node) => node.position.y))
      let ox = 0
      let oy = 0
      if (at && centre) {
        // Sized as the originals are showing, if they still are; a copy
        // from another board has only what was saved.
        const shown = new Map(nodes.map((node) => [node.id, node]))
        const sizes = source.nodes.map((node) => ({ node, ...sizeOf(shown.get(node.id) ?? node) }))
        const right = Math.max(...sizes.map(({ node, w }) => node.position.x + w))
        const bottom = Math.max(...sizes.map(({ node, h }) => node.position.y + h))
        if (Number.isFinite(right) && Number.isFinite(bottom)) {
          ox = (right - left) / 2
          oy = (bottom - top) / 2
        }
      }
      const dx = at && Number.isFinite(left) ? Math.round(at.x - ox - left) : 28
      const dy = at && Number.isFinite(top) ? Math.round(at.y - oy - top) : 28
      const remap = new Map(source.nodes.map((node) => [node.id, newId(node.type)]))
      // A picture copied with its frame goes into the copy of the frame; one
      // copied without it is in whatever frame it lands in, if any.
      const inFrame = (data: Record<string, unknown>) => {
        const kept = freshCopy(data)
        const home = typeof kept.frame === 'string' ? remap.get(kept.frame) : undefined
        if (home) kept.frame = home
        else delete kept.frame
        return kept
      }
      const loose = new Set(
        source.nodes
          .filter((node) => node.type === 'image' && !remap.has(String(node.data?.frame ?? '')))
          .map((node) => remap.get(node.id)!),
      )
      setNodes((prev) =>
        settle(
          [
            ...prev.map((node) => ({ ...node, selected: false })),
            ...source.nodes.map(
              (node) =>
                ({
                  ...node,
                  id: remap.get(node.id)!,
                  position: { x: node.position.x + dx, y: node.position.y + dy },
                  selected: true,
                  // Deliberately unset, so it lands on whatever page is being
                  // looked at. Keeping the page it was copied from is how a paste
                  // goes somewhere you are not — and copying a card from one sheet
                  // to another is most of what pages are for. The board gives it
                  // the page being looked at before anything reads it.
                  page: currentPage,
                  data: inFrame(node.data),
                }) as Node,
            ),
          ],
          loose,
        ),
      )
      setEdges((prev) => [
        ...prev,
        ...source.edges.map((edge) =>
          wire({
            id: newId('edge'),
            source: remap.get(edge.source)!,
            target: remap.get(edge.target)!,
          }),
        ),
      ])
    }

    /**
     * A copy made on another board: its pictures are brought into this
     * project first. A board shows only its own project's files, so a file id
     * from another project would draw nothing. Library pictures need nothing.
     */
    const pasteFrom = async (clip: StoredClip, at?: { x: number; y: number }, centre = false) => {
      if (clip.projectId === projectId || !projectId) {
        paste(clip.doc, at, centre)
        return
      }
      const ids = [
        ...new Set(
          clip.doc.nodes
            .map((node) => (node.data as { fileId?: unknown }).fileId)
            .filter((id): id is string => typeof id === 'string' && id.length > 0),
        ),
      ]
      try {
        const moved = ids.length > 0 ? await copyProjectFiles(projectId, ids) : {}
        paste(
          {
            ...clip.doc,
            nodes: clip.doc.nodes.map((node) => {
              const id = (node.data as { fileId?: unknown }).fileId
              return typeof id === 'string' && moved[id]
                ? { ...node, data: { ...node.data, fileId: moved[id] } }
                : node
            }),
          },
          at,
          centre,
        )
      } catch (error) {
        console.error('could not bring the pictures over from the other board', error)
        onNote('Those pictures could not be brought over from the other board')
      }
    }

    const readClip = (token: string): StoredClip | null => {
      try {
        const clip = JSON.parse(window.localStorage.getItem(CLIP_KEY) ?? 'null') as StoredClip | null
        return clip && clip.token === token && Array.isArray(clip.doc?.nodes) ? clip : null
      } catch (error) {
        console.warn('the board clipboard could not be read', error)
        return null
      }
    }

    /**
     * Ctrl+C on the board: the marker onto the system clipboard.
     *
     * In the copy event rather than keydown, because only there can the
     * clipboard be written without asking the browser for permission.
     */
    const onCopy = (event: ClipboardEvent) => {
      if (typing() || textSelected() || !pendingCopy.current) return
      event.clipboardData?.setData('text/plain', CLIP_MARK + pendingCopy.current)
      event.preventDefault()
      pendingCopy.current = ''
    }

    /**
     * Ctrl+V, in one place.
     *
     * Both kinds of paste are decided here rather than one in keydown and one
     * here: keydown cannot see what is on the system clipboard, so a paste
     * meant for a screenshot would have put down whatever nodes were copied an
     * hour earlier, and then the picture as well.
     *
     * WHICH ONE WINS: the most recent copy, always (Huanrui, 2026-09-30).
     * There is no "the board's own copy first" any more; see below. Pictures
     * waiting from "Copy to project" are never pasted by Ctrl+V -- they are
     * offered by the prompt on the board and nowhere else.
     */
    const onPaste = (event: ClipboardEvent) => {
      if (typing()) return
      const images = [...(event.clipboardData?.items ?? [])]
        .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter((file): file is File => Boolean(file))

      const wantsPicture = pasteWanted.current === 'picture'
      pasteWanted.current = 'nodes'
      const text = event.clipboardData?.getData('text/plain') ?? ''

      /*
       * WHICH ONE WINS: WHATEVER WAS COPIED LAST.
       *
       * If the clipboard still holds a board's marker, nothing has been copied
       * since, and the nodes are meant -- from this board, or another one.
       * Anything else on the clipboard is newer than any board copy, and is
       * what gets pasted. Ctrl+Shift+V still asks for the picture on purpose.
       */
      // Its middle on the pointer, while the pointer is over the board.
      const here = pointerOnBoard() ?? undefined
      if (!wantsPicture && text.startsWith(CLIP_MARK)) {
        event.preventDefault()
        pasteMarker(text, here, true)
        return
      }
      if (images.length > 0) {
        event.preventDefault()
        void onImages(images)
        return
      }
      // Nothing on the system clipboard at all -- a browser that would not
      // take the marker. The copy made here is then the only thing it can be.
      if (!wantsPicture && !text && clipboard.current.nodes.length > 0) {
        event.preventDefault()
        paste(clipboard.current, here, true)
      }
    }

    /* The things the keyboard and the right-click menu both do. */

    /**
     * What is selected, and what a copy or a duplicate takes: the selection,
     * and everything in a selected frame, which goes with its frame as in Miro.
     */
    const selection = () => {
      const selected = nodes.filter((node) => node.selected)
      const selectedFrames = new Set(selected.filter(isFrame).map((node) => node.id))
      const taken = [
        ...selected,
        ...nodes.filter((node) => !node.selected && selectedFrames.has(frameOf(node))),
      ]
      return { selected, selectedFrames, taken }
    }

    const removeSelected = (): boolean => {
      const { selected } = selection()
      // A wire is a thing on the board too. Click one and press Delete and
      // it should go — it was only ever nodes here, so a wire drawn to the
      // wrong skill could be drawn over but never taken away.
      const cutEdges = new Set(edges.filter((edge) => edge.selected).map((edge) => edge.id))
      if (selected.length === 0 && cutEdges.size === 0) return false
      remember(true)
      const gone = new Set(selected.map((node) => node.id))
      // A frame deleted leaves its pictures where they are, selected, and in
      // no frame -- what Miro does (Huanrui, 2026-09-30).
      const freed = new Set(
        nodes.filter((node) => gone.has(frameOf(node)) && !gone.has(node.id)).map((node) => node.id),
      )
      setNodes((prev) =>
        prev
          .filter((node) => !gone.has(node.id))
          .map((node) => {
            if (!freed.has(node.id)) return node
            const data = { ...(node.data as Record<string, unknown>) }
            delete data.frame
            return { ...node, selected: true, data }
          }),
      )
      setEdges((prev) =>
        prev.filter((e) => !cutEdges.has(e.id) && !gone.has(e.source) && !gone.has(e.target)),
      )
      return true
    }

    const selectAll = () => setNodes((prev) => prev.map((node) => ({ ...node, selected: true })))

    /**
     * Copy what is selected: the nodes into this browser's storage, a marker
     * onto the system clipboard.
     *
     * From the keyboard the marker also goes in through the copy event that
     * follows (`pendingCopy`). From the menu there is no copy event, so only
     * the direct write -- allowed because it answers a click.
     */
    const copySelected = (fromKey: boolean): boolean => {
      const { selected, taken } = selection()
      if (selected.length === 0) return false
      const ids = new Set(taken.map((node) => node.id))
      clipboard.current = {
        nodes: toDocument(taken, [], pages).nodes,
        // Wires between copied things come along; wires to anything else do
        // not, or a paste would quietly attach itself to the original.
        edges: toDocument([], edges, pages).edges.filter((e) => ids.has(e.source) && ids.has(e.target)),
      }
      const token = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
      clipToken.current = token
      if (fromKey) pendingCopy.current = token
      // Belt and braces: written directly as well, for a browser that does
      // not fire a copy event with nothing selected. Allowed without asking
      // inside a key press or a click; a refusal leaves the copy event to do it.
      void navigator.clipboard?.writeText?.(CLIP_MARK + token).catch((error: unknown) => {
        console.warn('the board copy marker could not be written directly', error)
      })
      try {
        window.localStorage.setItem(
          CLIP_KEY,
          JSON.stringify({ token, projectId, doc: clipboard.current } satisfies StoredClip),
        )
      } catch (error) {
        // This board can still paste its own copy; only other boards miss out.
        console.warn('the board clipboard could not be stored', error)
      }
      if (!fromKey) onNote(taken.length === 1 ? 'Copied' : `${taken.length} copied`)
      return true
    }

    /**
     * Cut (Huanrui, 2026-11-02): copied as Ctrl+C copies, then taken off the
     * board -- a frame with its pictures, and the wires to anything that
     * goes. One step on the undo stack. From the keyboard the marker goes in
     * through the cut event that follows, as a copy's does (onCopy).
     */
    const cutSelected = (fromKey: boolean): boolean => {
      const { taken } = selection()
      if (taken.length === 0 || !copySelected(fromKey)) return false
      remember(true)
      const gone = new Set(taken.map((node) => node.id))
      setNodes((prev) => prev.filter((node) => !gone.has(node.id)))
      setEdges((prev) => prev.filter((edge) => !gone.has(edge.source) && !gone.has(edge.target)))
      if (!fromKey) onNote(taken.length === 1 ? 'Cut' : `${taken.length} cut`)
      return true
    }

    const duplicateSelected = (): boolean => {
      const { taken } = selection()
      const ids = new Set(taken.map((n) => n.id))
      const source = {
        nodes: toDocument(taken, [], pages).nodes,
        edges: toDocument([], edges, pages).edges.filter((e) => ids.has(e.source) && ids.has(e.target)),
      }
      if (source.nodes.length === 0) return false
      paste(source)
      return true
    }

    /** A board copy's marker, found on the clipboard: put those nodes down. */
    const pasteMarker = (text: string, at?: { x: number; y: number }, centre = false) => {
      const token = text.slice(CLIP_MARK.length)
      if (token === clipToken.current && clipboard.current.nodes.length > 0) {
        paste(clipboard.current, at, centre)
        return
      }
      const clip = readClip(token)
      if (clip) void pasteFrom(clip, at, centre)
      else onNote('That copy is no longer available -- copy it again')
    }

    /**
     * Paste from the menu, where it was opened.
     *
     * There is no paste event to read from a click, so the clipboard is read
     * directly -- the browser asks once whether this site may. Whatever was
     * copied last wins, as with Ctrl+V.
     */
    const pasteAt = async (at: { x: number; y: number }) => {
      try {
        const items = await navigator.clipboard.read()
        for (const item of items) {
          if (!item.types.includes('text/plain')) continue
          const text = await (await item.getType('text/plain')).text()
          if (text.startsWith(CLIP_MARK)) {
            pasteMarker(text, at)
            return
          }
        }
        const files: File[] = []
        for (const item of items) {
          for (const type of item.types) {
            if (!type.startsWith('image/')) continue
            const blob = await item.getType(type)
            files.push(new File([blob], `pasted.${type.split('/')[1] || 'png'}`, { type }))
          }
        }
        if (files.length > 0) {
          await onImages(files)
          return
        }
        onNote('Nothing on the clipboard that can go on a board')
      } catch (error) {
        console.warn('the clipboard could not be read from the menu', error)
        onNote('This browser would not let the board read the clipboard -- use Ctrl+V')
      }
    }

    actions.current = {
      copy: () => void copySelected(false),
      cut: () => void cutSelected(false),
      paste: (at) => void pasteAt(at),
      duplicate: () => void duplicateSelected(),
      remove: () => void removeSelected(),
      selectAll,
    }

    const down = (event: KeyboardEvent) => {
      // Read here because the paste event that follows does not carry the
      // modifiers, and keydown always arrives first.
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v') {
        pasteWanted.current = event.shiftKey ? 'picture' : 'nodes'
      }
      if (event.code === 'Space' && !typing()) {
        // Otherwise space also scrolls the page and presses whatever button
        // happens to have focus — including Run.
        event.preventDefault()
        onSpace(true)
        return
      }
      if (typing()) return

      const meta = event.ctrlKey || event.metaKey
      const { selected, selectedFrames } = selection()

      if (!meta && (event.key === 'Delete' || event.key === 'Backspace')) {
        if (removeSelected()) event.preventDefault()
        return
      }

      if (meta && event.key.toLowerCase() === 'a') {
        event.preventDefault()
        selectAll()
        return
      }

      if (meta && event.key.toLowerCase() === 'z') {
        event.preventDefault()
        const stack = event.shiftKey ? future : past
        const other = event.shiftKey ? past : future
        const step = stack.current.pop()
        if (!step) return
        other.current = [...other.current, stamp(toDocument(nodes, edges, pages))].slice(-HISTORY)
        restore(keepRuns(step))
        return
      }

      if (meta && event.shiftKey && event.key.toLowerCase() === 'c') {
        // Tested before plain Ctrl+C: `key` is 'C' with Shift held, which
        // lowercases to the same letter, so the plain branch would swallow it.
        event.preventDefault()
        onCopyImage()
        return
      }

      if (meta && event.key.toLowerCase() === 'c') {
        // Text selected anywhere on the page: the browser copies that, as text.
        if (textSelected()) return
        // Not prevented: the copy event that follows puts the marker on the
        // system clipboard, and the nodes go where another board can read them.
        copySelected(true)
        return
      }

      if (meta && event.key.toLowerCase() === 'x') {
        // Text selected anywhere on the page: the browser cuts or copies that.
        if (textSelected()) return
        // Not prevented, as Ctrl+C: the cut event that follows carries the marker.
        cutSelected(true)
        return
      }

      if (meta && event.key.toLowerCase() === 'd') {
        if (duplicateSelected()) event.preventDefault()
        return
      }

      if (meta && (event.key === '0' || event.key === '1')) {
        event.preventDefault()
        if (event.key === '0') fitView()
        else zoomTo()
        return
      }

      if (event.key.startsWith('Arrow')) {
        if (selected.length === 0) return
        event.preventDefault()
        const step = event.shiftKey ? 10 : 1
        const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0
        const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0
        setNodes((prev) =>
          prev.map((node) =>
            // A nudged frame takes its pictures with it, as a dragged one does.
            node.selected || selectedFrames.has(frameOf(node))
              ? { ...node, position: { x: node.position.x + dx, y: node.position.y + dy } }
              : node,
          ),
        )
      }
    }

    const up = (event: KeyboardEvent) => {
      if (event.code === 'Space') onSpace(false)
    }

    // Where the last press landed: on the board, its copy is the cards'.
    const press = (event: PointerEvent) => {
      pressedOnBoard = event.target instanceof Element && event.target.closest('.ccanvas') !== null
    }

    window.addEventListener('pointerdown', press, true)
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    window.addEventListener('paste', onPaste)
    window.addEventListener('copy', onCopy)
    window.addEventListener('cut', onCopy)
    return () => {
      window.removeEventListener('pointerdown', press, true)
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
      window.removeEventListener('paste', onPaste)
      window.removeEventListener('copy', onCopy)
      window.removeEventListener('cut', onCopy)
    }
  }, [
    projectId,
    currentPage,
    actions,
    onNote,
    nodes,
    edges,
    pages,
    setNodes,
    setEdges,
    past,
    future,
    applying,
    stamp,
    keepRuns,
    onSpace,
    remember,
    pointerOnBoard,
    onImages,
    onCopyImage,
    fitView,
    zoomTo,
  ])

  return null
}
